import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Mic,
  Home,
  ListChecks,
  Stethoscope,
  MessageSquareText,
  MonitorPlay,
  Pencil,
  Paintbrush,
  Play,
  Lock,
  AlertTriangle,
  ExternalLink,
  WandSparkles,
  Tv,
  ChevronDown,
  Github,
} from 'lucide-react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useIpcQuery, useIpcMutation } from '../hooks/useIpc';
import {
  INITIAL_PROMPT_MESSAGE_ID_PREFIX,
  type AgentAskUserQuestionPush,
  type AgentSendAttachment,
  type AgentStreamChunk,
  type GameFileEntry,
  type GameStatusBadge,
  type GitHubRepoAccessResult,
  type InlineCheckpoint,
  type ProtoHubSource,
} from '@volley/crucible-ipc-types/ipc-channels';
import type { DesignBuildPlanSeed } from '@volley/crucible-ipc-types';
import { LoadingOverlay } from '../components/LoadingOverlay';
import { AttachmentThumbnail } from '../components/AttachmentThumbnail';
import { AttachmentRedactionInfo } from '../components/AttachmentRedactionInfo';
import {
  ScaffoldFirstGenreChip,
  shouldShowGenreChip,
  isScaffoldFirstInference,
} from '../components/ScaffoldFirstGenreChip';
import {
  readPersistedScaffoldFirst,
  pickDurableGenreInference,
  readCreatePrompt,
} from '../components/scaffold-first-storage';
import { buildRecastRouteState } from './NewGame';
import type { ScaffoldFirstInference } from '@volley/crucible/core/index.js';
import { PHASE_STYLE } from '../components/GameStatusPanel';
import { TemplateUpdateBanner } from '../components/TemplateUpdateBanner';
import { AutoSeededToast } from '../components/AutoSeededToast';
import {
  UpdateTemplateModal,
  synthesiseConflictResolutionPrompt,
} from '../components/UpdateTemplateModal';
import { CrucibleErrorState } from '../components/CrucibleErrorState';
import { ReviewAnswersModal } from '../components/ReviewAnswersModal';
import { DesignWorkspace } from './design/DesignWorkspace';
import { BugDraftCard } from '../components/BugDraftCard';
import { ConflictResolutionCard } from '../components/ConflictResolutionCard';
import { DeployFailureCard } from '../components/DeployFailureCard';
import { useDeployFailure } from '../hooks/useDeployFailure';
import { unflattenIpcError, type SerialisedError } from '@volley/crucible-ipc-types/error-types';
import { initialChatState, type ChatAction, type ChatEffect } from '../chat/state';
import { resizeImageFile } from '../chat/resize-image';
import { isSupportedTileUploadFile, prepareTileImageUpload } from '../tile-image-upload';
import { useChatReducer } from '../chat/use-chat-reducer';
import type { ChatMessage, MessageBlock, ToolCallEntry } from '../chat/types';
import {
  projectAgentHistoryToChatMessages,
  mergeHydrationMessages,
  type AgentHistoryPageLike,
} from '../chat/project-agent-history';
import { parseMarkdownQuestion } from '../chat/markdown-question';
import { ArtifactPane } from '../components/ArtifactPane';
import { BulkInterviewCard, type BulkInterviewCardRef } from '../components/BulkInterviewCard';
import { ChatErrorBoundary, ChatSurfaceContext } from '../components/ChatErrorBoundary';
import { DependencyApprovalModal } from '../components/DependencyApprovalModal';
import { InterviewSlotOverlay } from '../components/InterviewSlotOverlay';
import type { BulkInterviewQuestion } from '@volley/crucible-ipc-types/ipc-channels';
import type { PreviewPhaseGate } from '../components/PreviewLaunchButton';
import { PreviewOverlay } from '../components/PreviewOverlay';
import { WorkspaceRightPanel } from '../components/WorkspaceRightPanel';
import { DiagnosticsTab } from '../components/DiagnosticsTab';
import {
  isCloudPreviewSession,
  isPairedPreviewActive,
  previewDisplayUrl,
  previewPhoneControlsEnabled,
  usePreviewSession,
} from '../hooks/usePreviewSession';
import { usePreviewConstructionBridge } from '../hooks/usePreviewConstructionBridge';
import { useInstallStateForGame } from '../hooks/useInstallStateForGame';
import { InstallStateChip } from '../components/InstallStateChip';
import { PhaseStrip } from '../components/PhaseStrip';
import { BuildPartialBanner } from '../components/BuildPartialBanner';
import { usePhaseProgress } from '../hooks/usePhaseProgress';
import { useFoundryGameStateSnapshot } from '../hooks/useFoundryGameStateSnapshot';
import { useInterviewStatus, useStartOverInterview } from '../hooks/useBuildGameInterview';
import { usePhaseEventsStore } from '../stores/phase-events';
import { useInterviewState } from '../hooks/useBuildGameInterview';
import { PlanModeView } from '../components/PlanModeView';
import { PlanWorkspace } from '../components/PlanWorkspace';
import { CentreColumnWithBanner } from '../components/CentreColumnWithBanner';
import { SplitLayout } from '../components/SplitLayout';
import { Tooltip } from '../components/ui/tooltip';
import { BackToPlanConfirmModal } from '../components/BackToPlanConfirmModal';
import { ActivityCheckpointMessage } from '../components/ActivityCheckpointMessage';
import { AgentActivityPill } from '../components/AgentActivityPill';
import { VmStatusPill } from '../components/VmStatusPill';
import { FurnaceBuildIndicator } from '../components/FurnaceBuildIndicator';
import { FoundryShellHeader } from '../components/FoundryShellHeader';
import { FurnaceErrorBanner } from '../components/FurnaceErrorBanner';
import { VmDebugPane } from '../components/VmDebugPane';
import { CraftView } from '../components/CraftView';
import { ProtoHubSimulatorSurface } from '../components/ProtoHubSimulatorSurface';
import { useMaintainerMode } from '../hooks/useMaintainerMode';
import { useActivitySnapshot } from '../hooks/useActivitySnapshot';
import { useGameStatusLite } from '../hooks/useGameStatusLite';
import { useFeatureFlags, type FeatureFlagsState } from '../hooks/useFeatureFlags';
import { useAppFrame } from '../AppFrameContext';
import { useClientCapabilities } from '../transport/context';
import { useHostBridge } from '../transport/host-bridge';
import { canDisplayOnHub, canPublishRun } from '@volley/crucible-client';
import { useVmConnectionState } from '../hooks/useVmConnectionState';
import { computeFurnaceBuildState, type FurnaceBuildState } from '../workspace/furnace-build-state';
import {
  applyPreviewPhoneCapOverride,
  derivePreviewControllerPolicy,
  HARD_PREVIEW_PHONE_CAP,
} from '../workspace/preview-controller-policy';
import {
  classifyFurnaceError,
  scrubInfrastructureUrls,
  scrubSerialisedError,
  type FurnaceErrorView,
} from '../workspace/furnace-error-mapping';
import { useInterviewStatePoll } from '../hooks/useInterviewStatePoll';
import type {
  ActivitySnapshot,
  PhaseProgressUpdate,
  PublishProgress,
} from '@volley/crucible-ipc-types/ipc-channels';
import type {
  BuildGameInterviewState,
  PhaseBDecision,
} from '@volley/crucible-ipc-types/build-game-types';
import {
  getEffectiveInterviewDepth,
  getEffectiveInterviewSubPhase,
  getEffectivePlan,
} from '@volley/crucible-ipc-types/build-game-types';
import { savedPlanMatchesBuilt } from '../components/plan-state';
import {
  deriveWorkspaceRunState,
  isCompletionVerificationRetryRunState,
  resolvePendingPhaseBDecision,
  type BuildKickoffWatchdogState,
  type InitialKickoffFailureReason,
  type InitialKickoffState,
  type InitialKickoffStatus,
  type WorkspaceRunKind,
  type WorkspaceRunState,
} from '../workspace/run-state';
import {
  applyCreateInitialPromptFallback,
  readCreateHandoffModeFromRouteState,
  readCreateInitialPromptFromRouteState,
} from './create-handoff';
import {
  hydrationBackoffMs,
  shouldRetryHydration,
  HYDRATION_PREPARING_THRESHOLD_MS,
} from './chat-hydration-retry';
import { derivePreviewDevLoopState, type PreviewDevLoopState } from '../workspace/preview-state';
import { shouldAutoOpenPreview } from '../workspace/auto-open-preview';
import {
  deriveDeployActionState,
  type DeployActionState,
  type DeployState,
} from '../workspace/deploy-action-state';
import {
  derivePreviewLaunchPillState,
  initialWorkspaceModeForHandoff,
  mergePreviewDisabledReason,
  previewAvailableTitleForDevLoop,
  previewHandoffDisabledReasonForHandoff,
  showNewUiBuildViewFor,
} from '../workspace/plan-build-handoff';

type AgentSendEffect = Extract<ChatEffect, { type: 'ipc/agent-send' }>;
type WorkspaceMode = 'chat' | 'plan' | 'preview' | 'design' | 'protohub';

/**
 * Default width (px) of the chat panel in the side-by-side Develop
 * layout — matched to the right side panel (`w-96` = 384px). The preview
 * pane flexes to fill the rest. Drag-adjustable + persisted per-game.
 */
const DEFAULT_LIVE_PREVIEW_CHAT_WIDTH = 384;
type WorkspaceHeaderMode = WorkspaceMode | 'craft';
interface CloneDeployAccessWarning extends GitHubRepoAccessResult {
  repoFullName: string;
}

const FAST_BUILD_PLAN_BRIDGE_TIMEOUT_MS = 90_000;

/**
 * Selects the Workspace handler that should back the Now-summary resume CTA.
 *
 * Args:
 *   runState: The aggregate run state currently shown by the workspace.
 *
 * Returns:
 *   The resume action kind to invoke, or null when no primary resume action exists.
 *
 * Raises:
 *   Never.
 */
export function resumeBuildActionKindFromRunState(
  runState: WorkspaceRunState,
): 'start-building' | 'retry-auto-build' | 'resume-stalled-build' | 'rebuild-from-plan' | null {
  if (runState.kind !== 'needs-attention' || runState.primaryCta !== 'retry') return null;

  if (isCompletionVerificationRetryRunState(runState)) {
    return 'rebuild-from-plan';
  }

  if (runState.debugSource.endsWith(':stale-build')) {
    return 'resume-stalled-build';
  }

  // Stale final-review states recover through the same build kickoff as watchdog stalls.
  if (
    runState.debugSource.includes('watchdog') ||
    runState.debugSource.endsWith(':stale-final-review')
  ) {
    return 'start-building';
  }

  // Auto-build failures retry the auto-pipeline setup instead of re-sending kickoff.
  return runState.debugSource.startsWith('auto-build:') ? 'retry-auto-build' : null;
}

/**
 * Checks whether a publish progress push belongs to the active workspace route.
 *
 * Args:
 *   event: Publish progress payload delivered by the preload bridge.
 *   workspaceGameId: Game id from the current workspace route.
 *
 * Returns:
 *   True when the payload is for the active workspace; otherwise false.
 *
 * Raises:
 *   Never.
 */
export function shouldHandleWorkspacePublishProgress(
  event: Pick<PublishProgress, 'gameId'>,
  workspaceGameId: string | undefined,
): boolean {
  // Push events can outlive route changes; only the active game may update deploy UI.
  return !!workspaceGameId && event.gameId === workspaceGameId;
}

function phaseProgressInImplementation(progress: PhaseProgressUpdate | null | undefined): boolean {
  return (
    progress?.kind === 'derived' && progress.derived.current > 2 && progress.derived.current < 6
  );
}

function phaseProgressStartedAtMs(progress: PhaseProgressUpdate | null | undefined): number | null {
  if (progress?.kind !== 'derived') return null;
  const current = progress.derived.current;
  const transition = [...progress.derived.transitions]
    .reverse()
    .find((entry) => entry.phase === current);
  if (!transition?.at) return null;
  const parsed = Date.parse(transition.at);
  return Number.isFinite(parsed) ? parsed : null;
}

export function stalledBuildResumeAttemptKey(
  gameId: string | undefined,
  progress: PhaseProgressUpdate | null | undefined,
): string | null {
  if (!gameId || !phaseProgressInImplementation(progress) || progress?.kind !== 'derived') {
    return null;
  }
  return `${gameId}:${progress.derived.current}:${phaseProgressStartedAtMs(progress) ?? 'unknown'}`;
}

export function buildStalledBuildResumePrompt(
  progress: PhaseProgressUpdate | null | undefined,
): string {
  if (progress?.kind !== 'derived') {
    return [
      'Resume the stalled build.',
      'Inspect the current repo state and docs/build-progress.json first, preserve completed work, and continue implementing toward the existing Build Plan.',
      'If you cannot safely continue, explain what needs attention instead of restarting from scratch.',
    ].join(' ');
  }
  const phaseLabel = progress.derived.label || `phase ${progress.derived.current}`;
  const subPhaseText = progress.derived.subPhase ? ` (${progress.derived.subPhase})` : '';
  return [
    `Resume the stalled build from ${phaseLabel}${subPhaseText}.`,
    'Inspect the current repo state and docs/build-progress.json first, preserve completed work, and continue implementing toward the existing Build Plan.',
    'If you cannot safely continue, explain what needs attention instead of restarting from scratch.',
  ].join(' ');
}

/**
 * Issue #350 — chat-input text dispatched by `handleStartExpressInterview`.
 *
 * After PR G (hello-weekend v0.14.0) replaced Phase A's chat-driven
 * SKILL with the FSM engine, the legacy `/build-game --express` flag
 * was retired (the SKILL no longer recognises it; view mode is now a
 * renderer-only concern). The express handler dispatches the plain
 * `/build-game` kickoff and flips `interviewViewMode` to `'stacked'`
 * before the engine pushes its first slot.
 *
 * Exported so the express-toggle visibility test can pin the literal
 * — a future copy-edit re-introducing `--express` would silently dead-
 * end against a SKILL that ignores the flag, and the user's stacked
 * view would never land. This constant is the contract.
 */
export const EXPRESS_KICKOFF_TEXT = '/build-game';

/**
 * Issue #385 (NIT 2) — muscle-memory `--express` typed input detection.
 *
 * A power user who learned the v0.13 `/build-game --express` shortcut
 * may type the flag out manually instead of clicking the speed-run
 * button. The SKILL ignores the flag (the engine kicks in regardless),
 * but the renderer's `interviewViewMode` defaults to `'paginated'` —
 * so the typed-flag path lands on the one-card-at-a-time UX even
 * though the user explicitly asked for the stacked layout.
 *
 * This predicate normalises the typed text and returns `true` when
 * the user typed something that begins with `/build-game` AND
 * carries the `--express` flag as a whitespace-delimited token. We
 * deliberately match a whole-word `--express` (not a substring) so a
 * future flag like `--express-mode` or `--no-express` can't be
 * misinterpreted as the speed-run intent. Trailing extra text after
 * the flag (e.g. `/build-game --express racing game`) still trips
 * the predicate — the user opted into stacked, anything they tacked
 * on is just pitch text.
 *
 * Pure + exported so the visibility test can pin the contract.
 */
export function isExpressKickoffText(raw: string): boolean {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('/build-game')) return false;
  // Whole-word `--express` token. The flag must be surrounded by
  // whitespace (or sit at the end of the string); we explicitly
  // reject suffixed variants like `--express-mode` so a future
  // unrelated flag can't accidentally route to stacked mode.
  return /(^|\s)--express(\s|$)/.test(trimmed);
}

export interface InitialKickoffRendererReadySignals {
  tokensResolved: boolean;
  hasAnthropicKey: boolean;
  flagsLoaded: boolean;
  interviewEngineV1: boolean;
  interviewRendererReady: boolean;
}

export function shouldHoldInitialKickoffForRendererReady({
  tokensResolved,
  hasAnthropicKey,
  flagsLoaded,
  interviewEngineV1,
  interviewRendererReady,
}: InitialKickoffRendererReadySignals): boolean {
  if (!tokensResolved) return true;
  if (!hasAnthropicKey) return false;
  if (!flagsLoaded) return true;
  if (!interviewEngineV1) return false;
  return !interviewRendererReady;
}

/** Wall-clock budget for renderer-ready initial kickoff (Workspace timeout effect). */
export const INITIAL_KICKOFF_TIMEOUT_MS = 15_000;

const ACTIVE_INITIAL_KICKOFF_STATUSES = new Set<InitialKickoffStatus>([
  'waiting-for-renderer-ready',
  'dispatching',
]);

/** Whether kickoff is still in a pre-dispatch phase that shares one timeout window. */
export function isActiveInitialKickoffStatus(status: InitialKickoffStatus): boolean {
  return ACTIVE_INITIAL_KICKOFF_STATUSES.has(status);
}

/**
 * Stable key for the kickoff timeout effect: `startedAt` while active, else `null`.
 *
 * Status transitions among active phases create new `initialKickoff` object refs but
 * must not reset the 15s window — only a new `startedAt` (or leaving active) should.
 */
export function initialKickoffTimeoutStartedAt(kickoff: InitialKickoffState): number | null {
  if (!isActiveInitialKickoffStatus(kickoff.status)) return null;
  return kickoff.startedAt ?? null;
}

/** Remaining ms until kickoff should time out; clamps at zero for overdue `startedAt`. */
export function initialKickoffTimeoutDelayMs(
  startedAt: number,
  nowMs: number = Date.now(),
): number {
  return Math.max(0, INITIAL_KICKOFF_TIMEOUT_MS - (nowMs - startedAt));
}

/**
 * Issue #461 Part 2 — decision helper for the speed-run click.
 *
 * Background. The Round-1 fix dropped the `isAiTyping` clause from
 * `shouldShowExpressToggle` so the toggle stays visible during Phase A
 * (the FSM engine holds `isAiTyping=true` for every slot card). Codex
 * correctly flagged the follow-on problem: with the toggle now
 * reachable mid-turn, the legacy click handler's `user/type` +
 * `user/send` dispatch would land on the renderer reducer's
 * text-only-mid-turn branch (`chat/state.ts:491-522`), QUEUE the
 * `/build-game` text rather than fire it immediately, and replay it
 * AFTER the engine's Phase B SDK turn streams `done` — which collides
 * with the bouncer or kicks a fresh `/build-game` against a game
 * already built. Neither is what the user clicked Speed Run for.
 *
 * Cooperative resolution: split the click into two intents.
 *
 *   - `'dispatch'` — `isAiTyping=false`: no turn in flight. Behaviour
 *     matches the pre-fix path: flip the view mode AND dispatch the
 *     `/build-game` kickoff so the engine starts under the stacked
 *     overlay layout. Used by the empty-state prominent toggle and
 *     by the persistent composer icon when the chat is quiet.
 *   - `'mode-only'` — `isAiTyping=true`: a turn is already running.
 *     The click flips the renderer's `interviewViewMode` to
 *     `'stacked'`; the mounted `<InterviewSlotOverlay>`'s
 *     `useEffect([initialViewMode])` (`InterviewSlotOverlay.tsx`
 *     lines 285-287) picks the change up mid-flight and repaints
 *     with the stacked history pane. No dispatch fires, so no queue
 *     entry accumulates and no future bouncer collision is possible.
 *     If no overlay is mounted (Phase B SDK turn, or a non-build
 *     follow-up), the flip is stored for the next `/build-game`
 *     invocation and the click reads as a no-op visually — strictly
 *     safer than queueing a duplicate dispatch.
 *
 * Pure + exported so the unit test pins the contract without spinning
 * up the full Workspace render.
 */
export type ExpressClickIntent = 'dispatch' | 'mode-only' | 'yolo-remaining';

/**
 * Issue #464 — distinguish the mid-interview YOLO action from the
 * cooperative `mode-only` branch.
 *
 * The `mode-only` branch from PR #463 was a placeholder: it flipped
 * the view mode and returned, doing nothing user-perceptible when an
 * `<InterviewSlotOverlay>` was actively waiting for an answer in
 * paginated mode (one card at a time). The YOLO branch is the actual
 * fulfilment of the user's original "I've had enough — finish the
 * rest for me" ask on #461.
 *
 * Decision:
 *   - `isAiTyping=false`                       → `'dispatch'`
 *     (no turn in flight; flip viewmode + fire `/build-game`)
 *   - `isAiTyping=true`, `interviewActive=true` → `'yolo-remaining'`
 *     (slot card showing; invoke `interview:yolo-remaining` IPC)
 *   - `isAiTyping=true`, `interviewActive=false` → `'mode-only'`
 *     (Phase B / unrelated SDK turn — keep PR #463's cooperative
 *     viewmode flip so the NEXT overlay opens in stacked mode)
 *
 * `interviewActive` reflects whether the overlay is currently
 * showing a slot card (the renderer subscribes to `interview:show-
 * slot` and tracks the active-card state). If we don't know one way
 * or the other (the renderer hasn't yet wired the active-card
 * snapshot through), default to FALSE so we keep PR #463's
 * cooperative mode-only behaviour rather than firing the yolo IPC
 * into a void.
 */
export function decideExpressClickIntent(args: {
  isAiTyping: boolean;
  interviewActive?: boolean;
}): ExpressClickIntent {
  if (!args.isAiTyping) return 'dispatch';
  return args.interviewActive ? 'yolo-remaining' : 'mode-only';
}

/**
 * Codex Round-3 YELLOW 1 on PR #463 (#461 follow-up).
 *
 * Background. The cooperative `'mode-only'` branch in
 * `handleStartExpressInterview` flips `interviewViewMode` to `'stacked'`
 * and returns. If an `<InterviewSlotOverlay>` is mounted, its
 * `useEffect([initialViewMode])` repaints. But if NO overlay is mounted
 * at click time (e.g. Phase B SDK turn, or an unrelated `AskUserQuestion`
 * with no interview running), the flip is silently latched on the
 * renderer-side `useState` cell. The two existing reset triggers — the
 * `gameId`-change effect and the `interview:complete` push subscription
 * (Workspace.tsx ~775-789) — cannot clear it:
 *
 *   - `gameId` only changes when the user navigates between games. A
 *     non-build mode-only click during the SAME game leaves the latch
 *     in place.
 *   - `interview:complete` only fires when an interview actually ran.
 *     A mode-only click during a turn that never started an interview
 *     never sees a complete push.
 *
 * Concrete repro: click the icon during any Phase-B AI turn, wait for
 * the turn to finish, type a plain `/build-game` and press Send. The
 * next overlay opens with `data-view-mode="stacked"` even though the
 * user did NOT opt in for THIS build.
 *
 * Closure. `handleSendMessage` is the choke point where the user
 * explicitly initiates a fresh build via Send. At that point we know
 * the intent for THIS send:
 *
 *   - `/build-game --express` (typed-flag muscle memory) → stacked
 *     (existing `isExpressKickoffText` branch).
 *   - `/build-game` (plain) → paginated. THIS is the reset path the
 *     latched-stacked leak was missing.
 *   - anything else → leave the latch alone (Send of an answer / chat
 *     message doesn't redefine the next overlay's intent).
 *
 * This helper folds the three cases into a single decision so
 * `handleSendMessage` reads as `decideSendModeReset(...) → setMode(...)`
 * — the same shape as `decideExpressClickIntent`.
 *
 * Pure + exported so the unit test pins the contract.
 */
export function decideSendModeReset(args: { chatInput: string }): 'stacked' | 'paginated' | null {
  if (isExpressKickoffText(args.chatInput)) return 'stacked';
  if (args.chatInput.trim().startsWith('/build-game')) return 'paginated';
  return null;
}

/**
 * Codex Round-3 YELLOW 3 on PR #463 (#461 follow-up).
 *
 * Pure body of `handleStartExpressInterview`. Production calls THIS
 * helper from the `useCallback`; the integration test in
 * `Workspace.express-click-real-reducer.test.ts` calls it directly
 * against the real `reduceChatState` reducer. The Round-2 mirror
 * version of that test copied the callback's branching shape into the
 * test body, which meant a future refactor that removed the
 * `mode-only` short-circuit could keep the mirror test green while
 * breaking production. With the helper extracted, the test runs the
 * EXACT code production runs — an unconditional dispatch in
 * `handleStartExpressInterview` (or in this helper) trips the test.
 *
 * The helper deliberately accepts narrow types (no React refs, no
 * `ChatReducerHandle`) so it can be called from a test harness without
 * spinning up the hook. The `setInterviewViewMode` setter is the
 * Workspace-owned `useState` setter; `getChatState` is the
 * `useChatReducer.getState` ref-fresh reader (closes the chunk/done
 * tick race — see `use-chat-reducer.ts:62`).
 */
export function applyExpressClick(args: {
  setInterviewViewMode: (mode: 'stacked') => void;
  getChatState: () => { readonly isAiTyping: boolean };
  chatDispatch: (action: ChatAction) => void;
  /** Issue #464 — true when an `<InterviewSlotOverlay>` is currently
   *  rendering a slot card. Drives the `'yolo-remaining'` intent. The
   *  Workspace passes the rendered active-card snapshot through; the
   *  pure helper test injects its own boolean. */
  isInterviewActive?: boolean;
  /** Issue #464 — invokes the `interview:yolo-remaining` IPC. The
   *  Workspace passes a `useIpcMutation` wrapper; the pure test
   *  injects a vi.fn(). Optional so callers that pre-date #464 (or
   *  unit tests pinning only `mode-only` / `dispatch`) don't have to
   *  thread it. When the intent resolves to `'yolo-remaining'` but
   *  no invoker is supplied, we degrade to `'mode-only'` semantics
   *  so the user still sees the viewmode flip rather than nothing. */
  invokeYoloRemaining?: () => void;
}): void {
  args.setInterviewViewMode('stacked');
  const intent = decideExpressClickIntent({
    isAiTyping: args.getChatState().isAiTyping,
    interviewActive: args.isInterviewActive,
  });
  if (intent === 'yolo-remaining') {
    // Issue #464 — fire the YOLO IPC. Main reads the slot plan +
    // state, decides per-slot auto-answers, installs a gate policy
    // for subsequent slots, and resolves the currently-pending slot
    // so the engine drains to dispatch. If main reports blockers it
    // also pushes `interview:yolo-blocked` and the overlay surfaces
    // the blocker list — the renderer doesn't need to handle the
    // response here.
    if (args.invokeYoloRemaining) {
      args.invokeYoloRemaining();
      return;
    }
    // Fallback: no invoker wired (test or pre-#464 caller). Drop
    // through to mode-only so the user sees the viewmode flip and
    // can still click into stacked layout — strictly better than
    // a silent no-op.
    return;
  }
  if (intent === 'mode-only') {
    // Mid-turn cooperative path (no active interview slot — Phase B
    // SDK turn, unrelated AskUserQuestion, etc.). The overlay (if
    // mounted) reacts to the prop change via its `initialViewMode`
    // effect; if no overlay is mounted, the flip is stored for the
    // next `/build-game` invocation and the click reads as a no-op
    // visually — which is the right outcome compared to a queued
    // duplicate `/build-game` that would replay later. YELLOW 1's
    // `decideSendModeReset` in `handleSendMessage` also clears the
    // latch on the next plain `/build-game` so a stale mode-only
    // click can't bleed into an unrelated build.
    return;
  }
  args.chatDispatch({ type: 'user/type', text: EXPRESS_KICKOFF_TEXT });
  args.chatDispatch({ type: 'user/send' });
}

// ---------------------------------------------------------------------------
// #191 quick-batch — pure decision helpers exported so the integration
// test can pin the gate behaviour without mounting the full Workspace
// (which would need react-query, react-router, IPC stubs, etc.).
//
// `showPlanModeFor` decides whether the centre column renders
// `<PlanModeView>` instead of `<ChatPanel>`. The gate is conservative:
// only quick-depth interviews with a matching subPhase value flip the
// surface — pre-#191 games (no `depth` field) AND full-depth games
// AND build-phase games all fall through to the normal chat panel.
//
// #826 Defect C fix: the gate also requires plan content to be present
// (`plan != null && plan.trim().length > 0`). Without this guard the
// plan-mode view mounted as soon as the SKILL set subPhase='plan' —
// which fires when the agent STARTS drafting, before any plan text
// exists. The result was an empty "Drafting your plan..." surface that
// also incorrectly showed Phase 2 ("Plan review") on the phase strip
// even though no plan existed yet. `'back-to-plan'` is exempt because
// the user returning to plan review implies a plan was already visible.
//
// `showBackToPlanFor` decides whether the WorkspaceHeader renders the
// Back-to-plan button. Symmetrically narrow: quick-depth plus the two
// sub-phases where the user has committed a plan or is already in
// Phase B (`committed` and `build`) paints it.
//
// Both helpers take the narrowed depth + subPhase pair (the
// caller pulls them off the top-level state file fields per schema
// PR #543) rather than the full `BuildGameInterviewState` so the
// tests can construct minimal inputs without satisfying every
// canonical field.
// ---------------------------------------------------------------------------

export function showPlanModeFor(args: {
  depth: import('@volley/crucible-ipc-types/build-game-types').KnownInterviewDepth | undefined;
  subPhase:
    | import('@volley/crucible-ipc-types/build-game-types').KnownInterviewSubPhase
    | undefined;
  /**
   * #826 Defect C — plan markdown body from the interview state.
   * When absent / empty, `'plan'` and `'refine'` sub-phases do NOT
   * activate the plan-mode view (the agent is still drafting). Pass
   * the value of `getEffectivePlan(interviewState)` or `interviewState?.plan`.
   *
   * `'back-to-plan'` is NOT gated on this: reaching that sub-phase
   * implies the plan was already shown and the user elected to
   * revise it, so we unconditionally surface the view there.
   */
  plan?: string | null;
}): boolean {
  if (args.depth !== 'quick') return false;
  const sp = args.subPhase;
  // Per the canonical schema (PR #543), `KnownInterviewSubPhase` has
  // six values: 'quick' (bulk-pitch turn), 'plan', 'refine',
  // 'committed' (just after Start Building, pre-Phase-B kickoff),
  // 'build', and 'back-to-plan'. The plan-mode full-screen view
  // only surfaces in the three sub-phases where the user is
  // actively interacting with the plan markdown.
  //
  // #826 Defect C: for 'plan' and 'refine', additionally require that
  // plan content exists. The SKILL sets subPhase='plan' the instant it
  // BEGINS writing — before any plan text lands in the state file.
  // Without this check the view activates on an empty plan, which
  // incorrectly signals Phase 2 ("Plan review") to the user while the
  // agent is still in Phase 1 (planning/drafting).
  const hasPlan = typeof args.plan === 'string' && args.plan.trim().length > 0;
  if (sp === 'plan' || sp === 'refine') return hasPlan;
  return sp === 'back-to-plan';
}

export function showBackToPlanFor(args: {
  depth: import('@volley/crucible-ipc-types/build-game-types').KnownInterviewDepth | undefined;
  subPhase:
    | import('@volley/crucible-ipc-types/build-game-types').KnownInterviewSubPhase
    | undefined;
}): boolean {
  if (args.depth !== 'quick') return false;
  return args.subPhase === 'committed' || args.subPhase === 'build';
}

export function pendingPhaseBDecisionFromState(
  state: BuildGameInterviewState | null | undefined,
): PhaseBDecision | null {
  if (state?.status === 'built' || state?.status === 'built-partial') {
    return null;
  }
  return (
    state?.phaseBDecisions?.find(
      (decision) =>
        decision.actionStatus === 'pending' &&
        (decision.answerOptionId === null || decision.answerOptionId === undefined),
    ) ?? null
  );
}

const BUILD_KICKOFF_STALL_MS = 15_000;

function hasFreshRunningActivitySnapshot(snapshot: ActivitySnapshot | null): boolean {
  if (!snapshot) return false;
  return Object.values(snapshot.byAgentId).some(
    (row) => row.status === 'running' && row.stale !== true,
  );
}

/**
 * Should the build-kickoff watchdog TRACK this game (and, after its timeout,
 * fire an auto-resume retry)? Only when the kickoff is committed AND neither
 * liveness signal says a build is already running:
 *   - `hasFreshRunningActivity` — the push `activity:state` snapshot, lit only
 *     for a stream THIS GUI drives;
 *   - `buildInFlightFromPoll` — the reattach poll's `agent:get-status`, the
 *     authoritative server-side refcount, reliable for a DETACHED build the GUI
 *     only observes.
 * The poll signal is load-bearing: without it the watchdog fired a spurious
 * "kickoff retry" at every plan approval, because the push feed is structurally
 * dark for a detached build even though the build is running fine.
 */
export function shouldTrackBuildKickoff(input: {
  committed: boolean;
  hasFreshRunningActivity: boolean;
  buildInFlightFromPoll: boolean;
}): boolean {
  return input.committed && !input.hasFreshRunningActivity && !input.buildInFlightFromPoll;
}

/**
 * Checks whether derived progress has reached the terminal build phase.
 *
 * Args:
 *   phaseProgress: The latest phase progress payload, if one is available.
 *
 * Returns:
 *   True when derived progress has advanced to phase 7 or later.
 *
 * Raises:
 *   None.
 */
function isDerivedProgressComplete(phaseProgress: PhaseProgressUpdate | null | undefined): boolean {
  return phaseProgress?.kind === 'derived' && phaseProgress.derived.current >= 7;
}

function hasPhaseProgressBeyondPlanReview(
  progress: PhaseProgressUpdate | null | undefined,
): boolean {
  return progress?.kind === 'derived' && progress.derived.current > 2;
}

function isTerminalInterviewStatus(status: BuildGameInterviewState['status'] | undefined): boolean {
  return status === 'built' || status === 'built-partial';
}

function buildKickoffAttemptKey(
  gameId: string,
  planRevision: number | undefined,
  planHash: number | undefined,
): string {
  return `${gameId}:${planRevision ?? 'no-revision'}:${planHash ?? 'no-hash'}`;
}

// ---------------------------------------------------------------------------
// #553 — plan-mode refetch safety net. The pure predicate +
// cadence constant live in `shared/interview-poll-gate.ts` so the
// regression suite can pin them without dragging in the full
// Workspace tree. The polling effect itself is encapsulated in
// `hooks/useInterviewStatePoll.ts` and invoked once below.
//
// Why broadened: pre-#553 the inline polling effect gated on
//   `depth === 'quick' && subPhase in {plan, refine, back-to-plan,
//    committed, build}`
// which short-circuited on the most common path — engine writes
// the fresh state file with a full-flavoured shape; the renderer's
// first read sees `depth` absent; `getEffectiveInterviewDepth`
// folds the absence to `'full'`; the gate then refuses to poll. The
// SKILL's eventual `depth: 'quick' / subPhase: 'plan'` write is
// then visible ONLY via the fast `interview:plan-updated` push.
// When the push misses (watcher-arm race / atomic-rename event drop
// / push-channel hiccup), the renderer is stuck on the wrong view
// with no Start Building button. See `useInterviewStatePoll` for
// the full failure-mode prose.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// #191 quick-batch — IPC error formatter (PR #544 follow-up).
//
// Bugbot + Macroscope flagged that `handleStartBuilding` and
// `handleBackToPlanConfirm` originally used `try / finally` without a
// `catch`, so an IPC rejection became an unhandled promise rejection and
// the user got NO feedback while `planModeBusy` reset to false (the
// button re-enabled with the underlying transition silently failed).
// They ALSO ignored the `{ ok: false, reason }` resolution shape — main
// can legitimately resolve with `feature-disabled` / `no-anthropic-key` /
// `transition-in-flight` (see the IPC contract in `ipc-channels.ts`),
// and any of those returned with no visible feedback.
//
// This helper centralises the mapping so the two call sites stay in
// sync (the same defensive copy is used for Start Building and Back to
// plan, with only the action verb swapped). It's a pure function so
// the unit test can pin every branch without mounting the component.
//
// The helper is symmetric for both directions:
//   - happy path → null (caller treats as success)
//   - structured `{ ok: false, reason }` → user-facing copy mapped
//     per reason
//   - thrown / rejected error → fallback copy with the error message
// ---------------------------------------------------------------------------

/**
 * Shape returned by interview/build kickoff IPCs that resolve structured failures.
 * Kept in sync with `IpcMap` in `shared/ipc-channels.ts` — if main starts
 * returning new `reason` strings, this union must grow.
 */
export type InterviewSentinelResult = {
  ok: boolean;
  reason?:
    | 'feature-disabled'
    | 'no-anthropic-key'
    // Codex round-3 BLOCKING #1 — cloud-VM v1 fail-closed (v9 §10.5).
    // `CRUCIBLE_CLOUD_VM_V1=1` is set but the proxy URL or budget-
    // token env var is missing; main refused dispatch rather than
    // silently degrading to direct-to-Anthropic routing. The user-
    // facing copy lives in `formatInterviewSentinelError` below.
    | 'no-cloud-proxy-config'
    | 'transition-in-flight'
    // Crucible #606 — main writes `subPhase` to
    // `.foundry/build-game-interview.json` before dispatching the
    // sentinel chat message. These three reasons surface failures of
    // THAT write. The user-facing copy lives in
    // `formatInterviewSentinelError` below.
    | 'state-missing'
    | 'state-corrupt'
    | 'write-failed'
    // Auto-build retry can fail before dispatch when Design Mode is unavailable.
    | 'design-disabled'
    | 'not-armed'
    | 'conflict';
};

/**
 * Map an `interview:start-building` / `interview:back-to-plan` invocation
 * outcome to a human-readable error string, or `null` on the happy path.
 *
 * The `action` argument is the gerund label used in recovery copy
 * ("before starting the build", "try going back to plan again"). Generic
 * failure copy derives a grammatical verb phrase from it
 * ("Couldn't start building — …" vs "Couldn't go back to plan — …").
 *
 * Branches:
 *   - `{ ok: true }`                              → null (no error)
 *   - `{ ok: false, reason: 'no-anthropic-key' }` → "Add an Anthropic API key
 *                                                     in Settings before {action}."
 *   - `{ ok: false, reason: 'feature-disabled' }` → "The build-game interview
 *                                                     feature is disabled."
 *   - `{ ok: false, reason: 'design-disabled' }`  → auto-build retry unavailable
 *   - `{ ok: false, reason: 'not-armed' }`         → no persisted auto-build state
 *   - `{ ok: false, reason: 'conflict' }`          → state lock held by another write
 *   - `{ ok: false, reason: 'transition-in-flight' }` → silent (null) — this
 *                                                     reason fires on a rapid
 *                                                     double-click; main is
 *                                                     already processing the
 *                                                     first click, so a toast
 *                                                     would be misleading.
 *   - `{ ok: false, reason: <other> }`            → "Couldn't {action}: {reason}"
 *   - thrown / rejected (Error or non-Error)      → "Couldn't {action}: {message}"
 */
type InterviewSentinelAction =
  | 'starting the build'
  | 'going back to plan'
  | 'rebuilding from the plan';

function interviewSentinelFailureAction(action: InterviewSentinelAction): string {
  switch (action) {
    case 'starting the build':
      return 'start building';
    case 'going back to plan':
      return 'go back to plan';
    case 'rebuilding from the plan':
      return 'rebuild from the plan';
  }
}

function interviewSentinelRejectionMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return String(error);
}

export function formatInterviewSentinelError(
  outcome:
    | { kind: 'result'; result: InterviewSentinelResult }
    | { kind: 'rejection'; error: unknown },
  action: InterviewSentinelAction,
): string | null {
  const failureAction = interviewSentinelFailureAction(action);
  if (outcome.kind === 'rejection') {
    const message = interviewSentinelRejectionMessage(outcome.error);
    return `Couldn't ${failureAction}: ${message}`;
  }
  const { result } = outcome;
  if (result.ok) return null;
  switch (result.reason) {
    case 'no-anthropic-key':
      return `Add an Anthropic API key in Settings before ${action}.`;
    case 'no-cloud-proxy-config':
      // Codex round-3 BLOCKING #1 — cloud-VM v1 fail-closed.
      // Operator-actionable: either set the missing env vars or
      // unset the feature flag. We don't surface the env-var names
      // verbatim in user-facing copy because the cohort that opts
      // in to cloud-VM v1 is engineering-only for now; the IPC log
      // carries the diagnostic detail via the warn-log at the
      // refusal site.
      return `Cloud-mode runtime is missing its proxy config — re-authenticate or restart Foundry without CRUCIBLE_CLOUD_VM_V1, then try ${action} again.`;
    case 'feature-disabled':
      return `The build-game interview feature is disabled.`;
    case 'design-disabled':
      return `Design-to-build retry is unavailable because Design Mode is disabled.`;
    case 'not-armed':
      return `Build setup is no longer armed for retry. Reopen Build or start /build-game again.`;
    case 'conflict':
      return `Build setup is busy saving state. Wait a moment, then try ${action} again.`;
    case 'transition-in-flight':
      // Rapid double-click — main is already processing the first
      // click. Surfacing a toast here would be misleading ("it didn't
      // work" when in fact it IS working). Silent null; the planModeBusy
      // reset in `finally` re-enables the button once the in-flight slot
      // releases (~1.5s per the main-side timer).
      return null;
    case 'state-missing':
      // Crucible #606 — main tried to write `subPhase` to
      // `.foundry/build-game-interview.json` and ENOENT'd. The user
      // workflow that gets here is rare (the auto-seed on Workspace
      // open should have created the file) but possible if the user
      // deletes the file from disk between Workspace open and the
      // click. Tell them to refresh.
      return `Interview state is missing — reopen the workspace to re-seed it, then try ${action} again.`;
    case 'state-corrupt':
      // Crucible #606 — JSON.parse threw on the on-disk file. The
      // SKILL's next preflight has a recovery pathway that renames the
      // corrupt file out; the user can either run /build-game from
      // Build or close + reopen the workspace.
      return `Interview state file is unreadable — run /build-game in Build to recover, then try ${action} again.`;
    case 'write-failed':
      // Crucible #606 — fs error during the atomic write (ENOSPC,
      // EROFS, EACCES on .foundry/, antivirus interference). Surface
      // generically; the underlying cause is logged by the IPC catch.
      return `Couldn't ${failureAction} — failed to save the interview state. Check disk space / permissions and try again.`;
    case undefined:
      // Defensive: main returned `{ ok: false }` with no reason. Fall
      // through to the generic copy so the user still sees something.
      return `Couldn't ${failureAction}.`;
    default:
      // Future reason values main might add — surface the raw reason
      // so the user sees SOMETHING and the dev gets a clue from a
      // screenshot.
      return `Couldn't ${failureAction}: ${result.reason}`;
  }
}

function toolCallFromAskUserQuestionPush(push: AgentAskUserQuestionPush): ToolCallEntry {
  return {
    id: push.toolUseID,
    name: 'AskUserQuestion',
    input: push.input,
    status: 'running',
  };
}

export function shouldClearPinnedAskUserQuestion(
  toolUseID: string,
  chunk: AgentStreamChunk,
): boolean {
  if (chunk.type === 'tool_result') {
    return chunk.toolResult?.id === toolUseID;
  }
  return chunk.type === 'done' || chunk.type === 'interrupted' || chunk.type === 'error';
}

/**
 * Bridge-3 close-out — gate the failure-latch setter.
 *
 * The Workspace's `agent:stream` chunk handler calls
 * `setFurnaceLastFailure(view)` on every terminal `'error'` chunk
 * that satisfies this predicate.
 *
 * Decision matrix:
 *
 *   - `furnaceStatus === undefined` (status query still loading)
 *     → `true`. Pre-Bridge-3-close-out behaviour preserved: an
 *     in-cohort engineer whose first turn errors before
 *     `app:get-furnace-status` resolves still latches the
 *     failure so the indicator + banner can mount retroactively
 *     once status arrives. The downstream banner mount-gate
 *     (`furnaceBuildState.phase === 'failed' ? furnaceLastFailure : null`)
 *     + the resolve-to-out clearing effect together gate
 *     visibility. Codex round-1 MEDIUM (Agent N) anchored this
 *     intent + the close-out preserves it.
 *
 *   - Status resolved as in-cohort + flag-on → `true`. Steady-
 *     state Furnace failure latching.
 *
 *   - Status resolved as out-of-cohort OR flag-off → `false`.
 *     Bugbot Low #3 (close-out): a terminal `'error'` chunk
 *     that lands while DEFINITIVELY out-of-cohort must NEVER
 *     set the latch. The clearing effect
 *     `useEffect(() => { if (!isFurnaceRouted) setFurnaceLastFailure(null); }, [isFurnaceRouted])`
 *     only fires on a TRANSITION out of `isFurnaceRouted`. A
 *     latch set while out-of-cohort sticks until a transition
 *     out of routed (impossible — we're already out), so a
 *     subsequent cohort flip INTO `'in'` would resurface it as
 *     a stale Furnace failure banner. Gating the setter on the
 *     RESOLVED out-of-cohort case stops an out-of-cohort latch
 *     from ever existing, while still allowing the loading-
 *     window grace per the first row above.
 *
 * The `chunk.sessionId` truthy gate is intentionally NOT enforced
 * here — Bugbot Medium #2 (close-out): terminal `'error'` chunks
 * emitted by main-side pre-stream paths with an empty `sessionId`
 * reach the chat reducer and render in the chat bubble regardless.
 * The latch was the ONLY surface that stayed hidden, so the
 * indicator + banner went silent on those failures while the chat
 * bubble still showed a Furnace error. That's a three-surface
 * asymmetry; in-cohort engineers expect the indicator to flip when
 * a build fails, not just the chat bubble. The cross-workspace
 * concern that motivated the original sessionId gate (Codex
 * round-3 finding by Agent N) is mitigated by this function's
 * status-aware gate (out-of-cohort workspaces never latch) plus
 * the upstream cross-game regex guard `^gui-${gameId}-\\d+$`
 * (which drops sessionId-bearing chunks for other workspaces).
 * The remaining gap — sessionId-less chunks reaching the wrong
 * workspace — is tracked as a main-process emission-contract
 * follow-up.
 *
 * Cross-workspace contamination cost analysis (worst case): if
 * main emits a terminal error for workspace X to workspace Y's
 * stream subscription with an empty `sessionId`, both workspaces'
 * chat reducers already receive the chunk via the unconditional
 * `chatDispatch` downstream. The new latch behaviour matches the
 * chat bubble's: both workspaces see the error.
 *
 * Pure function. The downstream banner mount-gate keeps the
 * visibility honest, but gating the setter is cheaper than
 * relying on the visibility gate alone (a latch sitting in state
 * has no business existing if it'll never be shown).
 */
export function shouldLatchFurnaceFailure(args: {
  furnaceStatus: { cloudVmV1Enabled: boolean; cohort: 'in' | 'out' | 'unknown' } | undefined;
}): boolean {
  const { furnaceStatus } = args;
  // Loading window — preserve Bridge-3's eager-latch intent so
  // the indicator/banner can mount retroactively when status
  // resolves to in-cohort. The clearing effect cleans up if
  // status resolves to out-of-cohort instead.
  if (furnaceStatus === undefined) return true;
  // Status resolved — only latch when we know we're in-cohort.
  return furnaceStatus.cloudVmV1Enabled === true && furnaceStatus.cohort === 'in';
}

/**
 * Bridge-3 close-out — pick a scrub level for infrastructure-URL
 * stripping based on the live FurnaceStatus snapshot.
 *
 * Mirrors the decision matrix originally inlined in the
 * `agent:stream` chunk handler (added by Codex round-5) so the
 * same logic can be reused by `classifyKickoffFailure` (Codex
 * round-1 kickoff-detail path) and the `agent:send` catch path
 * (Codex round-4 user-bubble error dispatch). Threading the
 * level through every call site closes Bridge-3 close-out
 * Bugbot Medium #1: those two paths were hard-coded to
 * `'banned-only'`, so a pre-stream Furnace failure surfacing
 * through them while `furnaceStatus` was still loading could
 * leak an infrastructure URL into the chat bubble + kickoff-
 * failure detail line.
 *
 * Decision matrix (fail-closed):
 *   - `undefined` (status query loading)       → `'aggressive'`.
 *     We don't know whether this turn was Furnace-routed; assume
 *     it might be and strip every URL. A benign local URL gets
 *     redacted in this brief loading window — that's a small,
 *     edge-case regression compared to leaking an infrastructure
 *     URL when status hadn't resolved.
 *   - In-cohort + flag-on                      → `'aggressive'`.
 *     Definitely Furnace-routed; strip every URL.
 *   - Resolved + (flag-off OR cohort != 'in')  → `'banned-only'`.
 *     Definitely local. Preserve benign URLs (pre-Bridge-3
 *     identity for flag-OFF / out-of-cohort engineers — a local
 *     error referencing `http://localhost:3000` must stay
 *     visible verbatim). Banned infrastructure hostnames are
 *     still stripped on either path as defence-in-depth.
 *
 * The `'aggressive'` default is intentional: a caller that
 * forgets to pass a level still fails closed.
 *
 * Pure function — no side effects. Exported only so the
 * Workspace-internal tests can assert the matrix against the
 * same code path the renderer uses.
 */
export function pickFurnaceScrubLevel(
  status: { cloudVmV1Enabled: boolean; cohort: 'in' | 'out' | 'unknown' } | undefined,
  gameImported?: boolean | null,
): 'aggressive' | 'banned-only' {
  if (gameImported === true) return 'banned-only';
  if (status === undefined) return 'aggressive';
  if (status.cloudVmV1Enabled !== true || status.cohort !== 'in') return 'banned-only';
  if (gameImported === null) return 'aggressive';
  return 'aggressive';
}

function classifyKickoffFailure(
  error: unknown,
  scrubLevel: 'aggressive' | 'banned-only' = 'aggressive',
): {
  reason: InitialKickoffFailureReason;
  detail: string;
} {
  const serialised = unflattenIpcError(error);
  // Codex round-3 — the kickoff detail is rendered through the
  // run-state derivation into ActivityBanner. Scrub
  // infrastructure URLs from the user-facing message BEFORE
  // building the detail string so a pre-stream Furnace
  // provisioning failure can't leak a hostname through this
  // surface. The classifier still matches against the raw text
  // (substring scan for "cloud" / "proxy" / "anthropic" / etc.)
  // so the reason discriminator is unaffected.
  //
  // Bridge-3 close-out (Bugbot Medium #1) — `scrubLevel` is now
  // threaded from the caller. The default is `'aggressive'`
  // (fail-closed) so a missing argument can't downgrade the
  // scrub. Callers pass `pickFurnaceScrubLevel(furnaceStatus,
  // gameImported)` so the level mirrors the `agent:stream`
  // handler's loading-vs-known-state matrix.
  const detail =
    scrubInfrastructureUrls(serialised.message, scrubLevel) ||
    'The first agent message could not be sent.';
  const lower = `${serialised.code ?? ''} ${serialised.message ?? ''}`.toLowerCase();
  if (lower.includes('anthropic')) {
    return { reason: 'anthropic-token-missing', detail };
  }
  if (lower.includes('cloud') || lower.includes('proxy')) {
    return { reason: 'cloud-proxy-missing', detail };
  }
  if (lower.includes('in-flight') || lower.includes('already running')) {
    return { reason: 'turn-in-flight', detail };
  }
  if (lower.includes('interview state') || lower.includes('.foundry')) {
    return { reason: 'interview-state-missing', detail };
  }
  return { reason: 'agent-send-rejected', detail };
}

// ---------------------------------------------------------------------------
// Workspace (root)
// ---------------------------------------------------------------------------

// Stable module-level no-op. Used as the embedded preview's `onClose`
// and the pinned activity panel's `onClose` — neither surface is
// closable (the embedded preview guards its Escape / request-close
// pipeline on `!embedded`; the pinned panel ignores `open`/`onClose`).
// A module constant keeps the reference identity stable across renders
// (so PreviewOverlay's `[onClose]`-keyed effects never churn) without a
// per-render arrow — and satisfies the source-grep regression guard in
// PreviewOverlay.exit-affordances.test.tsx that bans inline `onClose`.
const NOOP = (): void => {};

export function isValidWorkspaceRouteGameId(gameId: string | undefined): gameId is string {
  // Mirrors the main-process `assertValidGameId` route shape for direct
  // renderer routes. In particular, reject macOS bundles like `Foundry.app`
  // before Workspace starts firing IPC for a non-game directory.
  return !!gameId && /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(gameId);
}

/** Reattach poll cadence (cloud-furnace 2026-06-22): how often to check
 *  whether a server-side build is running and, if so, merge fresh
 *  `agent:history` into the timeline so a reconnected / non-originating
 *  renderer catches up. `agent:get-status` (the gate) is a tiny RPC. */
const REATTACH_POLL_INTERVAL_MS = 5000;

type ChatHydrationInvokeChannel = 'chat:load' | 'agent:get-status' | 'agent:history';

export type GameScopedCrucibleInvoke = (
  channel: ChatHydrationInvokeChannel,
  payload: { gameId: string },
) => Promise<unknown>;

export interface ChatHydrationSnapshot {
  messages: ChatMessage[];
  agentInFlight: boolean;
}

/**
 * Loads persisted chat history, then snapshots whether an agent turn is
 * in flight. Agent status must be queried after history returns — a
 * parallel `agent:get-status` + slow `chat:load` can predate hydration
 * and yield a stale `inFlight` (busy UI with no stream, or initial-
 * prompt auto-send racing an active turn).
 */
export async function loadChatHistoryWithAgentStatus(
  invoke: GameScopedCrucibleInvoke,
  gameId: string,
  isCancelled: () => boolean,
): Promise<ChatHydrationSnapshot | null> {
  const saved = await invoke('chat:load', { gameId });
  if (isCancelled()) return null;
  // 3c — read the durable agent journal and project it into chat messages, so a
  // cloud Furnace build survives a full browser reload (chat:load is desktop-only
  // in cookie mode; the journal is the cloud hydration source). Best-effort: a
  // desktop build / pre-journal game / unrouted stub yields `[]` (caught), and
  // the merge collapses to just `saved` — byte-identical to the prior behaviour.
  const journalPage = await invoke('agent:history', { gameId }).catch(() => null);
  if (isCancelled()) return null;
  const savedMessages = (saved as ChatMessage[] | null | undefined) ?? [];
  const projected = projectAgentHistoryToChatMessages(journalPage as AgentHistoryPageLike | null);
  const messages =
    projected.length > 0 ? mergeHydrationMessages(savedMessages, projected) : savedMessages;
  const agentStatus = await invoke('agent:get-status', { gameId })
    .then((status) => status as { inFlight: boolean })
    .catch(() => ({ inFlight: false }));
  if (isCancelled()) return null;
  return {
    messages,
    agentInFlight: agentStatus.inFlight,
  };
}

export function renderDesignWorkspaceFallback({
  error,
  onReset,
}: {
  error: Error;
  onReset: () => void;
  gameId?: string;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      data-testid="design-error-boundary-fallback"
      className="flex h-full items-center justify-center bg-crucible-bg px-6"
    >
      <div className="w-full max-w-xl rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 shadow-2xl">
        <div className="flex items-start gap-3">
          <AlertTriangle size={18} strokeWidth={1.8} className="mt-0.5 shrink-0 text-amber-300" />
          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-wide text-amber-200/80">
              Design hit a render error
            </p>
            <p className="mt-1 text-sm text-white/80">
              Foundry caught a runtime error while opening Design and saved a diagnostic for
              support.
            </p>
            <details className="mt-3 text-xs text-white/45">
              <summary className="cursor-pointer hover:text-white/65">Error details</summary>
              <pre className="mt-1 max-h-28 overflow-auto whitespace-pre-wrap break-words text-[0.6875rem] text-white/55">
                {error.message}
              </pre>
            </details>
            <button
              type="button"
              onClick={onReset}
              data-testid="design-error-boundary-reset"
              className="mt-4 inline-flex items-center rounded-md border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/75 transition-colors hover:border-amber-500/40 hover:text-white"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function shouldQueryWorkspaceGameLiteList(
  flags: Pick<FeatureFlagsState, 'loaded' | 'lazyDashboardLoad'>,
): boolean {
  return flags.loaded && flags.lazyDashboardLoad;
}

export function Workspace({
  initialRightPanelOpen,
}: { initialRightPanelOpen?: boolean } = {}): React.JSX.Element | null {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const flags = useFeatureFlags();
  const clientCapabilities = useClientCapabilities();
  const publishBacked = canPublishRun(clientCapabilities);
  // crucible#2325: the native ProtoHub simulator is reachable only
  // from a preload-backed Electron renderer. In the cookie-mode cloud
  // SPA (and pure-web), `protohub:*` has no transport to Electron main
  // and would fall through to the pod ("Method not found:
  // protohub.open@1"). Gate every ProtoHub entry on this so the
  // post-deploy auto-open never mounts the surface where it can't work.
  const nativeSimulatorAvailable = canDisplayOnHub(clientCapabilities);
  // crucible#2326: when the native simulator is unreachable in-process (the
  // cookie-mode cloud SPA), the cloud desktop shell injects a host bridge so
  // we can still open the native ProtoHub focused on the game (staging origin).
  // null in local Electron (native path works directly) + pure-web (no host).
  const hostBridge = useHostBridge();
  // Live-preview scaffold-first (A2.3b): the inferred-genre outcome carried
  // from NewGame via route state, for the preview genre chip. Present only for
  // the create→preview session (a fresh nav / refresh clears it); the chip is
  // additionally gated on `flags.scaffoldFirstActive`. Route state is `unknown`
  // — VALIDATE the shape before trusting it (a crafted nav / refactor could
  // hand us a malformed object); a bad payload collapses to `null` → no chip.
  const location = useLocation();
  const routeState = location.state;
  const rawScaffoldFirst = (routeState as { scaffoldFirst?: unknown } | null)?.scaffoldFirst;
  const scaffoldFirstInference: ScaffoldFirstInference | null = isScaffoldFirstInference(
    rawScaffoldFirst,
  )
    ? rawScaffoldFirst
    : null;
  const createInitialPrompt = readCreateInitialPromptFromRouteState(routeState);
  const createHandoffMode = readCreateHandoffModeFromRouteState(routeState);

  // Left panel
  const [filesCollapsed, setFilesCollapsed] = useState(true);
  const [leftWidth, setLeftWidth] = useState(260);

  // Right panel
  const [rightWidth, setRightWidth] = useState(320);

  // Chat state — owned by the pure `reduceChatState` reducer in
  // `chat/state.ts`. Workspace.tsx is a thin shell that dispatches
  // actions and renders state. The reducer + its 29 tests pin every
  // desync we shipped through alpha.41 → alpha.44 (queue while
  // in-flight, terminal vs non-terminal errors, AskUserQuestion gate
  // dispatch, cross-game reset). See state.ts for the design notes.
  const {
    state: chat,
    dispatch: chatDispatch,
    setInterpreter,
    getState: getChatState,
  } = useChatReducer(initialChatState(gameId ?? ''));
  const {
    messages,
    chatInput,
    isAiTyping,
    queuedMessages,
    pendingAttachments,
    lastTurnSpend,
    pendingBulkInterview,
    pendingDependencyRequests,
    agents,
    mainAgent,
    // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — honest hydration state.
    hydrationStatus,
    hydrationError,
  } = chat;
  // Phase 3 visibility — read `showCostInChat` once per Workspace
  // mount. The flag flips rarely (engineer toggling on for spend
  // debugging), so a stable cached read is fine; useIpcQuery keeps
  // it consistent with the App-level theme query.
  const { data: workspaceConfig } = useIpcQuery('config:get');
  const showCostInChat = !!workspaceConfig?.showCostInChat;
  // Tier 5 (failure-mode polish 3.4 + 3.5): the previous .catch(() => {}) on
  // chat:load and chat:save dropped silent data-loss bugs on the floor — a
  // user with a corrupt history saw an empty chat with no warning, and a
  // user whose disk was full thought their conversation was being saved.
  // We now surface both as a structured banner above the chat panel,
  // de-duped to one error at a time so we don't toast on every keystroke.
  const [chatPersistError, setChatPersistError] = useState<SerialisedError | null>(null);
  // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — bumped by the "Try again"
  // button on the hydration error state. It is in the mount effect's dep
  // array, so incrementing it re-runs `chat:load` for the SAME game without
  // a full remount (the cross-game `gameId` dep is unchanged).
  const [hydrationAttemptKey, setHydrationAttemptKey] = useState(0);
  // GAP-3 — flips true once a single hydration load has been pending past
  // HYDRATION_PREPARING_THRESHOLD_MS. The cloud first-touch `chat.load@1`
  // can block on a git clone for ~120s; past the threshold the surface
  // swaps the quick "Loading chat history…" affordance for an explicit
  // "preparing your workspace" message so the long blank reads as work in
  // progress, not a hang. Reset to false at the start of every load.
  const [hydrationLongRunning, setHydrationLongRunning] = useState(false);
  const [initialKickoff, setInitialKickoff] = useState<InitialKickoffState>({
    status: 'idle',
    startedAt: null,
  });
  const [interviewRendererReady, setInterviewRendererReady] = useState(false);
  const pendingInitialAgentSendRef = useRef<AgentSendEffect | null>(null);
  const appliedCreateHandoffPromptKeyRef = useRef<string | null>(null);
  const featureFlags = useFeatureFlags();
  // Cloud-VM supportability TDD §B-1 — maintainer-mode gate for the
  // left-side VM Debug pane. Read here so the parent can thread the
  // boolean through to the conditional render below; A-1's lesson
  // learned (pulling `useFeatureFlags` into `<VmStatusPill>` broke
  // 13 SSR tests across 4 files) applies one-to-one — gate at the
  // call site, not inside the pane. See `useMaintainerMode` for the
  // hook contract.
  //
  // Codex round-1 HIGH fix: the gate MUST include the cloud-VM flag.
  // A user who launched Foundry with `CRUCIBLE_MAINTAINER_MODE=1`
  // but without the cloud-VM flag would otherwise lose `<FilesPanel>`
  // for an inert pane (the message-stream capture is never
  // constructed when the flag is OFF, so no messages ever land).
  // Pane mounts ONLY when BOTH gates are ON, matching the producer-
  // side construction guard in `main/index.ts`.
  const maintainerMode = useMaintainerMode();
  const showVmDebugPane =
    maintainerMode.loaded &&
    maintainerMode.maintainer &&
    featureFlags.loaded &&
    featureFlags.cloudVmV1;
  // Game status query. This lives before Furnace route derivation so imported
  // locality can close the renderer-side cloud gate during `game:status`
  // loading and for imported/user-owned local projects.
  const { data: gameStatus, isError: gameStatusErrored } = useIpcQuery(
    'game:status',
    gameId ?? '',
    { enabled: !!gameId },
  );
  const gameImportedForFurnace = gameStatus === undefined ? null : gameStatus.imported === true;

  // Bridge-3 — derive the Furnace build state. Inputs:
  //   - FurnaceStatus query (cohort + flag snapshot).
  //   - chat reducer's `isAiTyping` (turn in flight).
  //   - VM-connection state (latest push).
  //   - `furnaceLastFailure` — sticky latch reset on the next turn.
  // The component reads `state.routedToFurnace` to short-circuit on
  // flag-OFF / out-of-cohort, so renderer behaviour is identical
  // to pre-Bridge-3 main in those cases.
  const { data: furnaceStatus } = useIpcQuery('app:get-furnace-status');
  const vmConnectionState = useVmConnectionState();
  const [furnaceLastFailure, setFurnaceLastFailure] = useState<FurnaceErrorView | null>(null);
  // Mirror `furnaceStatus` into a ref so the `agent:stream`
  // subscription's chunk handler can read the current snapshot
  // without re-subscribing the channel on every status refresh.
  // Used to pick the scrub level (aggressive on Furnace-routed
  // turns, banned-only on local-build) so a local `http://localhost`
  // error is rendered verbatim per pre-Bridge-3 main behaviour
  // (Codex round-3 flag-OFF regression).
  const furnaceStatusRef = useRef(furnaceStatus);
  const gameImportedForFurnaceRef = useRef(gameImportedForFurnace);
  useEffect(() => {
    furnaceStatusRef.current = furnaceStatus;
  }, [furnaceStatus]);
  useEffect(() => {
    gameImportedForFurnaceRef.current = gameImportedForFurnace;
  }, [gameImportedForFurnace]);
  // Reset the failure latch whenever a new turn dispatches (isAiTyping
  // flips true) so the indicator clears as soon as the engineer
  // retries.
  useEffect(() => {
    if (isAiTyping) setFurnaceLastFailure(null);
  }, [isAiTyping]);
  // Reset on game switch too — a stale failure from game A must
  // not bleed into game B's chrome.
  useEffect(() => {
    setFurnaceLastFailure(null);
  }, [gameId]);
  // Belt-and-braces: when the routing gate closes mid-session
  // (cohort flip, flag flip, IPC drops back to null), clear any
  // latched failure so the banner doesn't outlive the route. The
  // banner mount-gate below ALSO checks `routedToFurnace`, so this
  // is defence-in-depth — but it keeps the latch state honest.
  //
  // Bridge-3 close-out (Bugbot Low #3): track `furnaceStatus`
  // itself rather than the derived `isFurnaceRouted` boolean.
  // Reason: when status transitions from `undefined` (loading)
  // to a resolved out-of-cohort value, `isFurnaceRouted` stays
  // `false` across both rows. A dep on `isFurnaceRouted` doesn't
  // re-fire, so any latch set during the loading-window grace
  // (per `shouldLatchFurnaceFailure`'s undefined → true row)
  // would persist through the loading → out transition and
  // resurface on a subsequent cohort flip into `'in'`. Tracking
  // `furnaceStatus` instead causes the effect to fire on every
  // status query update, where we re-derive routing freshness +
  // clear stale latches.
  const isFurnaceRouted =
    gameImportedForFurnace === false &&
    furnaceStatus !== undefined &&
    furnaceStatus.cloudVmV1Enabled === true &&
    furnaceStatus.cohort === 'in';
  useEffect(() => {
    if (!isFurnaceRouted) setFurnaceLastFailure(null);
  }, [furnaceStatus, isFurnaceRouted]);
  const furnaceBuildState: FurnaceBuildState = computeFurnaceBuildState({
    furnaceStatus: furnaceStatus ?? null,
    gameImported: gameImportedForFurnace,
    isAiTyping,
    vmConnectionState,
    lastFurnaceFailure: furnaceLastFailure !== null,
  });

  useEffect(() => {
    setInitialKickoff({ status: 'idle', startedAt: null });
    setInterviewRendererReady(false);
    pendingInitialAgentSendRef.current = null;
    appliedCreateHandoffPromptKeyRef.current = null;
    // Live-preview split is per-game UI state — never carry game A's
    // open split into game B's workspace, but DO restore the split the way
    // the user/auto-open latch last left game B (a building game we left
    // and came back to keeps its open split rather than being force-closed
    // while the auto-open latch refuses to reopen it).
    // Develop is the default workspace view: the side-by-side split opens
    // by default for a game (unless this game was explicitly left closed).
    setLivePreviewSplitOpen(
      gameId ? (livePreviewSplitOpenByGameRef.current.get(gameId) ?? true) : false,
    );
  }, [gameId]);

  // New-UI activity rail is pinned and ON by default — it reserves its
  // own column (no longer a slide-in overlay that covers the chat), so
  // "open" just means the rail is visible. The header toggle collapses
  // it. Defaults open so users see progress + live sub-agents without
  // hunting for a button. Storybook can still seed it closed via the prop.
  const [rightPanelOpen, setRightPanelOpen] = useState(initialRightPanelOpen ?? true);
  const [artifactSoftClosed, setArtifactSoftClosed] = useState(false);
  const bulkCardRef = useRef<BulkInterviewCardRef>(null);
  const [bulkSubmittable, setBulkSubmittable] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [craftOpen, setCraftOpen] = useState(false);
  const [workspaceMode, setWorkspaceMode] = useState<WorkspaceMode>('chat');
  const [protoHubSource, setProtoHubSource] = useState<ProtoHubSource>('manual');
  // Per-view Progress sidebar memory. The Preview tab maximises the embedded
  // game, so the rail must default hidden there and never steal preview width
  // on its own — but the user can still open it manually within Preview. The
  // non-preview views (chat / plan / design) share ONE remembered open state:
  // entering Preview snapshots it, leaving Preview restores it, so whatever the
  // user last left the rail as in chat reappears when they return — consistent
  // across non-preview tabs. (Toggling inside Preview never touches the
  // snapshot, so it can't leak back into chat.)
  const nonPreviewRightPanelOpenRef = useRef(initialRightPanelOpen ?? true);
  const prevWorkspaceModeForRailRef = useRef<WorkspaceMode>(workspaceMode);
  useEffect(() => {
    const prevMode = prevWorkspaceModeForRailRef.current;
    if (prevMode === workspaceMode) return;
    const wasImmersive = prevMode === 'preview' || prevMode === 'protohub';
    const isImmersive = workspaceMode === 'preview' || workspaceMode === 'protohub';
    if (isImmersive && !wasImmersive) {
      // Entering an immersive surface: remember the non-preview state, then hide the rail.
      nonPreviewRightPanelOpenRef.current = rightPanelOpen;
      setRightPanelOpen(false);
    } else if (!isImmersive && wasImmersive) {
      // Leaving an immersive surface: restore the remembered non-preview state.
      setRightPanelOpen(nonPreviewRightPanelOpenRef.current);
    }
    prevWorkspaceModeForRailRef.current = workspaceMode;
  }, [workspaceMode, rightPanelOpen]);
  const [designBuildPlanFocusNonce, setDesignBuildPlanFocusNonce] = useState(0);
  // #1408/#1420 — live-preview split. DISTINCT from the manual Preview
  // tab (`workspaceMode === 'preview'`): the split docks the embedded
  // preview ABOVE the chat in chat mode, so the agent stream, composer,
  // and interview cards stay mounted while the game assembles. Toggled
  // by the header Live pill and by the live-preview auto-open effect.
  const [livePreviewSplitOpen, setLivePreviewSplitOpen] = useState(true);
  const initialisedModeForGameRef = useRef<string | null>(null);
  // Per-game split-open memory. The component persists across game
  // switches, so the split state is per-game UI: leaving a building game
  // for another and returning must restore the split exactly as the user
  // (or the auto-open latch) last left it — closing it unconditionally on
  // every gameId change while the auto-open latch still records the game
  // as opened stranded the user with a permanently-closed split. Keyed by
  // gameId; absent → never opened for that game (default closed).
  const livePreviewSplitOpenByGameRef = useRef<Map<string, boolean>>(new Map());
  const setLivePreviewSplitOpenForGame = useCallback(
    (open: boolean) => {
      if (gameId) livePreviewSplitOpenByGameRef.current.set(gameId, open);
      setLivePreviewSplitOpen(open);
    },
    [gameId],
  );
  // Side-by-side chat-panel width (px) — pinned to the right-panel width
  // by default, drag-adjustable, persisted per-game (dragging the divider
  // in game A must not bleed into game B's layout).
  const [livePreviewChatWidth, setLivePreviewChatWidth] = useState(DEFAULT_LIVE_PREVIEW_CHAT_WIDTH);
  // Chat-panel tabs: Chat (default) | State (the right-side panel) |
  // Diagnostic (preview dev-server health).
  const [chatTab, setChatTab] = useState<'chat' | 'state' | 'diagnostic'>('chat');
  const livePreviewChatWidthByGameRef = useRef<Map<string, number>>(new Map());
  const setLivePreviewChatWidthForGame = useCallback(
    (width: number) => {
      if (gameId) livePreviewChatWidthByGameRef.current.set(gameId, width);
      setLivePreviewChatWidth(width);
    },
    [gameId],
  );
  useEffect(() => {
    setLivePreviewChatWidth(
      (gameId && livePreviewChatWidthByGameRef.current.get(gameId)) ||
        DEFAULT_LIVE_PREVIEW_CHAT_WIDTH,
    );
  }, [gameId]);

  /**
   * Preview dev-server lifecycle. Step 5: clicking the launch button
   * fires `dev:start` if the orchestrator isn't already up; the overlay
   * mounts iframes once `state.status === 'ready'` exposes URLs. Step 6
   * wires the error → click-to-send dispatch.
   *
   * Codex blocker on #199 (round 1): the prior version skipped
   * `dev:start` whenever `status === 'ready'`, but never confirmed
   * the ready session's gameId matched the current route. Navigating
   * from game A → game B without closing the overlay first served
   * A's URLs to B's iframes. Fix: when the route gameId changes,
   * reset the session so the next launch re-fires `dev:start` for
   * the new game.
   */
  const previewSession = usePreviewSession();
  const installStateForGame = useInstallStateForGame(gameId);

  // Live-preview (§3.G) PARENT→iframe construction-event bridge — the POST
  // side. The ref is forwarded onto the display `<iframe>` (both layouts);
  // the hook subscribes to `preview:construction-event` and relays each
  // record + the build-active banner signal into the display iframe via
  // `postMessage(msg, displayOrigin)` (targetOrigin = the display origin,
  // NEVER '*'). Gated on `flags.livePreview` — flag-OFF: no subscription, no
  // postMessage (byte-identical). The CHILD origin-validating listener is
  // the `v1:preview` Ingot's job, not this surface.
  const displayPreviewIframeRef = useRef<HTMLIFrameElement | null>(null);
  // Pod-preview Phase A — `previewDisplayUrl` resolves the iframe src
  // for EITHER session shape (cloud → `previewUrl`; local →
  // `urls.display`), so the live-preview bridge works in both modes.
  const displayPreviewUrl =
    previewSession.state.status === 'ready'
      ? previewDisplayUrl(previewSession.state.session)
      : undefined;
  const [loadedDisplayPreviewUrl, setLoadedDisplayPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    setLoadedDisplayPreviewUrl(null);
  }, [displayPreviewUrl]);
  const handleDisplayIframeLoad = useCallback(
    (url: string) => {
      if (url === displayPreviewUrl) {
        setLoadedDisplayPreviewUrl(url);
      }
    },
    [displayPreviewUrl],
  );
  usePreviewConstructionBridge({
    enabled: flags.livePreview,
    gameId,
    iframeRef: displayPreviewIframeRef,
    displayUrl: displayPreviewUrl,
    loadedDisplayUrl: loadedDisplayPreviewUrl,
    isAiTyping,
  });
  // Cursor Bugbot follow-up finding (Low, commit 212baf4):
  // `usePreviewSession()` returns a new object literal every render
  // (`{ state, reloadKey, start, stop, restart, reload, reset }`).
  // Using `previewSession` directly in `useCallback` deps recreates
  // the callback every render and quietly defeats memoization. The
  // individual methods (`start`/`reload`/`restart`) are themselves
  // stable `useCallback` references inside the hook, so destructuring
  // them at the call site gives a stable identity for downstream
  // deps. The narrow Bugbot finding named only handleReloadPreview +
  // handleRestartPreview — that's the scope of this destructure.
  // Other previewSession usages (state reads, reset() in JSX
  // onError handlers) stay as-is for now; they're either inside
  // identity-fresh JSX handlers or already correct.
  const {
    reload: reloadPreview,
    reset: resetPreview,
    restart: restartPreview,
    start: startPreview,
    stop: stopPreview,
  } = previewSession;
  const previewControllerPolicyRef = useRef(derivePreviewControllerPolicy(null));
  useEffect(() => {
    if (
      previewSession.state.status === 'ready' &&
      gameId &&
      previewSession.state.session.gameId !== gameId
    ) {
      // Issue #1016: when the user switches to a different game while a
      // dev session is still running for the previous game, fire
      // `dev:stop` so the orchestrator tears down the previous game's
      // server/display/controller processes. The previous behaviour
      // (`resetPreview()`) only flipped renderer state to idle and
      // left the child processes alive as orphans — those leaked
      // across workspace navigations and could surface a misleading
      // CRUCIBLE-405 toast on the NEXT Preview click (the dead
      // orphan's stdout would show up under the new game's
      // diagnostic).
      void stopPreview();
    }
  }, [gameId, previewSession.state, stopPreview]);

  /**
   * Issue #643 — toast surfaced by the Reload / Restart preview
   * affordances. Tone-coded ("info" sky-blue, "success" green,
   * "error" red) so the user can tell at a glance whether the
   * recovery succeeded. Success/info auto-fade after 4s; error
   * toasts are STICKY with a manual dismiss button (Codex review
   * #1 BLOCK — a 4s failure toast is too easy to miss, especially
   * when the user is staring at the overlay's centred error card).
   * Lives at page chrome scope so it surfaces with or without the
   * preview overlay being open.
   */
  const [previewToast, setPreviewToast] = useState<{
    tone: 'info' | 'success' | 'error';
    message: string;
  } | null>(null);
  useEffect(() => {
    if (!previewToast) return;
    // Error toasts are sticky — the user must dismiss them via the
    // close button. Info/success auto-fade because they're
    // confirmation, not actionable.
    if (previewToast.tone === 'error') return;
    const timer = setTimeout(() => setPreviewToast(null), 4000);
    return () => clearTimeout(timer);
  }, [previewToast]);

  const handleReloadPreview = useCallback(() => {
    // Pure renderer-side iframe remount. No subprocess churn, so we
    // don't bother awaiting anything — the iframes will repaint
    // synchronously on the next render. No confirmation toast — the
    // preview repainting is feedback enough.
    reloadPreview();
  }, [reloadPreview]);

  const handleStopPreview = useCallback(() => {
    if (previewSession.state.status !== 'ready') return;
    void stopPreview();
  }, [previewSession.state.status, stopPreview]);

  const handleRestartPreview = useCallback(() => {
    if (!gameId) return;
    // `previewSession.restart` returns a discriminated `RestartResult`
    // (Codex review #1 BLOCK fix). The previous shape always
    // resolved-with-void and swallowed errors via the `'error'`
    // state branch, so an `await restart(); toast('ok')` chain
    // misreported every failure as success. The discriminated
    // result gives us a single source of truth that survives
    // catching at the wrong layer.
    void (async () => {
      const result = await restartPreview(gameId, {
        maxControllerSlots: previewControllerPolicyRef.current.maxControllerSlots,
      });
      if (result.ok) {
        // No confirmation toast — the preview restarting is self-evident.
        return;
      }
      if ('reason' in result && result.reason === 'busy') {
        // Button-mash: another launch is already in flight. Silent
        // no-op — the user's first click is what actually drives
        // the recovery; surfacing a toast here would be noise.
        return;
      }
      if ('error' in result) {
        // Codex review #4 LOW — surface the structured error
        // message AND the recovery hint in the sticky toast.
        // Previously this dropped to generic "see logs" copy,
        // which discarded the carefully-authored CrucibleError
        // `message` + `recovery` fields. The restart menu is
        // available even when the overlay is closed (chat-header
        // mounted), so the toast is the user's only window into
        // what went wrong if they don't have the overlay open.
        const errMsg = result.error.message || 'Preview failed to restart';
        const recovery = result.error.recovery
          ? ` ${result.error.recovery}`
          : ' Open Help → Open Logs Folder for details.';
        setPreviewToast({
          tone: 'error',
          message: `${errMsg}${recovery}`,
        });
      }
    })();
  }, [gameId, restartPreview]);

  /**
   * Screenshot-paste MVP (#193) — last attachment-cap rejection,
   * shown above the input until the user pastes a smaller image or
   * dismisses. Cleared by `setAttachmentError(null)` from the chip
   * row's onDismiss + on every successful add-attachment. Lives at
   * page scope so the `ui/error` effect handler can populate it
   * without touching the chat reducer (the reducer is pure, so it
   * can't dismiss the toast itself).
   */
  const [attachmentError, setAttachmentError] = useState<string | null>(null);

  // Load chat history from game repo. The auto-fire of an
  // `initial-prompt-` shape is now handled inside the reducer's
  // `chat/loaded` action — see state.ts. The IPC kickoff that used
  // to live here is one of the effects that action returns, so the
  // interpreter wired up below picks it up.
  //
  // Cross-game race guard: chat:load is async, and gameId can change
  // (React Router /workspace/:gameId reuses this component). Without
  // a cancellation flag, a slow chat:load Promise from gameId=A
  // could resolve AFTER we've navigated to B and reset state — and
  // dispatch A's history onto B's reducer state. Worse, if A had a
  // pending initial-prompt the autofire would emit `ipc/agent-send`
  // tagged with `state.gameId` (now B) carrying A's prompt text:
  // cross-game send. The cleanup flag rejects late resolutions.
  //
  // Agent status is queried only after history returns — see
  // `loadChatHistoryWithAgentStatus` — so `agentInFlight` reflects
  // the moment hydration applies, not the start of a slow load.
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    // Crucible #602 PR #C4 (Issue E) — reset hydration state for this
    // gameId. A cross-game navigation reuses the Workspace instance, so
    // the hydration cursor needs to flip back to "not hydrated for
    // gameId X" until X's chat:load resolves. Buffered pushes from a
    // previous game's pre-hydration window are wiped — they're tagged
    // with the OLD gameId and the activity:checkpoint subscription's
    // cross-game guard would have dropped them anyway.
    hydratedForGameIdRef.current = null;
    pendingCheckpointPushesRef.current = [];

    // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — "preparing your
    // workspace" timer. If this single load is still pending past the
    // threshold, flip the surface from the quick loading affordance to the
    // explicit long-provisioning message. Cleared on settle / unmount.
    setHydrationLongRunning(false);
    const preparingTimer = setTimeout(() => {
      if (!cancelled) setHydrationLongRunning(true);
    }, HYDRATION_PREPARING_THRESHOLD_MS);

    // GAP-3 — drain checkpoint pushes buffered during the pre-hydration
    // window + flip the hydration cursor. Shared by BOTH the success and the
    // terminal-failure path (preserving the Crucible #602 PR #C4 invariant
    // that even a failed load still honours buffered checkpoints rather than
    // dropping them; the reducer's cross-game + duplicate-id guards keep
    // this safe).
    const flipCursorAndDrainCheckpoints = () => {
      hydratedForGameIdRef.current = gameId;
      const pending = pendingCheckpointPushesRef.current;
      pendingCheckpointPushesRef.current = [];
      for (const push of pending) {
        if (push.gameId !== gameId) continue; // belt-and-braces
        chatDispatch({
          type: 'chat/checkpoint-received',
          gameId: push.gameId,
          checkpoint: push.checkpoint,
        });
      }
    };

    const applyHydration = (hydration: ChatHydrationSnapshot) => {
      const { messages: loadedMessages, agentInFlight } = hydration;
      const handoffKey = createInitialPrompt && gameId ? `${gameId}\n${createInitialPrompt}` : null;
      const createFallback =
        handoffKey && appliedCreateHandoffPromptKeyRef.current !== handoffKey
          ? applyCreateInitialPromptFallback({
              loadedMessages,
              agentInFlight,
              routePrompt: createInitialPrompt,
            })
          : { messages: loadedMessages, applied: false };
      if (handoffKey && createFallback.applied) {
        appliedCreateHandoffPromptKeyRef.current = handoffKey;
      }
      const messagesForHydration = createFallback.messages;
      const shouldAutofireInitialPrompt =
        !agentInFlight &&
        messagesForHydration.length === 1 &&
        messagesForHydration[0]?.role === 'user' &&
        messagesForHydration[0].id.startsWith(INITIAL_PROMPT_MESSAGE_ID_PREFIX);
      setInitialKickoff(
        shouldAutofireInitialPrompt
          ? { status: 'waiting-for-renderer-ready', startedAt: Date.now() }
          : { status: 'idle', startedAt: null },
      );
      // `chat/loaded` moves hydrationStatus -> 'ready' (see state.ts).
      chatDispatch({
        type: 'chat/loaded',
        messages: messagesForHydration,
        agentInFlight,
      });
      // Successful load (even if empty) clears any previous persist error.
      setChatPersistError(null);
      // Crucible #602 PR #C4 (Issue E) — flip hydration cursor + drain
      // any checkpoint pushes that landed during the in-flight window.
      // Drain order is FIFO (push-arrival order); the reducer's
      // duplicate-id guard handles any push whose checkpoint id is
      // ALSO present in the just-rehydrated `saved` array (the emitter
      // wrote it to disk and pushed; we saw both copies).
      flipCursorAndDrainCheckpoints();
    };

    // GAP-3 — bounded retry-with-backoff loop. The success path is
    // unchanged; the failure path now distinguishes RETRYABLE transport
    // errors (retry up to MAX_HYDRATION_ATTEMPTS with exponential backoff)
    // from terminal/non-retryable errors (corrupt-history category 'state',
    // or budget exhausted), which fall through to a legible error state.
    // The whole loop respects the cross-game `cancelled` guard: every await
    // point re-checks it so a nav-away/unmount aborts the retries.
    const runHydrationWithRetry = async () => {
      let attemptsSoFar = 0;
      for (;;) {
        let hydration: ChatHydrationSnapshot | null;
        try {
          hydration = await loadChatHistoryWithAgentStatus(
            (channel, payload) => window.crucible.invoke(channel, payload),
            gameId,
            () => cancelled,
          );
        } catch (err: unknown) {
          if (cancelled) return;
          attemptsSoFar += 1;
          const decoded = unflattenIpcError(err);
          if (shouldRetryHydration(decoded, attemptsSoFar)) {
            // Wait the backoff, re-checking the cancel guard on the far
            // side so a nav-away during the wait aborts cleanly.
            await new Promise((resolve) =>
              setTimeout(resolve, hydrationBackoffMs(attemptsSoFar - 1)),
            );
            if (cancelled) return;
            continue;
          }
          // Terminal failure (non-retryable OR budget exhausted). Drive
          // hydrationStatus -> error so the surface renders a legible error
          // state instead of a silent permanent blank.
          //
          // ONE surface per failure class (Cursor Bugbot, PR #2026): the
          // in-panel retry block (`showHydrationRetryError`) renders for a
          // RETRYABLE transport error (category !== 'state'); the
          // `chatPersistError` banner renders for everything else. Setting
          // both for a retryable error double-surfaced the same failure
          // (banner + centered "Couldn't load chat history"). So set the
          // persist banner ONLY when the in-panel block will NOT handle it —
          // i.e. the corrupt-history path (CrucibleError category 'state',
          // which keeps its existing one-shot recovery banner pointing at the
          // backup file) and any other non-retryable error. The predicate
          // here is the exact complement of `showHydrationRetryError`.
          const handledByInPanelRetry = decoded.retryable === true && decoded.category !== 'state';
          if (!handledByInPanelRetry) {
            setChatPersistError(decoded);
          }
          chatDispatch({ type: 'chat/hydration-failed', error: decoded });
          // Crucible #602 PR #C4 (Issue E) — even on the failure path we
          // still flip the hydration cursor + drain buffered checkpoints,
          // so a user with a recoverable corrupt history doesn't lose a
          // checkpoint that fired during the load attempt.
          flipCursorAndDrainCheckpoints();
          return;
        }
        if (!hydration) return; // cancelled mid-load
        applyHydration(hydration);
        return;
      }
    };

    void runHydrationWithRetry();

    return () => {
      cancelled = true;
      clearTimeout(preparingTimer);
    };
    // hydrationAttemptKey is in the deps so the "Try again" button (which
    // bumps it) re-runs the load for the same game.
  }, [gameId, chatDispatch, createInitialPrompt, hydrationAttemptKey]);

  // Latest `agent:get-status` inFlight from the reattach poll below. Exposed as
  // state (not just a poll-local) so the build-kickoff watchdog can stand down
  // when the pod confirms a build IS running: the watchdog's other signal
  // (`activity:state` → activitySnapshot) is fed only by chunks this GUI itself
  // streams, so it's structurally DARK for a detached build the GUI merely
  // observes — which made the watchdog fire a spurious "kickoff retry" at every
  // plan approval. `agent:get-status` reads the pod refcount and is reliable for
  // the detached case. Defaults false; the poll flips it within one tick (5s).
  const [buildInFlightFromPoll, setBuildInFlightFromPoll] = useState(false);

  // A — reattach poll (cloud-furnace 2026-06-22). The live agent.send
  // chunk stream reaches ONLY the originating connection. After a
  // reconnect / tab-switch / on a second device, this renderer is no
  // longer the originator, so a build running server-side (continuation
  // driver, PR #2118) looks frozen here — and worse, when it FINISHES this
  // renderer never gets the terminal `chunk:done`, so `isAiTyping` stays
  // true and the UI shows "working" forever. `agent:get-status` is the
  // authoritative server-side liveness signal; we drive two things off it:
  //   1. while inFlight AND we're not mid-streaming a local message
  //      (currentAiMsgId===null), merge the journal so progress catches up;
  //   2. when NOT inFlight but the UI still shows busy, do a final merge
  //      then reconcile the build to idle (clears the stuck "working").
  // The merge is the SAME id-deduped projection #2065 uses on load (never
  // clobbers a streamed message, no-ops when nothing's new); on desktop /
  // pre-journal games agent:history yields [] so this is inert.
  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;
    let tickInFlight = false;
    const mergeHistory = async (): Promise<void> => {
      const page = await window.crucible.invoke('agent:history', { gameId }).catch(() => null);
      if (cancelled) return;
      const projected = projectAgentHistoryToChatMessages(page as AgentHistoryPageLike | null);
      if (projected.length > 0) {
        chatDispatch({ type: 'chat/agent-history-merged', messages: projected });
      }
    };
    const tick = async (): Promise<void> => {
      if (cancelled || tickInFlight) return;
      tickInFlight = true;
      try {
        const status =
          ((await window.crucible
            .invoke('agent:get-status', { gameId })
            .then((s) => s as { inFlight?: boolean })
            .catch(() => ({ inFlight: false }))) as { inFlight?: boolean }) ?? {};
        if (cancelled) return;
        // Surface the authoritative server-side liveness to the kickoff
        // watchdog (see buildInFlightFromPoll). Always set — it must also go
        // false when the build ends.
        setBuildInFlightFromPoll(status.inFlight === true);
        if (status.inFlight === true) {
          // Catch up progress, but only when we're not the live originator
          // mid-streaming a message (it owns in-place chunk mutation then).
          if (getChatState().currentAiMsgId === null) await mergeHistory();
        } else if (getChatState().isAiTyping) {
          // Build is done server-side but this renderer still shows busy —
          // the terminal chunk never reached us. Show the final timeline,
          // then clear "working".
          await mergeHistory();
          if (cancelled) return;
          chatDispatch({ type: 'chat/agent-flight-reconciled', inFlight: false });
        }
      } catch {
        /* reattach poll is best-effort — never surface a poll failure */
      } finally {
        tickInFlight = false;
      }
    };
    const interval = setInterval(() => void tick(), REATTACH_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gameId, chatDispatch, getChatState]);

  // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — "Try again" on the
  // hydration error state. Flip the reducer back to 'loading' (same-game
  // retry, so the message draft / queue are preserved — NOT a game/changed
  // reset) and bump the attempt key to re-run the mount load.
  const handleRetryHydration = useCallback(() => {
    chatDispatch({ type: 'chat/hydration-retrying' });
    setChatPersistError(null);
    setHydrationAttemptKey((k) => k + 1);
  }, [chatDispatch]);

  const kickoffTimeoutStartedAt = initialKickoffTimeoutStartedAt(initialKickoff);

  useEffect(() => {
    if (kickoffTimeoutStartedAt === null) return;
    const startedAt = kickoffTimeoutStartedAt;
    const timeout = setTimeout(() => {
      setInitialKickoff((current) => {
        if (!isActiveInitialKickoffStatus(current.status)) {
          return current;
        }
        return {
          status: 'timed-out',
          reason: 'renderer-not-ready-timeout',
          startedAt,
        };
      });
    }, initialKickoffTimeoutDelayMs(startedAt));
    return () => clearTimeout(timeout);
  }, [kickoffTimeoutStartedAt]);

  // Save chat history when messages change. We keep one piece of state
  // for the most recent persist failure rather than appending to a list:
  // a save typically fires on every keystroke, and rapid-fire toasts
  // would obscure each other. The user gets a single visible signal with
  // recovery copy, and dismissing it lets the next failure surface.
  //
  // Wire-shape projection: the renderer-side `ChatMessage` extends
  // shared with `toolCalls?` and `error?` (see chat/types.ts). Those
  // are local UI state — tool-call statuses come from agent:stream
  // push, errors come from `unflattenIpcError(...)`. Persisting them
  // would (a) bloat chat-history.json with structured tool-call
  // inputs that may carry secrets only redacted on the TEXT path,
  // and (b) round-trip stale `running` statuses on the next load
  // before the SDK re-emits them. Project to the shared shape at
  // the IPC boundary.
  useEffect(() => {
    if (!gameId || messages.length === 0) return;
    // Crucible #602 / #736 (C4 Issue B): widen the projection to preserve
    // the `checkpoint` payload on `role: 'inline-checkpoint'` rows per
    // `docs/c4-inline-checkpoints-design.md` §6.2. The spread is
    // CONDITIONAL — user/assistant rows leave `checkpoint` undefined and
    // we deliberately omit the field from their wire shape rather than
    // emit `"checkpoint": null`, which would pollute every non-checkpoint
    // row on disk. Tool-call statuses and `error` payloads remain
    // ephemeral per the existing convention (see `renderer/chat/types.ts`).
    const wireMessages = messages.map(
      ({ id, role, content, timestamp, checkpoint, source, streamSeq, streamStatus, turnId }) => ({
        id,
        role,
        content,
        timestamp,
        ...(source !== undefined ? { source } : {}),
        ...(streamSeq !== undefined ? { streamSeq } : {}),
        ...(streamStatus !== undefined ? { streamStatus } : {}),
        ...(turnId !== undefined ? { turnId } : {}),
        ...(checkpoint !== undefined ? { checkpoint } : {}),
      }),
    );
    window.crucible
      .invoke('chat:save', { gameId, messages: wireMessages })
      .then(() => {
        setChatPersistError((prev) =>
          // Only clear errors that came from save (CHAT-WRITE / CHAT-MKDIR);
          // a CHAT-CORRUPT error from a failed load shouldn't be auto-
          // cleared just because save still works against the new (empty)
          // file.
          prev?.code?.startsWith('CRUCIBLE-FOUNDRY-CHAT-WRITE') ||
          prev?.code?.startsWith('CRUCIBLE-FOUNDRY-CHAT-MKDIR')
            ? null
            : prev,
        );
      })
      .catch((err: unknown) => {
        setChatPersistError(unflattenIpcError(err));
      });
    // Deps: gameId + messages only. We deliberately omit
    // `chatPersistError` even though the closure reads it (via the
    // setState callback form on line 114) — including it would
    // re-fire the save on every error toggle, hammering disk on
    // failure paths.
  }, [gameId, messages]);

  // Deploy state (Preview removed in alpha.50 — see plan doc
  // `docs/plan-big-work-and-agents-ui.md`. Coming back as a header-
  // toggle mode in a follow-up restructure.) `editorError` and
  // `showTemplateModal` are unrelated to preview but live in the
  // same general "page chrome" group.
  const [deployState, setDeployState] = useState<DeployState>('idle');
  const [deployMessage, setDeployMessage] = useState('Preparing deployment...');
  // #2349 — dismiss flag for the pre-flight / non-CI deploy-error
  // banner. A deploy FAILURE flips `deployState='error'` + sets
  // `deployMessage`, but `deployMessage` only ever rendered inside the
  // LoadingOverlay (which hides the moment state leaves 'deploying'),
  // and the `<DeployFailureCard>` only arms once CI was triggered. So a
  // PRE-CI failure (e.g. CRUCIBLE-202 uncommitted-changes, a missing
  // remote, a quality-gate refusal) surfaced NOTHING. This flag drives a
  // dismissable error banner for exactly that gap; it's reset on the next
  // Deploy click so a fresh failure always re-surfaces.
  const [deployErrorDismissed, setDeployErrorDismissed] = useState(false);
  // Issue #415 Layer 3 (Codex round 2 fix): when a local pre-
  // flight quality-gate check fails (lint / typecheck / shared
  // build), main pushes the failed check name on
  // `publish:progress.failedCheck`. We surface it via the SAME
  // "Failed step" chip the existing #408/#413 deploy-failure UX
  // uses for CI failures — the chip's `failedStepName` source
  // becomes this local state when no remote
  // `deployFailureContext` exists, mirroring the spec's "use the
  // SAME UX as the existing deploy-failure flow" requirement.
  const [localFailedCheck, setLocalFailedCheck] = useState<string | null>(null);
  const [editorError, setEditorError] = useState<string | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  // Issue #597 — non-blocking toast surfaced when main auto-seeds the
  // interview state file on Workspace open (typical case: the user
  // cloned a game from GitHub and `.foundry/` was correctly gitignored
  // so the seed never made the trip). Auto-fades after 8s — slightly
  // longer than `editorError`'s 5s because the message is informational
  // and the user is likely about to type `/build-game` for the first
  // time, so giving them a beat to read it pays off.
  const [autoSeededBanner, setAutoSeededBanner] = useState<string | null>(null);
  const [pinnedAskUserQuestion, setPinnedAskUserQuestion] = useState<ToolCallEntry | null>(null);

  // Issue #277: when the deploy fails we flip
  // `deployFailureCardEnabled=true`, which kicks off the
  // `deploy:get-failure-context` query inside `useDeployFailure`. The
  // card mounts as soon as the query resolves with `status: 'failure'`
  // and unmounts when the user clicks Dismiss (which clears this
  // flag) or after a successful retry. We do NOT auto-poll — every
  // mount that flipped this flag burns a GitHub API call, and the
  // deploy lifecycle is action-driven (user clicks Deploy, deploy
  // resolves or rejects, card stays until dismissed).
  const [deployFailureCardEnabled, setDeployFailureCardEnabled] = useState(false);
  const { data: deployFailureContext, refetch: refetchDeployFailure } = useDeployFailure(
    gameId,
    deployFailureCardEnabled,
  );

  const clonedRepoFullName = gameStatus?.clonedFrom;
  // Lifecycle badge (Draft/Building/Prototype/Live/Error) for the project panel.
  const gameStatusLite = useGameStatusLite(gameId ?? '', { enabled: !!gameId });
  const { data: clonedRepoAccess } = useIpcQuery(
    'github:repo-access',
    { repoFullName: clonedRepoFullName ?? '' },
    { enabled: !!clonedRepoFullName, staleTime: 5 * 60_000 },
  );

  // Live-preview scaffold-first: the inferred-genre outcome for the preview
  // chip, from three sources in priority order, all validated
  // `ScaffoldFirstInference`s:
  //  1. DESKTOP durable (A2.3c) — `game:status` reads the persisted crucible.json
  //     (not served over the cloud RPC, so undefined on crucible-web);
  //  2. route-state flash (A2.3b) — set right after create, before queries resolve;
  //  3. WEB durable — the per-game localStorage cache (survives refresh / re-nav
  //     on the SPA where (1) isn't available). Memoised on gameId — a sync read.
  const persistedGenreInference = useMemo(() => readPersistedScaffoldFirst(gameId), [gameId]);
  // Freshness rule (see pickDurableGenreInference): `game:status` (desktop,
  // reads crucible.json) is authoritative; the localStorage cache is consulted
  // ONLY when that query ERRORED — i.e. the cloud RPC doesn't serve it
  // (crucible-web). Keying on the error state (not "data is undefined") means a
  // stale cache can't flicker during the desktop load window.
  const previewGenreInference: ScaffoldFirstInference | null = pickDurableGenreInference({
    statusErrored: gameStatusErrored,
    fromStatus: gameStatus?.scaffoldFirst ?? null,
    fromRouteState: scaffoldFirstInference,
    fromCache: persistedGenreInference,
  });

  // Facepile — contributors for the current game. We read the already-cached
  // `game:list:lite` query (populated by Dashboard) so no extra network round-
  // trip is needed; the data is stale-while-revalidate and typically already
  // in the QueryClient by the time the user navigates here.
  const { data: gameLiteList } = useIpcQuery('game:list:lite', {
    enabled: shouldQueryWorkspaceGameLiteList(flags),
  });
  const gameContributors = gameLiteList?.find((g) => g.gameId === gameId)?.contributors;

  // Crucible #416 Chunk C — phase strip subscription. The hook owns
  // the `phase-progress:subscribe` IPC + the `phase-progress:update`
  // listener for the active gameId; cleanup unsubscribes the watcher
  // in main on game switch and on unmount.
  const phaseProgress = usePhaseProgress(gameId);
  const foundryGameState = useFoundryGameStateSnapshot(gameId);
  const previewPhaseGate = useMemo<PreviewPhaseGate | undefined>(() => {
    if (foundryGameState?.build.terminal && foundryGameState.build.status === 'completed') {
      return undefined;
    }
    if (foundryGameState?.phase.current) {
      return {
        currentPhase: foundryGameState.phase.current,
        phaseLabel: foundryGameState.phase.label ?? `Phase ${foundryGameState.phase.current}`,
      };
    }
    return phaseProgress?.kind === 'derived'
      ? {
          currentPhase: phaseProgress.derived.current,
          phaseLabel: phaseProgress.derived.label,
        }
      : undefined;
  }, [
    foundryGameState?.build.status,
    foundryGameState?.build.terminal,
    foundryGameState?.phase.current,
    foundryGameState?.phase.label,
    phaseProgress,
  ]);
  const previewDevLoopState = useMemo(
    () =>
      derivePreviewDevLoopState({
        gameId,
        phaseGate: previewPhaseGate,
        installState: installStateForGame.data,
        installStateLoading: installStateForGame.isLoading,
        installRetrying: installStateForGame.isRetrying,
        installRetryError: installStateForGame.retryError,
        previewSession: previewSession.state,
        foundryPreviewDev: foundryGameState?.previewDev,
      }),
    [
      foundryGameState?.previewDev,
      gameId,
      installStateForGame.data,
      installStateForGame.isLoading,
      installStateForGame.isRetrying,
      installStateForGame.retryError,
      previewPhaseGate,
      previewSession.state,
    ],
  );
  const previewLaunchButtonLoading =
    previewDevLoopState.kind === 'checking' ||
    previewDevLoopState.kind === 'installing' ||
    previewDevLoopState.kind === 'starting' ||
    previewDevLoopState.kind === 'restarting' ||
    previewDevLoopState.kind === 'stopping';
  const [weekendPairingNextLaunch, setWeekendPairingNextLaunch] = useState(false);
  const inAppPairingAvailable = flags.loaded && flags.inAppPairing;
  const pairedPreviewActive = isPairedPreviewActive(previewSession.state);
  // Multi-controller (cloud + local): the +Add / −Remove / per-pane-✕
  // affordances are wired for any ready, non-paired session. Cloud is
  // intentionally INCLUDED (each pane gets its own same-origin testUserId);
  // excluding it here is the M2 blocker that made the feature unreachable.
  const phoneControlsEnabled = previewPhoneControlsEnabled(previewSession.state);
  useEffect(() => {
    if (!inAppPairingAvailable) {
      setWeekendPairingNextLaunch(false);
    }
  }, [inAppPairingAvailable]);
  const startPreviewForCurrentGame = useCallback(() => {
    if (!gameId) return;
    if (previewSession.state.status === 'busy') {
      return;
    }
    const policy = previewControllerPolicyRef.current;
    // Pod-preview Phase A — a cloud preview is display-only: no
    // controller slots to reconcile, and it's already the live
    // session for this game, so never re-fire start against it.
    if (
      previewSession.state.status === 'ready' &&
      previewSession.state.session.gameId === gameId &&
      isCloudPreviewSession(previewSession.state.session)
    ) {
      return;
    }
    if (
      previewSession.state.status === 'ready' &&
      !isCloudPreviewSession(previewSession.state.session) &&
      previewSession.state.session.gameId === gameId &&
      previewSession.state.session.urls.controllers.length === policy.defaultControllerSlots &&
      previewSession.state.session.maxControllerSlots === policy.maxControllerSlots &&
      pairedPreviewActive === (inAppPairingAvailable && weekendPairingNextLaunch)
    ) {
      return;
    }
    void startPreview(gameId, {
      phoneCount: policy.defaultControllerSlots,
      maxControllerSlots: policy.maxControllerSlots,
      ...(inAppPairingAvailable && weekendPairingNextLaunch ? { pairing: true } : {}),
    });
  }, [
    gameId,
    inAppPairingAvailable,
    pairedPreviewActive,
    previewSession.state,
    startPreview,
    weekendPairingNextLaunch,
  ]);

  const openPreviewSurface = useCallback(() => {
    setWorkspaceMode('preview');
  }, []);

  const openProtoHubSurface = useCallback(
    (source: ProtoHubSource) => {
      // No native simulator (cookie-mode cloud SPA / pure-web): do not
      // switch into 'protohub' mode — mounting the surface would invoke
      // host-only `protohub:*` over the wire and bannered
      // "Method not found: protohub.open@1" (crucible#2325). The manual
      // "Display on Hub" button is already hidden in these shells
      // (shellChrome !== 'electron'); this guards the post-deploy
      // auto-open, the one remaining caller that reaches here in cloud.
      if (!nativeSimulatorAvailable) return;
      setProtoHubSource(source);
      setWorkspaceMode('protohub');
    },
    [nativeSimulatorAvailable],
  );

  const handleDisplayOnHub = useCallback(() => {
    openProtoHubSurface('manual');
  }, [openProtoHubSurface]);

  const handleRetryInstallAndStartPreview = useCallback(() => {
    if (!gameId || installStateForGame.isRetrying) return;
    openPreviewSurface();
    void (async () => {
      const result = await installStateForGame.retryInstall();
      if (!result.ok) {
        setPreviewToast({
          tone: 'error',
          message: `Install failed: ${result.reason}`,
        });
        return;
      }
      const policy = previewControllerPolicyRef.current;
      void startPreview(gameId, {
        phoneCount: policy.defaultControllerSlots,
        maxControllerSlots: policy.maxControllerSlots,
        ...(inAppPairingAvailable && weekendPairingNextLaunch ? { pairing: true } : {}),
      });
    })();
  }, [
    gameId,
    inAppPairingAvailable,
    installStateForGame,
    openPreviewSurface,
    startPreview,
    weekendPairingNextLaunch,
  ]);

  const handleLaunchPreview = useCallback(() => {
    openPreviewSurface();
    if (!gameId) return;
    if (previewDevLoopState.primaryCta === 'retry-install-and-start') {
      handleRetryInstallAndStartPreview();
      return;
    }
    if (
      previewDevLoopState.primaryCta === 'start-preview' ||
      previewDevLoopState.primaryCta === 'retry-start-preview'
    ) {
      startPreviewForCurrentGame();
    }
  }, [
    gameId,
    handleRetryInstallAndStartPreview,
    openPreviewSurface,
    previewDevLoopState.primaryCta,
    startPreviewForCurrentGame,
  ]);

  const handleRetryPreviewFromOverlay = useCallback(() => {
    openPreviewSurface();
    startPreviewForCurrentGame();
  }, [openPreviewSurface, startPreviewForCurrentGame]);

  /**
   * Header Live pill (#1408/#1420) — toggle the chat-docked live-preview
   * split. From Plan/Preview modes the first press brings the user TO
   * the split (chat mode + open) rather than toggling it closed unseen.
   * Opening also bootstraps the preview session when the dev loop is
   * ready to start — mirroring `handleLaunchPreview` — so the split
   * shows a live game rather than a dormant surface.
   */
  // The "Develop" tab (default, side-by-side chat | preview). It NEVER
  // closes the split — clicking it from Plan/Design returns to chat mode
  // with the preview docked. (Option: always side-by-side; there is no
  // full-width-chat view.)
  const handleActivateDevelop = useCallback(() => {
    setWorkspaceMode('chat');
    setLivePreviewSplitOpenForGame(true);
    if (
      previewDevLoopState.primaryCta === 'start-preview' ||
      previewDevLoopState.primaryCta === 'retry-start-preview'
    ) {
      startPreviewForCurrentGame();
    }
  }, [previewDevLoopState.primaryCta, startPreviewForCurrentGame, setLivePreviewSplitOpenForGame]);

  // #1408 §9 Phase 0 / #1420 §5.1 — per-game latch for live-preview
  // auto-open (the effect lives below `workspaceRunState`; see there).
  const livePreviewAutoOpenedGamesRef = useRef<Set<string>>(new Set());
  const recordPhaseEvent = usePhaseEventsStore((s) => s.recordEvent);
  // Crucible #507: drives the PhaseStrip's smart empty-state when
  // markers haven't been written yet (e.g. pre-alpha.71 games, #506).
  // Crucible #519: also drives the BuildPartialBanner — surfaces a
  // sticky warning above the chat when status === 'built-partial'.
  const interviewStatusQuery = useInterviewStatus(gameId);
  const interviewStatus = interviewStatusQuery.data?.status;

  // Issue #791 — workspace-scoped activity snapshot for the top-bar
  // agent-activity pill. Same `gameId`-keyed hook the banner +
  // agents panel already subscribe to; the cross-game discard rules
  // live inside the hook so re-subscribing here is cheap and the
  // pill stays consistent with the other transparency surfaces. The
  // `gameId ?? ''` coalesce mirrors the `useIpcQuery('game:status'...)`
  // pattern below — the pre-resolve window leaves the hook keyed on
  // the empty string and the subscription's cross-game guard drops
  // every push until the real `gameId` lands a render later.
  const activitySnapshot = useActivitySnapshot(gameId ?? '');

  // Interview state + phase progress feed the phase strip, chat status
  // row, and plan panel below. The Deploy-to-TV button no longer gates
  // on build readiness — it is always available when the runtime can
  // publish, and deploy failures surface in-app (see
  // `deploy-action-state.ts`), so there is no readiness verdict or
  // stale-state refetch to maintain here.
  const interviewStateQuery = useInterviewState(gameId);

  // #191 quick-batch — surface the depth + subPhase + plan body off
  // the interview state. Per schema PR #543, the three fields live
  // at the TOP LEVEL of `BuildGameInterviewState` (siblings of
  // `status`, `phaseBDecisions`, etc.) — NOT nested under
  // `interview`. Use the canonical helpers
  // (`getEffectiveInterviewDepth` etc.) so the legacy-default rule
  // and the unknown-future-value defensive narrowing live in ONE
  // place. Pre-#191 games fold to `depth = 'full'` (no plan-mode
  // UI, no back-to-plan button); a SKILL-side new sub-phase value
  // we haven't learned yet folds to `subPhase = undefined` and
  // similarly suppresses plan-mode chrome.
  const [fastAnswersSubmittedAt, setFastAnswersSubmittedAt] = useState<number | null>(null);
  const [fastPlanMissingAt, setFastPlanMissingAt] = useState<number | null>(null);
  const fastAnswersSubmittedAtRef = useRef<number | null>(null);
  const fastPlanReadyRef = useRef(false);
  const interviewState = interviewStateQuery.data;
  const autoBuildState = interviewStatusQuery.data?.autoBuild ?? interviewState?.autoBuild ?? null;
  const interviewDepth = interviewState ? getEffectiveInterviewDepth(interviewState) : 'full';
  const interviewSubPhase = interviewState
    ? getEffectiveInterviewSubPhase(interviewState)
    : undefined;
  const interviewPlan = interviewState ? getEffectivePlan(interviewState) : '';
  useEffect(() => {
    fastAnswersSubmittedAtRef.current = fastAnswersSubmittedAt;
  }, [fastAnswersSubmittedAt]);
  useEffect(() => {
    const planReady =
      interviewDepth !== 'quick' ||
      interviewSubPhase !== 'quick' ||
      interviewPlan.trim().length > 0;
    fastPlanReadyRef.current = planReady;
    if (planReady || pendingBulkInterview) {
      setFastAnswersSubmittedAt(null);
      setFastPlanMissingAt(null);
    }
  }, [interviewDepth, interviewPlan, interviewSubPhase, pendingBulkInterview]);
  useEffect(() => {
    if (fastAnswersSubmittedAt === null) return undefined;
    const elapsed = Date.now() - fastAnswersSubmittedAt;
    const remaining = Math.max(0, FAST_BUILD_PLAN_BRIDGE_TIMEOUT_MS - elapsed);
    const timer = window.setTimeout(() => {
      if (!fastPlanReadyRef.current) setFastPlanMissingAt(Date.now());
      setFastAnswersSubmittedAt(null);
    }, remaining);
    return () => window.clearTimeout(timer);
  }, [fastAnswersSubmittedAt]);
  const pendingPhaseBDecision = useMemo(
    () =>
      resolvePendingPhaseBDecision({
        foundrySnapshot: foundryGameState,
        decision: pendingPhaseBDecisionFromState(interviewState),
        activitySnapshot,
        phaseProgress,
      }),
    [activitySnapshot, foundryGameState, interviewState, phaseProgress],
  );
  const previewControllerPolicy = useMemo(
    () =>
      applyPreviewPhoneCapOverride(
        derivePreviewControllerPolicy(interviewState),
        workspaceConfig?.previewPhoneCapOverride,
      ),
    [interviewState, workspaceConfig?.previewPhoneCapOverride],
  );
  previewControllerPolicyRef.current = previewControllerPolicy;
  // Crucible #823 — elapsed timer anchor for the plan-drafting empty
  // state. Use the orchestrator (`__main__`) agent's `startedAt` from
  // the live activity snapshot as the anchor epoch. This is the moment
  // the orchestrator's most-recent subsystem/action changed — a close
  // proxy for "when the composing pass started". Falls back to null
  // when the snapshot is absent (no active build) or when the plan is
  // already rendered (the timer is only shown while plan is empty).
  const draftingStartedAt: number | null =
    activitySnapshot?.byAgentId['__main__']?.startedAt ?? null;
  // Codex round-1 HIGH — thread the orchestrator's terminal `stoppedAt`
  // through to `<PlanModeView>` so the elapsed timer freezes when the
  // agent reaches a terminal state. Without this, the timer keeps
  // ticking through the activity-row linger window even after the
  // orchestrator has stopped. Null means the agent is still running.
  const draftingStoppedAt: number | null =
    activitySnapshot?.byAgentId['__main__']?.stoppedAt ?? null;
  const showPlanModeView = showPlanModeFor({
    depth: interviewDepth,
    subPhase: interviewSubPhase,
    // #826 Defect C: pass plan content so the gate can distinguish
    // "agent is drafting" (plan null/empty → stay on chat panel)
    // from "plan ready for review" (plan present → show PlanModeView).
    plan: interviewPlan,
  });
  // Modal state for the back-to-plan confirmation. Lives at Workspace
  // scope so the header button + modal share one source of truth.
  const [backToPlanModalOpen, setBackToPlanModalOpen] = useState(false);
  // True while either Start Building or Back to plan is in flight via
  // IPC. Disables the buttons so a second click can't double-dispatch.
  const [planModeBusy, setPlanModeBusy] = useState(false);
  // PR #544 follow-up (Bugbot + Macroscope MEDIUM): user-facing error
  // copy for the Start Building / Back to plan IPC. Originally the
  // two handlers used `try / finally` with no catch, so an IPC
  // rejection silently re-enabled the buttons; this state plus the
  // toast rendered at page chrome surfaces the failure so the user
  // knows their click had no effect. Renders at page-chrome scope so
  // BOTH plan-mode (full-screen `<PlanModeView>`) AND build-mode
  // (header back-to-plan button) clicks reuse the same surface. Auto-
  // clears after 6s — slightly longer than `editorError`'s 5s
  // because plan-mode failures often need the user to switch to
  // Settings, which takes longer than reading a chat-editor error.
  const [planModeError, setPlanModeError] = useState<string | null>(null);
  const planModeErrorTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const showPlanModeError = useCallback((message: string): void => {
    setPlanModeError(message);
    if (planModeErrorTimerRef.current) clearTimeout(planModeErrorTimerRef.current);
    planModeErrorTimerRef.current = setTimeout(() => setPlanModeError(null), 6_000);
  }, []);
  useEffect(() => {
    return () => {
      if (planModeErrorTimerRef.current) clearTimeout(planModeErrorTimerRef.current);
    };
  }, []);
  // True when the paginated interview overlay is currently waiting for
  // an answer. Keep this above the run-state selector so the header can
  // say "Waiting on you" even while the orchestrator row is still live.
  const [interviewSlotActive, setInterviewSlotActive] = useState(false);
  const buildKickoffFirstSeenRef = useRef<Map<string, number>>(new Map());
  const buildKickoffResumeAttemptedRef = useRef<Set<string>>(new Set());
  const stalledBuildResumeAttemptedRef = useRef<Set<string>>(new Set());
  const previousBuildKickoffGameIdRef = useRef<string | null>(null);
  const [buildKickoffClockNow, setBuildKickoffClockNow] = useState(() => Date.now());
  useEffect(() => {
    const previousGameId = previousBuildKickoffGameIdRef.current;
    if (previousGameId && previousGameId !== gameId) {
      const prefix = `${previousGameId}:`;
      for (const key of buildKickoffFirstSeenRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffFirstSeenRef.current.delete(key);
      }
      for (const key of buildKickoffResumeAttemptedRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffResumeAttemptedRef.current.delete(key);
      }
      for (const key of stalledBuildResumeAttemptedRef.current.keys()) {
        if (key.startsWith(prefix)) stalledBuildResumeAttemptedRef.current.delete(key);
      }
      // `<Workspace>` is NOT remounted per gameId (the route has no
      // `key={gameId}` — only child components do), so this poll-fed state must
      // be reset on a game switch like the kickoff refs above; otherwise a
      // stale `true` from the previous game could briefly suppress a legitimate
      // lost-kickoff retry for the new one until its first poll tick.
      setBuildInFlightFromPoll(false);
    }
    previousBuildKickoffGameIdRef.current = gameId ?? null;
  }, [gameId]);
  const buildKickoffKey =
    gameId && interviewDepth === 'quick'
      ? buildKickoffAttemptKey(gameId, interviewState?.planRevision, interviewState?.planHash)
      : null;
  const buildKickoffPhaseStarted = hasPhaseProgressBeyondPlanReview(phaseProgress);
  const currentPlanWasHandedToBuild = savedPlanMatchesBuilt({
    planRevision: interviewState?.planRevision,
    planHash: interviewState?.planHash,
    lastBuiltPlanRevision: interviewState?.lastBuiltPlanRevision,
    lastBuiltPlanHash: interviewState?.lastBuiltPlanHash,
  });
  const buildKickoffCommitted =
    !!gameId &&
    interviewDepth === 'quick' &&
    (interviewSubPhase === 'committed' || currentPlanWasHandedToBuild) &&
    !buildKickoffPhaseStarted &&
    !isTerminalInterviewStatus(interviewState?.status);
  // Stand down the kickoff watchdog when EITHER liveness signal says a build is
  // running (see shouldTrackBuildKickoff): the push `activitySnapshot` (lit for a
  // GUI-driven stream) OR the poll's `agent:get-status` (reliable for a detached,
  // server-driven build the GUI only observes).
  const buildKickoffShouldTrack = shouldTrackBuildKickoff({
    committed: buildKickoffCommitted,
    hasFreshRunningActivity: hasFreshRunningActivitySnapshot(activitySnapshot),
    buildInFlightFromPoll,
  });
  useEffect(() => {
    if (!gameId) return;
    if (!buildKickoffCommitted) {
      const prefix = `${gameId}:`;
      for (const key of buildKickoffFirstSeenRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffFirstSeenRef.current.delete(key);
      }
      for (const key of buildKickoffResumeAttemptedRef.current.keys()) {
        if (key.startsWith(prefix)) buildKickoffResumeAttemptedRef.current.delete(key);
      }
      return;
    }
    if (!buildKickoffShouldTrack || !buildKickoffKey) return;
    if (!buildKickoffFirstSeenRef.current.has(buildKickoffKey)) {
      const now = Date.now();
      buildKickoffFirstSeenRef.current.set(buildKickoffKey, now);
      setBuildKickoffClockNow(now);
    }
  }, [buildKickoffCommitted, buildKickoffKey, buildKickoffShouldTrack, gameId]);
  useEffect(() => {
    if (!buildKickoffCommitted) return undefined;
    const timer = window.setInterval(() => {
      setBuildKickoffClockNow(Date.now());
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [buildKickoffCommitted]);
  const buildKickoffWatchdog = useMemo<BuildKickoffWatchdogState | undefined>(() => {
    if (!buildKickoffShouldTrack || !buildKickoffKey) return undefined;
    const committedAt = buildKickoffFirstSeenRef.current.get(buildKickoffKey);
    if (committedAt === undefined) {
      return { status: 'pending' };
    }
    const stalledAt = committedAt + BUILD_KICKOFF_STALL_MS;
    if (buildKickoffResumeAttemptedRef.current.has(buildKickoffKey)) {
      return { status: 'resume-attempted', committedAt, stalledAt };
    }
    if (buildKickoffClockNow >= stalledAt) {
      return { status: 'stalled', committedAt, stalledAt };
    }
    return { status: 'pending', committedAt };
  }, [buildKickoffClockNow, buildKickoffKey, buildKickoffShouldTrack]);
  const [staleBuildClockNow, setStaleBuildClockNow] = useState(() => Date.now());
  const shouldTrackStaleBuild = phaseProgressInImplementation(phaseProgress);
  useEffect(() => {
    if (!shouldTrackStaleBuild) return undefined;
    setStaleBuildClockNow(Date.now());
    const timer = window.setInterval(() => {
      setStaleBuildClockNow(Date.now());
    }, 30_000);
    return () => window.clearInterval(timer);
  }, [shouldTrackStaleBuild]);
  // Slot cards render in the main chat (not the ArtifactPane) so the sidebar
  // stays visible during the one-at-a-time interview.
  const artifactPaneOpen =
    flags.newUi &&
    (!!pendingBulkInterview || interviewStatus === 'complete') &&
    !artifactSoftClosed;
  const workspaceRunState = useMemo(
    () =>
      deriveWorkspaceRunState({
        activitySnapshot,
        interviewState,
        interviewStatus,
        phaseProgress,
        foundrySnapshot: foundryGameState,
        isAiTyping,
        chatPersistError,
        planModeError,
        initialKickoff,
        isInterviewWaitingForUser: !!pendingBulkInterview || interviewSlotActive,
        buildKickoffWatchdog,
        fastAnswersSubmittedAt,
        fastPlanMissingAt,
        autoBuildState,
        nowMs: staleBuildClockNow,
      }),
    [
      activitySnapshot,
      interviewState,
      interviewStatus,
      phaseProgress,
      foundryGameState,
      isAiTyping,
      chatPersistError,
      planModeError,
      initialKickoff,
      pendingBulkInterview,
      interviewSlotActive,
      buildKickoffWatchdog,
      fastAnswersSubmittedAt,
      fastPlanMissingAt,
      autoBuildState,
      staleBuildClockNow,
    ],
  );
  const resumeBuildActionKind = resumeBuildActionKindFromRunState(workspaceRunState);
  const buildCompletionUnverified = isCompletionVerificationRetryRunState(workspaceRunState);
  const previewHandoffDisabledReason = previewHandoffDisabledReasonForHandoff({
    runStateKind: workspaceRunState.kind,
    previewDevLoopKind: previewDevLoopState.kind,
    previewPhaseGate,
  });
  const previewDisabledReason = mergePreviewDisabledReason(
    previewHandoffDisabledReason,
    previewDevLoopState.disabledReason,
  );
  const previewAvailableTitle = previewAvailableTitleForDevLoop(previewDevLoopState);

  // Three-zone build view (new UI). While a build is actually running
  // we swap the standard chat-centred layout for: embedded live preview
  // in the main area, a pinned sub-agent activity panel on the right,
  // and the chat (with input) docked along the bottom. Gated on
  // `flags.newUi` and active Phase B work per the run-state machine.
  // A committed plan is only a kickoff handoff; until Phase B actually
  // starts, keep the chat/plan layout instead of opening an empty or
  // stale preview.
  const buildView = flags.newUi && showNewUiBuildViewFor(workspaceRunState.kind);
  // #1408 §9 Phase 0 / #1420 §5.1 — auto-open the live preview the FIRST
  // time an agent (build) turn starts for the active game, when the
  // `livePreview` flag is on and the user hasn't opted out. The gating
  // logic is the pure `shouldAutoOpenPreview` helper (unit-tested
  // directly); the per-game latch SET makes it fire at most once per
  // game so it never fights a user who closes the preview mid-build —
  // a Set (not a single slot) because this component persists across
  // game switches, so switching to game B must not forget that A's
  // preview was already auto-opened/closed. Flag-OFF / opted-out is a
  // strict no-op — the preview stays a manual click (the Phase-0
  // flag-parity guarantee, #1420 §12). Suppress during
  // `build-kickoff-pending` so Start Building / Rebuild "returning to
  // chat" is not undone by the kickoff sentinel's `isAiTyping`.
  useEffect(() => {
    if (
      shouldAutoOpenPreview({
        flagsLoaded: flags.loaded,
        livePreview: flags.livePreview,
        livePreviewAutoOpen: workspaceConfig?.livePreviewAutoOpen,
        gameId,
        isAiTyping: chat.isAiTyping,
        buildKickoffPending: workspaceRunState.kind === 'build-kickoff-pending',
        initialKickoffActive: isActiveInitialKickoffStatus(initialKickoff.status),
        autoOpenedGames: livePreviewAutoOpenedGamesRef.current,
      })
    ) {
      if (gameId) livePreviewAutoOpenedGamesRef.current.add(gameId);
      // New UI: open the live-preview SPLIT (preview docked above chat)
      // rather than the manual Preview tab. Hijacking the Preview mode
      // here unmounted every chat surface — the agent stream, composer,
      // BulkInterview card, and the InterviewSlotOverlay whose
      // renderer-ready callback the initial kickoff waits on — leaving
      // the user blind to questions the agent was waiting on. The split
      // keeps all of those mounted; the Preview tab remains a separate,
      // manual, full-bleed surface. Legacy UI keeps the old overlay.
      if (flags.newUi) {
        // Switch to chat mode alongside opening the split: the split only
        // RENDERS (and the header Live pill only reads active) when
        // `livePreviewSplitActive` is true, which requires chat mode. If
        // the user is in Plan/Preview when auto-open fires, setting
        // `livePreviewSplitOpen` alone leaves the split open in state but
        // invisible and the pill inactive until they manually return to
        // chat. Mirror the manual Live-pill toggle, which also switches to
        // chat before opening. (Auto-open is already gated on an active
        // build turn and suppressed during build-kickoff-pending, so this
        // never yanks the user out of an intentional plan view pre-build.)
        setWorkspaceMode('chat');
        setLivePreviewSplitOpenForGame(true);
      } else {
        openPreviewSurface();
      }
    }
  }, [
    flags.loaded,
    flags.newUi,
    flags.livePreview,
    workspaceConfig?.livePreviewAutoOpen,
    gameId,
    chat.isAiTyping,
    workspaceRunState.kind,
    initialKickoff.status,
    openPreviewSurface,
    setLivePreviewSplitOpenForGame,
  ]);

  // Auto-START the preview session while the split is open — DECOUPLED
  // from the auto-open effect above. An earlier revision bootstrapped
  // the session inside the auto-open tick, which raced the dev loop on
  // fresh scaffolds: at split-open time (turn start) the loop's
  // primaryCta is often not yet 'start-preview' (install still
  // settling), the one-shot gate skipped silently, and the per-game
  // auto-OPEN latch meant no second chance — an open split with two
  // permanently dark panes (measured: zero `dev:start` across a full
  // loop iteration). This effect instead re-evaluates on every dev-loop
  // state change while the split is showing, with its own per-game
  // latch so a user who deliberately stops the preview mid-build isn't
  // fought by an auto-restart.
  // Note: deliberately NOT gated on the split being open. The dev-server
  // cold boot on a fresh scaffold measured ~5 minutes (install check →
  // workspace build → orchestrator); waiting for the split (turn start)
  // to begin booting means the user stares at dark panes for that long.
  // Warming on WORKSPACE MOUNT instead overlaps the boot with the
  // interview + plan phases (minutes themselves), so by the time the
  // split opens for Phase B the preview is hot. Flag-gated (opt-in
  // CRUCIBLE_LIVE_PREVIEW), once per game per session, and only from a
  // genuinely startable dev-loop state — error/install states keep
  // their explicit CTAs and a user who STOPS the preview isn't fought.
  // Attempt accounting (Codex stop-gate finding): a simple "attempted"
  // latch set before the start resolves PERMANENTLY retired auto-start
  // when the first `dev:start` failed transiently — the dev loop flips
  // to `retry-start-preview`, the gate would match, but the latch said
  // done; dark panes forever. Bounding instead at TWO auto attempts per
  // game (initial + one retry) self-heals transient failures without
  // retry-storming persistent ones (beyond 2, the overlay's manual CTA
  // owns recovery). On first SUCCESS the counter is pinned to Infinity:
  // a session that reached `ready` and was later STOPPED by the user
  // must never be fought by an auto-restart.
  const livePreviewAutoStartAttemptsRef = useRef<Map<string, number>>(new Map());
  useEffect(() => {
    if (!gameId) return;
    // Codex stop-gate: pin ONLY when the ready session belongs to THIS
    // game. On a game switch the route's gameId flips a tick before
    // the session state does — without the match, game A's still-ready
    // session pinned game B to Infinity and permanently disabled B's
    // auto-start. Same bug class (and same guard) as the #199
    // session/route mismatch fix in usePreviewSession.
    if (previewSession.state.status === 'ready' && previewSession.state.session.gameId === gameId) {
      livePreviewAutoStartAttemptsRef.current.set(gameId, Number.POSITIVE_INFINITY);
    }
  }, [gameId, previewSession.state]);
  useEffect(() => {
    if (!flags.livePreview || !flags.newUi || !gameId) return;
    const attempts = livePreviewAutoStartAttemptsRef.current.get(gameId) ?? 0;
    if (attempts >= 2) return;
    if (
      previewDevLoopState.primaryCta === 'start-preview' ||
      previewDevLoopState.primaryCta === 'retry-start-preview'
    ) {
      livePreviewAutoStartAttemptsRef.current.set(gameId, attempts + 1);
      startPreviewForCurrentGame();
    }
  }, [
    flags.livePreview,
    flags.newUi,
    gameId,
    previewDevLoopState.primaryCta,
    startPreviewForCurrentGame,
  ]);
  const previewModeVisible = flags.newUi && workspaceMode === 'preview';
  const protoHubModeVisible = workspaceMode === 'protohub';
  const designWorkspaceEnabled =
    flags.newUi &&
    flags.designTab &&
    clientCapabilities.hasLocalFilesystem &&
    !!gameId &&
    workspaceConfig?.designTabEnabled !== false;
  const planModeVisible = flags.newUi
    ? !designWorkspaceEnabled && workspaceMode === 'plan'
    : showPlanModeView;
  const chatModeVisible = flags.newUi ? workspaceMode === 'chat' : !showPlanModeView;
  // Design is a new-UI workspace surface gated by the process-level
  // CRUCIBLE_DESIGN_TAB hard-disable plus the user-facing GUI opt-out. Do not
  // read `gameStatus.designMode` here; that field remains only the legacy
  // Advanced/skip-interview marker.
  const designContentVisible = designWorkspaceEnabled && workspaceMode === 'design';
  // Live-preview split renders only in chat mode (the Preview tab keeps
  // its full-bleed layout — the two surfaces are deliberately separate).
  const livePreviewSplitActive =
    flags.newUi && flags.livePreview && livePreviewSplitOpen && chatModeVisible;

  // Develop (side-by-side) hides the right side panel BY DEFAULT — the
  // preview owns that space — but it stays reopenable via the header
  // panel toggle. Snapshot the pre-Develop rail state on entry and restore
  // it on exit; while Develop is active, don't fight a manual reopen.
  const preDevelopRightPanelOpenRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (livePreviewSplitActive) {
      if (preDevelopRightPanelOpenRef.current === null) {
        preDevelopRightPanelOpenRef.current = rightPanelOpen;
        if (rightPanelOpen) setRightPanelOpen(false);
      }
    } else if (preDevelopRightPanelOpenRef.current !== null) {
      setRightPanelOpen(preDevelopRightPanelOpenRef.current);
      preDevelopRightPanelOpenRef.current = null;
    }
  }, [livePreviewSplitActive, rightPanelOpen]);

  useEffect(() => {
    if (!flags.newUi || !gameId) return;
    if (initialisedModeForGameRef.current === gameId) return;
    initialisedModeForGameRef.current = gameId;
    setWorkspaceMode(
      initialWorkspaceModeForHandoff({
        buildView,
        previewReady: previewSession.state.status === 'ready',
        showPlanModeView,
        planReadyMode: designWorkspaceEnabled ? 'design' : 'plan',
        createHandoffMode: designWorkspaceEnabled ? createHandoffMode : null,
      }),
    );
  }, [
    buildView,
    createHandoffMode,
    designWorkspaceEnabled,
    flags.newUi,
    gameId,
    previewSession.state.status,
    showPlanModeView,
  ]);

  const previousShowPlanModeViewRef = useRef(showPlanModeView);
  useEffect(() => {
    if (!flags.newUi) {
      previousShowPlanModeViewRef.current = showPlanModeView;
      return;
    }
    if (showPlanModeView && !previousShowPlanModeViewRef.current) {
      if (designWorkspaceEnabled) {
        setDesignBuildPlanFocusNonce((n) => n + 1);
        setWorkspaceMode('design');
      } else {
        setWorkspaceMode('plan');
      }
    }
    previousShowPlanModeViewRef.current = showPlanModeView;
  }, [designWorkspaceEnabled, flags.newUi, showPlanModeView]);

  useEffect(() => {
    if (workspaceMode !== 'design' || designWorkspaceEnabled) return;
    setWorkspaceMode(showPlanModeView ? 'plan' : 'chat');
  }, [designWorkspaceEnabled, showPlanModeView, workspaceMode]);

  useEffect(() => {
    if (!flags.newUi || !designWorkspaceEnabled || workspaceMode !== 'plan') return;
    if (showPlanModeView) {
      setDesignBuildPlanFocusNonce((n) => n + 1);
      setWorkspaceMode('design');
    } else {
      setWorkspaceMode('chat');
    }
  }, [designWorkspaceEnabled, flags.newUi, showPlanModeView, workspaceMode]);

  // Codex round-4 HIGH-3: capture refetch via a ref so the
  // plan-updated subscription + 3s poll effects don't tear down on
  // every render. The React Query hook returns a fresh object on
  // every render (new identity even when the underlying state is
  // unchanged), so naively listing `interviewStateQuery` in an
  // effect's deps means the subscription rebinds and the interval
  // restarts on every chunk/state change. With heavy chat-stream
  // traffic the interval can keep getting cleared before its 3s
  // tick — missing the committed → build transition entirely. The
  // ref pattern stabilises the call site without losing freshness.
  const refetchInterviewStateRef = useRef(interviewStateQuery.refetch);
  refetchInterviewStateRef.current = interviewStateQuery.refetch;

  // Subscribe to `interview:plan-updated` pushes so the renderer
  // re-reads the state file the moment the SKILL writes a new plan
  // revision. The push is the fast path — main spins up an fs.watch
  // on the state file lazily on first read, so subsequent SKILL
  // writes get autonomous notifications.
  useEffect(() => {
    if (!gameId) return;
    const unsub = window.crucible.on('interview:plan-updated', (payload) => {
      if (payload.gameId !== gameId) return;
      refetchInterviewStateRef.current();
    });
    return unsub;
  }, [gameId]);

  // #553 belt-and-braces poller — see `hooks/useInterviewStatePoll`
  // for the full failure-mode + design prose. Mirrors the existing
  // `useInterviewStatus` refetch-interval policy onto the state
  // query, gated by `shouldPollInterviewState` (status-driven, not
  // depth-driven) so the SKILL's first quick-batch plan write is
  // observed even when the renderer's first read landed on a
  // pre-quick state file.
  //
  // Feed the gate off the STATE query's own status field (not the
  // separate `useInterviewStatus` summary). Two reasons:
  //   (1) Same query we're polling — no extra dependency / cross-query
  //       coupling that could rebind the effect on summary-only refetches.
  //   (2) Wider type — the state file persists the full
  //       `PersistedBuildGameInterviewStatus` (forward-compat for any
  //       future SKILL terminal), whereas the summary narrows to the
  //       closed `BuildGameInterviewStatus` literal. The poll predicate
  //       wants to keep polling on UNKNOWN statuses too, so reading the
  //       wide field is the safer plumbing.
  useInterviewStatePoll({
    gameId,
    status: interviewStateQuery.data?.status,
    refetch: interviewStateQuery.refetch,
  });

  const queryClient = useQueryClient();
  const startOverInterview = useStartOverInterview();

  const sendStartBuildingKickoff = useCallback(
    async (source: 'user' | 'auto-resume', seededPlan?: DesignBuildPlanSeed): Promise<boolean> => {
      if (!gameId || planModeBusy) return false;
      setPlanModeBusy(true);
      // PR #544 follow-up: clear any stale error before the new attempt
      // so a successful retry doesn't leave the previous failure's toast
      // hanging until its 6s timer expires.
      setPlanModeError(null);
      try {
        const result = await window.crucible.invoke('interview:start-building', {
          gameId,
          ...(seededPlan?.planRevision !== undefined
            ? { planRevision: seededPlan.planRevision }
            : interviewStateQuery.data?.planRevision !== undefined
              ? { planRevision: interviewStateQuery.data.planRevision }
              : {}),
        });
        const errorMessage = formatInterviewSentinelError(
          { kind: 'result', result },
          'starting the build',
        );
        if (!result.ok) {
          if (errorMessage) showPlanModeError(errorMessage);
          return false;
        }
        const now = Date.now();
        chatDispatch({
          type: 'chat/local-event',
          id: `build-started-${source}-${gameId}-${now}`,
          content:
            source === 'auto-resume'
              ? 'Build kickoff retry sent. Returning to Build.'
              : 'Build kickoff sent. Returning to Build.',
          timestamp: now,
        });
        setChatTab('chat');
        setWorkspaceMode('chat');
        void queryClient.invalidateQueries({
          queryKey: ['build-game:interview-status', gameId],
        });
        void queryClient.invalidateQueries({
          queryKey: ['build-game:get-interview-state', gameId],
        });
        // Refetch immediately so the renderer picks up the SKILL's
        // subPhase transition without waiting on the heartbeat.
        refetchInterviewStateRef.current();
        return true;
      } catch (err) {
        // Catch IPC rejections (main throws / preload bridge errors / etc.).
        // Without this, the rejection became an unhandled promise rejection
        // and the user had no idea the click did nothing.
        //
        // Bugbot MEDIUM (#544): the preload bridge flattens errors via
        // `flattenIpcError` on the main side; the raw `err` here carries
        // the wire-flattened shape. `unflattenIpcError` restores the
        // original Error semantics (CrucibleError reason chain, cause,
        // structured payload) so the user-facing message is accurate
        // rather than the raw flattened shape's `.message`.
        const unflattened = unflattenIpcError(err);
        const errorMessage = formatInterviewSentinelError(
          { kind: 'rejection', error: unflattened },
          'starting the build',
        );
        if (errorMessage) showPlanModeError(errorMessage);
        return false;
      } finally {
        setPlanModeBusy(false);
      }
    },
    [
      chatDispatch,
      gameId,
      interviewStateQuery.data?.planRevision,
      planModeBusy,
      queryClient,
      showPlanModeError,
    ],
  );

  const handleStartBuilding = useCallback(() => {
    void sendStartBuildingKickoff('user');
  }, [sendStartBuildingKickoff]);

  const handleResumeBuildKickoff = useCallback(() => {
    void sendStartBuildingKickoff('auto-resume');
  }, [sendStartBuildingKickoff]);

  const handleRetryAutoBuild = useCallback(() => {
    if (!gameId) return;
    void window.crucible
      .invoke('build:autoPipeline:retry', { gameId })
      .then((result) => {
        const errorMessage = formatInterviewSentinelError(
          { kind: 'result', result },
          'rebuilding from the plan',
        );
        if (!result.ok) {
          if (errorMessage) showPlanModeError(errorMessage);
          return;
        }
        void queryClient.invalidateQueries({
          queryKey: ['build-game:interview-status', gameId],
        });
        void queryClient.invalidateQueries({
          queryKey: ['build-game:get-interview-state', gameId],
        });
      })
      .catch((err: unknown) => {
        const unflattened = unflattenIpcError(err);
        const errorMessage = formatInterviewSentinelError(
          { kind: 'rejection', error: unflattened },
          'rebuilding from the plan',
        );
        if (errorMessage) showPlanModeError(errorMessage);
      });
  }, [gameId, queryClient, showPlanModeError]);

  const handleCancelAutoBuild = useCallback(() => {
    if (!gameId) return;
    void window.crucible
      .invoke('build:autoPipeline:cancel', { gameId })
      .then((result) => {
        if (!result.ok) return;
        const now = Date.now();
        chatDispatch({
          type: 'chat/local-event',
          id: `auto-build-cancelled-${gameId}-${now}`,
          content: 'Build setup cancelled.',
          timestamp: now,
        });
        void queryClient.invalidateQueries({
          queryKey: ['build-game:interview-status', gameId],
        });
        void queryClient.invalidateQueries({
          queryKey: ['build-game:get-interview-state', gameId],
        });
      })
      .catch((err: unknown) => {
        const unflattened = unflattenIpcError(err);
        showPlanModeError(`Could not cancel build setup: ${unflattened.message}`);
      });
  }, [chatDispatch, gameId, queryClient, showPlanModeError]);

  const sendStalledBuildResume = useCallback(() => {
    setChatTab('chat');
    setWorkspaceMode('chat');
    chatDispatch({
      type: 'user/send-text',
      text: buildStalledBuildResumePrompt(phaseProgress),
    });
  }, [chatDispatch, phaseProgress]);

  const handleResumeStalledBuild = useCallback(() => {
    sendStalledBuildResume();
  }, [sendStalledBuildResume]);

  useEffect(() => {
    if (resumeBuildActionKind !== 'resume-stalled-build') return;
    if (isAiTyping || planModeBusy) return;
    if (chatInput.trim().length > 0 || pendingAttachments.length > 0) return;
    const attemptKey = stalledBuildResumeAttemptKey(gameId, phaseProgress);
    if (!attemptKey || stalledBuildResumeAttemptedRef.current.has(attemptKey)) return;

    stalledBuildResumeAttemptedRef.current.add(attemptKey);
    sendStalledBuildResume();
  }, [
    chatInput,
    gameId,
    isAiTyping,
    pendingAttachments.length,
    phaseProgress,
    planModeBusy,
    resumeBuildActionKind,
    sendStalledBuildResume,
  ]);

  useEffect(() => {
    if (!buildKickoffKey) return;
    if (buildKickoffWatchdog?.status !== 'stalled') return;
    if (isAiTyping || planModeBusy) return;
    if (buildKickoffResumeAttemptedRef.current.has(buildKickoffKey)) return;
    buildKickoffResumeAttemptedRef.current.add(buildKickoffKey);
    setBuildKickoffClockNow(Date.now());
    void sendStartBuildingKickoff('auto-resume');
  }, [
    buildKickoffKey,
    buildKickoffWatchdog?.status,
    isAiTyping,
    planModeBusy,
    sendStartBuildingKickoff,
  ]);

  const handleSavePlan = useCallback(
    async (plan: string) => {
      if (!gameId) throw new Error('No game is open.');
      setPlanModeBusy(true);
      setPlanModeError(null);
      try {
        const result = await window.crucible.invoke('interview:save-plan', {
          gameId,
          plan,
          source: 'manual',
        });
        if (!result.ok) {
          throw new Error(`Couldn't save the plan (${result.reason ?? 'unknown error'}).`);
        }
        refetchInterviewStateRef.current();
      } catch (err) {
        const unflattened = unflattenIpcError(err);
        showPlanModeError(unflattened.message || 'Could not save the plan.');
        throw unflattened;
      } finally {
        setPlanModeBusy(false);
      }
    },
    [gameId, showPlanModeError],
  );

  const handleRebuildFromPlan = useCallback(async () => {
    if (!gameId || planModeBusy) return;
    setPlanModeBusy(true);
    setPlanModeError(null);
    try {
      const result = await window.crucible.invoke('interview:rebuild-from-plan', {
        gameId,
        ...(interviewStateQuery.data?.planRevision !== undefined
          ? { planRevision: interviewStateQuery.data.planRevision }
          : {}),
      });
      const errorMessage = formatInterviewSentinelError(
        { kind: 'result', result },
        'rebuilding from the plan',
      );
      if (!result.ok) {
        if (errorMessage) showPlanModeError(errorMessage);
        return;
      }
      const now = Date.now();
      chatDispatch({
        type: 'chat/local-event',
        id: `rebuild-started-${gameId}-${now}`,
        content: 'Rebuild kickoff sent. Returning to Build.',
        timestamp: now,
      });
      setWorkspaceMode('chat');
      refetchInterviewStateRef.current();
    } catch (err) {
      const unflattened = unflattenIpcError(err);
      const errorMessage = formatInterviewSentinelError(
        { kind: 'rejection', error: unflattened },
        'rebuilding from the plan',
      );
      if (errorMessage) showPlanModeError(errorMessage);
    } finally {
      setPlanModeBusy(false);
    }
  }, [
    chatDispatch,
    gameId,
    interviewStateQuery.data?.planRevision,
    planModeBusy,
    showPlanModeError,
  ]);

  const handleBackToPlanRequest = useCallback(() => {
    setBackToPlanModalOpen(true);
  }, []);

  const handleBackToPlanConfirm = useCallback(async () => {
    if (!gameId) return;
    setBackToPlanModalOpen(false);
    setPlanModeBusy(true);
    setPlanModeError(null);
    try {
      const result = await window.crucible.invoke('interview:back-to-plan', { gameId });
      const errorMessage = formatInterviewSentinelError(
        { kind: 'result', result },
        'going back to plan',
      );
      if (!result.ok) {
        if (errorMessage) showPlanModeError(errorMessage);
        return;
      }
      if (designWorkspaceEnabled) {
        setDesignBuildPlanFocusNonce((n) => n + 1);
        setWorkspaceMode('design');
      } else if (flags.newUi) {
        setWorkspaceMode('plan');
      }
      refetchInterviewStateRef.current();
    } catch (err) {
      // See handleStartBuilding for the unflattenIpcError rationale.
      const unflattened = unflattenIpcError(err);
      const errorMessage = formatInterviewSentinelError(
        { kind: 'rejection', error: unflattened },
        'going back to plan',
      );
      if (errorMessage) showPlanModeError(errorMessage);
    } finally {
      setPlanModeBusy(false);
    }
  }, [designWorkspaceEnabled, flags.newUi, gameId, showPlanModeError]);

  const handleBackToPlanCancel = useCallback(() => {
    setBackToPlanModalOpen(false);
  }, []);

  const handlePlanRefinement = useCallback(
    (text: string) => {
      // Bugbot MEDIUM (#544 round-2): the UI-level disable on the
      // refinement input can be bypassed by a rapid double-submit
      // before isAiTyping flips. Guard at dispatch with a fresh
      // getChatState() read; the UI disable handles the cosmetic
      // side, this catches the race.
      if (getChatState().isAiTyping) {
        showPlanModeError(
          'Plan refinement is already in flight — wait for the current turn to finish.',
        );
        return;
      }
      // Refinements travel through the normal chat path — the SKILL
      // observes `subPhase === 'plan'` and treats the user message as
      // a plan-refinement turn (rewriting the top-level `plan` markdown
      // per the canonical schema in #543).
      chatDispatch({ type: 'user/type', text });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch, getChatState, showPlanModeError],
  );

  // Design → build UPDATES go to Build (not Plan): switch to the Build tab and
  // auto-send the distilled edit list to the agent so it applies the changes
  // to the already-built game. Fired by the build-handoff dialog when the user
  // accepts an update (full first builds still route to Plan via onSentToBuild).
  const handleSendDesignUpdate = useCallback(
    (message: string) => {
      if (!message.trim()) return;
      setWorkspaceMode('chat');
      chatDispatch({ type: 'user/type', text: message });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch],
  );

  const handlePlanStartOver = useCallback(() => {
    if (!gameId) return;
    // Bugbot MEDIUM (#544): void-discarded promise swallows IPC
    // rejections. Surface them through the same toast affordance as
    // start-building / back-to-plan so the user knows the click did
    // nothing.
    startOverInterview.startOver(gameId).catch((err: unknown) => {
      const unflattened = unflattenIpcError(err);
      const message = unflattened instanceof Error ? unflattened.message : String(unflattened);
      showPlanModeError(`Couldn't start over: ${message}`);
    });
  }, [gameId, startOverInterview, showPlanModeError]);

  // Token status — drives whether the AI Chat panel renders. Chat is
  // backed by the Anthropic SDK; without a key the placeholder card
  // takes its place pointing the user at Settings (audit §4). We
  // distinguish "still loading" from "loaded with no key" so users
  // who do have a key never briefly see the "needs setup" copy
  // (Codex review: tokens?.anthropic === true is false for undefined,
  // which causes a placeholder flicker before the IPC resolves).
  //
  // Treat "errored" the same as "resolved with no key" — Macroscope
  // flagged that on the keychain-unavailable path the query rejects
  // and `tokens` stays undefined forever; without this the spinner
  // would render indefinitely with no error visible to the user.
  // (Settings.tsx surfaces the encryption-unavailable banner; the
  // chat panel just needs to stop spinning so the placeholder lands.)
  const { data: tokens, isLoading: tokensLoading, error: tokensError } = useIpcQuery('tokens:get');
  const hasAnthropicKey = tokens?.anthropic === true;
  const tokensResolved = !tokensLoading && (tokens !== undefined || tokensError !== null);

  const gameName = gameStatus?.displayName ?? gameId ?? 'Unknown Game';
  const handleDesignStartBuild = useCallback(
    async (seededPlan?: DesignBuildPlanSeed): Promise<void> => {
      if (gameId) livePreviewAutoOpenedGamesRef.current.add(gameId);
      const ok = await sendStartBuildingKickoff('user', seededPlan);
      if (!ok) {
        throw new Error(
          'Build kickoff did not complete. Check the plan error toast and try again.',
        );
      }
    },
    [gameId, sendStartBuildingKickoff],
  );
  const designBuildPlan = useMemo(
    () => ({
      gameName,
      focusNonce: designBuildPlanFocusNonce,
      plan: interviewPlan,
      answeredCount: interviewStatusQuery.data?.answeredCount,
      totalCount: interviewStatusQuery.data?.totalCount,
      planRevision: interviewState?.planRevision,
      planHash: interviewState?.planHash,
      planUpdatedAt: interviewState?.planUpdatedAt,
      planSource: interviewState?.planSource,
      lastBuiltPlanRevision: interviewState?.lastBuiltPlanRevision,
      lastBuiltPlanHash: interviewState?.lastBuiltPlanHash,
      buildCompletionUnverified,
      buildRequestPending: workspaceRunState.planRevisionLocked,
      busy: isAiTyping || planModeBusy,
      draftPlanPending: workspaceRunState.kind === 'drafting-plan',
      onSavePlan: handleSavePlan,
      onStartBuilding: handleDesignStartBuild,
      onRebuildFromPlan: handleRebuildFromPlan,
      onBackToChat: () => setWorkspaceMode('chat'),
      onSubmitRefinement: handlePlanRefinement,
      onStartOver: handlePlanStartOver,
    }),
    [
      designBuildPlanFocusNonce,
      gameName,
      handleDesignStartBuild,
      handlePlanRefinement,
      handlePlanStartOver,
      handleRebuildFromPlan,
      handleSavePlan,
      interviewPlan,
      interviewState?.lastBuiltPlanHash,
      interviewState?.lastBuiltPlanRevision,
      interviewState?.planHash,
      interviewState?.planRevision,
      interviewState?.planSource,
      interviewState?.planUpdatedAt,
      interviewStatusQuery.data?.answeredCount,
      interviewStatusQuery.data?.totalCount,
      isAiTyping,
      buildCompletionUnverified,
      planModeBusy,
      workspaceRunState.kind,
      workspaceRunState.planRevisionLocked,
    ],
  );
  const visibleDeployFailureContext =
    deployFailureCardEnabled && deployFailureContext?.status === 'failure'
      ? deployFailureContext
      : undefined;
  const deployActionState = useMemo(
    () =>
      publishBacked
        ? deriveDeployActionState({
            deployState,
            deployFailureContext: visibleDeployFailureContext,
            localFailedCheck,
            ciTriggeredThisDeploy: deployFailureCardEnabled,
            hasAnthropicKey,
          })
        : {
            kind: 'blocked' as const,
            label: 'Deploy unavailable',
            tone: 'neutral' as const,
            badgeClassName: 'bg-white/10 text-white/50',
            deployButtonLabel: 'Deploy to TV',
            deployButtonTitle: 'Deploy to TV is unavailable in this runtime.',
            deployButtonDisabled: true,
            deployButtonGated: true,
            failureSecondaryCtas: [],
            disabledReason: 'Deploy to TV is unavailable in this runtime.',
            debugSource: 'deploy:capability-unavailable',
          },
    [
      deployFailureCardEnabled,
      deployState,
      hasAnthropicKey,
      localFailedCheck,
      publishBacked,
      visibleDeployFailureContext,
    ],
  );
  const cloneDeployAccessWarning = useMemo<CloneDeployAccessWarning | undefined>(() => {
    if (!clonedRepoFullName) return undefined;
    const access = clonedRepoAccess ?? {
      state: 'unknown' as const,
      repoUrl: `https://github.com/${clonedRepoFullName}`,
      reason: 'Checking source repository access.',
    };
    if (access.state === 'writable') return undefined;
    return {
      repoFullName: clonedRepoFullName,
      ...access,
    };
  }, [clonedRepoAccess, clonedRepoFullName]);

  // Crucible #602 PR #C4 (Issue E) — hydration cursor for the
  // `activity:checkpoint` subscriber. Codex round-1 HIGH #1: between
  // Workspace mount and the `chat:load` IPC resolving, an
  // `activity:checkpoint` push could land + dispatch
  // `chat/checkpoint-received` (inserts the row into `state.messages`)
  // → then the in-flight `chat:load` resolves and `chat/loaded` REPLACES
  // `state.messages` with the snapshot the disk had BEFORE the emitter's
  // queued append committed. The freshly-inserted checkpoint vanishes
  // until the next mount.
  //
  // Fix: buffer in-flight checkpoint pushes that arrive before
  // hydration completes, then replay them after `chat/loaded` has been
  // dispatched. The replay is per-`gameId`-scoped via the `gameId`
  // closure on the subscription effect; a cross-game cleanup wipes
  // the buffer (the new game's state has its own).
  //
  // Implemented with a single boolean `hydratedForGameIdRef` and a
  // local `pendingPushesRef` keyed by the gameId we're currently
  // hydrating against. The buffer is drained once the load resolves;
  // any subsequent pushes flow through directly.
  const hydratedForGameIdRef = useRef<string | null>(null);
  const pendingCheckpointPushesRef = useRef<
    Array<{ gameId: string; checkpoint: InlineCheckpoint }>
  >([]);

  // Crucible #602 PR #C4 (Issue E) — inline-checkpoint push subscriber.
  // Adjacent to the `agent:stream` and `interview:*` subscriptions —
  // same `window.crucible.on/off` shape. The emitter side
  // (`main/inline-checkpoint-emitter.ts`) writes to disk FIRST and
  // pushes ONLY on a successful append (design §2.1 + §6.3); a stray
  // push for the wrong gameId is also defended against by the reducer's
  // cross-game guard in `chat/state.ts` "chat/checkpoint-received".
  //
  // The flag-gate is at registration time per design §12 Issue E and
  // mirrors `useFeatureFlags`'s contract — the subscription is NOT
  // registered until `loaded && agentTransparency`. A future flag-off
  // window therefore receives ZERO checkpoint pushes (no subscriber
  // exists), which is the FROZEN-wire-compatible way to disable the
  // surface.
  //
  // Pre-hydration buffering — Codex round-1 HIGH #1. If `chat:load`
  // hasn't resolved yet for the current `gameId`, the push is queued
  // into `pendingCheckpointPushesRef` and drained when the load's
  // `.then` dispatches `chat/loaded`. The buffer survives across
  // the brief Frame-1 → Frame-2 window described in design §8.
  useEffect(() => {
    if (!gameId) return;
    if (!featureFlags.loaded || !featureFlags.agentTransparency) return;
    const dispatchOrBuffer = (push: { gameId: string; checkpoint: InlineCheckpoint }): void => {
      if (hydratedForGameIdRef.current === gameId) {
        chatDispatch({
          type: 'chat/checkpoint-received',
          gameId: push.gameId,
          checkpoint: push.checkpoint,
        });
        return;
      }
      // Not hydrated yet — buffer for replay after `chat/loaded`.
      pendingCheckpointPushesRef.current.push(push);
    };
    const unsub = window.crucible.on('activity:checkpoint', (push) => {
      // Cross-game guard. The reducer also enforces this on the
      // dispatch path (action drop) but bailing here saves a render
      // tick + a `useChatReducer` `dispatch` round-trip when the same
      // renderer can observe pushes for any registry-active game.
      if (push.gameId !== gameId) return;
      // Defence-in-depth on the wire shape — a malformed push with
      // `checkpoint: undefined` would crash the reducer's
      // `state.messages.some((m) => m.id === action.checkpoint.id)`.
      // The emitter never produces this shape but ipc-bridge bugs and
      // forwarded test fixtures have a habit of slipping bad payloads
      // through; silently dropping is the right move (the disk write
      // is the durable surface anyway).
      if (!push.checkpoint || typeof push.checkpoint.id !== 'string') return;
      dispatchOrBuffer({ gameId: push.gameId, checkpoint: push.checkpoint });
    });
    return unsub;
  }, [gameId, chatDispatch, featureFlags.loaded, featureFlags.agentTransparency]);

  const shouldHoldInitialKickoff = useCallback(
    () =>
      shouldHoldInitialKickoffForRendererReady({
        tokensResolved,
        hasAnthropicKey,
        flagsLoaded: featureFlags.loaded,
        interviewEngineV1: featureFlags.interviewEngineV1,
        interviewRendererReady,
      }),
    [
      featureFlags.loaded,
      featureFlags.interviewEngineV1,
      hasAnthropicKey,
      interviewRendererReady,
      tokensResolved,
    ],
  );

  const invokeAgentSendEffect = useCallback(
    (effect: AgentSendEffect, isInitialKickoff: boolean): void => {
      if (isInitialKickoff) {
        setInitialKickoff((current) => ({
          status: 'dispatching',
          startedAt: current.startedAt ?? Date.now(),
        }));
      }
      window.crucible
        .invoke('agent:send', {
          gameId: effect.gameId,
          message: effect.message,
          ...(effect.userMessageId ? { userMessageId: effect.userMessageId } : {}),
          assistantMessageId: effect.assistantMessageId ?? effect.assistantMsgId,
          ...(effect.userTimestamp !== undefined ? { userTimestamp: effect.userTimestamp } : {}),
          ...(effect.assistantTimestamp !== undefined
            ? { assistantTimestamp: effect.assistantTimestamp }
            : {}),
          ...(effect.turnId ? { turnId: effect.turnId } : {}),
          ...(effect.attachments && effect.attachments.length > 0
            ? { attachments: [...effect.attachments] }
            : {}),
        })
        .then(() => {
          if (!isInitialKickoff) return;
          setInitialKickoff((current) => {
            if (
              current.status !== 'waiting-for-renderer-ready' &&
              current.status !== 'dispatching'
            ) {
              return current;
            }
            return {
              status: 'dispatched',
              startedAt: current.startedAt ?? null,
            };
          });
        })
        .catch((err: unknown) => {
          // Bridge-3 close-out (Bugbot Medium #1) — pick the scrub
          // level from the LIVE FurnaceStatus snapshot via the ref
          // (so the closure stays stable across status refreshes).
          // The previous hard-coded `'banned-only'` could leak an
          // infrastructure URL through both the kickoff-failure
          // detail and the user-bubble error dispatch when status
          // was still loading at the moment of failure (the
          // `agent:stream` chunk handler already picks
          // `'aggressive'` in the same loading window, so the
          // mismatch was the bug).
          const scrubLevel = pickFurnaceScrubLevel(
            furnaceStatusRef.current,
            gameImportedForFurnaceRef.current,
          );
          if (isInitialKickoff) {
            const failure = classifyKickoffFailure(err, scrubLevel);
            setInitialKickoff((current) => ({
              status: 'failed',
              reason: failure.reason,
              detail: failure.detail,
              startedAt: current.startedAt ?? null,
            }));
          }
          // Codex round-3 — scrub infrastructure URLs from the
          // decoded SerialisedError before dispatch to the chat
          // reducer. The reducer stores the error verbatim on
          // the assistant bubble, where `CrucibleErrorState`
          // renders `.message` AND `.recovery` AND nested
          // `.processOutput` / `.cause` fields. The recursive
          // scrubber visits every user-facing string field so
          // a pre-stream Furnace failure can't leak a hostname
          // through any of them.
          //
          // Bridge-3 close-out (Bugbot Medium #1) — `scrubLevel`
          // now mirrors the `agent:stream` chunk handler's
          // decision (aggressive when status is loading or
          // in-cohort, banned-only when definitively local). The
          // pre-Bridge-3 identity for flag-OFF / out-of-cohort
          // engineers is preserved — `pickFurnaceScrubLevel`
          // returns `'banned-only'` once status has resolved as
          // local, so a local error referencing
          // `http://localhost:3000` stays visible verbatim.
          chatDispatch({
            type: 'ipc/send-failed',
            assistantMsgId: effect.assistantMsgId,
            error: scrubSerialisedError(unflattenIpcError(err), scrubLevel),
          });
          // Bridge-3 close-out (Bugbot Medium, round-2) — also
          // fire the Furnace failure-latch on pre-stream
          // `agent:send` rejections. The chat reducer was
          // already rendering these in the chat bubble (via the
          // dispatch above) but the indicator + banner stayed
          // silent because the latch only fired from the
          // `agent:stream` chunk handler. That left the same
          // three-surface asymmetry Med #2 closed for streaming
          // errors. Pre-stream rejections (e.g. cohort consult
          // refusal, no-cloud-proxy-config) are the highest-
          // signal Furnace failure class — they MUST flip the
          // indicator + banner. Classify the RAW error so the
          // classifier matches against the original substrings
          // (consistent with the stream handler — the classified
          // copy is built from constants, not the raw text, so
          // no infrastructure URL leaks through). The latch is
          // gated through `shouldLatchFurnaceFailure` so
          // out-of-cohort engineers + flag-OFF engineers don't
          // see a Furnace surface for a local rejection.
          if (
            shouldLatchFurnaceFailure({
              furnaceStatus: furnaceStatusRef.current,
            })
          ) {
            const raw = err instanceof Error ? err.message : typeof err === 'string' ? err : '';
            const view = classifyFurnaceError(raw);
            setFurnaceLastFailure(view);
          }
        });
    },
    [chatDispatch],
  );

  useEffect(() => {
    const pending = pendingInitialAgentSendRef.current;
    if (!pending) return;
    if (initialKickoff.status === 'timed-out' || initialKickoff.status === 'failed') {
      pendingInitialAgentSendRef.current = null;
      return;
    }
    if (shouldHoldInitialKickoff()) return;
    pendingInitialAgentSendRef.current = null;
    invokeAgentSendEffect(pending, true);
  }, [initialKickoff.status, shouldHoldInitialKickoff, invokeAgentSendEffect]);

  // Subscribe to agent:stream push events from main process. Every
  // chunk routes through the reducer's `chunk/received` action — the
  // case-by-case handling (append text, upsert tool calls, drain
  // queue on terminal) lives in `chat/state.ts`.
  useEffect(() => {
    const unsub = window.crucible.on('agent:stream', (chunk: AgentStreamChunk) => {
      // Multi-game stream-discriminator invariant (Codex round 3 §4 +
      // round 4 §5 of PR #149's plan-doc review). Every chunk carries
      // a sessionId of the form `gui-<gameId>-<timestamp>` where
      // timestamp is a positive integer (Date.now()). The
      // `agent:stream` channel is global so a chunk for game A can
      // technically reach a Workspace mounted on game B.
      //
      // Naive predicates fail in two directions:
      //   - `.includes('-<gameId>-')` false-accepts `gui-foo-bar-…`
      //     on a `foo` Workspace because the substring "-foo-" matches.
      //   - `.startsWith('gui-<gameId>-')` ALSO false-accepts
      //     `gui-foo-bar-…` on a `foo` Workspace because "gui-foo-bar"
      //     starts with "gui-foo-".
      //
      // The structurally correct check is "the bit between `gui-` and
      // the trailing `-<digits>` is exactly `<gameId>`". Match against
      // an anchored regex: gameIds are pre-validated upstream as
      // `[a-zA-Z0-9_-]+` (assertValidGameId) so no regex metachars
      // are smuggled in here.
      if (gameId) {
        const matcher = new RegExp(`^gui-${gameId}-\\d+$`);
        if (!matcher.test(chunk.sessionId)) {
          return;
        }
      }
      // Bridge-3 — infrastructure-URL scrub. Codex round-1 HIGH:
      // the chat reducer's `error` branch stores `chunk.error`
      // verbatim, and `CrucibleErrorState` renders it in the
      // assistant bubble. A raw Furnace failure embedding
      // `forge-dev.volley-services.net` would otherwise leak the
      // hostname into the chat surface even while the new
      // `<FurnaceErrorBanner>` stayed clean.
      //
      // Scrub-level decision matrix (Codex round-5 fail-closed):
      //   - Status not yet resolved (undefined) → 'aggressive'.
      //     Fail closed — we don't know whether this turn was
      //     Furnace-routed; assume it might be and strip every
      //     URL. A benign local URL gets redacted in this
      //     loading window, but that's a brief, edge-case
      //     regression compared to leaking infrastructure URLs
      //     when status hadn't resolved.
      //   - In-cohort + flag-on → 'aggressive'.
      //   - Resolved + (flag-off OR cohort != 'in') →
      //     'banned-only'. We have a DEFINITIVE non-Furnace
      //     answer; preserve benign URLs (pre-Bridge-3 identity
      //     for flag-OFF / out-of-cohort engineers).
      const liveStatus = furnaceStatusRef.current;
      const isFurnaceRoutedNow =
        liveStatus !== undefined &&
        liveStatus.cloudVmV1Enabled === true &&
        liveStatus.cohort === 'in';
      const isDefinitelyLocal =
        liveStatus !== undefined &&
        (liveStatus.cloudVmV1Enabled === false || liveStatus.cohort !== 'in');
      const scrubLevel = isFurnaceRoutedNow || !isDefinitelyLocal ? 'aggressive' : 'banned-only';
      const scrubbedChunk: AgentStreamChunk =
        (chunk.type === 'error' || chunk.type === 'warning') && chunk.error !== undefined
          ? { ...chunk, error: scrubInfrastructureUrls(chunk.error, scrubLevel) }
          : chunk;

      if (chunk.type === 'error') {
        setInitialKickoff((current) => {
          if (
            current.status !== 'waiting-for-renderer-ready' &&
            current.status !== 'dispatching' &&
            current.status !== 'dispatched'
          ) {
            return current;
          }
          // Bridge-3 close-out (Bugbot Medium #1) — thread the
          // same `scrubLevel` the handler picked for the chunk
          // itself into the kickoff-failure detail so the two
          // user-facing surfaces (the ActivityBanner kickoff
          // detail + the chat-bubble error rendering of
          // `scrubbedChunk`) agree on the strip-aggressiveness.
          const failure = classifyKickoffFailure(chunk.error ?? 'Unknown error', scrubLevel);
          return {
            status: 'failed',
            reason: failure.reason,
            detail: failure.detail,
            startedAt: current.startedAt ?? null,
          };
        });
        // Bridge-3 — Furnace failure-latch. Classify the RAW error
        // (pre-scrub) so the classifier can match against the
        // original substrings; the classified copy is never built
        // from the raw text so no infrastructure URL leaks into the
        // user-facing surface anyway.
        //
        // Bridge-3 close-out — Bugbot Low #3 + Medium #2 stacked
        // gate. See `shouldLatchFurnaceFailure` JSDoc for the
        // full matrix; short version:
        //
        //   - During the loading window (`furnaceStatus === undefined`)
        //     we still latch eagerly so an in-cohort engineer whose
        //     first turn errors before `app:get-furnace-status`
        //     resolves still gets the indicator + banner
        //     retroactively (Codex round-1 MEDIUM by Agent N's
        //     original design intent). The clearing useEffect above
        //     mops up if status resolves to out-of-cohort instead —
        //     it depends on `furnaceStatus` so it fires on every
        //     status update, not just on `isFurnaceRouted`
        //     transitions (the Low #3 root cause).
        //
        //   - Once status resolves, we only latch when known
        //     in-cohort. A resolved-out latch never lands, so a
        //     subsequent cohort flip into 'in' can't resurface a
        //     stale failure (Bugbot Low #3 fix).
        //
        //   - Main now rejects sessionless terminal `agent:stream`
        //     chunks and pre-stream failures get a GUI-scoped
        //     `gui-<gameId>-<digits>` id, so the cross-game guard
        //     above can run for every chunk.
        //
        // The downstream banner mount-gate
        // (`furnaceBuildState.phase === 'failed' ? furnaceLastFailure : null`)
        // is the load-bearing visibility gate; the setter-level
        // gate above is the secondary one that keeps the latch
        // state honest.
        if (shouldLatchFurnaceFailure({ furnaceStatus: liveStatus })) {
          const view = classifyFurnaceError(chunk.error ?? '');
          setFurnaceLastFailure(view);
        }
      } else {
        setInitialKickoff((current) => {
          if (
            current.status !== 'waiting-for-renderer-ready' &&
            current.status !== 'dispatching' &&
            current.status !== 'dispatched'
          ) {
            return current;
          }
          return { status: 'idle', startedAt: null };
        });
      }
      setPinnedAskUserQuestion((pending) =>
        pending && shouldClearPinnedAskUserQuestion(pending.id, scrubbedChunk) ? null : pending,
      );
      if (
        fastAnswersSubmittedAtRef.current !== null &&
        (scrubbedChunk.type === 'done' ||
          scrubbedChunk.type === 'interrupted' ||
          scrubbedChunk.type === 'error')
      ) {
        if (!fastPlanReadyRef.current) setFastPlanMissingAt(Date.now());
        setFastAnswersSubmittedAt(null);
      }
      chatDispatch({ type: 'chunk/received', chunk: scrubbedChunk });
    });

    return unsub;
  }, [gameId, chatDispatch]);

  // Crucible #416 Chunk C — mirror chat messages into the in-session
  // phase-events buffer. The phase-detail panel filters this buffer
  // by `event.phaseSeq` to show "what happened during phase N". The
  // buffer is per-game (the hook scopes via `setGameId(gameId)`), and
  // the store's `recordEvent` tags every entry with the current
  // `phaseSeq` snapshot — see `stores/phase-events.ts`.
  //
  // We track the count rather than diffing the array because the
  // chat reducer's append-only invariant means new messages always
  // land at the end; replaying the entire array on every render
  // would re-buffer events the store already has. The ref keeps the
  // last-mirrored count between renders.
  //
  // Crucible #602 PR #C4 (Issue E) — Codex round-1 HIGH #2: the chat
  // reducer's `chat/checkpoint-received` action can INSERT a row at a
  // non-tail index (the action sorts by timestamp). When that happens
  // every row after the insertion point shifts forward by one and the
  // cursor walks back over rows we've already mirrored. The store's
  // `recordEvent` enforces id-dedupe (see `phase-events.ts:76`) so
  // duplicates are silently dropped, but we ALSO skip rows by id here
  // to avoid the redundant cross-call. The cursor stays index-based
  // (cheap; advancement is O(delta)); id-tracking is layered on top.
  const lastMirroredCount = useRef(0);
  const mirroredMessageIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (messages.length < lastMirroredCount.current) {
      // The reducer wiped the array (game switch / start over). Reset
      // our cursor + id-tracker so we start fresh.
      lastMirroredCount.current = 0;
      mirroredMessageIdsRef.current = new Set();
    }
    for (let i = lastMirroredCount.current; i < messages.length; i++) {
      const msg = messages[i];
      if (!msg) continue;
      // Crucible #602 PR #C1: `'inline-checkpoint'` is a reserved
      // role for system-milestone rows (PR #C3 emits them). They
      // are chrome, not chat — skip them from the phase-events
      // mirror so the phase strip's per-phase filter doesn't
      // attribute checkpoints to whatever phase happened to be
      // active when the checkpoint was emitted.
      if (msg.role === 'inline-checkpoint') continue;
      // Crucible #602 PR #C4 (Issue E) — Codex round-1 HIGH #2:
      // skip rows we've already mirrored. The reducer's by-timestamp
      // insertion of checkpoints can shift later user/assistant rows
      // back into the cursor's range; without this guard we'd call
      // `recordPhaseEvent` for the shifted row a second time (the
      // store's id-dedupe drops it, but we save the round-trip + a
      // potentially-wrong-phase first-record path).
      if (mirroredMessageIdsRef.current.has(msg.id)) continue;
      mirroredMessageIdsRef.current.add(msg.id);
      recordPhaseEvent({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
      });
    }
    lastMirroredCount.current = messages.length;
  }, [messages, recordPhaseEvent]);

  // Install the effect interpreter — converts the reducer's
  // `ipc/agent-*` effects into real `window.crucible.invoke` calls.
  // Re-installs whenever the initial-kickoff readiness gate or
  // send invoker changes. The `setInterpreter` call is idempotent:
  // it replaces the previous closure and drains any effects that
  // fired before the very first installation (e.g. an autofire
  // `chat/loaded` effect dispatched synchronously inside a render
  // that beat this useEffect).
  useEffect(() => {
    setInterpreter((effect) => {
      switch (effect.type) {
        case 'ipc/agent-send': {
          const isInitialKickoff =
            effect.userMessageId?.startsWith(INITIAL_PROMPT_MESSAGE_ID_PREFIX) === true;
          if (isInitialKickoff && shouldHoldInitialKickoff()) {
            pendingInitialAgentSendRef.current = effect;
            setInitialKickoff((current) => ({
              status: 'waiting-for-renderer-ready',
              startedAt: current.startedAt ?? Date.now(),
            }));
            return;
          }
          invokeAgentSendEffect(effect, isInitialKickoff);
          return;
        }
        case 'ipc/agent-interrupt':
          // Issue #140: fire-and-forget. The adapter emits an
          // `interrupted` chunk on `agent:stream` once the SDK
          // iterator unwinds — that's what stamps "(stopped)" on
          // the in-progress bubble (see the reducer's
          // `chunk: interrupted` branch). Errors here only surface
          // if the IPC bridge itself wedged.
          void window.crucible
            .invoke('agent:interrupt', { gameId: effect.gameId })
            .catch((err: unknown) => {
              console.error('[chat] agent:interrupt failed', err);
            });
          return;
        case 'ipc/agent-answer':
          // Reserved for the AskUserQuestion gate path. The
          // AskUserQuestionCard currently fires its own IPC directly
          // (see `sendAnswer` below) — this case lets a future
          // refactor route gate answers through the reducer for
          // testability without changing the wire.
          void window.crucible
            .invoke('agent:answer-user-question', {
              toolUseID: effect.toolUseID,
              answers: effect.answers,
            })
            .catch((err: unknown) => {
              console.error('[chat] agent:answer-user-question failed', err);
            });
          return;
        case 'ui/error':
          // Screenshot-paste MVP (#193): the reducer surfaces
          // attachment-cap rejections via this effect. A `console.warn`
          // for telemetry; the input-row banner reads directly from
          // `chat.lastAttachmentError` for the visible UI. (Setting
          // it back through dispatch would require yet another action
          // — for the MVP we just store it locally on the chat slice
          // when the reducer rejects the chip.)
          console.warn('[chat] ui/error', effect.message);
          setAttachmentError(effect.message);
          return;
        case 'ipc/bulk-interview-answer':
          // #86 — fire `bulk-interview:answer` with the full answer
          // batch (or an empty batch + `cancelled: true`). Fire-and-
          // forget with a log-only catch: the reducer has already
          // cleared `pendingBulkInterview` so the form is gone from
          // the UI; a flaky main-side reject doesn't justify
          // resurrecting it. If the gate path turns out to be flaky
          // in production the right fix is to harden the main-side
          // handler, not to paper over here.
          void window.crucible
            .invoke('bulk-interview:answer', {
              gameId: effect.gameId,
              sessionId: effect.sessionId,
              answers: effect.answers,
              ...(effect.cancelled ? { cancelled: true } : {}),
            })
            .catch((err: unknown) => {
              console.error('[chat] bulk-interview:answer failed', err);
            });
          return;
        case 'ipc/deps-respond':
          // Crucible #548 — fire `deps:respond` carrying the user's
          // decision. Fire-and-forget with a log-only catch: the
          // reducer has already popped the entry from the queue so
          // the modal is gone; a flaky main-side handler shouldn't
          // resurrect it. Approval path branches in main (drives
          // pnpm and synthesises the tool result); decline path
          // simply resolves the gate.
          if (effect.approved) {
            void window.crucible
              .invoke('deps:respond', {
                sessionId: effect.sessionId,
                approved: true,
                finalVersion: effect.finalVersion,
              })
              .catch((err: unknown) => {
                console.error('[chat] deps:respond (approve) failed', err);
              });
          } else {
            void window.crucible
              .invoke('deps:respond', {
                sessionId: effect.sessionId,
                approved: false,
                reason: effect.reason,
              })
              .catch((err: unknown) => {
                console.error('[chat] deps:respond (decline) failed', err);
              });
          }
          return;
      }
    });
  }, [setInterpreter, shouldHoldInitialKickoff, invokeAgentSendEffect, setAttachmentError]);

  /**
   * Issue #350 — initial view mode for the FSM interview overlay.
   * Hoisted above `handleSendMessage` (and the express handler) so
   * both the click-driven (button) and type-driven (NIT 2 below)
   * speed-run paths can flip it before dispatching `user/send`. The
   * effects further down reset it on `gameId` change and on the
   * engine's `interview:complete` push.
   */
  const [interviewViewMode, setInterviewViewMode] = useState<'paginated' | 'stacked'>('paginated');
  const handleInterviewRendererReady = useCallback(() => {
    setInterviewRendererReady(true);
  }, []);

  const handleSendMessage = useCallback(() => {
    // "Waiting on your input…" fix — when an AskUserQuestion gate is open,
    // a typed reply must reach the paused agent, not queue behind the
    // in-flight turn. Route it to the gate's answer (same IPC the card uses,
    // which resumes the turn — no new send, no #170 bounce) instead of the
    // `user/send` queue path. Scoped to a single-question gate; see
    // `decideComposerSend`.
    const gateDecision = decideComposerSend({ pinnedAskUserQuestion, text: chatInput });
    if (gateDecision.kind === 'answer-question') {
      void window.crucible
        .invoke('agent:answer-user-question', {
          toolUseID: gateDecision.toolUseID,
          answers: gateDecision.answers,
        })
        .catch(() => {
          // Mirror AskUserQuestionCard: log only. We do NOT fall back to a
          // chat send — that bounces off the still-in-flight gate turn.
          console.error('[chat] agent:answer-user-question (composer) IPC failed');
        });
      // Clear the composer; the pinned card clears itself when the gate's
      // tool_result lands (`shouldClearPinnedAskUserQuestion`).
      chatDispatch({ type: 'user/type', text: '' });
      return;
    }
    // Issue #385 NIT 2 + Codex Round-3 YELLOW 1 — pre-send view-mode
    // decision. The send is the choke point where the user explicitly
    // commits to a build kickoff; `decideSendModeReset` returns:
    //
    //   - `'stacked'` for a typed `/build-game --express` (muscle
    //     memory — SKILL ignores the flag post-PR G, but the renderer
    //     honours the explicit opt-in).
    //   - `'paginated'` for a typed plain `/build-game` (defaults,
    //     including clearing any stale latch from a prior `mode-only`
    //     icon click on a turn where no overlay was mounted — YELLOW 1
    //     leak: the existing reset triggers at ~775-789 only catch
    //     game changes and `interview:complete` pushes).
    //   - `null` for anything else (a typed answer / chat message
    //     doesn't redefine the next overlay's intent).
    //
    // Reading the latest `chatInput` from the reducer state keeps this
    // in lockstep with what the reducer's `user/send` is about to
    // consume — no race against an unflushed `user/type`.
    const nextMode = decideSendModeReset({ chatInput });
    if (nextMode !== null) {
      setInterviewViewMode(nextMode);
    }
    chatDispatch({ type: 'user/send' });
  }, [chatDispatch, chatInput, pinnedAskUserQuestion]);

  const handleStopMessage = useCallback(() => {
    chatDispatch({ type: 'user/stop' });
  }, [chatDispatch]);

  /**
   * Issue #369 — chat-bubble retry. Wired only when
   * `message.error.retryable === true` (currently emitted by PR #368
   * for the inFlight bouncer's `CRUCIBLE-FOUNDRY-TURN-IN-FLIGHT` case;
   * future retryable codes get the same affordance for free).
   *
   * Debounce window is sized to the agent-adapter bouncer's grace
   * timer (`agent-adapter.ts:2986-2996`, 1500ms): a rapid second click
   * inside that window would just hit the same bouncer again, and the
   * user has no way to tell that the first click took. The ref-based
   * debounce stops the second dispatch synchronously without burning
   * an IPC round-trip. We fall through to the reducer for the actual
   * "is a turn in flight" guard so the two checks compose cleanly.
   */
  const lastRetryAtRef = useRef(0);
  const handleRetry = useCallback(
    (message: ChatMessage) => {
      const now = Date.now();
      if (now - lastRetryAtRef.current < 1500) return;
      lastRetryAtRef.current = now;
      chatDispatch({ type: 'user/retry', messageId: message.id });
    },
    [chatDispatch],
  );

  /**
   * Issue #215 — fallback parser click handler. The bubble
   * component matched a `Question N of M:` markdown shape and the
   * user clicked one of the synthetic option buttons. Dispatch the
   * option text via the same `user/type` + `user/send` path the
   * textarea uses, so:
   *
   *   - Mid-turn semantics are identical to typing the answer
   *     (queue when busy, send directly otherwise — handled by the
   *     reducer).
   *   - Cross-game guards apply (the reducer checks `state.gameId`
   *     when building the IPC effect).
   *   - The user's answer appears as a normal user-bubble in the
   *     timeline, just like typing it would.
   *
   * Pre-set `chatInput` first so an in-flight Compose-state value
   * doesn't get clobbered if a click and a textarea-edit race;
   * `user/send` reads from the reducer's `chatInput` field, so the
   * sequence MUST be type-then-send.
   */
  const handleAnswerMarkdownQuestion = useCallback(
    (optionText: string) => {
      chatDispatch({ type: 'user/type', text: optionText });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch],
  );

  /**
   * #86 — bulk-interview Submit. The card collected answers locally,
   * validated required fields, and now hands them off as a single
   * batch keyed by question id. The reducer clears
   * `pendingBulkInterview` and emits the IPC effect.
   */
  const handleBulkInterviewSubmit = useCallback(
    (sessionId: string, answers: Record<string, string>) => {
      const now = Date.now();
      fastAnswersSubmittedAtRef.current = now;
      setFastAnswersSubmittedAt(now);
      setFastPlanMissingAt(null);
      chatDispatch({ type: 'bulk-interview/answer', sessionId, answers });
      chatDispatch({
        type: 'chat/local-event',
        id: `answers-submitted-${sessionId}-${now}`,
        content: 'Interview answers submitted.',
        timestamp: now,
      });
    },
    [chatDispatch],
  );

  /**
   * #86 — bulk-interview Cancel. Same shape as Submit but flagged
   * cancelled. The user keeps their partial input in the audit log,
   * but main-side falls back to per-question mode (the SKILL drops
   * out of bulk and continues asking via AskUserQuestion).
   */
  const handleBulkInterviewCancel = useCallback(
    (sessionId: string, partialAnswers: Record<string, string>) => {
      chatDispatch({
        type: 'bulk-interview/cancel',
        sessionId,
        answers: partialAnswers,
      });
    },
    [chatDispatch],
  );

  /**
   * Issue #350 — express-mode toggle, re-wired to the FSM slot-card
   * engine. PR G (hello-weekend v0.14.0) replaced Phase A's chat-
   * driven interview with Crucible's FSM engine, so the legacy
   * `/build-game --express` chat command no longer reaches a SKILL
   * that recognises the flag — the SKILL now starts at Phase B.
   *
   * The new wiring is purely renderer-side:
   *
   *   1. Flip the renderer's `interviewViewMode` state to `'stacked'`,
   *      which the `<InterviewSlotOverlay>` reads as `initialViewMode`
   *      so its very first slot push paints with the answered-history
   *      pane and the "Show one" toggle.
   *   2. Type `/build-game` into the composer and fire `user/send` so
   *      the engine kicks off through the same code path the user
   *      would hit if they typed `/build-game` themselves. (No
   *      `--express` flag — the SKILL ignores it now; mode is a
   *      renderer concern, not a SKILL contract.)
   *
   * The `interviewViewMode` state was hoisted above
   * `handleSendMessage` so the typed-flag detection path (#385 NIT 2)
   * can flip it without forward-referencing the setter.
   *
   * Issue #461 Part 2 — cooperative mid-Phase-A click. The Round-1
   * fix dropped the `isAiTyping` clause from `shouldShowExpressToggle`
   * so the button stays visible during Phase A (the FSM engine holds
   * the renderer-side `isAiTyping=true` for every slot card it pushes
   * because the main-side `session.inFlight` latch is set BEFORE the
   * engine kickoff and stays set until Phase B's SDK turn streams
   * `done`). Codex correctly flagged the follow-on problem: with the
   * button now reachable mid-flow, an `isAiTyping=true` click here
   * would have `user/send`'s text-only reducer branch queue the
   * `/build-game` text rather than fire it (renderer chat/state.ts
   * lines 491-522 — text-only sends queue mid-turn instead of
   * triggering the bouncer). That queued message would then replay
   * AFTER Phase B's `done`, colliding with `session.inFlight=true`
   * (still) or kicking a fresh `/build-game` against a game already
   * built — neither is what the user clicked Speed Run for.
   *
   * Cooperative resolution: when a turn is in flight (`isAiTyping`),
   * the click ONLY flips the view mode. The mounted overlay's
   * `useEffect([initialViewMode])` (`InterviewSlotOverlay.tsx`
   * lines 285-287) picks up the prop change mid-flight and re-paints
   * with the stacked history pane + active slot card. No `user/type`
   * + `user/send` dispatch → no queue entry → no future bouncer
   * collision. If `isAiTyping=false` (fresh game / quiet pre-Phase-A
   * moment), behaviour matches the pre-fix kickoff — flip mode and
   * dispatch `/build-game` so the engine starts.
   *
   * Note on the `isAiTyping=true` no-overlay edge case: if the user
   * clicks the toggle mid-Phase-B (engine done, SDK streaming a build
   * agent) or mid-non-build turn, the `interviewViewMode` flip is
   * harmless — no overlay is mounted to read it, and the next fresh
   * `/build-game` invocation will reset it on `interview:complete`
   * (the existing `useEffect` at line 663 below). Hiding the toggle
   * in those windows is a separate concern; for now the cooperative
   * no-op is strictly safer than the queued-then-replayed dispatch.
   *
   * Codex Round-2 WARN 6 — stale-state race closure. The rendered
   * `isAiTyping` prop and the reducer's `stateRef.current.isAiTyping`
   * can disagree for one tick at the boundary between Phase A's last
   * AskUserQuestion and Phase B's SDK turn:
   *
   *   - Rendered prop: `isAiTyping=false` (React just flushed the
   *     `chunk/done` dispatch that flipped the latch).
   *   - Reducer ref:   `stateRef.current.isAiTyping=true` (a fresh
   *     `chunk/text` arrived in the same tick and re-flipped it
   *     before the render flushed).
   *
   * If the user clicks the speed-run toggle in that one-tick window,
   * reading the rendered `isAiTyping=false` would route the click
   * through the `'dispatch'` branch — but the `user/send` dispatch
   * lands on the reducer's view of state (which reads `isAiTyping=true`
   * from `stateRef`) and queues `/build-game` for replay. The
   * `mode-only` branch is supposed to prevent exactly that outcome;
   * the race lets it slip past.
   *
   * Closure: read `isAiTyping` from `getChatState()` (the same
   * `stateRef.current` the reducer uses) at click time instead of the
   * rendered prop. The callback's `useCallback` deps drop the rendered
   * `isAiTyping` — `getChatState` is a stable ref-reader, so the
   * callback no longer needs to be re-memoised on every `isAiTyping`
   * flip. This is option (A) from the Round-2 fold; option (B)
   * (a reducer-side defence) is still available as a defence-in-depth
   * follow-up if this race ever fires in practice.
   */
  const handleStartExpressInterview = useCallback(() => {
    // Codex Round-3 YELLOW 3 — body lives in the pure `applyExpressClick`
    // helper so the integration test in
    // `Workspace.express-click-real-reducer.test.ts` exercises the
    // EXACT branching logic production runs (the prior Round-2 test
    // copied the body, so an unconditional dispatch in production
    // could pass while breaking the user-facing semantics). The
    // helper reads `isAiTyping` from `getChatState()` (the ref-fresh
    // reader from `useChatReducer`), closing the one-tick race between
    // a `chunk/done` flipping the latch and React flushing the render
    // — see the helper's docblock and `use-chat-reducer.ts:62` for the
    // rationale.
    //
    // Issue #464 — when the click lands mid-interview (overlay is
    // showing a slot card), the helper routes to the `'yolo-remaining'`
    // intent and we invoke `interview:yolo-remaining`. The IPC is
    // fire-and-forget from the renderer's perspective: main resolves
    // the current slot via the gate, installs a per-game policy on the
    // gate, and lets the engine drain to dispatch. Errors are surfaced
    // via the existing engine error path (`interview:complete` doesn't
    // fire; the chat shows a normal error chunk).
    applyExpressClick({
      setInterviewViewMode,
      getChatState,
      chatDispatch,
      isInterviewActive: interviewSlotActive,
      invokeYoloRemaining: gameId
        ? () => {
            void window.crucible.invoke('interview:yolo-remaining', { gameId }).catch((err) => {
              // Log + swallow — the IPC return shape includes
              // `feature-disabled` and `no-interview` non-throwing
              // discriminators; an actual thrown error here means
              // main hit an unrecoverable fault (state-file IO,
              // slot-plan validation, etc.). Treat as a silent
              // no-op for the click — the user can still answer
              // slots manually.

              console.error('[workspace] interview:yolo-remaining failed', err);
            });
          }
        : undefined,
    });
  }, [chatDispatch, getChatState, gameId, interviewSlotActive]);

  // Reset the express opt-in whenever the workspace navigates between
  // games. A fresh `/build-game` on game B should default to paginated
  // unless the user re-opts in via the toggle. The overlay's own
  // `useEffect` clears its history-pane state on the same trigger;
  // this hook just keeps the pre-flight state in lockstep.
  useEffect(() => {
    setInterviewViewMode('paginated');
  }, [gameId]);

  // Reset the express opt-in once the engine signals completion
  // (`interview:complete`). The next pitch — same game, fresh
  // interview run — defaults to paginated unless the user re-opts in
  // via the speed-run button.
  useEffect(() => {
    const unsub = window.crucible.on('interview:complete', (push) => {
      if (gameId && push.gameId !== gameId) return;
      setInterviewViewMode('paginated');
    });
    return unsub;
  }, [gameId]);

  // Issue #464 — track whether the interview overlay is currently
  // showing a slot card. Drives the `'yolo-remaining'` intent in
  // `applyExpressClick` (the speed-run button's click handler).
  // Subscribed at Workspace scope so the click handler can read it
  // synchronously via the closure; the overlay subscribes in
  // parallel for its own state. See `interviewSlotActive` docblock.
  useEffect(() => {
    setInterviewSlotActive(false);
  }, [gameId]);
  // Re-open the artifact pane when a new interview push arrives after a soft-close.
  useEffect(() => {
    if (!!pendingBulkInterview || interviewSlotActive) {
      setArtifactSoftClosed(false);
    }
  }, [pendingBulkInterview, interviewSlotActive]);
  // Note: the activity rail is now a pinned column whose visibility is
  // derived at render time (`rightPanelOpen && (buildView ||
  // !artifactPaneOpen)`) — when the artifact pane claims the right slot
  // (discovery / plan review) the rail yields to it, then returns once
  // the artifact pane closes. `rightPanelOpen` is therefore owned purely
  // by the header toggle; the old auto-open/auto-close effects (which
  // existed to manage the slide-in overlay) have been removed so they
  // don't fight the toggle or strand the rail closed after an artifact
  // pane session.
  useEffect(() => {
    const unsubShow = window.crucible.on('interview:show-slot', (push) => {
      if (gameId && push.gameId !== gameId) return;
      setInterviewSlotActive(true);
    });
    const unsubReview = window.crucible.on('interview:show-review', (push) => {
      if (gameId && push.gameId !== gameId) return;
      setInterviewSlotActive(false);
    });
    const unsubTakeover = window.crucible.on('interview:show-takeover', (push) => {
      if (gameId && push.gameId !== gameId) return;
      setInterviewSlotActive(false);
    });
    const unsubComplete = window.crucible.on('interview:complete', (push) => {
      if (gameId && push.gameId !== gameId) return;
      setInterviewSlotActive(false);
    });
    return () => {
      unsubShow();
      unsubReview();
      unsubTakeover();
      unsubComplete();
    };
  }, [gameId]);

  // Issue #164 follow-up — reset chat state when the workspace
  // navigates between games. React Router reuses the same Workspace
  // component instance for `/workspace/:gameId` route changes, so
  // navigating from game A to game B does NOT unmount us. The reducer's
  // `game/changed` action wipes messages + queue + in-flight back to
  // initial-state for the new gameId. The chat:load effect re-runs on
  // gameId change and repopulates `messages` from the new game's
  // history file.
  useEffect(() => {
    if (!gameId) return;
    chatDispatch({ type: 'game/changed', gameId });
    setPinnedAskUserQuestion(null);
  }, [gameId, chatDispatch]);

  // Phase 4: subscribe to the SDK's `AskUserQuestion` push channel.
  // When the permission gate holds a tool call open, we inject a
  // synthetic tool-call entry via the reducer's `chunk/received`
  // action with a synthesised `tool_call` chunk — that runs through
  // the same upsert path as canonical SDK tool_call chunks, which
  // dedupes if the SDK has already streamed one for the same id.
  useEffect(() => {
    const unsub = window.crucible.on('agent:ask-user-question', (push) => {
      if (gameId && push.gameId !== gameId) return;
      const toolCall = toolCallFromAskUserQuestionPush(push);
      setPinnedAskUserQuestion(toolCall);
      chatDispatch({
        type: 'chunk/received',
        chunk: {
          type: 'tool_call',
          // Push channel doesn't carry a sessionId — the reducer
          // doesn't read this field, but the AgentStreamChunk type
          // requires it. Tag the synthetic origin for forensic logs.
          sessionId: 'synthetic-ask-user-question',
          toolCall: {
            id: toolCall.id,
            name: toolCall.name,
            input: toolCall.input,
          },
        },
      });
    });
    return unsub;
  }, [gameId, chatDispatch]);

  // The legacy `foundry:ask-user-question:answer` CustomEvent
  // listener was removed alongside its sole dispatcher in
  // `AskUserQuestionCard.sendAnswer`. The dispatch was firing
  // `agent:send` while the SDK was still mid-turn waiting on the
  // gate's Promise resolution — bouncing off the in-flight bouncer.
  // The IPC gate path (`agent:answer-user-question`) is now the
  // single source of truth for delivering option-button answers.
  // See the comment in `sendAnswer` for the full retro.

  // #86 — subscribe to the bulk-interview push channel. The SKILL
  // pushes a single question plan via `bulk-interview:show` once it's
  // gathered all the questions for the express interview; the renderer
  // routes the chat surface to `<BulkInterviewCard>` instead of the
  // bubble timeline. Drops the push silently when the gameId doesn't
  // match the current Workspace — same cross-game guard the
  // `agent:stream` subscription uses.
  useEffect(() => {
    const unsub = window.crucible.on('bulk-interview:show', (push) => {
      if (gameId && push.gameId !== gameId) return;
      chatDispatch({
        type: 'bulk-interview/show',
        gameId: push.gameId,
        sessionId: push.sessionId,
        questions: push.questions,
      });
    });
    return unsub;
  }, [gameId, chatDispatch]);

  // Crucible #548 — subscribe to the `deps:request` push channel. The
  // agent-adapter's `requestDependency` MCP tool handler pushes a
  // request whenever a specialist asks to add an npm dependency. The
  // reducer enqueues it; the modal mount further down reads the
  // first entry and surfaces it to the user. Drops the push silently
  // when the gameId doesn't match the active Workspace — same cross-
  // game guard the other push channels use.
  useEffect(() => {
    const unsub = window.crucible.on('deps:request', (push) => {
      if (gameId && push.gameId !== gameId) return;
      chatDispatch({ type: 'deps/request', request: push });
    });
    return unsub;
  }, [gameId, chatDispatch]);

  // Preview start handler removed in alpha.50; the dev:start IPC
  // remains intact main-side for the eventual Preview restructure.

  // Codex P2 round 1: only arm the failure card when CI was actually
  // triggered for THIS deploy attempt. Pre-CI failures (no remote,
  // pre-flight refusal, push failure) MUST NOT mount the card —
  // `deploy:get-failure-context` would otherwise look up the latest
  // workflow run for the repo, which is from a PREVIOUS deploy and
  // unrelated to the failure the user just hit. The card would then
  // surface stale annotations and let the user paste irrelevant
  // errors into chat.
  //
  // Codex P2 round 2: the gate is keyed on `ci-triggered` ONLY.
  // The IPC handler in main overloads `tests` as the renderer step
  // for BOTH the CLI's pre-flight phase AND the CI's quality-gate
  // job, so `tests` is ambiguous: a pre-flight failure flips to
  // step `tests` BEFORE any push or workflow exists. The other
  // CI-phase steps (`build-image`, `deploy`, `verify`) come strictly
  // AFTER `ci-triggered` in the pipeline, so they're a valid
  // post-CI signal too — we include them so a deploy that races
  // past `ci-triggered` (rare but possible if the CI poll cadence
  // misses the intermediate event) still arms the card on later
  // failures. We deliberately exclude `tests` to avoid the
  // pre-flight / quality-gate ambiguity.
  const ciTriggeredThisDeployRef = useRef(false);
  const deployAttemptIdRef = useRef(0);
  const protoHubOpenedDeployAttemptRef = useRef<string | null>(null);
  const openProtoHubAfterDeploy = useCallback(
    (deployedGameId: string) => {
      if (!shouldHandleWorkspacePublishProgress({ gameId: deployedGameId }, gameId)) return;
      const deployAttemptKey = `${gameId}:${deployAttemptIdRef.current}`;
      if (protoHubOpenedDeployAttemptRef.current === deployAttemptKey) return;
      protoHubOpenedDeployAttemptRef.current = deployAttemptKey;
      // crucible#2326: in the cookie-mode cloud SPA the native simulator is
      // unreachable in-process (#2325 gate), but the cloud desktop shell
      // exposes a host bridge — ask it to open the native ProtoHub focused on
      // the just-deployed game (cloud/staging origin). Falls back to the
      // in-process native surface in local Electron; in pure-web there is no
      // bridge and no native simulator, so this is a no-op (surface suppressed).
      if (!nativeSimulatorAvailable && hostBridge) {
        hostBridge.requestProtoHubPreview(deployedGameId);
        return;
      }
      openProtoHubSurface('post-deploy');
    },
    [gameId, openProtoHubSurface, nativeSimulatorAvailable, hostBridge],
  );

  // Subscribe to publish progress events
  useEffect(() => {
    const unsub = window.crucible.on('publish:progress', (event) => {
      if (!shouldHandleWorkspacePublishProgress(event, gameId)) return;
      if (event.message) setDeployMessage(event.message);
      // Issue #415 Layer 3: capture local pre-flight failed-check
      // name so the "Failed step" chip can render it after the
      // LoadingOverlay unmounts on rejection.
      if (event.failedCheck) {
        setLocalFailedCheck(event.failedCheck);
      }
      // Codex P2 round 2: gate ONLY on unambiguous post-CI steps.
      // `tests` is overloaded with pre-flight in main's mapper, so
      // including it here would re-introduce the bug round 2 just
      // fixed (pre-flight failure surfaces stale annotations).
      const POST_CI_STEPS: ReadonlyArray<typeof event.step> = [
        'ci-triggered',
        'build-image',
        'deploy',
        'verify',
        'done',
      ];
      if (POST_CI_STEPS.includes(event.step)) {
        ciTriggeredThisDeployRef.current = true;
      }
      if (event.complete) {
        deployResolvedRef.current = true;
        setDeployState('deployed');
        // A successful deploy clears any stale failure card from a
        // previous run. Without this the card from a previous failed
        // deploy would survive across a successful retry.
        setDeployFailureCardEnabled(false);
        // Clear any local pre-flight failure marker on a clean
        // deploy — without this a prior gate failure's chip would
        // linger across a successful retry.
        setLocalFailedCheck(null);
        openProtoHubAfterDeploy(event.gameId);
      }
      if (event.error) {
        deployResolvedRef.current = true;
        setDeployMessage(event.error);
        setDeployState('error');
        // Only arm the card when CI actually ran. Pre-CI failures
        // get the legacy toast surface so we don't show stale
        // annotations from a previous deploy's run.
        if (ciTriggeredThisDeployRef.current) {
          setDeployFailureCardEnabled(true);
          refetchDeployFailure();
        }
      }
    });
    return unsub;
  }, [gameId, openProtoHubAfterDeploy, refetchDeployFailure]);

  // Subscribe to editor errors
  const editorErrorTimerRef = useRef<NodeJS.Timeout>(undefined);
  useEffect(() => {
    const unsub = window.crucible.on('editor:error', (msg) => {
      setEditorError(msg);
      clearTimeout(editorErrorTimerRef.current);
      editorErrorTimerRef.current = setTimeout(() => setEditorError(null), 5000);
    });
    return () => {
      unsub();
      clearTimeout(editorErrorTimerRef.current);
    };
  }, []);

  // Issue #597 — banner trigger. Two paths feed the banner:
  //
  //   (1) `gameStatus.autoSeeded` on the IPC response — the
  //       load-bearing signal. Always-available because the renderer
  //       reads it from React Query state, no subscription timing.
  //       (Codex review round 1 finding: the push alone races the
  //       subscription registration.)
  //
  //   (2) `interview:auto-seeded` push — fires from `agent:send`'s
  //       belt-and-braces seed path too, so a user who slips past
  //       the `game:status` race still gets the banner the moment
  //       their first `/build-game` turn lands the seed.
  //
  // De-dupe by gameId so we don't double-paint if both paths fire
  // for the same seed. Auto-fades after 8s; non-blocking; "Prepared"
  // (not "Started") per Codex review — the seed is the precondition,
  // not the interview itself.
  const autoSeededTimerRef = useRef<NodeJS.Timeout>(undefined);
  const lastBannerKeyRef = useRef<string | null>(null);
  // Phase 0a e2e sweep — when the auto-seed lands AFTER the
  // renderer's first `build-game:get-interview-state` query has
  // already resolved (typical on slow disks / first-mount race),
  // React Query's cache holds `null` until a manual reload. The
  // Deploy gate then reports "Game has no build history" even
  // though the seed file is on disk. Invalidating the cached
  // queries on auto-seeded triggers a refetch and the gate
  // observes the fresh state. Pre-existing race exposed by the
  // Playwright workspace suite; fix is renderer-side.
  const previewGameStateQueryArgs = useMemo(() => ({ gameId: gameId ?? '' }), [gameId]);
  const previewReadySessionId =
    previewSession.state.status === 'ready' ? previewSession.state.session.sessionId : null;
  const { data: previewGameState } = useIpcQuery('game-state:get', previewGameStateQueryArgs, {
    enabled: !!gameId && previewSession.state.status === 'ready',
    staleTime: 1_000,
  });
  // `preview:add-phone` / `preview:remove-phone` persist updated
  // diagnostics on main via `syncRunningPreviewDiagnostics` but do
  // not push to the renderer — refetch `game-state:get` when the
  // ready session or active phone count changes so the diagnostics
  // panel tracks controller URL count and warnings.
  const previewPhoneCount = previewSession.controllerUrls.length;
  useEffect(() => {
    if (!gameId || !previewReadySessionId) return;
    void queryClient.invalidateQueries({
      queryKey: ['game-state:get', previewGameStateQueryArgs],
    });
  }, [gameId, previewGameStateQueryArgs, previewReadySessionId, previewPhoneCount, queryClient]);
  const previewDiagnostics = previewGameState?.previewDev ?? null;
  const fireAutoSeededBanner = useCallback(
    (forGameId: string) => {
      const key = forGameId;
      // Always invalidate the interview-state queries even if we've
      // already painted the banner — a duplicate auto-seed push (path
      // 1 + path 2) is harmless to invalidate twice; missing the
      // refetch the second time leaves the gate stuck.
      void queryClient.invalidateQueries({
        queryKey: ['build-game:get-interview-state', forGameId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['build-game:interview-status', forGameId],
      });
      if (lastBannerKeyRef.current === key) return; // de-dupe banner paint only
      lastBannerKeyRef.current = key;
      setAutoSeededBanner(
        'No build session found for this game — prepared a fresh quick-batch interview. Type /build-game to begin.',
      );
      clearTimeout(autoSeededTimerRef.current);
      autoSeededTimerRef.current = setTimeout(() => setAutoSeededBanner(null), 8000);
    },
    [queryClient],
  );

  // Path (1) — IPC response carries `autoSeeded: true`. Fires once
  // per game session because the React Query cache persists the
  // flag; the `lastBannerKeyRef` de-dupe prevents re-firing on
  // re-renders.
  useEffect(() => {
    if (!gameId) return;
    if (gameStatus?.autoSeeded) {
      fireAutoSeededBanner(gameId);
    }
  }, [gameId, gameStatus?.autoSeeded, fireAutoSeededBanner]);

  // Path (2) — push subscription. Belt-and-braces for the
  // `agent:send` seed call site. Gameid-scoped so a future
  // multi-window setup doesn't paint another window's seed event
  // on this one.
  useEffect(() => {
    const unsub = window.crucible.on('interview:auto-seeded', (payload) => {
      if (payload.gameId !== gameId) return;
      fireAutoSeededBanner(payload.gameId);
    });
    return () => {
      unsub();
      clearTimeout(autoSeededTimerRef.current);
    };
  }, [gameId, fireAutoSeededBanner]);

  // Reset the de-dupe ref AND clear any stale banner state when the
  // user switches games. Bugbot round 2 MEDIUM: previously this
  // effect only cleared the ref, so a banner that was painted for
  // game A could persist into game B if the user switched within
  // the 8s timer window. Clearing the banner + the timer here
  // closes that gap. The new game's auto-seed (if any) will still
  // re-paint via the path (1) / path (2) effects above.
  useEffect(() => {
    lastBannerKeyRef.current = null;
    setAutoSeededBanner(null);
    clearTimeout(autoSeededTimerRef.current);
  }, [gameId]);

  useEffect(() => {
    const unsub = window.crucible.on('build:autoPipeline:progress', (payload) => {
      if (payload.gameId !== gameId) return;
      void queryClient.invalidateQueries({
        queryKey: ['build-game:interview-status', payload.gameId],
      });
      void queryClient.invalidateQueries({
        queryKey: ['build-game:get-interview-state', payload.gameId],
      });
    });
    return unsub;
  }, [gameId, queryClient]);

  // Deploy to TV via publish:run
  // Use a ref to avoid the race between publish:progress events and the
  // publish:run promise — if progress already resolved/errored, skip the catch.
  const deployResolvedRef = useRef(false);
  const handleDeploy = useCallback(async () => {
    if (!gameId || deployState === 'deploying') return;
    const ok = confirm(
      'Deploy to TV will commit any uncommitted local changes and push to GitHub.\n\nContinue?',
    );
    if (!ok) return;
    deployAttemptIdRef.current += 1;
    protoHubOpenedDeployAttemptRef.current = null;
    deployResolvedRef.current = false;
    // Reset the CI-triggered gate at the start of every attempt
    // (Codex P2 round 1) — without this a previous attempt that
    // reached CI would leave the gate true, and a fresh attempt
    // that fails pre-CI (e.g. user revoked their PAT between
    // deploys) would still arm the card with stale annotations.
    ciTriggeredThisDeployRef.current = false;
    setDeployState('deploying');
    setDeployMessage('Preparing deployment...');
    // #2349 — a fresh deploy attempt clears a prior dismissal so the
    // pre-flight error banner re-surfaces if this attempt also fails.
    setDeployErrorDismissed(false);
    // Hide any stale failure card before kicking off a fresh deploy.
    // Without this the previous card would linger underneath the
    // LoadingOverlay and re-appear when the new deploy completes.
    setDeployFailureCardEnabled(false);
    // Clear the prior local pre-flight failure marker so an old
    // gate-failure chip doesn't linger underneath the fresh
    // deploy's LoadingOverlay (#415 Layer 3).
    setLocalFailedCheck(null);
    try {
      const result = await window.crucible.invoke('publish:run', { gameId });
      if (deployResolvedRef.current) return; // progress listener already handled it
      if (result.success) {
        setDeployState('deployed');
        setDeployMessage('Deployed successfully!');
        openProtoHubAfterDeploy(gameId);
      } else {
        setDeployState('error');
        setDeployMessage(result.error ?? 'Deployment failed');
        // #277: arm the deploy-failure card on the publish:run promise
        // resolution path too — main may resolve with `success=false`
        // without ever firing a `publish:progress` error event (e.g.
        // a pre-flight refusal that returns a structured failure
        // payload rather than emitting). Gate on the CI-triggered
        // flag so pre-CI failures don't surface stale annotations
        // (Codex P2 round 1).
        if (ciTriggeredThisDeployRef.current) {
          setDeployFailureCardEnabled(true);
          refetchDeployFailure();
        }
      }
    } catch (err) {
      if (deployResolvedRef.current) return; // progress listener already handled it
      setDeployState('error');
      setDeployMessage(err instanceof Error ? err.message : String(err));
      // #277: thrown rejections (the common case for CI failures —
      // `publishGame` throws CRUCIBLE-501 when the workflow run
      // concludes failure) also need the card. Same gating as the
      // success-false branch above.
      if (ciTriggeredThisDeployRef.current) {
        setDeployFailureCardEnabled(true);
        refetchDeployFailure();
      }
    }
  }, [gameId, deployState, openProtoHubAfterDeploy, refetchDeployFailure]);

  /**
   * Handler for the "Fix in agent" CTA on `<DeployFailureCard>`.
   * Receives the structured prompt from the IPC layer and dispatches
   * it through the chat reducer like the user typed it. The
   * dispatched user message goes through the same queue-while-busy
   * machinery any other user message does, so an in-flight agent
   * turn is correctly handled (the Fix-in-agent message gets
   * queued and drained when the active turn completes).
   */
  const handleFixInAgent = useCallback(
    (message: string) => {
      chatDispatch({ type: 'user/type', text: message });
      chatDispatch({ type: 'user/send' });
    },
    [chatDispatch],
  );

  const handleDismissDeployFailure = useCallback(() => {
    setDeployFailureCardEnabled(false);
  }, []);

  /**
   * Select-to-chat P1 — receives the cropped preview selection from
   * the display pane's select-area overlay and routes it through the
   * EXISTING paste pipeline: `resizeImageFile` (cap-aware downscale)
   * → `user/add-attachment`, with the formatted `crucible-selection`
   * block riding along so the reducer can prepend it to the message
   * text at send time. Fire-and-forget like `handlePaste`:
   * `resizeImageFile` falls back to original bytes on decode/encode
   * failure, and the main-side cap stays authoritative.
   */
  // Transient "added to chat" confirmation: pulses the Develop tab + shows
  // a popover beneath it for 5s. Used only by the FULL Preview mode, where the
  // chat panel isn't on screen.
  const [selectionAddedHint, setSelectionAddedHint] = useState(false);
  const selectionHintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashSelectionAdded = useCallback(() => {
    setSelectionAddedHint(true);
    if (selectionHintTimerRef.current) clearTimeout(selectionHintTimerRef.current);
    selectionHintTimerRef.current = setTimeout(() => setSelectionAddedHint(false), 5000);
  }, []);
  // In-editor (Develop split) confirmation: the attachment thumbnail above the
  // composer pops to highlight the just-attached selection — no tab popover.
  const [attachmentPop, setAttachmentPop] = useState(false);
  const attachmentPopTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flashAttachmentPop = useCallback(() => {
    setAttachmentPop(false);
    if (attachmentPopTimerRef.current) clearTimeout(attachmentPopTimerRef.current);
    // Re-arm on the next frame so the animation restarts even on rapid adds.
    requestAnimationFrame(() => {
      setAttachmentPop(true);
      attachmentPopTimerRef.current = setTimeout(() => setAttachmentPop(false), 600);
    });
  }, []);
  // Read the live workspace mode inside the (stable) add-selection callback.
  const workspaceModeRef = useRef(workspaceMode);
  workspaceModeRef.current = workspaceMode;
  useEffect(
    () => () => {
      if (selectionHintTimerRef.current) clearTimeout(selectionHintTimerRef.current);
      if (attachmentPopTimerRef.current) clearTimeout(attachmentPopTimerRef.current);
    },
    [],
  );

  const handleAddSelectionToChat = useCallback(
    (payload: { file: File; contextBlock: string }) => {
      void resizeImageFile(payload.file)
        .then((result) => {
          chatDispatch({
            type: 'user/add-attachment',
            attachment: {
              mimeType: result.mimeType,
              base64: result.base64,
              byteLength: result.byteLength,
              ...(result.didResize
                ? {
                    resizedFrom: {
                      width: result.originalWidth,
                      height: result.originalHeight,
                      finalWidth: result.finalWidth,
                      finalHeight: result.finalHeight,
                    },
                  }
                : {}),
            },
            selectionContext: payload.contextBlock,
          });
          // Full Preview mode → pulse the Develop tab (no chat visible).
          // In-editor (Develop split) → pop the composer thumbnail instead.
          if (workspaceModeRef.current === 'preview') {
            flashSelectionAdded();
          } else {
            flashAttachmentPop();
          }
        })
        .catch((err: unknown) => {
          console.error('[preview] selection attach failed', err);
          setPreviewToast({
            tone: 'error',
            message: "Couldn't attach that selection - try selecting again.",
          });
        });
    },
    [chatDispatch, flashSelectionAdded, flashAttachmentPop, setPreviewToast],
  );

  useEffect(() => {
    if (!isValidWorkspaceRouteGameId(gameId)) navigate('/dashboard', { replace: true });
  }, [gameId, navigate]);

  if (!isValidWorkspaceRouteGameId(gameId)) return null;

  // Dedicated preview surface. In the new UI this is rendered only when
  // WorkspaceMode is "preview"; chat never owns preview frames or lifecycle
  // controls. The header action zone owns Stop / Reload / Restart.
  const previewSurfaceEl = (
    <PreviewOverlay
      embedded
      // Full Preview mode has no chat panel on screen → fly to the Develop tab.
      // In-editor (Develop split) → fly to the composer thumbnail.
      flyTo={workspaceMode === 'preview' ? 'chat-tab' : 'composer'}
      newUi={flags.newUi}
      gameName={gameName}
      onClose={NOOP}
      displayUrl={displayPreviewUrl}
      displayIframeRef={displayPreviewIframeRef}
      onDisplayIframeLoad={handleDisplayIframeLoad}
      onAddSelectionToChat={handleAddSelectionToChat}
      onSelectionError={(message) => setPreviewToast({ tone: 'error', message })}
      // Lifecycle controls now live in the preview's floating toolbar
      // (below the TV), not the header.
      onStopPreview={handleStopPreview}
      onReloadPreview={handleReloadPreview}
      onRestartPreview={handleRestartPreview}
      previewMenuEnabled={previewSession.state.status === 'ready'}
      controllerUrls={previewSession.controllerUrls}
      pairing={
        previewSession.state.status === 'ready' &&
        !isCloudPreviewSession(previewSession.state.session)
          ? previewSession.state.session.pairing
          : undefined
      }
      previewMode={
        previewSession.state.status === 'ready' &&
        !isCloudPreviewSession(previewSession.state.session)
          ? previewSession.state.session.previewMode
          : undefined
      }
      onAddPhone={
        phoneControlsEnabled
          ? () => {
              // Preview lets you add phones up to the overall hard cap
              // (HARD_PREVIEW_PHONE_CAP), independent of the plan's
              // player-count policy — no per-game cap control. Cloud
              // sessions support multiple controllers (each pane gets a
              // distinct same-origin testUserId), so the affordance is
              // wired for cloud too — only paired sessions are excluded.
              void previewSession.addPhone({
                maxControllerSlots: HARD_PREVIEW_PHONE_CAP,
              });
            }
          : undefined
      }
      onRemovePhone={
        phoneControlsEnabled
          ? () => {
              void previewSession.removePhone();
            }
          : undefined
      }
      onRemovePhoneAt={
        phoneControlsEnabled
          ? (index) => previewSession.removePhoneAt(index)
          : undefined
      }
      maxControllerSlots={HARD_PREVIEW_PHONE_CAP}
      playerCountLabel={previewControllerPolicy.label}
      playerCountSource={previewControllerPolicy.playerCountSource}
      diagnostics={previewDiagnostics}
      loading={previewLaunchButtonLoading}
      loadingLabel={previewDevLoopState.label}
      loadingDetail={previewDevLoopState.detail}
      reloadKey={previewSession.reloadKey}
      onRetryPreview={handleRetryPreviewFromOverlay}
      error={previewSession.state.status === 'error' ? previewSession.state.error : null}
      onSendErrorToChat={(err) => {
        const code = err.code ?? 'CRUCIBLE-UNKNOWN';
        const text =
          `Preview failed to start: ${code}. ${err.message ?? ''}`.trim() +
          '\n\nPlease run /diagnose and fix the dev server.';
        chatDispatch({ type: 'user/type', text });
        chatDispatch({ type: 'user/send' });
        resetPreview();
      }}
      onDismissError={() => {
        resetPreview();
      }}
    />
  );

  return (
    <div className="flex h-full flex-col">
      {/* `<WhatsNewCard />` is now mounted at the App shell (see App.tsx)
          so Dashboard-only users see release notes too. Issue #293. */}

      {/* Deploy loading overlay */}
      <LoadingOverlay message={deployMessage} isVisible={deployState === 'deploying'} />

      {/* Preview overlay moved to the end of this tree — see the
          PreviewOverlay block right before the closing </div>. The
          earlier position here caused Electron's drag-region hit-test
          to award the WorkspaceHeader's `app-region-drag` over the
          overlay's `app-region-no-drag` because the OS compositor
          resolves overlapping regions by DOM order (later sibling
          wins), independent of visual z-index. Issue #1016 part C —
          recurring regression of #531 / #804. */}

      {/* Crucible #548 — user-gated dependency-add approval modal.
          Rendered above the workspace chrome whenever the
          `requestDependency` MCP tool has pushed a request (queued in
          `pendingDependencyRequests`). The modal blocks all chat
          interaction until the user picks Install / Decline because
          the agent's turn is stalled waiting on the gate. Multiple
          concurrent requests stack — the modal always shows the
          first entry; subsequent entries surface as that entry
          resolves. */}
      {pendingDependencyRequests.length > 0 && pendingDependencyRequests[0] && (
        <DependencyApprovalModal
          key={pendingDependencyRequests[0].sessionId}
          packageName={pendingDependencyRequests[0].package}
          requestedVersion={pendingDependencyRequests[0].version}
          reason={pendingDependencyRequests[0].reason}
          scope={pendingDependencyRequests[0].scope}
          requestedAt={pendingDependencyRequests[0].requestedAt}
          onApprove={(finalVersion) => {
            const session = pendingDependencyRequests[0];
            if (!session) return;
            chatDispatch({
              type: 'deps/approve',
              sessionId: session.sessionId,
              finalVersion,
            });
          }}
          onDecline={(reason) => {
            const session = pendingDependencyRequests[0];
            if (!session) return;
            chatDispatch({
              type: 'deps/decline',
              sessionId: session.sessionId,
              reason,
            });
          }}
        />
      )}

      {/* Editor error toast */}
      {editorError && (
        <div className="fixed right-4 top-4 z-40 max-w-sm rounded-lg border border-red-500/30 bg-red-900/80 px-4 py-3 text-sm text-red-200 shadow-lg backdrop-blur-sm">
          {editorError}
        </div>
      )}

      {/* Issue #643 — preview recovery toast. Info/success auto-fade
          after 4s (cleared by the effect on `[previewToast]`).
          Error toasts are STICKY with a dismiss button — a 4s failure
          notice is too easy to miss when the user is staring at the
          overlay's centred error card (Codex review #1 BLOCK).
          Tone-coded so success / info / error read distinctly at a
          glance. Sits slightly lower than the editor-error toast so
          both can be on-screen at once without overlapping —
          vanishingly unlikely but harmless. */}
      {previewToast && (
        <div
          data-testid="preview-toast"
          data-tone={previewToast.tone}
          role={previewToast.tone === 'error' ? 'alert' : 'status'}
          className={
            previewToast.tone === 'error'
              ? // Codex review #2 LOW + #3 LOW + #4 MEDIUM —
                // preview toast layering. Three-tier z-index plan:
                //   z-50  — PreviewOverlay (the fullscreen iframe
                //           pane that the toast must surface ABOVE
                //           so in-overlay restart failures are
                //           visible without first closing it).
                //   z-[55] — this preview-recovery toast. Sits
                //           above the preview overlay but BELOW
                //           the user-blocking modals.
                //   z-[60] — user-blocking modals
                //           (DependencyApprovalModal,
                //           BackToPlanConfirmModal,
                //           ReviewAnswersModal,
                //           UpdateTemplateModal). Bumped from z-50
                //           in this PR so they correctly beat the
                //           toast — higher z-index wins regardless
                //           of DOM order, so the previous "DOM-
                //           order tiebreaker" reasoning was wrong.
                'fixed right-4 top-16 z-[55] flex max-w-sm items-start gap-2 rounded-lg border border-red-500/30 bg-red-900/80 px-4 py-3 text-sm text-red-200 shadow-lg backdrop-blur-sm'
              : previewToast.tone === 'success'
                ? 'fixed right-4 top-16 z-[55] max-w-sm rounded-lg border border-emerald-500/30 bg-emerald-900/80 px-4 py-3 text-sm text-emerald-100 shadow-lg backdrop-blur-sm'
                : 'fixed right-4 top-16 z-[55] max-w-sm rounded-lg border border-sky-500/30 bg-sky-900/80 px-4 py-3 text-sm text-sky-100 shadow-lg backdrop-blur-sm'
          }
        >
          <span className="flex-1">{previewToast.message}</span>
          {previewToast.tone === 'error' && (
            <button
              type="button"
              onClick={() => setPreviewToast(null)}
              data-testid="preview-toast-dismiss"
              aria-label="Dismiss"
              className="-mt-0.5 -mr-1 shrink-0 rounded p-0.5 text-red-300 transition-colors hover:bg-red-500/20 hover:text-red-100"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M18 6L6 18" />
                <path d="M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      )}

      {/* Issue #597 — auto-seed banner. Non-blocking informational
          toast that fires once on the Workspace-open IPC when main
          auto-seeds the interview state for a cloned game. Sky tone
          (not red) so the user reads it as "Foundry did the right
          thing for me" rather than a failure. Stacks below the
          editor-error toast on the chance both fire on the same
          mount (vanishingly unlikely but the `top-28` offset is
          deliberate to keep them from overlapping). Auto-fades after
          8s — no dismiss button by design. */}
      {autoSeededBanner && <AutoSeededToast message={autoSeededBanner} newUi={flags.newUi} />}

      {/* PR #544 follow-up — Start Building / Back to plan IPC error
          toast. Rendered at page-chrome scope so it surfaces in BOTH
          plan-mode (full-screen `<PlanModeView>`) and build-mode (header
          Back-to-plan button). Stacks below the editor-error toast when
          both fire; the `top-16` offset is deliberate so they don't
          overlap. */}
      {planModeError && (
        <div
          data-testid="plan-mode-error-toast"
          role="alert"
          className="fixed right-4 top-16 z-40 max-w-sm rounded-lg border border-red-500/30 bg-red-900/80 px-4 py-3 text-sm text-red-200 shadow-lg backdrop-blur-sm"
        >
          {planModeError}
        </div>
      )}

      <UpdateTemplateModal
        gameId={gameId}
        open={showTemplateModal}
        onClose={() => setShowTemplateModal(false)}
        // Issue #391: when the user picks "Resolve with the agent" on
        // the conflict picker, dispatch the synthesised prompt through
        // the chat reducer the same way a typed user message would land
        // — `user/type` populates the composer, `user/send` consumes it
        // and emits `ipc/agent-send` via the effect interpreter. Going
        // through the reducer (instead of invoking `agent:send` directly
        // from here) means the user sees their request in the chat
        // timeline, the chat-history persists it, and the in-flight /
        // queue semantics behave the same as if they'd typed it.
        onResolveWithAgent={(conflictedFiles) => {
          const prompt = synthesiseConflictResolutionPrompt(conflictedFiles);
          chatDispatch({ type: 'user/type', text: prompt });
          chatDispatch({ type: 'user/send' });
        }}
      />

      {/* Header */}
      <WorkspaceHeader
        gameId={gameId}
        gameName={gameName}
        deployState={deployState}
        deployActionState={deployActionState}
        selectionAddedHint={selectionAddedHint}
        activeMode={workspaceMode}
        onChat={() => setWorkspaceMode('chat')}
        onPlan={flags.newUi && designWorkspaceEnabled ? undefined : () => setWorkspaceMode('plan')}
        onDesign={designWorkspaceEnabled ? () => setWorkspaceMode('design') : undefined}
        onCraft={
          flags.craftMode && flags.newUi && !!gameId ? () => setCraftOpen((v) => !v) : undefined
        }
        gameStatus={gameStatus}
        statusBadge={gameStatusLite.data?.statusBadge ?? 'draft'}
        repoUrl={clonedRepoFullName ? `https://github.com/${clonedRepoFullName}` : undefined}
        cloneDeployAccessWarning={cloneDeployAccessWarning}
        deployFailureContext={visibleDeployFailureContext}
        localFailedCheck={localFailedCheck}
        activitySnapshot={activitySnapshot}
        interviewStatus={interviewStatus}
        onPreview={handleLaunchPreview}
        onProtoHub={handleDisplayOnHub}
        onLivePreview={
          flags.newUi && flags.livePreview && !!gameId ? handleActivateDevelop : undefined
        }
        livePreviewSplitActive={livePreviewSplitActive}
        previewLoading={previewLaunchButtonLoading}
        previewMenuEnabled={previewSession.state.status === 'ready'}
        previewForceDisabled={previewHandoffDisabledReason !== undefined}
        previewDisabledReason={previewDisabledReason}
        previewAvailableTitle={previewAvailableTitle}
        inAppPairingAvailable={inAppPairingAvailable}
        inAppPairingNextLaunch={weekendPairingNextLaunch}
        onInAppPairingNextLaunchChange={setWeekendPairingNextLaunch}
        previewPhaseGate={
          phaseProgress?.kind === 'derived'
            ? {
                currentPhase: phaseProgress.derived.current,
                phaseLabel: phaseProgress.derived.label,
              }
            : undefined
        }
        onDeploy={handleDeploy}
        // WorkspaceRunState owns aggregate CTA visibility so the
        // header, banner, and plan/build chrome do not drift.
        showBackToPlanButton={workspaceRunState.showBackToPlan}
        backToPlanBusy={planModeBusy}
        onBackToPlanRequest={handleBackToPlanRequest}
        runState={workspaceRunState}
        cloudVmEnabled={featureFlags.loaded && featureFlags.cloudVmV1}
        maintainerMode={showVmDebugPane}
        contributors={gameContributors}
        rightPanelOpen={rightPanelOpen}
        // The rail can only occupy the right slot when the artifact pane
        // isn't already there (or we're in a build, where the artifact
        // pane isn't rendered). Pass `undefined` when the slot is taken so
        // the header hides the toggle entirely — otherwise the toggle's
        // accent/"Close" state would lie (claim the rail is open while the
        // artifact pane is actually filling the slot) and clicking it would
        // be a no-op. When the toggle IS shown, `rightPanelOpen` maps 1:1
        // to rail visibility. (Codex stop-review: "toggle state can lie".)
        onToggleRightPanel={
          workspaceMode === 'preview' || workspaceMode === 'protohub' || !artifactPaneOpen
            ? () => setRightPanelOpen((v) => !v)
            : undefined
        }
        furnaceBuildState={furnaceBuildState}
      />

      {/* Template update banner — below the header so the header stays flush
          with the top of the window (traffic lights align with the header). */}
      <TemplateUpdateBanner
        gameId={gameId}
        onUpdateClick={() => setShowTemplateModal(true)}
        newUi={flags.newUi}
      />

      {/* Preview/install recovery banner. Workspace owns the combined
          "retry install, then start preview" CTA so the old install chip does
          not compete with the dev-loop state spine. */}
      {previewDevLoopState.kind === 'install-needed' && (
        <div className="px-6 pt-3">
          <PreviewDevLoopBanner state={previewDevLoopState} onPrimary={handleLaunchPreview} />
        </div>
      )}

      {/* Issue #277 — `<DeployFailureCard>` renders OUTSIDE the
          ChatPanel's hasAnthropicKey gate (Cursor Bugbot round 1).
          Users without an Anthropic key still need to see deploy
          failures with the workflow run link + View on GitHub CTA;
          they just can't use Fix-in-agent. The card itself disables
          the Fix-in-agent button when no failedJobs are present —
          here the card mounts in a top-of-workspace band so it
          surfaces regardless of which centre column variant is
          showing.

          The "Fix in agent" CTA still dispatches into the chat
          reducer; on no-Anthropic-key workspaces the dispatch
          ends up queued in chatState but never consumed (no
          chat panel to render it). That's an acceptable
          degradation — the user without a key wouldn't be able
          to act on the prompt anyway. The other two CTAs
          (View on GitHub, Retry deploy) stay fully functional. */}
      {gameId && deployFailureCardEnabled && deployFailureContext?.status === 'failure' && (
        <div className="border-b border-white/10 bg-black/20 px-6 py-3">
          <DeployFailureCard
            gameId={gameId}
            context={deployFailureContext}
            actionState={deployActionState}
            hasAnthropicKey={hasAnthropicKey}
            onFixInAgent={handleFixInAgent}
            onDismiss={handleDismissDeployFailure}
            onRetry={handleDeploy}
          />
        </div>
      )}

      {/* #2349 — pre-flight / non-CI deploy-error banner. The
          `<DeployFailureCard>` above only arms once CI was triggered,
          and `deployMessage` otherwise only renders inside the
          LoadingOverlay (which hides the moment the deploy leaves the
          'deploying' state). So a deploy that fails BEFORE CI — an
          uncommitted-changes refusal (CRUCIBLE-202), a missing remote
          (CRUCIBLE-203), a local quality-gate failure (CRUCIBLE-210),
          or a thrown pre-flight error — used to surface nothing at all.
          This dismissable banner fills that gap: it renders the actual
          `deployMessage` text whenever the deploy is in the error state
          AND the failure card is NOT showing, so the two paths never
          double-render. Cleared on dismiss and on the next Deploy
          click (which resets `deployErrorDismissed`). */}
      {deployState === 'error' &&
        !deployErrorDismissed &&
        !(deployFailureCardEnabled && deployFailureContext?.status === 'failure') && (
          <div className="border-b border-red-500/20 bg-red-900/20 px-6 py-3">
            <div
              data-testid="deploy-preflight-error"
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-900/40 px-4 py-3 text-sm text-red-200"
            >
              <span className="flex-1">
                <span className="font-medium">Deploy failed.</span>{' '}
                {deployMessage}
              </span>
              <button
                type="button"
                onClick={() => setDeployErrorDismissed(true)}
                data-testid="deploy-preflight-error-dismiss"
                aria-label="Dismiss"
                className="-mt-0.5 -mr-1 shrink-0 rounded p-0.5 text-red-300 transition-colors hover:bg-red-500/20 hover:text-red-100"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

      {/* Crucible #416 Chunk C — phase strip. Sticky above the
          chat scroll region; renders all seven phase chips per the
          v10 ASCII mockup, plus a click-to-expand detail panel.
          Stays mounted on every game with a workspace open (the
          hook degrades to a skeleton until the first IPC arrives)
          and tears down its watcher on game switch. */}
      {gameId && (
        <>
          {/* Crucible #519: sticky bail-out warning. Surfaces when the
              SKILL recorded a budget-bail-out as 'built-partial'. Renders
              above the strip so it can't get scrolled past. Null-render
              when no bail-out happened (no chrome cost).
              `key={gameId}` forces a fresh mount on game switch so a
              dismissal on game A doesn't suppress game B's banner —
              Workspace is reused across `/workspace/:gameId` route
              changes (see the route-keep-mounted comment above).
              Crucible #796: derive skipped sub-phase IDs from the phase
              progress wire so the banner can list them by name instead of
              asking the user to grep the JSON file. */}
          <BuildPartialBanner
            key={gameId}
            gameId={gameId}
            status={interviewStatus}
            skippedSubPhases={
              phaseProgress?.kind === 'derived'
                ? // Deduplicate by subPhase — the SKILL could theoretically
                  // emit two transitions with budgetSkipped for the same
                  // sub-phase (e.g. a retry that also bails). Preserve the
                  // first-seen order; Array.from(Set) is stable in JS.
                  Array.from(
                    new Set(
                      phaseProgress.derived.transitions
                        .filter((t) => t.budgetSkipped === true)
                        .map((t) => t.subPhase),
                    ),
                  )
                : undefined
            }
          />
        </>
      )}

      {/* Three-panel layout (two-panel when no Anthropic key — the
          AI chat panel depends on the Anthropic SDK and is useless
          without a key. We hide it entirely rather than render a
          disabled placeholder: cleaner for users who haven't opted
          in, and users who set a key later get the panel back on
          next workspace open. Settings surfaces a hint that the
          Anthropic key unlocks the in-app chat so the feature is
          discoverable). */}
      <div className="relative flex flex-1 overflow-hidden">
        {/* Legacy UI left column — Files OR VM Debug pane (B-1). When
            the engineer launched Foundry with `CRUCIBLE_MAINTAINER_MODE=1`
            AND the `app:is-maintainer` IPC has resolved, the left
            column renders the maintainer-only VM Debug pane in place
            of the files tree. Gate lives at this call site so the
            pane component itself stays SSR-friendly for tests that
            render `<Workspace>` without a `QueryClientProvider`.
            (A-1's lesson learned — see useMaintainerMode docstring.)
            Flag-OFF: the `<FilesPanel>` renders unchanged.
            The new UI hides this column entirely; the right panel
            owns equivalent surfaces. */}
        {!flags.newUi &&
          (showVmDebugPane ? (
            <VmDebugPane />
          ) : (
            <FilesPanel
              gameId={gameId}
              collapsed={filesCollapsed}
              width={leftWidth}
              onToggleCollapse={() => setFilesCollapsed(!filesCollapsed)}
              onResize={setLeftWidth}
            />
          ))}

        {/* Centre: AI Chat panel when an Anthropic key is configured;
            otherwise an inline placeholder pointing at Settings (audit §4
            / top-3 ranked fix — the previous behaviour silently
            disappeared the centre column when no key was set, leaving
            users with no clue why). While `tokens:get` is in flight we
            render an empty centre column so users with a key never
            briefly see the "needs setup" placeholder (the IPC almost
            always resolves in <50ms but the flicker is real on cold
            launch).

            #191 quick-batch: when the interview is in quick mode AND
            the SKILL has flipped subPhase to plan / refine /
            back-to-plan, replace the ChatPanel with a full-screen
            plan-mode view. Build / undefined subPhase falls through
            to the normal chat surface so the user always sees agent
            chatter once specialists start running.

            #671: the four mounts below are wrapped in
            `<CentreColumnWithBanner>` so the transparency banner
            band (TemplateTooOldBanner + ActivityBanner) sits above
            whichever mount is active. The shell is the canonical
            CRUCIBLE_AGENT_TRANSPARENCY flag-gate per design §12
            Issue E — when the flag is OFF, the shell renders just
            the children and the centre column behaves exactly as
            before. `key={gameId}` remounts the subtree on cross-
            game navigation as belt-and-braces for the snapshot
            hook's session-discard rules. `suppressBanner=
            {!tokensResolved}` keeps the loader fork visually clean
            per design plan §2 ("Tokens-not-resolved fork: the
            banner does NOT render"). */}
        {designContentVisible ? (
          <div className="relative min-w-0 flex-1 overflow-hidden">
            <ChatErrorBoundary gameId={gameId} renderFallback={renderDesignWorkspaceFallback}>
              <DesignWorkspace
                gameId={gameId ?? ''}
                buildPlan={designBuildPlan}
                onSentToBuild={handleDesignStartBuild}
                onSendUpdate={handleSendDesignUpdate}
                autoBuildStatus={autoBuildState?.status ?? null}
                buildActivity={{
                  runState: workspaceRunState,
                  phaseProgress,
                  activitySnapshot,
                }}
              />
            </ChatErrorBoundary>
          </div>
        ) : previewModeVisible ? (
          <div className="relative min-w-0 flex-1 overflow-hidden">
            {previewSurfaceEl}
            {/* Live-preview scaffold-first (A2.3b/c): the one-tap genre chip,
                overlaid on the preview. Only when scaffold-first is active AND
                an inferred-genre outcome is available — durably from
                crucible.json (game:status) or the create-session route state.
                The "change" action is a LIGHTWEIGHT start-over: navigate to New
                Game pre-filled with this game's original prompt (cached at
                create) so the user re-describes to get a different genre. The
                explicit recast marker means a missing / garbage cached prompt
                still starts over from a BLANK form — never an unrelated stored
                draft. (The heavier in-place re-cast — interrupt + re-overlay —
                is deferred.) */}
            {shouldShowGenreChip(flags.scaffoldFirstActive, previewGenreInference) ? (
              <div className="pointer-events-none absolute left-3 top-3 z-10">
                <div className="pointer-events-auto">
                  <ScaffoldFirstGenreChip
                    inference={previewGenreInference}
                    onChange={() => {
                      navigate('/new-game', {
                        state: buildRecastRouteState(readCreatePrompt(gameId)),
                      });
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        ) : protoHubModeVisible ? (
          <div className="relative min-w-0 flex-1 overflow-hidden">
            <ProtoHubSimulatorSurface
              target="local"
              source={protoHubSource}
              gameId={gameId}
              onClose={() => setWorkspaceMode('chat')}
            />
          </div>
        ) : (
          <CentreColumnWithBanner
            gameId={gameId}
            key={gameId}
            suppressBanner={!tokensResolved}
            chatAgents={agents}
            chatMainAgent={mainAgent}
            runState={workspaceRunState}
          >
            {/* Bridge-3 — actionable Furnace failure banner. Gate
              on `phase === 'failed'`, NOT on
              `routedToFurnace && latch !== null`. The build-state
              derivation already prioritises an active turn over a
              stale latch (see `computeFurnaceBuildState` rule 2);
              keying the banner off the SAME phase the indicator
              reads keeps the two surfaces in lock-step. Without
              this, a queued message that auto-drains after an
              error (chat reducer's queue-drain on `chunk: error`)
              can flash the failure banner over the next live
              turn between "isAiTyping flips true" and "passive
              latch-reset effect fires" — Codex round-2 follow-up. */}
            <FurnaceErrorBanner
              view={furnaceBuildState.phase === 'failed' ? furnaceLastFailure : null}
              onPrimaryCta={(kind) => {
                switch (kind) {
                  case 'retry': {
                    // Re-dispatch the most-recent errored turn. Find
                    // the latest assistant message that carries an
                    // `error` and replay against its id — the chat
                    // reducer's `user/retry` action resolves the
                    // preceding user message and re-sends. Clear the
                    // failure latch FIRST so the banner unmounts even
                    // if the retry fails to fire (defence-in-depth
                    // against a stale-error UX).
                    setFurnaceLastFailure(null);
                    const erroredMessage = [...messages]
                      .reverse()
                      .find((m) => m.role === 'assistant' && m.error !== undefined);
                    if (erroredMessage) {
                      handleRetry(erroredMessage);
                    }
                    return;
                  }
                  case 'open-diagnostics':
                  case 'check-cohort':
                  case 'check-connections':
                    // All three CTAs route to the Settings page —
                    // the engineer lands on the appropriate section
                    // (Diagnostics / Advanced / Connections) but the
                    // navigation entry-point is the same. Future
                    // anchor links can deep-link more precisely.
                    navigate('/settings');
                    return;
                }
              }}
              onDismiss={() => setFurnaceLastFailure(null)}
            />
            {tokensResolved &&
              hasAnthropicKey &&
              planModeVisible &&
              (flags.newUi ? (
                <PlanWorkspace
                  gameId={gameId}
                  gameName={gameName}
                  plan={interviewPlan}
                  answeredCount={interviewStatusQuery.data?.answeredCount}
                  totalCount={interviewStatusQuery.data?.totalCount}
                  planRevision={interviewState?.planRevision}
                  planHash={interviewState?.planHash}
                  planUpdatedAt={interviewState?.planUpdatedAt}
                  planSource={interviewState?.planSource}
                  lastBuiltPlanRevision={interviewState?.lastBuiltPlanRevision}
                  lastBuiltPlanHash={interviewState?.lastBuiltPlanHash}
                  buildComplete={workspaceRunState.kind === 'done'}
                  buildCompletionUnverified={buildCompletionUnverified}
                  buildRequestPending={workspaceRunState.planRevisionLocked}
                  busy={isAiTyping || planModeBusy}
                  onSavePlan={handleSavePlan}
                  onStartBuilding={handleStartBuilding}
                  onRebuildFromPlan={handleRebuildFromPlan}
                  onBackToChat={() => setWorkspaceMode('chat')}
                  onSubmitRefinement={handlePlanRefinement}
                  onStartOver={handlePlanStartOver}
                />
              ) : showPlanModeView ? (
                <PlanModeView
                  gameId={gameId}
                  plan={interviewPlan}
                  subPhase={
                    interviewSubPhase === 'plan' ||
                    interviewSubPhase === 'refine' ||
                    interviewSubPhase === 'back-to-plan'
                      ? interviewSubPhase
                      : 'plan'
                  }
                  refinementBusy={isAiTyping || planModeBusy}
                  startBuildingBusy={planModeBusy}
                  draftingStartedAt={draftingStartedAt}
                  draftingStoppedAt={draftingStoppedAt}
                  onSubmitRefinement={handlePlanRefinement}
                  onStartBuilding={handleStartBuilding}
                  onStartOver={handlePlanStartOver}
                />
              ) : null)}
            {tokensResolved &&
              hasAnthropicKey &&
              chatModeVisible &&
              (() => {
                /* Codex review fix — flag-OFF DOM parity: with
                 `CRUCIBLE_LIVE_PREVIEW` unset, ChatPanel renders UNWRAPPED,
                 byte-identical to the pre-split tree. Flag-ON gets the
                 wrapper: `display: contents` while the split is closed (no
                 box, same DOM parent across split toggles — no remount, no
                 IPC resubscribe), the flex dock constraint while open. */
                const chatPanelEl = (
                  <ChatPanel
                    gameId={gameId}
                    gameName={gameName}
                    messages={messages}
                    chatInput={chatInput}
                    isAiTyping={isAiTyping}
                    runState={workspaceRunState}
                    activitySnapshot={activitySnapshot}
                    phaseProgress={phaseProgress}
                    onInputChange={(v) => chatDispatch({ type: 'user/type', text: v })}
                    onSend={handleSendMessage}
                    onStop={handleStopMessage}
                    queuedMessages={[...queuedMessages]}
                    onUnqueueMessage={(idx) => chatDispatch({ type: 'user/unqueue', index: idx })}
                    persistError={chatPersistError}
                    onDismissPersistError={() => setChatPersistError(null)}
                    hydrationStatus={hydrationStatus}
                    hydrationError={hydrationError}
                    hydrationLongRunning={hydrationLongRunning}
                    onRetryHydration={handleRetryHydration}
                    pendingAttachments={pendingAttachments}
                    attachmentPop={attachmentPop}
                    onAddAttachment={(a) => {
                      setAttachmentError(null);
                      chatDispatch({ type: 'user/add-attachment', attachment: a });
                    }}
                    onRemoveAttachment={(idx) =>
                      chatDispatch({ type: 'user/remove-attachment', index: idx })
                    }
                    attachmentError={attachmentError}
                    onDismissAttachmentError={() => setAttachmentError(null)}
                    lastTurnSpend={lastTurnSpend}
                    showCostInChat={showCostInChat}
                    onAnswerMarkdownQuestion={handleAnswerMarkdownQuestion}
                    onRetryMessage={handleRetry}
                    onCancelAutoBuild={handleCancelAutoBuild}
                    onRetryAutoBuild={handleRetryAutoBuild}
                    pinnedAskUserQuestion={pinnedAskUserQuestion}
                    pendingPhaseBDecision={pendingPhaseBDecision}
                    pendingBulkInterview={pendingBulkInterview}
                    onBulkInterviewSubmit={handleBulkInterviewSubmit}
                    onBulkInterviewCancel={handleBulkInterviewCancel}
                    onStartExpressInterview={handleStartExpressInterview}
                    interviewViewMode={interviewViewMode}
                    interviewSlotActive={interviewSlotActive}
                    onInterviewRendererReady={handleInterviewRendererReady}
                    // In the build view the artifact pane is not rendered, so
                    // it is never "active" there regardless of interview status.
                    isArtifactPaneActive={
                      artifactPaneOpen && workspaceMode !== 'preview' && !livePreviewSplitActive
                    }
                    showReviewModal={showReviewModal}
                    onCloseReviewModal={() => setShowReviewModal(false)}
                    // Develop side-by-side: the chat lives in a narrow panel,
                    // so drop the wide centred max-width and tighten padding.
                    compact={livePreviewSplitActive}
                  />
                );
                return (
                  <WorkspaceChatSurface
                    flagsLoaded={flags.loaded}
                    newUi={flags.newUi}
                    livePreview={flags.livePreview}
                    chatPanel={chatPanelEl}
                    chatTab={chatTab}
                    onChatTabChange={setChatTab}
                    statePanel={
                      <WorkspaceRightPanel
                        variant="pinned"
                        open
                        onClose={NOOP}
                        phaseProgress={phaseProgress}
                        activitySnapshot={activitySnapshot}
                        runState={workspaceRunState}
                        interviewState={interviewState}
                        interviewSummary={interviewStatusQuery.data}
                        onResumeBuild={
                          resumeBuildActionKind === 'start-building'
                            ? handleResumeBuildKickoff
                            : resumeBuildActionKind === 'retry-auto-build'
                              ? handleRetryAutoBuild
                              : resumeBuildActionKind === 'rebuild-from-plan'
                                ? handleRebuildFromPlan
                                : undefined
                        }
                        onEditDiscovery={() => {
                          setArtifactSoftClosed(false);
                          setChatTab('chat');
                        }}
                      />
                    }
                    diagnosticsPanel={<DiagnosticsTab diagnostics={previewDiagnostics} />}
                    previewSurface={previewSurfaceEl}
                    livePreviewSplitActive={livePreviewSplitActive}
                    livePreviewChatWidth={livePreviewChatWidth}
                    onLivePreviewChatWidthChange={setLivePreviewChatWidthForGame}
                  />
                );
              })()}
            {tokensResolved && !hasAnthropicKey && (
              <ChatPanelPlaceholder
                onOpenSettings={() => navigate('/settings')}
                // Codex review (2026-05-01): chatPersistError must surface
                // even in the no-Anthropic-key branch. A board member can
                // hit chat-history corruption (CHAT-CORRUPT / CHAT-SHAPE)
                // before they've ever pasted a key — a previous app run
                // might have errored out, and the next launch must show
                // them the recovery card and the backup path. The earlier
                // gating left them with a blank "needs setup" placeholder
                // and no clue why.
                persistError={chatPersistError}
                onDismissPersistError={() => setChatPersistError(null)}
              />
            )}
            {!tokensResolved && (
              <section
                className="flex flex-1 items-center justify-center overflow-hidden"
                aria-busy="true"
                aria-label="Loading Build panel"
              />
            )}
          </CentreColumnWithBanner>
        )}

        {/* Artifact split-pane — new UI only. Persists mounted so CSS
            width-transition can animate smoothly. The InterviewSlotOverlay
            is only rendered when the pane is open; when closed the chat-input
            area mounts its own overlay instead (see below). This prevents
            duplicate IPC subscriptions for the same gameId. */}
        {flags.newUi && workspaceMode !== 'preview' && !livePreviewSplitActive && gameId && (
          <ArtifactPane
            isOpen={artifactPaneOpen}
            onClose={pendingBulkInterview ? undefined : () => setArtifactSoftClosed(true)}
            title={
              pendingBulkInterview
                ? 'Fast questions'
                : interviewStatus === 'complete'
                  ? 'Game Plan'
                  : 'Discovery'
            }
            headerActions={
              pendingBulkInterview ? (
                <>
                  <button
                    type="button"
                    onClick={() => bulkCardRef.current?.triggerCancel()}
                    className="rounded-md border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-medium text-white/60 transition-colors hover:border-white/20 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={() => bulkCardRef.current?.triggerSubmit()}
                    disabled={!bulkSubmittable}
                    className="rounded-md bg-crucible-accent px-3 py-1 text-[11px] font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Submit answers
                  </button>
                </>
              ) : undefined
            }
          >
            {artifactPaneOpen && pendingBulkInterview && (
              <BulkInterviewCard
                ref={bulkCardRef}
                sessionId={pendingBulkInterview.sessionId}
                questions={pendingBulkInterview.questions}
                onSubmit={(answers) =>
                  handleBulkInterviewSubmit(pendingBulkInterview.sessionId, answers)
                }
                onCancel={(partial) =>
                  handleBulkInterviewCancel(pendingBulkInterview.sessionId, partial)
                }
                onSubmittableChange={setBulkSubmittable}
                hideBottomActions
              />
            )}
            {artifactPaneOpen && !pendingBulkInterview && interviewStatus === 'complete' && (
              <GamePlanView
                gameName={gameName}
                answeredCount={interviewStatusQuery.data?.answeredCount ?? 0}
                totalCount={interviewStatusQuery.data?.totalCount ?? 0}
                plan={interviewStateQuery.data?.plan ?? null}
              />
            )}
            {artifactPaneOpen && (
              <InterviewSlotOverlay
                gameId={gameId}
                initialViewMode={interviewViewMode}
                onRendererReady={handleInterviewRendererReady}
              />
            )}
          </ArtifactPane>
        )}

        {/* The pinned activity rail (progress + live sub-agent panel) moved
            INTO the chat panel as the "State" tab (see the ChatTabBar in the
            chat-mode render above). It is no longer a standalone right
            column. */}

        {/* Craft canvas — overlays the content area only (header stays visible).
            Requires both CRUCIBLE_NEW_UI=1 and CRUCIBLE_CRAFT_MODE=1. */}
        {craftOpen && flags.craftMode && flags.newUi && gameId && (
          <div className="absolute inset-0 z-10 overflow-hidden">
            <CraftView gameId={gameId} />
          </div>
        )}
      </div>

      {/* #191 quick-batch — confirmation modal for the back-to-plan
          escape hatch. Mounted unconditionally; `open` controls
          visibility so the modal's keydown listener is only active
          when the modal is open (`useEffect` early-returns on
          `!open`). */}
      <BackToPlanConfirmModal
        open={backToPlanModalOpen}
        onCancel={handleBackToPlanCancel}
        onConfirm={handleBackToPlanConfirm}
      />
    </div>
  );
}

function PreviewDevLoopBanner({
  state,
  onPrimary,
}: {
  state: PreviewDevLoopState;
  onPrimary: () => void;
}): React.JSX.Element {
  return (
    <div
      data-testid="preview-dev-loop-banner"
      role="alert"
      className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm"
    >
      <div className="flex items-center gap-2 text-amber-200">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M8 1.5L14.5 13.5H1.5L8 1.5Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <path
            d="M8 6v3.5M8 11v.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
        <span data-testid="preview-dev-loop-banner-message">
          {state.label}{' '}
          {state.detail && (
            <span className="font-mono text-xs text-amber-300/80">({state.detail})</span>
          )}
        </span>
      </div>
      <button
        type="button"
        data-testid="preview-dev-loop-primary-cta"
        onClick={onPrimary}
        className="rounded border border-amber-500/40 bg-amber-500/20 px-3 py-1 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/30 disabled:opacity-50"
      >
        Retry install & start preview
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const DEPLOY_BADGE: Record<DeployState, { label: string; className: string }> = {
  idle: { label: 'Draft', className: 'bg-white/10 text-white/50' },
  starting: { label: 'Starting...', className: 'bg-blue-500/20 text-blue-400' },
  running: { label: 'Running', className: 'bg-green-500/20 text-green-400' },
  deploying: { label: 'Deploying...', className: 'bg-yellow-500/20 text-yellow-400' },
  deployed: { label: 'Deployed', className: 'bg-green-500/20 text-green-400' },
  error: { label: 'Error', className: 'bg-red-500/20 text-red-400' },
};

/**
 * Exported for the deploy-failure-pill test (#408). The header is
 * otherwise an internal Workspace concern; test export does NOT
 * widen the public surface — the only consumer is `Workspace.tsx`
 * itself plus the unit test for the failed-step chip + deep-link.
 */
export function WorkspaceHeader({
  gameId,
  gameName,
  deployState,
  deployActionState,
  selectionAddedHint,
  gameStatus,
  cloneDeployAccessWarning,
  deployFailureContext,
  localFailedCheck,
  activitySnapshot,
  interviewStatus,
  onPreview,
  onChat,
  onPlan,
  onDesign,
  onCraft,
  onLivePreview,
  onProtoHub,
  livePreviewSplitActive = false,
  previewLoading,
  previewMenuEnabled,
  previewForceDisabled = false,
  previewDisabledReason,
  previewAvailableTitle,
  inAppPairingAvailable = false,
  inAppPairingNextLaunch = false,
  onInAppPairingNextLaunchChange,
  statusBadge = 'draft',
  repoUrl,
  previewPhaseGate,
  onDeploy,
  showBackToPlanButton,
  backToPlanBusy,
  onBackToPlanRequest,
  activeMode = 'chat',
  contributors,
  rightPanelOpen,
  onToggleRightPanel,
  runState,
  cloudVmEnabled = false,
  maintainerMode = false,
  furnaceBuildState = {
    phase: 'inactive',
    routedToFurnace: false,
    label: '',
  },
}: {
  gameId: string;
  gameName: string;
  deployState: DeployState;
  deployActionState?: DeployActionState;
  /** Select-to-chat — pulse the Build tab + show a transient "added to
   *  Build" popover beneath it (true for ~5s after a selection lands). */
  selectionAddedHint?: boolean;
  gameStatus?: import('@volley/crucible-ipc-types/ipc-channels').GameStatus;
  /** Game lifecycle badge for the project settings panel (Draft/Live/…). */
  statusBadge?: GameStatusBadge;
  /** GitHub repo URL for the project panel's Repo button (cloned games only). */
  repoUrl?: string;
  cloneDeployAccessWarning?: CloneDeployAccessWarning;
  /**
   * Issue #791 — live agent-activity snapshot from
   * `useActivitySnapshot(gameId)`. Drives the new agent-activity
   * pill's three-mode derivation (Ready / Running / Waiting on you).
   * Optional + defaults to `null` so existing call sites (including
   * the `WorkspaceHeader` unit tests that pre-date this prop) keep
   * working — the pill folds to `Ready` when both surfaces are
   * absent.
   */
  activitySnapshot?: import('@volley/crucible-ipc-types/ipc-channels').ActivitySnapshot | null;
  /**
   * Issue #791 — `build-game:interview-status` summary status. Covers
   * the pre-snapshot-registration window at the start of a build
   * where the SKILL has written `status: 'in-progress'` to disk but
   * no `activity:state` push has landed yet. Without this the pill
   * would briefly read `Ready` while a question is already on the
   * screen. Optional + defaults to `null` for the same back-compat
   * reasoning as `activitySnapshot`.
   */
  interviewStatus?:
    | import('@volley/crucible-ipc-types/build-game-types').BuildGameInterviewStatus
    | null
    | undefined;
  /**
   * User-facing aggregate run state. When present this is the single
   * source for the header activity pill copy/tone.
   */
  runState?: WorkspaceRunState | null;
  /**
   * Issue #408 — when the deploy widget is in `error` state AND we
   * have a failure context for the latest run, pull the failed
   * step name + deep-link to the failed job page so the pill can
   * answer "what broke?" without the user opening the card. Always
   * undefined unless `deployState === 'error'`.
   */
  deployFailureContext?: import('@volley/crucible-ipc-types/ipc-channels').DeployFailureContext;
  /**
   * Issue #415 Layer 3 — when a LOCAL pre-flight quality-gate
   * check fails (lint / typecheck / shared build), the renderer
   * has no `deployFailureContext` to pull a failed-step name from
   * (no GitHub workflow run exists yet). Main pushes the failing
   * check name on `publish:progress.failedCheck`, the renderer
   * stores it in `localFailedCheck`, and the same "Failed step"
   * chip from #408/#413 renders it. Same UX, different source —
   * exactly the spec's "use the SAME UX as the existing deploy-
   * failure flow" requirement.
   */
  localFailedCheck?: string | null;
  onPreview: () => void;
  onChat?: () => void;
  onPlan?: () => void;
  /** Enter Design mode. Only passed when the process flag and user setting
   *  both allow the Design workspace. */
  onDesign?: () => void;
  /** Toggle Craft viewer. Only passed when `CRUCIBLE_CRAFT_MODE=1`. */
  onCraft?: () => void;
  /**
   * Toggle the live-preview split (#1408/#1420 — preview docked above
   * the chat). Only passed when live preview is enabled in the new
   * UI, so flag-OFF headers render identical chrome. Deliberately
   * WEAKER-gated than the Preview pill: the pill toggles a surface
   * (whose embedded overlay shows its own loading/launch states), so
   * it disables only on no-game / hard force-disable — never on
   * `previewLoading` or the phase gate (see the pill render site).
   */
  onLivePreview?: () => void;
  /** Open the Mac-app-only ProtoHub TV simulator. */
  onProtoHub?: () => void;
  /** Whether the live-preview split is currently showing (pill active state). */
  livePreviewSplitActive?: boolean;
  previewLoading: boolean;
  previewMenuEnabled: boolean;
  /** Additional parent-owned gate for the Preview pill. */
  previewForceDisabled?: boolean;
  /** User-facing title copy when the Preview pill is disabled by parent state. */
  previewDisabledReason?: string | undefined;
  /** User-facing title copy when Preview is enabled. */
  previewAvailableTitle?: string | undefined;
  inAppPairingAvailable?: boolean;
  inAppPairingNextLaunch?: boolean;
  onInAppPairingNextLaunchChange?: (next: boolean) => void;
  previewPhaseGate?: { currentPhase: number; phaseLabel: string } | undefined;
  onDeploy?: () => void;
  showBackToPlanButton?: boolean;
  backToPlanBusy?: boolean;
  onBackToPlanRequest?: () => void;
  /** Which mode pill is currently active. Defaults to 'chat'. */
  activeMode?: WorkspaceHeaderMode;
  /** GitHub contributors for the current game — shown as a facepile in the new UI. */
  contributors?: { login: string; avatarUrl: string }[];
  rightPanelOpen?: boolean;
  onToggleRightPanel?: () => void;
  /**
   * Cloud-VM supportability TDD §A-1 — gates the `<VmStatusPill />`
   * mount. The pill calls no hooks that need a provider; gating here
   * keeps SSR tests of `WorkspaceHeader` free of `useFeatureFlags`'s
   * TanStack Query dependency. Defaults to `false` so existing test
   * callers that pre-date the pill keep rendering identical chrome.
   */
  cloudVmEnabled?: boolean;
  /**
   * Cloud-VM supportability TDD §B-1 — true when Foundry was launched
   * with `CRUCIBLE_MAINTAINER_MODE=1`. Mirrors the `cloudVmEnabled`
   * pattern: the parent reads the gate hook (`useMaintainerMode`)
   * once and threads the boolean through here so the header stays
   * SSR-friendly for tests that don't wrap in a QueryClientProvider.
   * Currently informational only (no in-header surface flips on this
   * yet); reserved so a future maintainer-mode indicator pill can
   * be added without changing the call sites. Defaults to false so
   * existing test callers that pre-date the prop keep rendering
   * identical chrome.
   */
  maintainerMode?: boolean;
  /**
   * Bridge-3 UX — derived Furnace build state from
   * `computeFurnaceBuildState`. The parent owns the inputs
   * (`FurnaceStatus` query + chat reducer's `isAiTyping` + VM
   * connection subscription + failure latch) so the header stays
   * SSR-friendly for tests that render it without a
   * QueryClientProvider. Defaults to a permanently-inactive value
   * (routedToFurnace=false) when omitted so existing test callers
   * that pre-date the prop render identical chrome.
   */
  furnaceBuildState?: FurnaceBuildState;
}): React.JSX.Element {
  const { newUi, tileImageUpload } = useFeatureFlags();
  const frame = useAppFrame();
  const shellChrome = frame === 'embedded' ? 'web' : 'electron';
  // Suppress the unused-prop lint while the maintainer-mode header
  // surface is reserved-but-unused. Removing the void once a
  // consumer mounts off the flag.
  void maintainerMode;
  const deployUiState =
    deployActionState ??
    deriveDeployActionState({
      deployState,
      deployFailureContext,
      localFailedCheck,
      ciTriggeredThisDeploy: false,
      hasAnthropicKey: true,
    });
  // Project settings panel — opened by clicking the game title (new UI).
  const [projectPanelOpen, setProjectPanelOpen] = useState(false);
  const titleBtnRef = useRef<HTMLButtonElement>(null);
  const badge = newUi
    ? { label: deployUiState.label, className: deployUiState.badgeClassName }
    : DEPLOY_BADGE[deployState];

  const gitBranch = gameStatus?.localGit?.branch;
  const bifrostPhase = gameStatus?.bifrost?.phase;
  const registryVersion = gameStatus?.registry?.version;

  // Issue #408 — pick the first failed job's step name + deep-link
  // off the failure context. We surface BOTH next to the pill (a
  // separate "Failed step:" chip and a clickable arrow icon) rather
  // than swallowing the existing pill — the pill colour is the
  // load-bearing visual cue, the chip is the additive context.
  //
  // Issue #415 Layer 3 — when no remote failure context exists
  // (i.e. the failure was a LOCAL pre-flight quality-gate check
  // and CI never ran), fall back to `localFailedCheck` so the
  // chip still surfaces the cause. This is the SAME chip the
  // #408/#413 work introduced; we just changed the source.
  const firstFailedJob = deployFailureContext?.failedJobs?.[0];
  const failedStepName = firstFailedJob?.failedStep ?? localFailedCheck ?? undefined;
  const failedRunUrl =
    deployFailureContext?.failedRunUrl ??
    firstFailedJob?.htmlUrl ??
    deployFailureContext?.workflowRun?.htmlUrl;

  const handleOpenInCursor = (): void => {
    window.crucible.invoke('game:open-in-editor', gameId);
  };

  if (!newUi) {
    return (
      <FoundryShellHeader
        chrome={shellChrome}
        leadingClassName="col-span-3 gap-0 overflow-visible"
        centerClassName="hidden"
        actionsClassName="hidden"
        leading={
          <>
            <Link
              to="/dashboard"
              className="mr-4 text-sm text-white/40 transition-colors hover:text-white/80"
            >
              &larr; Back
            </Link>
            <div className="mr-4 h-4 w-px bg-white/10" />
            <span className="mr-3 text-sm font-medium text-white/80">{gameName}</span>
            <span
              data-testid="deploy-state-pill"
              className={`mr-2 rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}
            >
              {badge.label}
            </span>
            {deployState === 'error' && failedStepName && (
              <span
                data-testid="deploy-failure-step-chip"
                className="mr-2 truncate rounded-md bg-red-500/10 px-2 py-0.5 text-xs text-red-200/90"
                title={`Failed step: ${failedStepName}`}
              >
                <span className="text-red-300/60">Failed step:</span>{' '}
                <span className="font-mono">{failedStepName}</span>
              </span>
            )}
            {deployState === 'error' && failedRunUrl && (
              <a
                href={failedRunUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="deploy-failure-run-link-pill"
                className="mr-2 rounded-md border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[11px] text-red-200/80 transition-colors hover:bg-red-500/15"
                title="Open the failed run on GitHub"
              >
                View on GitHub ↗
              </a>
            )}
            {gitBranch && (
              <span className="mr-2 rounded-md bg-white/5 px-2 py-0.5 font-mono text-xs text-white/40">
                {gitBranch}
              </span>
            )}
            {bifrostPhase && (
              <span
                className={`mr-2 rounded-full px-2 py-0.5 text-xs font-medium ${PHASE_STYLE[bifrostPhase] ?? 'bg-white/10 text-white/50'}`}
              >
                {bifrostPhase}
              </span>
            )}
            {registryVersion && (
              <span className="mr-2 text-xs text-white/30">{registryVersion}</span>
            )}
            <div className="flex-1" />
            {showBackToPlanButton && onBackToPlanRequest && (
              <button
                type="button"
                onClick={onBackToPlanRequest}
                disabled={backToPlanBusy}
                data-testid="workspace-back-to-plan-button"
                className="mr-3 flex items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-1.5 text-xs text-amber-100 transition-colors hover:border-amber-400/50 hover:bg-amber-500/15 disabled:cursor-not-allowed disabled:opacity-40"
              >
                ← Back to plan
              </button>
            )}
            <button
              onClick={handleOpenInCursor}
              title="Open in Cursor"
              className="mr-3 flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white/50 transition-colors hover:border-white/20 hover:text-white/80"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M5 2L2 8l3 6M11 2l3 6-3 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path d="M8 6v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Open in Cursor
            </button>
            {cloneDeployAccessWarning && (
              <CloneDeployAccessWarningPill warning={cloneDeployAccessWarning} />
            )}
            {onProtoHub && shellChrome === 'electron' && (
              <button
                type="button"
                onClick={onProtoHub}
                data-testid="display-on-hub-button"
                className="mr-3 flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs text-white/55 transition-colors hover:border-white/20 hover:text-white/85"
              >
                <MonitorPlay size={13} strokeWidth={1.75} />
                Display on Hub
              </button>
            )}
            <button
              onClick={onDeploy}
              disabled={deployUiState.deployButtonDisabled}
              data-testid="deploy-to-tv-button"
              data-deploy-gated={deployUiState.deployButtonGated ? 'true' : 'false'}
              title={deployUiState.deployButtonTitle}
              className="rounded-lg bg-green-600 px-4 py-1.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {deployUiState.deployButtonLabel}
            </button>
          </>
        }
      />
    );
  }

  return (
    <FoundryShellHeader
      chrome={shellChrome}
      className="relative z-30 border-b border-white/[0.08] pr-4"
      style={{
        background: 'var(--color-crucible-bg)',
      }}
    >
      <div className="flex min-w-0 items-center overflow-hidden">
        {/* Home button — circle icon matching the Dashboard settings button */}
        <Link
          to="/dashboard"
          className="app-region-no-drag flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/[0.11] transition-all hover:border-white/[0.18]"
          style={{
            background: 'var(--color-crucible-bg)',
            color: 'var(--text-3)',
            transitionTimingFunction: 'var(--ease-flora)',
            transitionDuration: 'var(--motion-fast)',
          }}
          aria-label="Back to Dashboard"
        >
          <Home size={16} strokeWidth={1.75} />
        </Link>

        {/* Vertical divider */}
        <span className="mx-3 h-4 w-px shrink-0 rounded-full bg-white/10" />

        <div className="relative min-w-0 shrink">
          {/* New UI: the title opens the project settings panel (editing now
              lives in the panel). Legacy keeps the plain title. */}
          {newUi ? (
            <>
              <button
                ref={titleBtnRef}
                type="button"
                onClick={() => setProjectPanelOpen((v) => !v)}
                data-testid="project-title-trigger"
                aria-haspopup="dialog"
                aria-expanded={projectPanelOpen}
                title="Project settings"
                className="app-region-no-drag f-title group flex min-w-0 max-w-full items-center gap-1 rounded px-1 transition-colors hover:bg-white/[0.06]"
                style={{ color: 'var(--text-1)' }}
              >
                <span className="min-w-0 truncate">{gameName}</span>
                <ChevronDown
                  size={15}
                  strokeWidth={2}
                  className={`shrink-0 text-white/45 transition-transform ${
                    projectPanelOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {projectPanelOpen && gameId && (
                <ProjectSettingsPanel
                  gameId={gameId}
                  gameName={gameName}
                  statusBadge={statusBadge}
                  version={gameStatus?.registry?.version}
                  repoUrl={repoUrl}
                  repoFullName={gameStatus?.clonedFrom}
                  inAppPairingAvailable={inAppPairingAvailable}
                  inAppPairingNextLaunch={inAppPairingNextLaunch}
                  onInAppPairingNextLaunchChange={onInAppPairingNextLaunchChange}
                  tileImageEnabled={tileImageUpload}
                  triggerRef={titleBtnRef}
                  onClose={() => setProjectPanelOpen(false)}
                />
              )}
            </>
          ) : (
            <span className="f-title" style={{ color: 'var(--text-1)' }}>
              {gameName}
            </span>
          )}
        </div>

        {/* Issue #791 — agent-activity pill. New UI surfaces this in
            WorkspaceRightPanel instead; legacy UI keeps the header pill. */}
        {!newUi && (
          <AgentActivityPill
            snapshot={activitySnapshot ?? null}
            interviewStatus={interviewStatus}
            runState={runState}
          />
        )}

        {/* New UI: status chips grouped + muted in a single pill row.
            Legacy UI keeps main's unwrapped chip layout below. */}
        {newUi && (
          <div className="ml-3 flex min-w-0 items-center gap-1.5 overflow-hidden">
            {deployState === 'error' && failedStepName && (
              <span
                data-testid="deploy-failure-step-chip"
                className="max-w-[11rem] truncate rounded-full bg-red-500/10 px-2 py-0.5 text-[11px] text-red-200/90"
                title={`Failed step: ${failedStepName}`}
              >
                <span className="text-red-300/60">Failed:</span>{' '}
                <span className="font-mono">{failedStepName}</span>
              </span>
            )}
            {deployState === 'error' && failedRunUrl && (
              <a
                href={failedRunUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="deploy-failure-run-link-pill"
                className="shrink-0 rounded-full border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[11px] text-red-200/80 transition-colors hover:bg-red-500/15"
                title="Open the failed run on GitHub"
              >
                View on GitHub ↗
              </a>
            )}
            {gitBranch && gitBranch !== 'main' && gitBranch !== 'master' && (
              <span
                className="min-w-0 max-w-[16rem] truncate font-mono text-[11px]"
                style={{ color: 'var(--text-3)' }}
                title={gitBranch}
              >
                {gitBranch}
              </span>
            )}
            {bifrostPhase && (
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${PHASE_STYLE[bifrostPhase] ?? 'bg-white/10 text-white/50'}`}
                title={`TV deployment: ${bifrostPhase}`}
              >
                {bifrostPhase}
              </span>
            )}
            {/* Version moved to the project settings panel (title click). */}
          </div>
        )}

        {/* Bridge-3 — Furnace build-status indicator. Renders only when
            the engineer is genuinely on the Furnace path (in cohort +
            flag on + IPC loaded) AND a turn is in flight OR the most-
            recent terminal chunk was a Furnace failure. The component
            self-hides on `routedToFurnace=false`, so flag-OFF + out-of-
            cohort + IPC-not-loaded all render identical chrome to
            pre-Bridge-3 main. */}
        {furnaceBuildState.routedToFurnace && (
          <div className="ml-2 shrink-0">
            <FurnaceBuildIndicator state={furnaceBuildState} />
          </div>
        )}

        {/* Legacy UI chip layout — main's existing surface. Preserved
            exactly so the legacy UI is unchanged when newUi is OFF. */}
        {!newUi && (
          <>
            {/* Cloud-VM supportability TDD §A-1 — VM-status pill, mounted
                only when the cloud-VM feature flag is ON. Gate lives at this
                call site so `WorkspaceHeader` stays SSR-friendly for tests
                that render it without a QueryClientProvider; the pill itself
                still self-gates on whether a `vm:connection-state` push has
                been received before rendering anything. */}
            {cloudVmEnabled ? <VmStatusPill /> : null}

            <span
              data-testid="deploy-state-pill"
              className={`mr-2 rounded-full px-2.5 py-0.5 text-xs font-medium ${badge.className}`}
            >
              {badge.label}
            </span>

            {deployState === 'error' && failedStepName && (
              <span
                data-testid="deploy-failure-step-chip"
                className="mr-2 truncate rounded-md bg-red-500/10 px-2 py-0.5 text-xs text-red-200/90"
                title={`Failed step: ${failedStepName}`}
              >
                <span className="text-red-300/60">Failed step:</span>{' '}
                <span className="font-mono">{failedStepName}</span>
              </span>
            )}
            {deployState === 'error' && failedRunUrl && (
              <a
                href={failedRunUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="deploy-failure-run-link-pill"
                className="mr-2 rounded-md border border-red-500/30 bg-red-500/5 px-2 py-0.5 text-[11px] text-red-200/80 transition-colors hover:bg-red-500/15"
                title="Open the failed run on GitHub"
              >
                View on GitHub ↗
              </a>
            )}

            {gitBranch && (
              <span className="mr-2 rounded-md bg-white/5 px-2 py-0.5 font-mono text-xs text-white/40">
                {gitBranch}
              </span>
            )}

            {bifrostPhase && (
              <span
                className={`mr-2 rounded-full px-2 py-0.5 text-xs font-medium ${PHASE_STYLE[bifrostPhase] ?? 'bg-white/10 text-white/50'}`}
              >
                {bifrostPhase}
              </span>
            )}

            {registryVersion && (
              <span className="mr-2 text-xs text-white/30">{registryVersion}</span>
            )}
          </>
        )}
      </div>

      {/* Mode pills — a real center grid column, so long left metadata
          truncates instead of painting underneath the navigation. */}
      <div className="app-region-no-drag flex min-w-0 items-center justify-center">
        <div
          className="flex h-10 items-center gap-0.5 px-1 rounded-full"
          style={{
            background: 'var(--surface-mode-pill-bar)',
            boxShadow: 'var(--surface-mode-pill-bar-ring)',
          }}
        >
          {/* First tab. With live-preview ON it is "Develop" (wand) — the
              default side-by-side chat | preview view; the plain "Chat"
              tab is retired. With the flag OFF it falls back to the
              original full-width Chat tab. The select-to-chat "added"
              hint anchors to whichever is shown. */}
          <span className="relative inline-flex">
            {onLivePreview ? (
              <button
                type="button"
                data-chat-tab
                data-testid="live-preview-pill"
                className={`mode-pill${selectionAddedHint ? ' animate-selection-pop ring-2 ring-crucible-accent' : ''}`}
                data-active={activeMode === 'chat' ? 'true' : 'false'}
                data-selection-added={selectionAddedHint ? 'true' : 'false'}
                data-disabled={!gameId || previewForceDisabled ? 'true' : 'false'}
                onClick={!gameId || previewForceDisabled ? undefined : onLivePreview}
                disabled={!gameId || previewForceDisabled}
                title={
                  !gameId || previewForceDisabled
                    ? (previewDisabledReason ?? 'Open a game to develop')
                    : 'Develop — chat with the agent alongside the live preview'
                }
              >
                {previewLoading ? (
                  <PreviewSpinnerIcon />
                ) : !gameId || previewForceDisabled ? (
                  <PreviewLockIcon />
                ) : (
                  <DevelopIcon />
                )}
                <span>Develop</span>
              </button>
            ) : (
              <button
                type="button"
                data-chat-tab
                className={`mode-pill${selectionAddedHint ? ' animate-selection-pop ring-2 ring-crucible-accent' : ''}`}
                data-active={activeMode === 'chat' ? 'true' : 'false'}
                data-selection-added={selectionAddedHint ? 'true' : 'false'}
                onClick={onChat}
                title="Open Build"
              >
                <ChatIcon />
                <span>Build</span>
              </button>
            )}
            {selectionAddedHint && (
              <span
                role="status"
                data-testid="selection-added-hint"
                // Matches the shared Tooltip primitive's surface (neutral
                // hairline border, not a gold accent border) so it reads like
                // every other hovering popover.
                className="app-region-no-drag absolute left-1/2 top-[calc(100%+8px)] z-50 w-[240px] -translate-x-1/2 rounded-[var(--radius-md)] border border-white/[0.14] bg-[#18151f] px-3 py-2 text-[12px] leading-snug text-white/85 shadow-xl"
              >
                Your selection was added to Build. Ask the build agent to make changes.
              </span>
            )}
          </span>
          {onPlan && (
            <button
              type="button"
              className={`mode-pill${activeMode === 'chat' ? ' ml-3.5' : ''}`}
              data-active={activeMode === 'plan' ? 'true' : 'false'}
              onClick={onPlan}
              title="Open Plan"
            >
              <PlanIcon />
              <span>Plan</span>
            </button>
          )}
          {/* Design pill — rendered whenever Design is available for the current
              local game under feature/config gates. */}
          {onDesign && (
            <button
              type="button"
              className={`mode-pill${activeMode === 'chat' && !onPlan ? ' ml-3.5' : ''}`}
              data-testid="design-mode-button"
              data-active={activeMode === 'design' ? 'true' : 'false'}
              onClick={onDesign}
              title="Open Design"
            >
              <PlanIcon />
              <span>Design</span>
            </button>
          )}
          {onCraft && (
            <button
              type="button"
              className={`mode-pill${activeMode === 'plan' ? ' ml-3.5' : ''}`}
              data-active={activeMode === 'craft' ? 'true' : 'false'}
              onClick={onCraft}
              title="Open Craft Mode"
            >
              <CraftIcon />
              <span>Craft</span>
            </button>
          )}
          {/* The Weekend-app pairing toggle moved into the project settings
              panel (opened from the game title). */}
        </div>
      </div>

      <div className="flex min-w-0 items-center justify-end gap-2">
        {cloneDeployAccessWarning && (
          <CloneDeployAccessWarningPill warning={cloneDeployAccessWarning} />
        )}
        {/* Preview lifecycle controls (Stop / Play / Reload) moved into
            the preview's floating toolbar below the TV (2026-06-13). */}

        {/* The right-panel toggle was removed — the activity rail is now the
            "State" tab inside the chat panel, so there's no standalone panel
            to collapse. */}

        {/* Open in Cursor — icon-only; hover reveals the label. Opens the
            game workspace via the game:open-in-editor IPC. */}
        <span className="relative inline-flex">
          <button
            type="button"
            onClick={handleOpenInCursor}
            aria-label="Open in Cursor"
            data-testid="open-in-cursor-button"
            className="peer app-region-no-drag flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/10 text-white/60 transition-colors hover:border-white/20 hover:text-white/90"
          >
            <CursorGlyph />
          </button>
          <Tooltip align="end">Open in Cursor</Tooltip>
        </span>

        {/* Preview — icon-only; hover reveals the (gating-aware) purpose.
            The manual full-bleed preview surface (was a centre pill). */}
        {(() => {
          const pill = derivePreviewLaunchPillState({
            gameId,
            previewLoading,
            previewForceDisabled,
            previewDisabledReason,
            previewAvailableTitle,
            previewPhaseGate,
          });
          // The button is a toggle: Play to open the larger preview for final
          // testing, Stop (when already previewing) to end it and return to
          // Develop. The tooltip describes the FUNCTION, not the dev-server
          // state (gating/loading reasons still surface when disabled).
          const inPreview = activeMode === 'preview';
          const tooltip = inPreview
            ? 'Stop the preview and return to Develop'
            : pill.disabled
              ? pill.title
              : 'Open a larger preview for final testing';
          return (
            <span className="relative inline-flex">
              <button
                type="button"
                data-testid="preview-launch-button"
                data-active={inPreview ? 'true' : 'false'}
                data-disabled={pill.disabled ? 'true' : 'false'}
                data-phase-gated={pill.phaseGated ? 'true' : 'false'}
                onClick={pill.disabled ? undefined : inPreview ? onChat : onPreview}
                disabled={pill.disabled}
                aria-label={inPreview ? 'Stop preview' : 'Preview'}
                className={`peer app-region-no-drag relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                  inPreview
                    ? 'border-crucible-accent/60 text-crucible-accent'
                    : 'border-white/10 text-white/70 hover:border-white/20 hover:text-white/90'
                }`}
              >
                {pill.icon === 'spinner' ? (
                  <PreviewSpinnerIcon />
                ) : pill.icon === 'lock' ? (
                  <PreviewLockIcon />
                ) : inPreview ? (
                  <PreviewStopIcon />
                ) : (
                  <PreviewPlayIcon />
                )}
                {!pill.disabled && deployState && deployState !== 'idle' && (
                  <span className="absolute right-0.5 top-0.5">
                    <PreviewStatusDot state={deployState} />
                  </span>
                )}
              </button>
              <Tooltip align="end">{tooltip}</Tooltip>
            </span>
          );
        })()}

        {onProtoHub && shellChrome === 'electron' && (
          <span className="relative inline-flex">
            <button
              type="button"
              onClick={onProtoHub}
              data-testid="display-on-hub-button"
              aria-label="Display on Hub"
              className={`peer app-region-no-drag flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors ${
                activeMode === 'protohub'
                  ? 'border-cyan-300/60 text-cyan-200'
                  : 'border-white/10 text-white/70 hover:border-white/20 hover:text-white/90'
              }`}
            >
              <MonitorPlay size={15} strokeWidth={1.8} />
            </button>
            <Tooltip align="end">Display on Hub</Tooltip>
          </span>
        )}

        {/*
          Issue #524 — Deploy-to-TV gate. The button is disabled when the
          game isn't fully built (framework-only / partial / wrong phase),
          with the gate's reason rendered as a hover-tooltip so the user
          can see WHY they can't deploy. Falls through to the existing
          `deployState === 'deploying'` lock when a deploy is already in
          flight. Defence-in-depth alongside #519's state-recording fix.
         */}
        <span className="relative inline-flex">
          <button
            type="button"
            onClick={onDeploy}
            disabled={deployUiState.deployButtonDisabled}
            data-testid="deploy-to-tv-button"
            data-deploy-gated={deployUiState.deployButtonGated ? 'true' : 'false'}
            aria-label={deployUiState.deployButtonLabel}
            className="peer brand-gradient-fill app-region-no-drag inline-flex items-center gap-1.5 rounded-full bg-crucible-accent px-4 py-1.5 text-sm font-medium transition-colors hover:bg-crucible-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Tv size={15} strokeWidth={2} />
            {deployUiState.deployButtonLabel}
          </button>
          <Tooltip align="end">{deployUiState.deployButtonTitle}</Tooltip>
        </span>
      </div>
    </FoundryShellHeader>
  );
}

function PlanIcon() {
  return <Pencil size={14} strokeWidth={1.75} />;
}

function ChatIcon() {
  return <MessageSquareText size={14} strokeWidth={1.75} />;
}

function DevelopIcon() {
  return <WandSparkles size={14} strokeWidth={1.75} />;
}

// Cursor app icon — the isometric cube mark, rendered in three tonal faces
// via currentColor opacities so it reads as the Cursor logo at chip size.
function CursorGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2.5 20.5 7.25 12 12 3.5 7.25Z" fill="currentColor" fillOpacity="0.55" />
      <path d="M3.5 7.25 12 12 12 21.5 3.5 16.75Z" fill="currentColor" fillOpacity="0.95" />
      <path d="M20.5 7.25 12 12 12 21.5 20.5 16.75Z" fill="currentColor" fillOpacity="0.3" />
      <path
        d="M12 2.5 20.5 7.25V16.75L12 21.5 3.5 16.75V7.25Z"
        stroke="currentColor"
        strokeOpacity="0.5"
        strokeWidth="0.9"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CraftIcon() {
  return <Paintbrush size={14} strokeWidth={1.75} />;
}

function PreviewPlayIcon() {
  return <Play size={13} strokeWidth={1.75} />;
}

function PreviewStopIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <rect x="3.5" y="3.5" width="9" height="9" rx="2" />
    </svg>
  );
}

function PreviewSpinnerIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

function PreviewLockIcon() {
  return <Lock size={13} strokeWidth={1.75} />;
}

function CloneDeployAccessWarningPill({
  warning,
}: {
  warning: CloneDeployAccessWarning;
}): React.JSX.Element {
  const title =
    warning.state === 'read-only'
      ? `You can edit this local copy, but deploy/publish cannot push to ${warning.repoFullName} without write access.`
      : `You can edit this local copy, but Foundry could not verify write access to ${warning.repoFullName}.`;
  return (
    <div
      data-testid="clone-deploy-access-warning"
      className="app-region-no-drag flex max-w-[300px] items-center gap-1.5 rounded-full border border-amber-400/25 bg-amber-500/10 px-2.5 py-1 text-[11px] text-amber-100/90"
      title={`${title}${warning.reason ? ` ${warning.reason}` : ''}`}
    >
      <AlertTriangle size={12} strokeWidth={1.8} className="shrink-0 text-amber-300" />
      <span className="truncate">
        {warning.state === 'read-only' ? 'Source repo read-only' : 'Source repo access unknown'}
      </span>
      <button
        type="button"
        onClick={() => window.open(warning.repoUrl, '_blank', 'noopener,noreferrer')}
        className="inline-flex shrink-0 items-center gap-1 text-amber-100/75 transition-colors hover:text-amber-50"
        aria-label={`Open ${warning.repoFullName} on GitHub`}
      >
        <ExternalLink size={11} strokeWidth={1.8} />
      </button>
    </div>
  );
}

/** Inline status dot for the Preview mode pill — mirrors StatusDot in PreviewLaunchButton. */
function PreviewStatusDot({
  state,
}: {
  state: 'starting' | 'running' | 'deploying' | 'deployed' | 'error';
}): React.JSX.Element {
  const base = 'h-1.5 w-1.5 shrink-0 rounded-full';
  if (state === 'running' || state === 'deployed')
    return <span className={`${base} bg-green-400`} aria-hidden="true" />;
  if (state === 'deploying' || state === 'starting')
    return <span className={`${base} bg-amber-400 animate-pulse`} aria-hidden="true" />;
  if (state === 'error') return <span className={`${base} bg-red-400`} aria-hidden="true" />;
  return <></>;
}

// ---------------------------------------------------------------------------
// Files Panel (left, collapsible)
// ---------------------------------------------------------------------------

interface FileNode {
  name: string;
  /** POSIX-style relative path from the game root, used as a stable React key. */
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
}

/**
 * Reshape the flat list returned from the `game:files` IPC into a nested tree
 * the existing tree renderer can consume. The IPC sorts paths
 * lexicographically so a parent prefix always appears before its children.
 *
 * Exported so the renderer-side reshape can be unit-tested directly.
 */
export function buildTreeFromFlat(entries: GameFileEntry[]): FileNode[] {
  const root: FileNode[] = [];
  // Map of relative-path \u2192 that path's children array, for O(1) inserts.
  const childrenByPath = new Map<string, FileNode[]>();
  childrenByPath.set('', root);

  for (const entry of entries) {
    const parts = entry.path.split('/');
    const name = parts[parts.length - 1] ?? entry.path;
    const parentPath = parts.slice(0, -1).join('/');
    const parent = childrenByPath.get(parentPath);
    // Defensive: if the IPC returned a child before its parent the
    // parent lookup misses \u2014 skip rather than crash.
    if (!parent) continue;

    const node: FileNode = {
      name,
      path: entry.path,
      type: entry.isDirectory ? 'directory' : 'file',
    };
    if (entry.isDirectory) {
      node.children = [];
      childrenByPath.set(entry.path, node.children);
    }
    parent.push(node);
  }

  return root;
}

/**
 * Decide whether the chat panel should render the typing indicator
 * underneath the assistant placeholder bubble. Issue #200: during
 * Foundry's `/build-game` Phase A interview the agent emits a cascade
 * of `tool_call` chunks (Edits to `.foundry/build-game-interview.json`)
 * BEFORE any narrative text. The indicator must remain visible while
 * the bubble has only tool-call activity — otherwise the user clicks
 * an answer and sees no signal that the agent is working.
 *
 * Contract:
 *   - `isAiTyping` must be true (the in-flight gate)
 *   - The active assistant bubble has no text content yet (toolCalls
 *     don't count — `m.content` is text-only by reducer design)
 *
 * Crucible #602 PR #C4 — `inline-checkpoint` rows are chrome, not
 * chat. The emitter (Issue A) can land a checkpoint AFTER an
 * assistant bubble has already streamed text, which would flip the
 * trailing-message inspection back to a non-assistant row and
 * spuriously show the indicator for the next render tick. Walk
 * backwards past checkpoint rows so the predicate reads the latest
 * actual chat turn — the indicator's behaviour is unchanged for
 * chat-only timelines.
 *
 * Pure helper exported for unit testing — the JSX gate calls through
 * to this so the predicate is the same in production and in tests.
 */
export function shouldShowTypingIndicator(
  messages: readonly ChatMessage[],
  isAiTyping: boolean,
): boolean {
  if (!isAiTyping) return false;
  // Walk backwards past `inline-checkpoint` chrome to find the most
  // recent chat row. Checkpoints don't carry the "the agent is
  // talking now" signal — they're durable records of past moments,
  // so they must not flip the typing indicator on or off.
  let lastChatMsg: ChatMessage | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (!m) continue;
    if (m.role === 'inline-checkpoint') continue;
    lastChatMsg = m;
    break;
  }
  const hasText = lastChatMsg?.role === 'assistant' && lastChatMsg.content.length > 0;
  return !hasText;
}

/**
 * Decide whether a keydown on the chat textarea should fire `onSend`.
 * Issue #242 inverted the default: plain Enter sends, Shift+Enter
 * inserts a newline. Cmd/Ctrl+Enter is kept as a redundant accelerator
 * so users with muscle memory from the old binding still get a send
 * (and so any docs/screenshots referencing the old combo still work).
 *
 * Critical guard: `e.isComposing` (or `e.keyCode === 229`, the legacy
 * IME marker) means the user is mid-IME composition — e.g. converting
 * Japanese kana → kanji, or picking a Chinese candidate. Enter in that
 * state belongs to the IME, not us; firing send would chop the
 * composition off mid-word. We bail in that case and let the IME
 * handle the keystroke.
 *
 * Pure predicate exported for unit testing — the textarea handler
 * calls through to this so production and tests share one predicate.
 */
export function shouldSendOnEnter(
  e: Pick<
    React.KeyboardEvent<HTMLTextAreaElement>,
    'key' | 'shiftKey' | 'metaKey' | 'ctrlKey' | 'nativeEvent'
  > & { isComposing?: boolean },
): boolean {
  if (e.key !== 'Enter') return false;
  // React 18 surfaces `nativeEvent.isComposing`; some test harnesses
  // pass a synthetic with the flag at the top level. Check both.
  const composing =
    e.isComposing === true ||
    (e.nativeEvent as { isComposing?: boolean } | undefined)?.isComposing === true ||
    (e.nativeEvent as { keyCode?: number } | undefined)?.keyCode === 229;
  if (composing) return false;
  if (e.shiftKey) return false;
  // Plain Enter, or Cmd/Ctrl+Enter (redundant accelerator) → send.
  return true;
}

/**
 * Decide whether the Express-mode toggle should be visible at the
 * current moment.
 *
 * Issue Volley-Inc/hello-weekend#110 follow-up. The original
 * visibility (`messages.length === 0 && !isAiTyping &&
 * !pendingBulkInterview`) only painted the toggle on a completely-
 * empty fresh game. That made it discoverable for first-pitch users
 * but invisible to anyone who had already typed anything (a "hello"
 * test message, an exploratory question) — and invisible after the
 * `/build-game` flow had already started but before Phase B had
 * fired, even though that's exactly when a power user might decide
 * "actually I want to bulk-answer everything".
 *
 * The widened predicate keeps the toggle reachable any quiet moment
 * BEFORE Phase B starts:
 *
 *   - `gameId` must be defined (we need a game to start an interview
 *     against).
 *   - `pendingBulkInterview` must be falsy (we're not already in a
 *     bulk-interview round-trip — the user can't speed-run something
 *     they're already speed-running).
 *   - `isAiTyping` must be false (a quiet moment — firing the
 *     express command while a turn is in flight would be queued and
 *     confusing).
 *   - The chat must not yet contain Phase B specialist evidence —
 *     proxied by "no assistant message has fired more than two
 *     bubbles" (the typical Phase A.6 summary turn produces ≤ 2
 *     assistant bubbles before the user opts to dispatch). Once
 *     three or more assistant messages have landed, Phase B is
 *     almost certainly running and an express toggle would do nothing
 *     useful.
 *
 * The "≤ 2 assistant bubbles" heuristic is intentionally loose — a
 * false positive (toggle visible after Phase B started) is harmless
 * because clicking it just types `/build-game` and fires `user/send`
 * (PR #384 / #385 — the click handler now flips the renderer's
 * `interviewViewMode` to `'stacked'` and dispatches the plain
 * kickoff; the legacy `--express` flag is gone — view mode is a
 * renderer concern, not a SKILL contract). A false negative (toggle
 * hidden mid-Phase A) was the original bug.
 *
 * Pure predicate exported for unit testing — the JSX gate calls
 * through so production + tests share one predicate.
 */
export function shouldShowExpressToggle(args: {
  gameId: string | undefined;
  messages: readonly ChatMessage[];
  isAiTyping: boolean;
  pendingBulkInterview: { sessionId: string } | null;
  chatInput: string;
  pendingAttachments: readonly AgentSendAttachment[];
}): boolean {
  if (!args.gameId) return false;
  if (args.pendingBulkInterview) return false;
  // Note (#461): the `isAiTyping` clause used to live here and hid the
  // toggle while the SDK turn was open. Under the FSM interview engine
  // (default-on per PR G), `isAiTyping` stays true for ALL of Phase A
  // because the SDK turn is held open by `AskUserQuestion` permission
  // promises — so the button was hidden for every question, even
  // though the user should be able to YOLO at any time. Dropping the
  // gate matches the design intent (toggle reachable at any quiet
  // moment before Phase B) and the remaining guards still cover the
  // legitimate hide-cases (no workspace, in-flight bulk interview,
  // pending draft / attachments).
  // Hide while the user has unsent draft content. Codex P2 (#286):
  // `onStartExpressInterview` dispatches `user/type` with
  // `/build-game` (issue #350: no longer `--express` — the SKILL
  // ignores it and view mode is now a renderer concern) then
  // `user/send`, which would (a) overwrite a typed-but-unsent draft
  // and (b) consume any pasted screenshot attachments by sending
  // them attached to the express command. Hiding the toggle while a
  // draft is pending is the correct UX — the user explicitly chose
  // to compose; don't silently discard it on a stray click.
  if (args.chatInput.trim().length > 0) return false;
  if (args.pendingAttachments.length > 0) return false;
  // Note (#282 Codex P2 round 2 / #461): the original gate counted
  // assistant messages and hid the toggle past 2 bubbles as a coarse
  // Phase B signal. Codex correctly flagged this as a false negative
  // — Phase A itself emits one assistant bubble per question, so a
  // normal 5-question conversational interview crosses the cap
  // mid-flow and the toggle disappears exactly when a power user
  // might want to bail to express. The original PR design called this
  // heuristic "intentionally loose" with the explicit note that a
  // false positive (toggle visible post-Phase-B) is harmless because
  // the SKILL's already-running guard handles a stray
  // `/build-game --express`. Dropping the gate honours that design
  // and removes the false negative. The same class of bug recurred
  // under #461 via the `isAiTyping` clause — see the comment above
  // about the FSM engine holding the SDK turn open for all of Phase
  // A. Both are coarse Phase-A/Phase-B proxies and both got dropped
  // for the same reason. The remaining predicate fires are all real
  // signals (gameId, pendingBulkInterview, draft-pending) — none
  // guess at Phase B/A state.
  void args.messages;
  return true;
}

export function shouldShowFreshChatEmptyState(args: {
  messagesLength: number;
  isAiTyping: boolean;
  hasPinnedAskUserQuestion: boolean;
  hasPendingBulkInterview: boolean;
  isArtifactPaneActive: boolean;
  interviewSlotActive: boolean;
  runStateKind: WorkspaceRunKind;
}): boolean {
  if (args.runStateKind !== 'ready') return false;
  return (
    args.messagesLength === 0 &&
    !args.isAiTyping &&
    !args.hasPinnedAskUserQuestion &&
    !args.hasPendingBulkInterview &&
    (args.isArtifactPaneActive || !args.interviewSlotActive)
  );
}

function FilesPanel({
  gameId,
  collapsed,
  width,
  onToggleCollapse,
  onResize,
}: {
  gameId: string;
  collapsed: boolean;
  width: number;
  onToggleCollapse: () => void;
  onResize: (w: number) => void;
}): React.JSX.Element {
  const effectiveWidth = collapsed ? 40 : width;

  // Real file tree from `game:files` IPC (audit \u00A77 \u2014 the dead Files
  // panel previously rendered the same hard-coded `apps/display`,
  // `apps/controller`, `apps/server` tree for every game). The handler
  // returns a flat list of POSIX-style relative paths; we reshape into
  // a nested tree for the existing renderer. We refetch on gameId
  // change but skip the IPC entirely while the panel is collapsed.
  const [tree, setTree] = useState<FileNode[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (collapsed) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    window.crucible
      .invoke('game:files', { gameId })
      .then((entries) => {
        if (cancelled) return;
        setTree(buildTreeFromFlat(entries));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [gameId, collapsed]);

  return (
    <aside
      className="relative flex flex-col border-r border-white/[0.11] transition-[width] duration-200"
      style={{ width: effectiveWidth, minWidth: effectiveWidth, background: 'var(--bg-elev-1)' }}
    >
      {/* Toggle button */}
      <button
        onClick={onToggleCollapse}
        className="flex h-10 w-full items-center justify-center text-xs text-white/40 hover:text-white/60"
        title={collapsed ? 'Expand files' : 'Collapse files'}
      >
        {collapsed ? '\u25B6' : '\u25C0'}
      </button>

      {/* File tree */}
      {!collapsed && (
        <div className="flex-1 overflow-auto px-2 pb-3">
          <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-white/40">
            Files
          </h2>
          {loading && tree.length === 0 && (
            <p className="px-1 py-2 text-xs text-white/30">Loading files...</p>
          )}
          {loadError && <p className="px-1 py-2 text-xs text-red-400/70">{loadError}</p>}
          {!loading && !loadError && tree.length === 0 && (
            <p className="px-1 py-2 text-xs text-white/30">No files yet.</p>
          )}
          {tree.length > 0 && <FileTreeNode nodes={tree} depth={0} />}
        </div>
      )}

      {/* Resize handle */}
      {!collapsed && (
        <ResizeHandle
          side="right"
          currentSize={width}
          minSize={180}
          maxSize={400}
          onResize={onResize}
        />
      )}
    </aside>
  );
}

function FileTreeNode({ nodes, depth }: { nodes: FileNode[]; depth: number }): React.JSX.Element {
  return (
    <>
      {nodes.map((node) => (
        <FileTreeItem key={node.path} node={node} depth={depth} />
      ))}
    </>
  );
}

function FileTreeItem({ node, depth }: { node: FileNode; depth: number }): React.JSX.Element {
  const [expanded, setExpanded] = useState(depth < 2);
  const isDir = node.type === 'directory';
  const paddingLeft = 8 + depth * 14;

  return (
    <div>
      <button
        onClick={() => isDir && setExpanded(!expanded)}
        className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-xs hover:bg-white/5"
        style={{ paddingLeft }}
      >
        {isDir ? (
          <span className="text-white/30">{expanded ? '\u25BE' : '\u25B8'}</span>
        ) : (
          <span className="w-3" />
        )}
        <span className={isDir ? 'text-white/60' : 'text-white/40'}>{node.name}</span>
      </button>
      {isDir && expanded && node.children && (
        <FileTreeNode nodes={node.children} depth={depth + 1} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Chat Panel (centre, primary)
// ---------------------------------------------------------------------------

/**
 * Phase 3 visibility strip — turn count + (optional) USD spend for
 * the last completed turn. Renders inline next to the AI Chat title.
 * Hidden when `spend === null` so a fresh chat / mid-turn state shows
 * nothing rather than a stale value from a previous turn.
 *
 * Cost is gated behind `showCost` (mirror of `coreConfig.showCostInChat`,
 * default false). Turn count is always shown when present — it's
 * activity, not money, and gives the user feedback that the agent
 * actually did something even when they don't care about cost.
 */
function VisibilityStrip({
  spend,
  showCost,
}: {
  spend: { totalCostUsd?: number; numTurns?: number } | null;
  showCost: boolean;
}): React.JSX.Element | null {
  if (!spend) return null;
  const turnsLabel =
    typeof spend.numTurns === 'number'
      ? `${spend.numTurns} turn${spend.numTurns === 1 ? '' : 's'}`
      : null;
  const costLabel =
    showCost && typeof spend.totalCostUsd === 'number'
      ? `$${spend.totalCostUsd.toFixed(spend.totalCostUsd >= 1 ? 2 : 3)}`
      : null;
  if (!turnsLabel && !costLabel) return null;
  return (
    <div
      className="flex items-center gap-2 text-[11px] tabular-nums text-white/40"
      data-testid="chat-visibility-strip"
    >
      {turnsLabel && (
        <span
          data-testid="chat-visibility-turns"
          title="Agent loop turns the SDK ran on the last message"
        >
          {turnsLabel}
        </span>
      )}
      {turnsLabel && costLabel && <span className="text-white/20">·</span>}
      {costLabel && (
        <span
          data-testid="chat-visibility-cost"
          title="Total USD spend reported by the SDK for the last turn"
        >
          {costLabel}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline-editable game title (new UI only)
// ---------------------------------------------------------------------------

// Game lifecycle badge styling — mirrors the Dashboard's STATUS_PILL so the
// editor's project panel reads the same as the dashboard cards.
const GAME_STATUS_PILL: Record<GameStatusBadge, { label: string; dot: string; text: string }> = {
  draft: { label: 'Draft', dot: 'bg-white/40', text: 'text-white/60' },
  building: { label: 'Building', dot: 'bg-[#2145F7]', text: 'text-[#4d6ef9]' },
  prototype: { label: 'Prototype', dot: 'bg-[#FFDA0A]', text: 'text-[#FFDA0A]' },
  live: { label: 'Live', dot: 'bg-[#588F3D]', text: 'text-[#7ab856]' },
  error: { label: 'Error', dot: 'bg-[#F72149]', text: 'text-[#f9506f]' },
};

/**
 * Game tile image section (behind the `tileImageUpload` flag). Shows the
 * current tile, with hover/drag-and-drop/click to upload a replacement, plus a
 * caption nudging the user to chat with the agent to generate one.
 */
function GameTileImageField({ gameId }: { gameId: string }): React.JSX.Element {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [overrideUrl, setOverrideUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const { data: tile } = useIpcQuery('game:get-tile-image', { gameId });
  const imageUrl = overrideUrl ?? tile?.assetUrl ?? null;

  const handleFile = useCallback(
    async (file: File | undefined | null): Promise<void> => {
      if (!file) return;
      if (uploading) return;
      if (!isSupportedTileUploadFile(file)) {
        setUploadError('That file is not supported - pick a PNG, JPG, or WebP.');
        return;
      }
      setUploading(true);
      setUploadError(null);
      try {
        const upload = await prepareTileImageUpload(file);
        const result = await window.crucible.invoke('game:set-tile-image', {
          gameId,
          ...upload,
        });
        // Cache-bust so the <img> reloads the freshly-written file.
        setOverrideUrl(`${result.assetUrl}?v=${Date.now()}`);
      } catch (err) {
        // Peel off Electron's wire-format `[CRUCIBLE_ERR_V1]` envelope so the
        // console logs the real message (not the encoded payload) and the
        // user gets a readable, non-silent failure.
        const decoded = unflattenIpcError(err);
        console.error('[tile-image] upload failed', decoded);
        setUploadError(decoded.message || 'Upload failed - please try again.');
      } finally {
        setUploading(false);
      }
    },
    [gameId, uploading],
  );

  return (
    <div className="pb-3">
      <p
        className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em]"
        style={{ color: 'var(--text-3)' }}
      >
        Game tile image
      </p>
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFile(e.dataTransfer.files?.[0]);
        }}
        data-testid="game-tile-image-dropzone"
        className={`group relative flex aspect-video w-full cursor-pointer items-center justify-center overflow-hidden rounded-lg border bg-white/[0.04] transition-colors ${
          dragOver ? 'border-crucible-accent' : 'border-white/10 hover:border-white/20'
        }`}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Game tile"
            className="h-full w-full object-cover"
            draggable={false}
          />
        ) : (
          <span className="text-[11px]" style={{ color: 'var(--text-3)' }}>
            No tile image yet
          </span>
        )}
        <div
          className={`absolute inset-0 flex items-center justify-center bg-black/55 text-[12px] font-medium text-white/90 transition-opacity ${
            dragOver || uploading ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          }`}
        >
          {uploading ? 'Uploading…' : dragOver ? 'Drop to upload' : imageUrl ? 'Replace' : 'Upload'}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
          className="hidden"
          onChange={(e) => {
            void handleFile(e.currentTarget.files?.[0]);
            e.currentTarget.value = '';
          }}
        />
      </div>
      <p className="mt-1.5 text-[11px] leading-snug" style={{ color: 'var(--text-3)' }}>
        Upload an image, or ask the agent in chat to generate a new game tile image.
      </p>
      {uploadError && (
        <p
          role="alert"
          data-testid="game-tile-image-error"
          className="mt-1 text-[11px] leading-snug text-[#f9506f]"
        >
          {uploadError}
        </p>
      )}
    </div>
  );
}

/**
 * Project settings popover — opened by clicking the game title in the editor
 * top bar. Holds the editable title, the immutable project name + GitHub repo
 * link, version, the lifecycle status, and the Weekend-app toggle. Dismisses
 * on Escape, on a click outside (excluding the title trigger), or by clicking
 * the title again (handled by the trigger's own toggle).
 */
function ProjectSettingsPanel({
  gameId,
  gameName,
  statusBadge,
  version,
  repoUrl,
  repoFullName,
  inAppPairingAvailable,
  inAppPairingNextLaunch,
  onInAppPairingNextLaunchChange,
  tileImageEnabled,
  triggerRef,
  onClose,
}: {
  gameId: string;
  gameName: string;
  statusBadge: GameStatusBadge;
  version?: string;
  repoUrl?: string;
  repoFullName?: string;
  inAppPairingAvailable: boolean;
  inAppPairingNextLaunch: boolean;
  onInAppPairingNextLaunchChange?: (value: boolean) => void;
  tileImageEnabled?: boolean;
  triggerRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
}): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement>(null);
  // The header's leading cell is `overflow-hidden`, so an absolutely-positioned
  // popover would be clipped. Render it `fixed`, anchored to the trigger's rect
  // (no transformed ancestor, so fixed escapes the clip).
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useLayoutEffect(() => {
    const update = (): void => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (r) setPos({ left: r.left, top: r.bottom + 8 });
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [triggerRef]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    const onDown = (e: MouseEvent): void => {
      const target = e.target as Node;
      // Ignore clicks on the title trigger — it toggles the panel itself.
      if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [onClose, triggerRef]);

  const pill = GAME_STATUS_PILL[statusBadge];
  // Each section is divided by a hairline rule (first has no top border).
  const Field = ({
    label,
    first,
    children,
  }: {
    label: string;
    first?: boolean;
    children: React.ReactNode;
  }): React.JSX.Element => (
    <div className={first ? 'pb-3' : 'border-t border-white/[0.08] py-3'}>
      <p
        className="mb-1 text-[10px] font-medium uppercase tracking-[0.08em]"
        style={{ color: 'var(--text-3)' }}
      >
        {label}
      </p>
      {children}
    </div>
  );

  return (
    <div
      ref={panelRef}
      data-testid="project-settings-panel"
      role="dialog"
      aria-label="Project settings"
      className="app-region-no-drag fixed z-50 w-80 rounded-xl border border-white/10 p-4"
      style={{
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
        background: 'var(--surface-card)',
        boxShadow: 'var(--shadow-modal)',
      }}
    >
      {tileImageEnabled && <GameTileImageField gameId={gameId} />}

      <Field label="Game title" first={!tileImageEnabled}>
        <EditableGameTitle gameId={gameId} gameName={gameName} />
      </Field>

      <Field label="Project name">
        <div className="flex items-center justify-between gap-2">
          <span
            className="min-w-0 truncate font-mono text-[13px]"
            style={{ color: 'var(--text-2)' }}
            title={gameId}
          >
            {gameId}
          </span>
          {repoUrl && (
            <button
              type="button"
              onClick={() => window.open(repoUrl, '_blank', 'noopener,noreferrer')}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/[0.12] bg-white/[0.06] px-2.5 py-1 text-[11px] font-medium text-white/72 transition-colors hover:border-white/[0.24] hover:text-white"
              aria-label={`Open ${repoFullName ?? 'the repository'} on GitHub`}
              title="Open the GitHub repo in your browser"
            >
              <Github size={12} strokeWidth={2} />
              Repo
            </button>
          )}
        </div>
        {version && (
          <p className="mt-1 text-[11px]" style={{ color: 'var(--text-3)' }}>
            v{version}
          </p>
        )}
      </Field>

      <Field label="Status">
        <span className="inline-flex items-center gap-1.5 text-[13px]">
          <span className={`h-2 w-2 shrink-0 rounded-full ${pill.dot}`} aria-hidden />
          <span className={pill.text}>{pill.label}</span>
        </span>
      </Field>

      {inAppPairingAvailable && (
        <label className="flex cursor-pointer items-center gap-2 border-t border-white/[0.08] pt-3 text-[12px] text-white/70">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 accent-sky-400"
            checked={inAppPairingNextLaunch}
            onChange={(event) => onInAppPairingNextLaunchChange?.(event.currentTarget.checked)}
          />
          <span>Weekend app (next Preview launch)</span>
        </label>
      )}
    </div>
  );
}

function EditableGameTitle({
  gameId,
  gameName,
}: {
  gameId: string;
  gameName: string;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [renameError, setRenameError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = (): void => {
    setDraft(gameName);
    setRenameError(null);
    setEditing(true);
  };

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = async (): Promise<void> => {
    const trimmed = draft.trim();
    setEditing(false);
    if (!trimmed || trimmed === gameName) return;
    try {
      await window.crucible.invoke('game:set-display-name', { gameId, displayName: trimmed });
      setRenameError(null);
      void queryClient.invalidateQueries({ queryKey: ['game:status', gameId] });
      void queryClient.invalidateQueries({ queryKey: ['game:list:lite'] });
    } catch (err) {
      const decoded = unflattenIpcError(err);
      setRenameError(decoded.message ?? 'Rename failed');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit();
    }
    if (e.key === 'Escape') {
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          void commit();
        }}
        onKeyDown={handleKeyDown}
        className="app-region-no-drag f-title w-48 rounded bg-white/[0.06] px-1 outline-none ring-1 ring-white/20 focus:ring-crucible-accent"
        style={{ color: 'var(--text-1)' }}
        data-testid="game-title-input"
      />
    );
  }

  return (
    <div className="flex min-w-0 flex-col">
      <button
        type="button"
        onClick={startEdit}
        title="Click to rename"
        className="app-region-no-drag f-title group flex min-w-0 max-w-full items-center gap-1.5 rounded px-1 transition-colors hover:bg-white/[0.06]"
        style={{ color: 'var(--text-1)' }}
        data-testid="game-title-edit-btn"
      >
        <span className="min-w-0 truncate">{gameName}</span>
        <Pencil
          size={11}
          strokeWidth={1.75}
          // Always visible (not hover-only) so it's clear the title is editable.
          className="shrink-0 opacity-40 transition-opacity group-hover:opacity-70"
          aria-hidden
        />
      </button>
      {renameError && (
        <span className="px-1 text-[11px] text-red-400" data-testid="game-title-rename-error">
          {renameError}
        </span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Game Plan view — shown in the ArtifactPane once the interview is complete.
// Renders a structured design-spec document derived from the interview answers.
// ─────────────────────────────────────────────────────────────────────────────

function GamePlanSection({ heading, body }: { heading: string; body: string }): React.JSX.Element {
  return (
    <div>
      <p
        className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em]"
        style={{ color: 'var(--text-3)' }}
      >
        {heading}
      </p>
      <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-1)' }}>
        {body}
      </p>
    </div>
  );
}

function GamePlanView({
  gameName,
  answeredCount,
  totalCount,
  plan,
}: {
  gameName: string;
  answeredCount: number;
  totalCount: number;
  /** Markdown plan written by the SKILL after quick-batch discovery.
   *  Null for the legacy conversational flow which produces no plan doc. */
  plan: string | null;
}): React.JSX.Element {
  return (
    <div className="space-y-5">
      <div>
        <p className="text-[10px] uppercase tracking-[0.12em]" style={{ color: 'var(--text-3)' }}>
          Game Design Spec
        </p>
        <h2 className="mt-1 text-base font-medium" style={{ color: 'var(--text-1)' }}>
          {gameName}
        </h2>
      </div>
      <div style={{ height: '1px', background: 'var(--border-alpha-1)' }} />
      {plan ? (
        <div className="text-[13px] leading-relaxed" style={{ color: 'var(--text-1)' }}>
          <MessageContent content={plan} />
        </div>
      ) : (
        <p className="text-[13px] leading-relaxed" style={{ color: 'var(--text-2)' }}>
          Discovery complete. The full game design spec will be visible here once the plan is
          drafted.
        </p>
      )}
      <div style={{ height: '1px', background: 'var(--border-alpha-1)' }} />
      <p className="text-[11px]" style={{ color: 'var(--text-3)' }}>
        Discovery complete — {answeredCount}/{totalCount} questions answered
      </p>
    </div>
  );
}

/**
 * Derives the compact status row displayed above the chat composer.
 *
 * Args:
 *   runState: Durable Workspace-level run state for the active game.
 *   isAiTyping: Whether the active chat turn is still streaming.
 *   activitySnapshot: Latest agent activity snapshot, if one is available.
 *   phaseProgress: Latest derived build phase progress, if one is available.
 *
 * Returns:
 *   The status row label/tone, or null when no row should be shown.
 *
 * Raises:
 *   None.
 */
export function deriveChatStatusRow(
  runState: WorkspaceRunState,
  isAiTyping: boolean,
  activitySnapshot: ActivitySnapshot | null,
  phaseProgress: PhaseProgressUpdate | null | undefined,
): { label: string; tone: 'active' | 'waiting' | 'warning' | 'idle' } | null {
  if (runState.kind === 'waiting-for-plan-review') {
    return { label: 'Review Build Plan...', tone: 'waiting' };
  }
  if (runState.kind === 'drafting-plan') {
    if (runState.debugSource.startsWith('auto-build:')) {
      return { label: 'Preparing build...', tone: 'active' };
    }
    return { label: 'Preparing Build Plan...', tone: 'active' };
  }
  if (runState.kind === 'waiting-on-user') {
    return { label: 'Waiting on your input...', tone: 'waiting' };
  }
  if (runState.kind === 'needs-attention') {
    return { label: 'Needs attention', tone: 'warning' };
  }
  if (
    runState.kind === 'building' &&
    phaseProgress?.kind === 'derived' &&
    !isDerivedProgressComplete(phaseProgress) &&
    !hasFreshRunningActivitySnapshot(activitySnapshot) &&
    !isAiTyping
  ) {
    return { label: 'Build running - no live agents', tone: 'waiting' };
  }
  if (
    isAiTyping ||
    runState.kind === 'starting-interview' ||
    runState.kind === 'interview-in-progress' ||
    runState.kind === 'build-kickoff-pending' ||
    runState.kind === 'building'
  ) {
    return { label: 'Working...', tone: 'active' };
  }
  return null;
}

function ChatProgressEmptyState({ runState }: { runState: WorkspaceRunState }): React.JSX.Element {
  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-2 text-center"
      data-testid="chat-progress-empty-state"
    >
      <p className="text-sm font-medium text-white/70">{runState.label}</p>
      {runState.detail && (
        <p className="max-w-md text-xs leading-relaxed text-white/35">{runState.detail}</p>
      )}
    </div>
  );
}

function autoBuildStatusFromRunState(runState: WorkspaceRunState): string | null {
  if (!runState.debugSource.startsWith('auto-build:')) return null;
  return runState.debugSource.split(':')[2] ?? null;
}

function AutoBuildProgressCard({
  runState,
  onCancel,
  onRetry,
}: {
  runState: WorkspaceRunState;
  onCancel: () => void;
  onRetry: () => void;
}): React.JSX.Element {
  const status = autoBuildStatusFromRunState(runState);
  const canCancel =
    status === 'armed' || status === 'generating-docs' || status === 'exporting-package';
  const canRetry = runState.kind === 'needs-attention' && runState.primaryCta === 'retry';
  return (
    <div
      className="mb-4 rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3"
      data-testid="auto-build-progress-card"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-white/75">{runState.label}</p>
          {runState.detail && (
            <p className="mt-1 text-xs leading-relaxed text-white/40">{runState.detail}</p>
          )}
        </div>
        <span
          className={`mt-1 size-2 shrink-0 rounded-full ${
            runState.tone === 'error' || runState.tone === 'warning'
              ? 'bg-amber-300'
              : 'animate-pulse bg-crucible-accent'
          }`}
          aria-hidden="true"
        />
      </div>
      {(canCancel || canRetry) && (
        <div className="mt-3 flex justify-end gap-2">
          {canCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-white/10 px-2.5 py-1.5 text-xs text-white/65 hover:border-white/20 hover:bg-white/[0.05]"
            >
              Cancel
            </button>
          )}
          {canRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md bg-crucible-accent px-2.5 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              Retry setup
            </button>
          )}
        </div>
      )}
    </div>
  );
}

type ChatTabId = 'chat' | 'state' | 'diagnostic';

function ChatTabBar({
  active,
  onChange,
}: {
  active: ChatTabId;
  onChange: (tab: ChatTabId) => void;
}): React.JSX.Element {
  const tabs: Array<{ id: ChatTabId; label: string; Icon: typeof MessageSquareText }> = [
    { id: 'chat', label: 'Chat', Icon: MessageSquareText },
    { id: 'state', label: 'State', Icon: ListChecks },
    { id: 'diagnostic', label: 'Diagnostic', Icon: Stethoscope },
  ];
  return (
    <div
      role="tablist"
      aria-label="Chat panel"
      className="flex shrink-0 items-center gap-1 border-b border-white/10 px-3 pt-2"
    >
      {tabs.map(({ id, label, Icon }) => {
        const isActive = active === id;
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-testid={`chat-tab-${id}`}
            onClick={() => onChange(id)}
            className={`relative flex items-center gap-1.5 rounded-t-md px-3 py-1.5 text-[12px] font-medium transition-colors ${
              isActive ? 'text-crucible-accent' : 'text-white/45 hover:text-white/75'
            }`}
          >
            <Icon size={13} strokeWidth={1.75} />
            {label}
            {isActive && (
              <span className="brand-gradient-fill absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-crucible-accent" />
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface WorkspaceChatSurfaceProps {
  flagsLoaded: boolean;
  newUi: boolean;
  livePreview: boolean;
  chatPanel: React.ReactNode;
  chatTab: ChatTabId;
  onChatTabChange: (tab: ChatTabId) => void;
  statePanel: React.ReactNode;
  diagnosticsPanel: React.ReactNode;
  previewSurface: React.ReactNode;
  livePreviewSplitActive: boolean;
  livePreviewChatWidth: number;
  onLivePreviewChatWidthChange: (width: number) => void;
}

/**
 * Renders the Workspace chat tree under a stable live-preview root.
 *
 * Args:
 *   props: Feature flags, chat tab state, and the panels that make up the chat
 *     surface.
 *
 * Returns:
 *   A loading placeholder, the bare chat panel, or the live-preview split shell.
 *
 * Raises:
 *   None.
 */
export function WorkspaceChatSurface(props: WorkspaceChatSurfaceProps): React.JSX.Element {
  if (!props.flagsLoaded) {
    // Do not mount ChatPanel until flags resolve; otherwise live-preview ON
    // swaps the root from bare chat to SplitLayout and remounts the stream.
    return (
      <section
        className="flex flex-1 items-center justify-center overflow-hidden"
        aria-busy="true"
        aria-label="Loading Build panel"
        data-testid="workspace-chat-feature-flags-loading"
      />
    );
  }

  if (!props.newUi) {
    // Legacy UI still renders ChatPanel unwrapped for pre-split DOM parity.
    return <>{props.chatPanel}</>;
  }

  const previewActive = props.livePreview && props.livePreviewSplitActive;

  // New UI: ChatPanel lives permanently in SplitLayout's secondary slot.
  return (
    <SplitLayout
      secondary={
        <div className="flex h-full min-h-0 flex-col overflow-hidden">
          <ChatTabBar active={props.chatTab} onChange={props.onChatTabChange} />
          <div className={props.chatTab === 'chat' ? 'flex min-h-0 flex-1 flex-col' : 'hidden'}>
            {props.chatPanel}
          </div>
          {props.chatTab === 'state' && (
            <div className="min-h-0 flex-1 overflow-hidden">{props.statePanel}</div>
          )}
          {props.chatTab === 'diagnostic' && (
            <div className="min-h-0 flex-1 overflow-hidden">{props.diagnosticsPanel}</div>
          )}
        </div>
      }
      primary={previewActive ? props.previewSurface : null}
      secondaryWidth={props.livePreviewChatWidth}
      onSecondaryWidthChange={props.onLivePreviewChatWidthChange}
      dividerLabel="Resize chat and preview"
      data-testid={previewActive ? 'live-preview-split' : undefined}
    />
  );
}

function ChatPanel({
  gameId,
  gameName,
  messages,
  chatInput,
  isAiTyping,
  runState,
  activitySnapshot,
  phaseProgress,
  onInputChange,
  onSend,
  onStop,
  queuedMessages,
  onUnqueueMessage,
  persistError,
  onDismissPersistError,
  hydrationStatus,
  hydrationError,
  hydrationLongRunning,
  onRetryHydration,
  pendingAttachments,
  attachmentPop,
  onAddAttachment,
  onRemoveAttachment,
  attachmentError,
  onDismissAttachmentError,
  lastTurnSpend,
  showCostInChat,
  onAnswerMarkdownQuestion,
  onRetryMessage,
  onCancelAutoBuild,
  onRetryAutoBuild,
  pinnedAskUserQuestion,
  pendingPhaseBDecision,
  pendingBulkInterview,
  onBulkInterviewSubmit,
  onBulkInterviewCancel,
  onStartExpressInterview,
  interviewViewMode,
  interviewSlotActive,
  onInterviewRendererReady,
  isArtifactPaneActive,
  showReviewModal,
  onCloseReviewModal,
  compact = false,
}: {
  gameId: string | undefined;
  gameName: string;
  messages: ChatMessage[];
  chatInput: string;
  isAiTyping: boolean;
  runState: WorkspaceRunState;
  activitySnapshot: ActivitySnapshot | null;
  phaseProgress: PhaseProgressUpdate | null | undefined;
  onInputChange: (v: string) => void;
  onSend: () => void;
  /**
   * Issue #140: invoked when the user clicks the Stop button while
   * the agent is mid-turn. The handler IPCs `agent:interrupt`; the
   * adapter emits an `interrupted` chunk back on `agent:stream`,
   * which is what actually clears the typing indicator and finalises
   * the assistant message bubble. The button itself only triggers
   * the request — it does NOT preemptively flip `isAiTyping`.
   */
  onStop: () => void;
  /**
   * Issue #164 follow-up: messages the user typed while the agent
   * was still mid-turn, queued in render order. Drained
   * automatically by the parent Workspace's effect when
   * `isAiTyping` flips false. Surfaced here as removable chips
   * above the input so the user can SEE what's pending and pull a
   * message back out before it fires.
   */
  queuedMessages: string[];
  /**
   * Remove a queued message by index. Used by the per-chip "x"
   * button so the user can change their mind before the queue
   * drains. The chip's index is stable across renders because new
   * messages are appended (push, not unshift).
   */
  onUnqueueMessage: (index: number) => void;
  persistError: SerialisedError | null;
  onDismissPersistError: () => void;
  /**
   * GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — honest hydration state.
   * `'loading'` renders a loading affordance (or the "preparing your
   * workspace" variant when `hydrationLongRunning`), `'error'` with a
   * retryable `hydrationError` renders a "Try again" error state, and
   * `'ready'` renders the normal timeline / fresh-empty state. A
   * non-retryable error (corrupt-history, category 'state') is left to the
   * existing `persistError` banner — `onRetryHydration` is not offered.
   */
  hydrationStatus: 'loading' | 'ready' | 'error';
  hydrationError: SerialisedError | null;
  hydrationLongRunning: boolean;
  onRetryHydration: () => void;
  /**
   * Screenshot-paste MVP (#193). Images the user has pasted into the
   * input but not yet sent. Rendered as removable chips above the
   * textarea; consumed by the next `onSend`.
   */
  pendingAttachments: readonly AgentSendAttachment[];
  /** Briefly true after an in-editor select-to-chat add — pops the chips row. */
  attachmentPop?: boolean;
  onAddAttachment: (a: AgentSendAttachment) => void;
  onRemoveAttachment: (index: number) => void;
  /** Last attachment-cap rejection message; null when no error. */
  attachmentError: string | null;
  onDismissAttachmentError: () => void;
  /**
   * Phase 3 visibility strip — last completed turn's SDK-reported
   * cost / turn count. `null` while a turn is in flight or if the
   * SDK didn't include the fields. Cost is rendered only when
   * `showCostInChat` is enabled — turn count is always shown.
   */
  lastTurnSpend: { totalCostUsd?: number; numTurns?: number } | null;
  /** Mirror of `coreConfig.showCostInChat`. Off by default. */
  showCostInChat: boolean;
  /**
   * Issue #215 — fallback parser. Invoked when the user clicks one
   * of the synthetic option buttons rendered below an assistant
   * bubble whose markdown matched the `Question N of M:` drift
   * shape. The handler dispatches the option text through the same
   * `user/type` + `user/send` path the textarea uses.
   */
  onAnswerMarkdownQuestion: (optionText: string) => void;
  /**
   * Issue #369 — chat-bubble retry handler. Invoked when the user
   * clicks the "Try again" button on an error bubble whose
   * `retryable: true` flag was set by the structured error from main
   * (PR #368). The bubble passes its `ChatMessage` so the parent's
   * reducer dispatch can locate the preceding user input and re-fire
   * the send.
   */
  onRetryMessage: (message: ChatMessage) => void;
  onCancelAutoBuild: () => void;
  onRetryAutoBuild: () => void;
  pinnedAskUserQuestion: ToolCallEntry | null;
  pendingPhaseBDecision: PhaseBDecision | null;
  /**
   * #86 — bulk-interview slice from the chat reducer. When non-null,
   * the chat surface routes to `<BulkInterviewCard>` in place of the
   * regular bubble timeline. The card collects answers locally and
   * calls `onBulkInterviewSubmit` (or `onBulkInterviewCancel`) when
   * the user is done.
   */
  pendingBulkInterview: {
    readonly sessionId: string;
    readonly questions: readonly BulkInterviewQuestion[];
  } | null;
  onBulkInterviewSubmit: (sessionId: string, answers: Record<string, string>) => void;
  onBulkInterviewCancel: (sessionId: string, partialAnswers: Record<string, string>) => void;
  /**
   * #86 + #350 — clicking the in-input speed-run toggle. Re-wired
   * for the FSM slot-card engine: the handler flips the workspace's
   * `interviewViewMode` to `'stacked'` (so the overlay paints with
   * the express layout from the very first push) and types
   * `/build-game` into the input + fires Send so the engine kicks
   * off through the same code path the user would hit by typing
   * `/build-game` themselves. The `--express` flag is gone — view
   * mode is now a renderer-only concern, not a SKILL contract.
   * Visible per `shouldShowExpressToggle` for the persistent icon
   * + per the empty-state predicate for the prominent first-pitch
   * button.
   */
  onStartExpressInterview: () => void;
  /**
   * Issue #350 — initial view mode for the FSM interview overlay.
   * `paginated` is the default one-card-at-a-time UX; `stacked`
   * surfaces the answered-history pane above the active slot card,
   * matching v0.13's express-mode visual family. Set to `stacked` by
   * the empty-state speed-run button + persistent composer icon
   * toggle through `onStartExpressInterview`. Threaded through to
   * `<InterviewSlotOverlay>` as `initialViewMode`.
   */
  interviewViewMode: 'paginated' | 'stacked';
  interviewSlotActive: boolean;
  onInterviewRendererReady: () => void;
  /** When true, the artifact pane owns BulkInterviewCard + InterviewSlotOverlay rendering. */
  isArtifactPaneActive: boolean;
  showReviewModal: boolean;
  onCloseReviewModal: () => void;
  /**
   * Develop side-by-side mode: the chat is in a narrow panel, so the
   * message column + composer drop their wide centred `max-w-3xl` and use
   * the full panel width with tighter horizontal padding.
   */
  compact?: boolean;
}): React.JSX.Element {
  const { newUi } = useFeatureFlags();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // The first auto-scroll (opening a game with an existing log) jumps instantly
  // — animating a long log from top to bottom on open is jarring. Live updates
  // after that animate smoothly.
  const didInitialScrollRef = useRef(false);
  // Scroll-affordance fades: show a top fade when there's log above the view
  // and a bottom fade when there's more below, so it's clear the log scrolls.
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const [chatScrollEdges, setChatScrollEdges] = useState({ top: false, bottom: false });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // showReviewModal / onCloseReviewModal are lifted to the parent Workspace
  // so WorkspaceRightPanel's onEditDiscovery can trigger the modal from
  // outside ChatPanel's scope.
  // Tracks whether the textarea content has wrapped beyond one line.
  // When true the input renders as an expanding rectangle with a bottom
  // toolbar; when false it stays as the compact pill layout.
  const [isMultiLine, setIsMultiLine] = useState(false);
  const [answeredPhaseBDecisionId, setAnsweredPhaseBDecisionId] = useState<string | null>(null);
  // Baseline scrollHeight for a single line of text — measured the first
  // time the textarea is empty so we can detect wrapping reliably.
  const singleLineHeightRef = useRef<number>(0);
  // The composer corner radius is pinned to HALF the single-line field height
  // so the expanded (multi-line) box keeps the exact same corner curvature as
  // the original single-line pill instead of re-rounding to a taller capsule
  // as it grows. We measure the single-line wrapper (font/zoom-proof) and
  // reuse the value as a fixed radius on the expanded box.
  const composerWrapRef = useRef<HTMLDivElement>(null);
  const [pillRadius, setPillRadius] = useState<number | null>(null);

  useEffect(() => {
    // React Router reuses ChatPanel across games; each game gets one instant hydration scroll.
    didInitialScrollRef.current = false;
  }, [gameId]);

  // Auto-scroll to bottom when new messages or interview prompts arrive.
  useEffect(() => {
    // The initial population — opening a game whose log loads async (often
    // []→[full log] in one update) — must NOT animate. Stay 'instant' until
    // the first render that actually has content; only live updates after that
    // animate smoothly. (Flipping on every run would let the async log load —
    // the 2nd run — animate the long scroll, which is what users were seeing.)
    const hasContent =
      messages.length > 0 ||
      isAiTyping ||
      !!pendingBulkInterview ||
      !!pinnedAskUserQuestion ||
      !!pendingPhaseBDecision ||
      interviewSlotActive;
    const behavior: ScrollBehavior = didInitialScrollRef.current ? 'smooth' : 'instant';
    if (hasContent) didInitialScrollRef.current = true;
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, [
    messages,
    isAiTyping,
    pendingBulkInterview,
    pinnedAskUserQuestion,
    pendingPhaseBDecision,
    interviewSlotActive,
    runState.debugSource,
    runState.detail,
  ]);

  // Recompute the scroll-fade affordances (content above / below the view).
  const updateChatScrollEdges = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const top = el.scrollTop > 4;
    const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 4;
    setChatScrollEdges((prev) =>
      prev.top === top && prev.bottom === bottom ? prev : { top, bottom },
    );
  }, []);
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    updateChatScrollEdges();
    el.addEventListener('scroll', updateChatScrollEdges, { passive: true });
    const ro =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateChatScrollEdges) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateChatScrollEdges);
      ro?.disconnect();
    };
  }, [updateChatScrollEdges]);
  // Content changes (new messages, interview prompts) change scrollHeight, so
  // re-evaluate the fades alongside the auto-scroll.
  useEffect(() => {
    updateChatScrollEdges();
  }, [
    updateChatScrollEdges,
    messages,
    isAiTyping,
    pendingBulkInterview,
    pinnedAskUserQuestion,
    pendingPhaseBDecision,
    interviewSlotActive,
  ]);

  useEffect(() => {
    if (!pendingPhaseBDecision || pendingPhaseBDecision.intentId !== answeredPhaseBDecisionId) {
      setAnsweredPhaseBDecisionId(null);
    }
  }, [answeredPhaseBDecisionId, pendingPhaseBDecision]);

  // Auto-resize textarea + detect multi-line wrapping.
  // useLayoutEffect (not useEffect) so the height is recomputed BEFORE the
  // browser paints. With a post-paint useEffect, the keystroke that adds a new
  // row painted once at the OLD height (textarea overflowing / a scrollbar
  // flashing) and then snapped to the taller box on the next frame — the jump
  // seen when text fills the 3rd line. Measuring + resizing pre-paint makes the
  // box grow by exactly one row in a single frame.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = 'auto';
      const scrollH = el.scrollHeight;
      // Capture single-line baseline whenever the field is empty
      if (chatInput === '') {
        singleLineHeightRef.current = scrollH;
        setIsMultiLine(false);
      } else if (singleLineHeightRef.current > 0 && scrollH > singleLineHeightRef.current) {
        // Latch ON once the content wraps — and intentionally do NOT switch
        // back to the single-line pill while the field still has text. The
        // pill and the expanded rectangle give the textarea different widths,
        // so re-measuring after every keystroke near the wrap boundary made
        // the layout flip-flop (pill ↔ rectangle) on each character. Staying
        // expanded until the field is cleared keeps the box stable; the empty
        // branch above resets it to the pill when the input is sent/cleared.
        setIsMultiLine(true);
      }
      el.style.height = `${Math.min(scrollH, 160)}px`;
    }
  }, [chatInput]);

  // When the layout switches (pill ↔ rectangle), the textarea is remounted.
  // Re-focus and re-apply the correct height so the cursor stays and the box
  // is sized properly without requiring the user to click again. Layout effect
  // (pre-paint) so the freshly-mounted textarea never paints once at its
  // default 1-row height before snapping to the measured height.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    // Move cursor to end after remount
    const len = el.value.length;
    el.setSelectionRange(len, len);
    el.focus();
  }, [isMultiLine]);

  // Capture the single-line field height (→ half = the fixed corner radius for
  // the expanded box). Measured only while the single-line pill is mounted; the
  // value is constant for one line, so this settles on mount and re-confirms
  // whenever the field collapses back.
  useLayoutEffect(() => {
    if (isMultiLine) return;
    const el = composerWrapRef.current;
    if (el && el.offsetHeight > 0) setPillRadius(el.offsetHeight / 2);
  }, [isMultiLine]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (shouldSendOnEnter(e)) {
      e.preventDefault();
      onSend();
    }
  };

  /**
   * Screenshot-paste MVP (#193) + auto-resize (#212). Pull image
   * entries out of the paste's `clipboardData.items`, run them
   * through `resizeImageFile` (which decides whether to downscale
   * via `<canvas>` based on dimension / byte-size thresholds, then
   * base64-encodes the result), and dispatch into the reducer. Each
   * image fires its own `onAddAttachment` so the cap-check in the
   * reducer can reject one without losing the others.
   *
   * Issue #212 motivation: a Mac retina screenshot at 2880×1656 weighs
   * ~4-5 MB raw, slips past our 5 MB renderer cap, then Anthropic's
   * vision API rejects it server-side and the agent reports back
   * "I couldn't see it as it was too large" — no actionable next
   * step for the user. Resizing to 1920 px on the longest side
   * before encoding keeps the screenshot useful, dodges the
   * server-side rejection, and saves cost. The resize chain
   * preserves MIME (PNG → PNG, JPEG → JPEG, WebP → WebP) and falls
   * through to the original bytes on any decode failure so the
   * pre-fix behaviour is the worst case.
   *
   * `e.preventDefault()` runs only when at least one image was found —
   * we don't want to block a normal text paste. The async resize step
   * means `preventDefault` has to happen in the synchronous part of
   * the handler; that's fine because the textarea's text-paste branch
   * is a separate code path the browser handles natively.
   */
  // Shared image-file ingest for BOTH paste and drag-drop (#2268). Filters to
  // images, resizes, and dispatches each as an attachment. Fire-and-forget —
  // `resizeImageFile` handles its own decode/encode failures and falls back to
  // the original bytes so the reducer always sees a usable attachment.
  const ingestImageFiles = useCallback(
    (files: File[]): void => {
      for (const file of files) {
        if (!file.type.startsWith('image/')) continue;
        void resizeImageFile(file)
          .then((result) => {
            onAddAttachment({
              mimeType: result.mimeType,
              base64: result.base64,
              byteLength: result.byteLength,
              ...(result.didResize
                ? {
                    resizedFrom: {
                      width: result.originalWidth,
                      height: result.originalHeight,
                      finalWidth: result.finalWidth,
                      finalHeight: result.finalHeight,
                    },
                  }
                : {}),
            });
          })
          .catch((err: unknown) => {
            console.error('[chat] image attach failed', err);
          });
      }
    },
    [onAddAttachment],
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const images: File[] = [];
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it && it.kind === 'file' && it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) images.push(f);
        }
      }
      if (images.length === 0) return;
      e.preventDefault();
      ingestImageFiles(images);
    },
    [ingestImageFiles],
  );

  // #2268 — drag-and-drop image files onto the composer (parity with the +
  // picker and paste). The attachment display + send pipeline is mode-agnostic,
  // so this works for local and cloud previews alike once the cloud transport
  // carries attachments.
  const handleComposerDragOver = useCallback((e: React.DragEvent<HTMLDivElement>): void => {
    // Only claim the drop when files are being dragged — let text / other drags
    // fall through to default handling.
    if (e.dataTransfer?.types?.includes('Files')) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleComposerDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>): void => {
      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;
      // Claim the drop for ANY file payload (matching the dragOver guard) so a
      // non-image / mixed drop can't fall through to the browser default and
      // navigate the webview to the dropped file. Only images are ingested.
      e.preventDefault();
      const images = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (images.length === 0) return;
      ingestImageFiles(images);
    },
    [ingestImageFiles],
  );

  const statusRow = deriveChatStatusRow(runState, isAiTyping, activitySnapshot, phaseProgress);
  // Fresh-game empty state: no messages, no active prompt, and no durable
  // run/progress state. Once phase progress says the game has started,
  // the main pane must not regress to the first-pitch prompt.
  // GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — honest hydration gates.
  // These take precedence over the fresh-empty / progress-empty states while
  // hydration is still pending or has errored, so a slow clone or a transient
  // transport failure reads as "loading" / "couldn't load", not an
  // indistinguishable blank "start a conversation" panel.
  //
  // The LOADING overlay shows only when there is genuinely nothing to display
  // yet: status 'loading' AND no messages AND no in-flight turn. The
  // `messages.length === 0 && !isAiTyping` guard is load-bearing — without it a
  // live/streaming turn (or a hydration that hasn't yet flipped to 'ready', e.g.
  // cookie-mode where `agent:get-status` resolves only once GAP-1 is live) would
  // be hidden behind the spinner, wedging the chat at "Loading…" while text is
  // actually streaming (regressed the cookie-mode `agent.send@1` e2e, PR #2026).
  // NOTE the asymmetry with the error gate below: the retry gate is status-ONLY
  // (no `messages.length` guard) because a drained `activity:checkpoint` row must
  // NOT suppress the "Try again" affordance (Cursor Bugbot, PR #2026); a live
  // turn never coincides with a terminal error, so only the loading gate needs
  // the message/typing guard.
  const showHydrationLoading =
    hydrationStatus === 'loading' && messages.length === 0 && !isAiTyping;
  // Only retryable transport errors get the in-panel "Try again" error state.
  // Corrupt-history (CrucibleError category 'state', retryable:false) is left
  // to the existing `persistError` recovery banner above — unchanged — so we
  // do NOT double up an error UI on it.
  const showHydrationRetryError =
    hydrationStatus === 'error' &&
    hydrationError?.retryable === true &&
    hydrationError.category !== 'state';
  const hydrationStateActive = showHydrationLoading || showHydrationRetryError;

  const isEmpty =
    !hydrationStateActive &&
    shouldShowFreshChatEmptyState({
      messagesLength: messages.length,
      isAiTyping,
      hasPinnedAskUserQuestion: pinnedAskUserQuestion !== null,
      hasPendingBulkInterview: pendingBulkInterview !== null,
      isArtifactPaneActive,
      interviewSlotActive,
      runStateKind: runState.kind,
    });
  const autoBuildProgressActive = autoBuildStatusFromRunState(runState) !== null;
  const showProgressEmptyState =
    newUi &&
    !isEmpty &&
    !hydrationStateActive &&
    messages.length === 0 &&
    runState.kind !== 'ready' &&
    !autoBuildProgressActive &&
    !pendingBulkInterview &&
    !pinnedAskUserQuestion &&
    (isArtifactPaneActive || !interviewSlotActive);
  const showPendingPhaseBDecisionCard =
    newUi && !isArtifactPaneActive && !pinnedAskUserQuestion && pendingPhaseBDecision !== null;
  const handlePhaseBDecisionAnswer = useCallback(
    (label: string) => {
      if (!pendingPhaseBDecision || answeredPhaseBDecisionId === pendingPhaseBDecision.intentId) {
        return;
      }
      setAnsweredPhaseBDecisionId(pendingPhaseBDecision.intentId);
      onAnswerMarkdownQuestion(label);
    },
    [answeredPhaseBDecisionId, onAnswerMarkdownQuestion, pendingPhaseBDecision],
  );

  return (
    <section
      className={`flex flex-1 flex-col overflow-hidden${newUi && isEmpty ? ' justify-center' : ''}`}
      style={{ background: 'var(--color-crucible-bg)' }}
    >
      {/* Crucible #936 — defence-in-depth error boundary. */}
      <ChatErrorBoundary gameId={gameId}>
        {/* Chat header — legacy UI only. New UI removed this bar; the
          game title lives in WorkspaceHeader and preview controls
          moved to the workspace header as well. */}
        {!newUi && (
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-2.5">
            <h2 className="text-sm font-medium text-white/60">Build &mdash; {gameName}</h2>
            <div className="flex items-center gap-3">
              <VisibilityStrip spend={lastTurnSpend} showCost={showCostInChat} />
            </div>
          </div>
        )}
        {persistError && (
          <div className="mx-4 mt-3" data-testid="chat-persist-error">
            <CrucibleErrorState error={persistError} compact />
            <button
              type="button"
              onClick={onDismissPersistError}
              className="mt-1 text-xs text-red-300/70 underline-offset-2 hover:underline"
              data-testid="chat-persist-error-dismiss"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Messages — in new-UI mode, hidden when empty so IPC subscriptions stay active. */}
        <div className={newUi && isEmpty ? 'hidden' : 'relative flex min-h-0 flex-1 flex-col'}>
          {/* Top fade — content scrolled above the view. */}
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-10 bg-gradient-to-b from-[var(--color-crucible-bg)] to-transparent transition-opacity duration-200 ${
              chatScrollEdges.top ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <div ref={chatScrollRef} className="flex min-h-0 flex-1 flex-col overflow-auto">
            <div
              className={
                compact
                  ? 'flex w-full flex-1 flex-col px-4 py-6'
                  : 'mx-auto flex max-w-3xl flex-1 flex-col px-6 py-8'
              }
            >
              {/* GAP-3 (cloud-vm/gui-parity-plan.md Phase 2) — loading
                  affordance. Quick "Loading chat history…" by default; once
                  the load has been pending past the threshold (a cloud
                  first-touch git clone can take ~120s) it upgrades to an
                  explicit "preparing your workspace" message so the blank
                  window reads as provisioning, not a hang. Spinner + label
                  per the AGENTS-PROJECT.md loading-state visual spec. */}
              {showHydrationLoading && (
                <div
                  className="flex h-full flex-col items-center justify-center gap-3"
                  data-testid="chat-hydration-loading"
                  aria-busy="true"
                >
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-white/[0.12] border-t-white/50" />
                  <p className="text-center text-[15px]" style={{ color: 'var(--text-2)' }}>
                    {hydrationLongRunning ? 'Preparing your workspace…' : 'Loading chat history…'}
                  </p>
                  {hydrationLongRunning && (
                    <p
                      className="max-w-md text-center text-[13px]"
                      style={{ color: 'var(--text-3)' }}
                    >
                      First open can take a moment while we set things up.
                    </p>
                  )}
                </div>
              )}

              {/* GAP-3 — retryable hydration error. Corrupt-history
                  (non-retryable, category 'state') is handled by the
                  persistError banner above, so this block only ever shows
                  for transport-class failures the user can retry. */}
              {showHydrationRetryError && (
                <div
                  className="flex h-full flex-col items-center justify-center gap-3"
                  data-testid="chat-hydration-error"
                >
                  <p className="text-center text-[15px]" style={{ color: 'var(--text-2)' }}>
                    Couldn&rsquo;t load chat history
                  </p>
                  <p
                    className="max-w-md text-center text-[13px]"
                    style={{ color: 'var(--text-3)' }}
                  >
                    {hydrationError?.message ?? 'A connection problem interrupted hydration.'}
                  </p>
                  <button
                    type="button"
                    onClick={onRetryHydration}
                    data-testid="chat-hydration-retry"
                    className="mt-1 inline-flex items-center gap-1.5 rounded-full border border-crucible-accent/40 bg-crucible-accent/10 px-3.5 py-1.5 text-xs font-medium text-crucible-accent-hover transition-colors hover:border-crucible-accent/70 hover:bg-crucible-accent/20 hover:text-white"
                  >
                    Try again
                  </button>
                </div>
              )}

              {isEmpty && (
                <div className="flex h-full flex-col items-center justify-center gap-3">
                  <div className="text-4xl text-white/10">&#9993;</div>
                  <p className="max-w-md text-center text-sm text-white/30">
                    Start a conversation with the AI to build your game.
                    <br />
                    <span className="text-white/20">
                      Press Enter to send · Shift+Enter for newline.
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={onStartExpressInterview}
                    data-testid="express-interview-toggle"
                    className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-crucible-accent/40 bg-crucible-accent/10 px-3.5 py-1.5 text-xs font-medium text-crucible-accent-hover transition-colors hover:border-crucible-accent/70 hover:bg-crucible-accent/20 hover:text-white"
                  >
                    <span aria-hidden="true">⚡</span>
                    <span>All questions</span>
                  </button>
                  <p className="max-w-md text-center text-[11px] italic text-white/20">
                    Answer all the build-game questions in one form instead of one at a time.
                  </p>
                </div>
              )}

              {showProgressEmptyState && <ChatProgressEmptyState runState={runState} />}

              {/* GAP-3 — while a hydration loading/error state is showing,
                  suppress the message list + typing indicator. Status is
                  'loading'/'error' only when there is no successfully-loaded
                  timeline; the only rows `messages` can hold here are stray
                  buffered `activity:checkpoint` pushes drained on a failed
                  load, which must not paint beneath the spinner / retry UI
                  (Cursor Bugbot, PR #2026). A successful load flips status to
                  'ready', so the real timeline renders normally. */}
              {!hydrationStateActive &&
                messages.map((msg) => (
                  <ChatBubble
                    key={msg.id}
                    message={msg}
                    onAnswerMarkdownQuestion={onAnswerMarkdownQuestion}
                    onRetry={onRetryMessage}
                    newUi={newUi}
                    pinnedAskUserQuestionId={pinnedAskUserQuestion?.id ?? null}
                  />
                ))}

              {!hydrationStateActive && shouldShowTypingIndicator(messages, isAiTyping) && (
                <div className="mb-3 flex justify-start" data-testid="chat-typing-indicator">
                  <div className="glint-thinking rounded-2xl rounded-bl-sm bg-crucible-surface px-4 py-3 text-sm text-white/70">
                    <TypingIndicator />
                  </div>
                </div>
              )}

              {newUi && autoBuildProgressActive && (
                <AutoBuildProgressCard
                  runState={runState}
                  onCancel={onCancelAutoBuild}
                  onRetry={onRetryAutoBuild}
                />
              )}

              {/* #86 — bulk-interview card is a new prompt in the conversation,
            so render it after the existing bubbles. Rendering it at the
            top made the user scroll away from the latest agent turn right
            when Foundry needed an answer. */}
              {!isArtifactPaneActive && pendingBulkInterview && (
                <div
                  className="mb-4"
                  data-testid="bulk-interview-mount"
                  data-session-id={pendingBulkInterview.sessionId}
                >
                  <BulkInterviewCard
                    sessionId={pendingBulkInterview.sessionId}
                    questions={pendingBulkInterview.questions}
                    onSubmit={(answers) =>
                      onBulkInterviewSubmit(pendingBulkInterview.sessionId, answers)
                    }
                    onCancel={(partial) =>
                      onBulkInterviewCancel(pendingBulkInterview.sessionId, partial)
                    }
                  />
                </div>
              )}

              {/* TDD §6.2 / §17.2 — interview engine v1 overlay.
            Legacy (non-newUi) path: render inside the message stream so
            questions scroll in with the conversation. NewUi path: slot
            card mounts in the input area below (replacing the composer)
            so the sidebar stays visible and the question feels like a
            prompt rather than a chat bubble. */}
              {!newUi && !isArtifactPaneActive && gameId && (
                <div className="mb-4" data-testid="interview-overlay-mount">
                  <InterviewSlotOverlay
                    gameId={gameId}
                    initialViewMode={interviewViewMode}
                    onRendererReady={onInterviewRendererReady}
                  />
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
            {/* end max-w-3xl column */}
          </div>
          {/* Bottom fade — more content below the view. */}
          <div
            aria-hidden
            className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 bg-gradient-to-t from-[var(--color-crucible-bg)] to-transparent transition-opacity duration-200 ${
              chatScrollEdges.bottom ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </div>

        {/* New-UI empty state — heading shown when flag is on and no messages. */}
        {newUi && isEmpty && (
          <>
            <div className="flex flex-col items-center pb-4">
              <h2
                className="text-center"
                style={{
                  color: 'var(--text-1)',
                  fontSize: '2.5rem',
                  fontWeight: 300,
                  letterSpacing: '-0.015em',
                }}
              >
                What should we make?
              </h2>
            </div>
          </>
        )}

        {/* NewUi slot card — replaces the composer when the engine pushes a
          one-at-a-time question. Renders in the input area so the sidebar
          stays open and the question feels like a prompt, not a chat bubble.
          Always mounted (when newUi && !isArtifactPaneActive && gameId) so
          the IPC subscription catches the initial push before interviewSlotActive
          flips — same "always mounted" pattern as the ArtifactPane instance. */}
        {newUi && !isArtifactPaneActive && gameId && (
          <div
            className={`min-h-0 px-6 pb-6 pt-2${interviewSlotActive ? ' max-h-[calc(100vh-10rem)] overflow-y-auto overscroll-contain' : ' hidden'}`}
            data-testid="new-ui-interview-scroll-shell"
          >
            <div
              className={compact ? 'w-full' : 'mx-auto max-w-3xl'}
              data-testid="interview-overlay-mount"
            >
              <InterviewSlotOverlay
                gameId={gameId}
                initialViewMode={interviewViewMode}
                onRendererReady={onInterviewRendererReady}
              />
            </div>
          </div>
        )}

        {/* Input — Figma Make-style pill bar */}
        <div
          className={`${compact ? 'px-4' : 'px-6'} pb-6 pt-2${newUi && !isArtifactPaneActive && interviewSlotActive ? ' hidden' : ''}`}
        >
          <div className={compact ? 'w-full' : 'mx-auto max-w-3xl'}>
            <div className="mb-1 flex justify-end">
              <VisibilityStrip spend={lastTurnSpend} showCost={showCostInChat} />
            </div>
            {/* Issue #246: `<BugDraftCard>` watches
            `<gameDir>/.foundry/bug-draft.json` via the `bug:get-draft`
            poll and surfaces the SKILL-assembled bug report for
            user review. Mounts above the input strip — the same
            visual band as the queued-message chips and pasted-image
            chips, so the user sees pending-confirmation surfaces in
            one consistent place. Self-unmounts when the draft file
            is gone (success path or explicit cancel). */}
            {gameId && <BugDraftCard gameId={gameId} />}
            {/* Issue #592: `<ConflictResolutionCard>` watches
            `<gameDir>/.foundry/pending-conflicts.json` via the
            `conflicts:get-pending` poll and surfaces the
            agentic Resolve-with-AI flow when `crucible update`
            (no flag) leaves conflict markers. Mounts in the same
            band as `<BugDraftCard>` so all pending-confirmation
            surfaces are visually consolidated. Self-unmounts when
            main deletes the sidecar (option β cleanup hook fires
            after the agent's edits, or user clicks "Resolve
            manually"). */}
            {gameId && <ConflictResolutionCard gameId={gameId} />}
            {gameId && showReviewModal && (
              <ReviewAnswersModal gameId={gameId} onClose={onCloseReviewModal} />
            )}
            {newUi && !isArtifactPaneActive && pinnedAskUserQuestion && (
              <div
                className="mb-2 max-h-[min(24rem,42vh)] overflow-y-auto overscroll-contain"
                data-testid="pinned-ask-user-question"
              >
                <AskUserQuestionCard toolCall={pinnedAskUserQuestion} />
              </div>
            )}
            {showPendingPhaseBDecisionCard && pendingPhaseBDecision && (
              <div
                className="mb-2 max-h-[min(24rem,42vh)] overflow-y-auto overscroll-contain"
                data-testid="pending-phase-b-decision"
              >
                <PendingPhaseBDecisionCard
                  decision={pendingPhaseBDecision}
                  answered={answeredPhaseBDecisionId === pendingPhaseBDecision.intentId}
                  onAnswer={handlePhaseBDecisionAnswer}
                />
              </div>
            )}
            {statusRow && (
              <div
                className="mb-2 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-xs text-white/60"
                data-testid="chat-status-row"
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full ${
                    statusRow.tone === 'active'
                      ? 'animate-pulse bg-crucible-accent'
                      : statusRow.tone === 'waiting' || statusRow.tone === 'warning'
                        ? 'bg-amber-300'
                        : 'bg-white/30'
                  }`}
                  aria-hidden="true"
                />
                <span>{statusRow.label}</span>
              </div>
            )}
            {queuedMessages.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5" data-testid="chat-queue-chips">
                {queuedMessages.map((msg, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => onUnqueueMessage(idx)}
                    title="Click to remove from queue"
                    className="group flex max-w-[24rem] items-center gap-1.5 rounded-full border border-crucible-accent/30 bg-crucible-accent/10 px-3 py-1 text-xs text-white/70 transition-colors hover:border-red-500/50 hover:bg-red-500/10 hover:text-red-200"
                  >
                    <span className="text-[10px] uppercase tracking-wider text-white/40 group-hover:text-red-300/60">
                      Queued
                    </span>
                    <span className="truncate">{msg}</span>
                    <span className="text-white/30 group-hover:text-red-300/80">×</span>
                  </button>
                ))}
              </div>
            )}
            {/* Screenshot-paste MVP (#193) → thumbnail rework: image
            thumbnails (the pixels ARE the information; byte size
            dropped) with a hover/focus popover carrying the larger
            exact-aspect preview + the #193 redaction note, which used
            to be an always-visible banner here. Renders directly
            above the textarea so the user can see what's about to
            ride with their next send. */}
            {pendingAttachments.length > 0 && (
              <div
                className="mb-2 flex flex-wrap items-center gap-1.5"
                data-testid="chat-attachment-chips"
              >
                {pendingAttachments.map((att, idx) => {
                  // Pop ONLY the just-added (last) thumbnail, not the whole row.
                  const popLatest = attachmentPop && idx === pendingAttachments.length - 1;
                  return (
                    <span
                      key={idx}
                      className={popLatest ? 'inline-flex animate-selection-pop' : 'inline-flex'}
                    >
                      <AttachmentThumbnail
                        attachment={att}
                        onRemove={() => onRemoveAttachment(idx)}
                      />
                    </span>
                  );
                })}
                {/* Single row-level privacy disclosure — global/static
                    info shown once, not per thumbnail. */}
                <AttachmentRedactionInfo />
              </div>
            )}
            {attachmentError && (
              <div
                className="mb-2 flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200"
                data-testid="chat-attachment-error"
              >
                <span className="flex-1">{attachmentError}</span>
                <button
                  type="button"
                  onClick={onDismissAttachmentError}
                  className="text-red-200/70 hover:text-red-100"
                  aria-label="Dismiss attachment error"
                >
                  ×
                </button>
              </div>
            )}
            {/* Hidden file input — triggered by the + button */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void resizeImageFile(file).then((resized) => onAddAttachment(resized));
                e.currentTarget.value = '';
              }}
            />

            {/* Chat input — pill when single-line, Gemini-style rectangle when multi-line.
            The layout transitions the moment text wraps past one line. */}
            {isMultiLine ? (
              /* ── Expanded rectangle layout ── */
              <div
                key="composer-multiline"
                ref={composerWrapRef}
                // Distinct key from the single-line branch so React MOUNTS a
                // fresh node instead of reusing the pill's <div> — otherwise
                // `transition` would animate the corner radius from the pill's
                // full radius down to the fixed one (the brief small→large
                // flicker). `transition-colors` also keeps border-radius out of
                // any transition entirely.
                className="chat-composer-surface flex flex-col rounded-[26px] transition-colors"
                style={pillRadius != null ? { borderRadius: pillRadius } : undefined}
                onDragOver={handleComposerDragOver}
                onDrop={handleComposerDrop}
              >
                {/* Textarea — grows up to ~160px, then scrolls */}
                <textarea
                  ref={textareaRef}
                  value={chatInput}
                  onChange={(e) => onInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  // Wide (full-screen, no in-editor preview) keeps the longer
                  // hint with the screenshot affordance; the narrow split
                  // composer trims it so it fits the slimmer column.
                  placeholder={
                    compact
                      ? 'Tell the AI what to build…'
                      : 'Tell the AI what to build, or paste a screenshot…'
                  }
                  rows={1}
                  className="resize-none overflow-y-auto border-0 bg-transparent px-4 pt-3.5 pb-1 text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
                  style={{ color: 'var(--text-1)' }}
                />
                {/* Bottom toolbar */}
                <div className="flex items-center justify-between px-3 pb-2.5 pt-1">
                  {/* Attach — bare at rest, flat circle on hover */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="Attach an image"
                    className="flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                    style={{ color: 'var(--text-3)' }}
                  >
                    <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                      <path
                        d="M6.5 1v11M1 6.5h11"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                  {isAiTyping ? (
                    <button
                      onClick={onStop}
                      type="button"
                      aria-label="Stop the agent"
                      title="Stop the agent"
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500/80 text-white transition-colors hover:bg-red-500"
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                        <rect width="10" height="10" rx="1.5" />
                      </svg>
                    </button>
                  ) : (
                    /* Expanded layout is always armed (text required to reach multi-line) */
                    <button
                      type="button"
                      onClick={onSend}
                      data-testid="chat-send-button"
                      data-armed="true"
                      aria-label="Send message"
                      title="Send message"
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-crucible-accent/20 text-crucible-accent-hover transition-colors hover:bg-crucible-accent/30 hover:text-white disabled:opacity-30"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M8 13V3M3 8l5-5 5 5" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* ── Compact pill layout (single line) ── */
              <div
                key="composer-single"
                ref={composerWrapRef}
                className="chat-composer-surface flex items-center gap-3 rounded-full px-3 py-3"
                onDragOver={handleComposerDragOver}
                onDrop={handleComposerDrop}
              >
                {/* Attach — bare at rest, flat circle on hover */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  aria-label="Attach an image"
                  className="shrink-0 flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                  style={{ color: 'var(--text-3)' }}
                >
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                    <path
                      d="M6.5 1v11M1 6.5h11"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </button>
                {/* Textarea */}
                <textarea
                  ref={textareaRef}
                  value={chatInput}
                  onChange={(e) => onInputChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  // See the expanded-layout twin: long hint on the wide
                  // composer, trimmed hint on the narrow live-preview split.
                  placeholder={
                    compact
                      ? 'Tell the AI what to build…'
                      : 'Tell the AI what to build, or paste a screenshot…'
                  }
                  rows={1}
                  className="flex-1 resize-none overflow-hidden border-0 bg-transparent text-[15px] leading-relaxed placeholder:text-white/30 outline-none"
                  style={{ color: 'var(--text-1)' }}
                />
                {/* Right icon: stop when busy; armed arrow (with bg) or bare ⚡ otherwise */}
                {isAiTyping ? (
                  <button
                    onClick={onStop}
                    type="button"
                    aria-label="Stop the agent"
                    title="Stop the agent"
                    className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-red-500/80 text-white transition-colors hover:bg-red-500"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                      <rect width="10" height="10" rx="1.5" />
                    </svg>
                  </button>
                ) : (
                  (() => {
                    const armed = !!(chatInput.trim() || pendingAttachments.length > 0);
                    return armed ? (
                      <button
                        type="button"
                        onClick={onSend}
                        data-testid="chat-send-button"
                        data-armed="true"
                        aria-label="Send message"
                        title="Send message"
                        className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full bg-crucible-accent/20 text-crucible-accent-hover transition-colors hover:bg-crucible-accent/30 hover:text-white"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M8 13V3M3 8l5-5 5 5" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        type="button"
                        title="Voice input coming soon"
                        aria-label="Voice input (coming soon)"
                        data-testid="chat-send-button"
                        data-armed="false"
                        className="shrink-0 flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-white/[0.08]"
                        style={{ color: 'var(--text-3)' }}
                      >
                        <Mic size={16} strokeWidth={1.75} />
                      </button>
                    );
                  })()
                )}
              </div>
            )}
          </div>
        </div>
      </ChatErrorBoundary>
    </section>
  );
}

/**
 * Inline placeholder shown in place of the AI Chat panel when no Anthropic
 * key is configured. Replaces the previous "render nothing" branch (audit
 * §4: users landed on the Workspace and saw an empty centre column with
 * no clue why). The card is keyboard-accessible — the button is a real
 * `<button>` (focusable, Enter/Space activatable), it has a focus ring,
 * and the heading + button each carry an explicit aria label.
 */
function ChatPanelPlaceholder({
  onOpenSettings,
  persistError,
  onDismissPersistError,
}: {
  onOpenSettings: () => void;
  persistError?: SerialisedError | null;
  onDismissPersistError?: () => void;
}): React.JSX.Element {
  return (
    <section
      aria-labelledby="chat-placeholder-heading"
      className="flex flex-1 flex-col items-center justify-center overflow-hidden bg-crucible-bg px-6 py-10"
    >
      {persistError && (
        <div className="mb-4 w-full max-w-md">
          <CrucibleErrorState error={persistError} compact />
          {onDismissPersistError && (
            <button
              type="button"
              onClick={onDismissPersistError}
              className="mt-2 text-xs text-white/40 underline-offset-2 hover:underline"
            >
              Dismiss
            </button>
          )}
        </div>
      )}
      <div className="max-w-md rounded-xl border border-white/10 bg-crucible-surface p-6 text-center shadow-sm">
        <h2 id="chat-placeholder-heading" className="mb-2 text-base font-semibold text-white/80">
          AI assistant needs setup
        </h2>
        <p className="mb-5 text-sm leading-relaxed text-white/50">
          Foundry uses Claude to help build your game. Add your Anthropic API key in Settings to
          start.
        </p>
        <button
          type="button"
          onClick={onOpenSettings}
          aria-label="Open Settings to add your Anthropic API key"
          className="inline-flex items-center gap-2 rounded-lg bg-crucible-accent px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-crucible-accent focus-visible:ring-offset-2 focus-visible:ring-offset-crucible-surface"
        >
          Open Settings
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </section>
  );
}

// Exported for unit tests (issue #369 — pin the retry-button
// visibility gate); not part of any public API.
export function ChatBubble({
  message,
  onAnswerMarkdownQuestion,
  onRetry,
  newUi,
  pinnedAskUserQuestionId = null,
}: {
  message: ChatMessage;
  /**
   * Issue #215 — fallback parser hook. When the assistant emits a
   * plain-markdown `Question N of M:` block instead of a structured
   * `AskUserQuestion` tool call, the bubble paints a button strip
   * below the rendered markdown. Clicking a button calls this
   * handler with the option's plain text (no `(a) ` prefix), which
   * the parent dispatches as a normal user message via the same
   * path the textarea uses.
   *
   * Optional so unrelated callers (tests, future re-uses) don't
   * have to wire it up — the strip simply doesn't render when the
   * handler is absent.
   */
  onAnswerMarkdownQuestion?: (optionText: string) => void;
  /**
   * Issue #369 — chat-bubble retry. Wired through to
   * `<CrucibleErrorState onRetry>` only when the bubble's error has
   * `retryable: true` (set by PR #368 for the inFlight bouncer's
   * structured-error path). Optional so tests / future re-uses can
   * mount a bubble without a parent that knows how to retry.
   */
  onRetry?: (message: ChatMessage) => void;
  newUi?: boolean;
  pinnedAskUserQuestionId?: string | null;
}): React.JSX.Element | null {
  // Crucible #602 PR #C4 (Issue E) — inline-checkpoint discriminator
  // branch. Mounted as a single-line early-return at the top of the
  // function per design §9 ("`ChatBubble` discriminator branch").
  // Defence-in-depth: a malformed message that arrives with
  // `role === 'inline-checkpoint'` but no structured `checkpoint`
  // payload short-circuits to `null` so the renderer never crashes
  // on a corrupt row (the reducer + emitter both guard against this,
  // but a hand-edited `chat-history.json` could surface one).
  // `<ActivityCheckpointMessage>` is itself flag-gated for belt-and-
  // braces — see §9 "Why no `useActiveBuild` gating".
  if (message.role === 'inline-checkpoint') {
    if (!message.checkpoint) return null;
    return (
      <ActivityCheckpointMessage checkpoint={message.checkpoint} timestamp={message.timestamp} />
    );
  }
  const isUser = message.role === 'user';
  // Per-bubble "answered" flag: once the user clicks a button in the
  // fallback strip, every button in THIS strip disables. Keyed by the
  // bubble (the `useState` is local to this instance) so a new
  // message id mounts a fresh, un-answered bubble. Cross-game nav
  // resets messages → React unmounts these components → flag goes
  // with them. No ref to message.id needed.
  const [answeredFallback, setAnsweredFallback] = useState(false);

  // Detect the AskUserQuestion drift shape. Only fires for assistant
  // bubbles whose content has no structured tool call already (the
  // structured card path stays authoritative). Memo deps include
  // the tool-call ARRAY LENGTH rather than the array reference —
  // the reducer rebuilds `message.toolCalls` immutably on every
  // chunk, so a reference dep would defeat memoisation. Length
  // changes capture the structural transition we actually care
  // about (0 → ≥1 = structured card now exists, suppress fallback).
  const toolCallCount = message.toolCalls?.length ?? 0;
  const fallbackQuestion = useMemo(() => {
    if (isUser) return null;
    if (toolCallCount > 0) return null;
    if (!message.content) return null;
    return parseMarkdownQuestion(message.content);
  }, [isUser, toolCallCount, message.content]);

  return (
    <div className={`flex ${isUser ? 'mb-4 justify-end' : 'mb-8 justify-start'}`}>
      <div
        // Issue #328: the bubble wrapper itself MUST NOT carry
        // `select-text` / `cursor-text` — interactive descendants
        // (option buttons, tool-call cards) would become text-draggable.
        // Selectability is scoped to the message-content shim below.
        className={
          isUser
            ? 'max-w-[75%] rounded-[18px] rounded-br-md px-4 py-2.5 text-[14px] leading-[1.55]'
            : 'w-full text-[15px] leading-[1.7]'
        }
        style={
          isUser
            ? { background: 'var(--surface-chat)', color: 'var(--text-1)' }
            : { color: 'var(--text-1)' }
        }
        data-testid="chat-message-bubble"
      >
        {/* Interleaved render (Claude-Code style): when the reducer
            captured ordered `blocks`, paint text / thinking / tool cards
            in EMIT ORDER so commentary and actions thread together.
            Falls back to the legacy "all text, then all tool cards" path
            for messages without `blocks` (e.g. rehydrated from history's
            concatenated finalText). User bubbles never carry blocks. */}
        {!isUser && message.blocks && message.blocks.length > 0 ? (
          <InterleavedMessageBody
            blocks={message.blocks}
            toolCalls={message.toolCalls ?? []}
            newUi={newUi}
            pinnedAskUserQuestionId={pinnedAskUserQuestionId}
          />
        ) : (
          <>
            {message.content && (
              // Shim div: `MessageContent` returns a fragment so we can't
              // attach className directly. `select-text` makes the prose
              // copyable; `cursor-text` advertises the affordance.
              <div className="cursor-text select-text">
                <MessageContent content={message.content} />
              </div>
            )}
            {message.toolCalls && message.toolCalls.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {newUi
                  ? renderNewUiToolCallSegments(message.toolCalls, pinnedAskUserQuestionId)
                  : message.toolCalls.map((tc) =>
                      renderToolCallEntry(tc, newUi, pinnedAskUserQuestionId),
                    )}
              </div>
            )}
          </>
        )}
        {fallbackQuestion && onAnswerMarkdownQuestion && (
          <MarkdownQuestionFallbackStrip
            question={fallbackQuestion}
            answered={answeredFallback}
            onAnswer={(text) => {
              if (answeredFallback) return;
              setAnsweredFallback(true);
              onAnswerMarkdownQuestion(text);
            }}
          />
        )}
        {message.error && (
          <div className="mt-2">
            {/* Issue #369 — retry is wired ONLY when the structured
                error declares itself `retryable: true` (currently the
                inFlight bouncer per PR #368; future retryable codes
                pick this up automatically). Non-retryable errors fall
                through to the original "Copy details + Bug report"
                affordance — silently re-firing a multi-turn-state
                error would be confusing UX, so the gate stays
                explicit. The handler in `Workspace.handleRetry`
                debounces to the bouncer's 1.5s grace window. */}
            <CrucibleErrorState
              error={message.error}
              compact
              onRetry={message.error.retryable && onRetry ? () => onRetry(message) : undefined}
            />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Render an assistant message's content as an ordered sequence of
 * text / thinking / tool-call / sub-agent segments (emit order), instead of
 * the legacy text-then-cards flattening. Contiguous text runs are already
 * coalesced into single `text` blocks by the reducer, so this maps 1:1
 * over `blocks`. Each `tool_call` block resolves its live
 * `ToolCallEntry` from `toolCalls` by id (status/output update in place
 * without disturbing the block order). Blocks attributed to a sub-agent
 * (`block.agentId`) are indented with a left rule so the user can see which
 * specialist produced them (Claude-Code parity).
 */
function InterleavedMessageBody({
  blocks,
  toolCalls,
  newUi,
  pinnedAskUserQuestionId,
}: {
  blocks: readonly MessageBlock[];
  toolCalls: readonly ToolCallEntry[];
  newUi?: boolean;
  pinnedAskUserQuestionId: string | null;
}): React.JSX.Element {
  const byId = useMemo(() => {
    const map = new Map<string, ToolCallEntry>();
    for (const tc of toolCalls) map.set(tc.id, tc);
    return map;
  }, [toolCalls]);

  // Resolve a sub-agent's display name from its most recent lifecycle marker,
  // so an attributed text/tool block can be labelled even though the chunk only
  // carries the opaque agentId.
  const agentTypeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of blocks) {
      if (b.kind === 'subagent') map.set(b.agentId, b.agentType);
    }
    return map;
  }, [blocks]);

  const renderInner = (block: MessageBlock): React.ReactNode => {
    if (block.kind === 'text') {
      if (!block.text) return null;
      return (
        <div className="cursor-text select-text">
          <MessageContent content={block.text} />
        </div>
      );
    }
    if (block.kind === 'thinking') {
      if (!block.text) return null;
      return <ThinkingBlock text={block.text} />;
    }
    if (block.kind === 'subagent') {
      return <SubagentMarker block={block} />;
    }
    // tool_call — resolve the live entry; skip if it isn't tracked
    // yet (defensive: a ref block whose entry was trimmed/never
    // landed shouldn't crash the render).
    const entry = byId.get(block.toolCallId);
    if (!entry) return null;
    return renderToolCallEntry(entry, newUi, pinnedAskUserQuestionId);
  };

  return (
    <div className="space-y-2">
      {blocks.map((block, i) => {
        const inner = renderInner(block);
        if (inner === null) return null;
        // Attribution indent: a block produced inside a sub-agent gets a left
        // rule + a small label so the transcript reads like Claude Code's
        // nested Task output. `subagent` markers carry their own styling.
        const agentId = block.kind === 'subagent' ? undefined : block.agentId;
        if (agentId !== undefined) {
          const label = agentTypeById.get(agentId) ?? 'sub-agent';
          return (
            <div
              key={`b-${i}`}
              className="border-l-2 pl-3"
              style={{ borderColor: 'var(--border-alpha-2)' }}
            >
              <div
                className="mb-1 text-[11px] font-medium uppercase tracking-wide"
                style={{ color: 'var(--text-3)' }}
              >
                {label}
              </div>
              {inner}
            </div>
          );
        }
        return <div key={`b-${i}`}>{inner}</div>;
      })}
    </div>
  );
}

/**
 * Inline sub-agent (Task) lifecycle marker — "▸ dispatched art-director" /
 * "✓ art-director finished" — threaded into the transcript at the point the
 * Task started/stopped, so the user sees specialist hand-offs in context
 * (Claude-Code parity) rather than only in the right-column Agents panel.
 */
function SubagentMarker({
  block,
}: {
  block: Extract<MessageBlock, { kind: 'subagent' }>;
}): React.JSX.Element {
  const isStart = block.phase === 'start';
  const failed = block.stopReason === 'error' || block.stopReason === 'interrupted';
  const glyph = isStart ? '▸' : failed ? '⚠' : '✓';
  const verb = isStart
    ? 'dispatched'
    : block.stopReason === 'interrupted'
      ? 'stopped'
      : block.stopReason === 'error'
        ? 'failed'
        : 'finished';
  return (
    <div
      className="flex items-center gap-1.5 text-[12px]"
      style={{ color: failed ? 'var(--text-2)' : 'var(--text-3)' }}
    >
      <span aria-hidden>{glyph}</span>
      <span>
        {isStart ? (
          <>
            Dispatched <span className="font-medium">{block.agentType}</span>
          </>
        ) : (
          <>
            <span className="font-medium">{block.agentType}</span> {verb}
          </>
        )}
      </span>
    </div>
  );
}

/**
 * Dim, collapsible reasoning block — the model's extended-thinking
 * content surfaced inline (Claude-Code style). Collapsed by default so a
 * long reasoning trace doesn't dominate the transcript; the user expands
 * it to read the commentary.
 */
function ThinkingBlock({ text }: { text: string }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      className="rounded-lg border px-3 py-2 text-[13px]"
      style={{ borderColor: 'var(--border-alpha-1)', color: 'var(--text-3)' }}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center gap-1.5 text-left"
        style={{ color: 'var(--text-3)' }}
        aria-expanded={expanded}
      >
        <span aria-hidden className="text-[11px]">
          {expanded ? '▾' : '▸'}
        </span>
        <span className="italic">Thinking</span>
      </button>
      {expanded && (
        <div className="mt-1.5 cursor-text select-text whitespace-pre-wrap italic leading-relaxed">
          {text}
        </div>
      )}
    </div>
  );
}

function renderToolCallEntry(
  toolCall: ToolCallEntry,
  newUi?: boolean,
  pinnedAskUserQuestionId?: string | null,
): React.JSX.Element | null {
  // AskUserQuestion gets a dedicated interactive card — option buttons
  // that auto-fill + send the answer — rather than the generic row.
  if (
    newUi &&
    toolCall.name === 'AskUserQuestion' &&
    toolCall.status === 'running' &&
    toolCall.id === pinnedAskUserQuestionId
  ) {
    return null;
  }
  return toolCall.name === 'AskUserQuestion' ? (
    <AskUserQuestionCard key={toolCall.id} toolCall={toolCall} />
  ) : (
    <ToolCallCard key={toolCall.id} toolCall={toolCall} newUi={newUi} />
  );
}

function renderNewUiToolCallSegments(
  toolCalls: readonly ToolCallEntry[],
  pinnedAskUserQuestionId: string | null,
): React.ReactNode[] {
  return groupNewUiToolCallsForDisplay(toolCalls).map((segment) => {
    if (segment.kind === 'routine-group') {
      return <NewUiRoutineToolGroup key={segment.key} toolCalls={segment.toolCalls} />;
    }
    return renderToolCallEntry(segment.toolCall, true, pinnedAskUserQuestionId);
  });
}

/**
 * Issue #215 — fallback option-button strip rendered underneath an
 * assistant bubble whose markdown content matched
 * `parseMarkdownQuestion`. Visual style mirrors `AskUserQuestionCard`
 * (the structured-tool-call form) so a user can't tell the difference
 * between a model that obeyed the SKILL contract and a model that
 * drifted. Buttons disable in unison once the user clicks any one of
 * them — the parent already dispatched the answer through the same
 * path the textarea uses.
 *
 * Exported for unit tests / SSR snapshots. Not used elsewhere.
 */
export function MarkdownQuestionFallbackStrip({
  question,
  answered,
  onAnswer,
}: {
  question: { question: string; options: readonly string[] };
  answered: boolean;
  onAnswer: (optionText: string) => void;
}): React.JSX.Element {
  return (
    <div
      className="mt-2 rounded-lg border border-crucible-accent/30 bg-crucible-accent/5 px-3 py-3"
      data-testid="markdown-question-fallback-strip"
    >
      <p className="mb-2 text-xs italic text-white/40">Quick answer:</p>
      <div className="grid gap-1.5">
        {question.options.map((opt, oi) => (
          <button
            key={oi}
            type="button"
            disabled={answered}
            onClick={() => onAnswer(opt)}
            className="group flex flex-col items-start rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-left transition-colors hover:border-crucible-accent/40 hover:bg-crucible-accent/10 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-white/10 disabled:hover:bg-black/20"
          >
            <span className="text-sm font-medium text-white/90 group-hover:text-white">{opt}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function phaseBDecisionCategoryLabel(category: PhaseBDecision['category']): string {
  switch (category) {
    case 'reviewer-block':
      return 'The plan review found a blocker. Choose how Foundry should proceed.';
    case 'reviewer-concerns-high':
      return 'The review raised high concerns. Choose how Foundry should proceed.';
    case 'install-failed':
      return 'The build install failed. Choose the recovery path.';
    case 'missing-env-var':
      return 'The build needs missing configuration. Choose the recovery path.';
    case 'destructive-confirm':
      return 'This step needs confirmation before continuing.';
    case 'build-path':
      return 'Choose the build path before continuing.';
    case 'same-finding-3-rounds':
      return 'The same finding repeated. Choose how to handle it.';
    case 'scope-ambiguity':
      return 'The build scope is ambiguous. Choose a path forward.';
  }
}

export function PendingPhaseBDecisionCard({
  decision,
  answered,
  onAnswer,
}: {
  decision: PhaseBDecision;
  answered: boolean;
  onAnswer: (optionLabel: string) => void;
}): React.JSX.Element {
  const options = decision.optionIds
    .map((id) => ({ id, label: decision.optionLabels[id] ?? id }))
    .filter((option) => option.label.trim().length > 0);

  if (answered) {
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 text-xs font-medium uppercase text-emerald-300/80">Sent</span>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-white/40">Decision sent</p>
            <p className="mt-0.5 text-sm font-medium text-white/90">
              Foundry will continue from your selected path.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-crucible-accent/30 bg-crucible-accent/5 px-3 py-3">
      <span className="mb-1 inline-block rounded-full bg-crucible-accent/20 px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-crucible-accent-hover">
        Need direction
      </span>
      <p className="mb-2 text-sm font-medium text-white/90">
        {phaseBDecisionCategoryLabel(decision.category)}
      </p>
      {options.length > 0 ? (
        <div className="grid gap-1.5">
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => onAnswer(option.label)}
              className="group flex flex-col items-start rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-left transition-colors hover:border-crucible-accent/40 hover:bg-crucible-accent/10"
            >
              <span className="text-sm font-medium text-white/90 group-hover:text-white">
                {option.label}
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-white/50">Type your answer below to continue.</p>
      )}
    </div>
  );
}

/** Compact card showing a tool call and its status */
/**
 * Issue #200: detect the SDK's "Edit was a no-op because old_string
 * and new_string are identical" error. The Edit tool emits this as
 * a `<tool_use_error>` payload that the reducer marks as
 * `status: 'error'` and the renderer would otherwise paint with the
 * red \u2717 icon \u2014 even though nothing went wrong, the agent simply
 * tried to overwrite a value with itself. Treating it as a soft
 * yellow "no change" indicator avoids the panic-inducing red and
 * keeps real Edit failures (file not found, permission denied,
 * etc.) visually distinct.
 *
 * Match is intentionally narrow \u2014 the literal substring the SDK
 * emits. Anything else with `<tool_use_error>` falls through and
 * stays red.
 *
 * Pure helper exported for unit tests.
 */
const EDIT_NOOP_MARKER =
  '<tool_use_error>No changes to make: old_string and new_string are exactly the same.</tool_use_error>';

export function isEditNoopFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Edit') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes(EDIT_NOOP_MARKER);
}

/**
 * Issue #216: detect the SDK's "Edit found N matches but `replace_all`
 * is false" error. The agent self-recovers from this on the very next
 * turn by re-firing the same Edit with `replace_all: true`. Rendering
 * it as a panic-inducing red \u2717 between two green successes is
 * visible noise that makes the build look rougher than it is \u2014
 * particularly on the CEO build-a-game video. Treat it as a soft
 * amber dot, the same way #200 treats the `old_string === new_string`
 * no-op.
 *
 * Format pinned against the literal SDK template (verified by
 * inspection of the bundled `claude.exe` shipped with
 * `@anthropic-ai/claude-agent-sdk@0.2.x`):
 *
 *   `Found ${W} matches of the string to replace, but replace_all is
 *    false. To replace all occurrences, set replace_all to true. ...`
 *
 * The match is anchored against the substring `matches of the string
 * to replace, but replace_all is false` so a different number of
 * matches still classifies, but a free-text chat bubble that happens
 * to mention "replace_all" in passing does not over-match. We also
 * require the `<tool_use_error>` wrapper so we never reclassify a
 * normal user message that quotes the error text.
 *
 * Pure helper exported for unit tests.
 */
const EDIT_REPLACE_ALL_MARKER = 'matches of the string to replace, but replace_all is false';

export function isEditReplaceAllRecoverableFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Edit') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  // Require BOTH the SDK's tool-error wrapper AND the marker substring
  // so that a chat bubble or assistant text quoting the error does
  // not accidentally trigger amber treatment.
  return out.includes('<tool_use_error>') && out.includes(EDIT_REPLACE_ALL_MARKER);
}

/**
 * Issue #216: detect the agent firing a `Grep` (or other path-taking
 * search tool) without the required `path` argument. The Anthropic
 * SDK rejects this with an `InputValidationError` and the agent
 * self-recovers on the next turn by re-firing with the path filled
 * in. Render as amber \u25cb rather than red \u2717 so the user doesn't
 * read it as a real failure.
 *
 * Format pinned against the literal SDK template (verified by
 * inspection of `claude.exe`):
 *
 *   `<tool_use_error>InputValidationError: Grep failed due to the
 *    following issue:\nThe required parameter \`path\` is missing
 *    </tool_use_error>`
 *
 * Multiple missing params get joined as plural "issues" so we don't
 * pin against the singular wording. We DO require:
 *   1. The `<tool_use_error>` wrapper (so a chat bubble can't trigger).
 *   2. The literal `InputValidationError` token (so a real
 *      tool-execution failure can't trigger).
 *   3. The substring `` The required parameter `path` is missing ``
 *      (the exact format produced by the SDK's `hQH` Zod-error
 *      formatter for a missing required `path` field).
 *
 * Scoped to `Grep` for now \u2014 that's the shape Pratik observed in
 * the Earth-Invaders local build. Other path-taking tools (Glob,
 * Read) follow the same self-recovery pattern but we'll only widen
 * the scope when we observe them in the wild, to keep the
 * over-match risk low.
 *
 * Pure helper exported for unit tests.
 */
const GREP_MISSING_PATH_MARKER = 'The required parameter `path` is missing';

export function isGrepMissingPathFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Grep') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return (
    out.includes('<tool_use_error>') &&
    out.includes('InputValidationError') &&
    out.includes(GREP_MISSING_PATH_MARKER)
  );
}

/**
 * Issue #224: detect the legacy build-game interview preflight read
 * against `.foundry/build-game-interview.json` on a fresh game. That
 * exact missing-file read is expected and the agent self-recovers.
 *
 * Format pinned against the literal SDK template (verified by
 * inspection of the bundled `claude.exe`):
 *
 *   `<tool_use_error>File does not exist. Note: your current
 *    working directory is /path/to/cwd</tool_use_error>`
 *
 * Match is conservative \u2014 we require:
 *   1. tool name = `Read` (other tools have different self-recovery
 *      shapes and are matched by their own helpers).
 *   2. The exact legacy interview path, either game-relative or as
 *      the trailing absolute path.
 *   3. The `<tool_use_error>` wrapper, so a chat bubble that quotes
 *      the error in passing cannot trigger amber treatment.
 *   4. The literal `File does not exist.` marker (with the trailing
 *      period \u2014 that's part of the SDK template).
 *
 * OTHER Read failures \u2014 `EACCES: permission denied`,
 * `EISDIR: illegal operation on a directory`, etc. \u2014 do NOT contain
 * either marker and stay red so the user can see what actually went
 * wrong.
 *
 * Pure helper exported for unit tests.
 */
const READ_FILE_NOT_FOUND_MARKER = 'File does not exist.';
const LEGACY_BUILD_GAME_INTERVIEW_READ_PATH = '.foundry/build-game-interview.json';

function isExpectedLegacyBuildGameInterviewRead(toolCall: ToolCallEntry): boolean {
  const input = toolCall.input as Record<string, unknown>;
  const rawPath =
    typeof input.file_path === 'string'
      ? input.file_path
      : typeof input.path === 'string'
        ? input.path
        : '';
  const normalised = rawPath.replace(/\\/g, '/');
  return (
    normalised === LEGACY_BUILD_GAME_INTERVIEW_READ_PATH ||
    normalised.endsWith(`/${LEGACY_BUILD_GAME_INTERVIEW_READ_PATH}`)
  );
}

export function isReadFileNotFoundFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Read') return false;
  if (toolCall.status !== 'error') return false;
  if (!isExpectedLegacyBuildGameInterviewRead(toolCall)) return false;
  const out = toolCall.output ?? '';
  const trimmed = out.trim();
  if (trimmed.startsWith(READ_FILE_NOT_FOUND_MARKER)) return true;
  if (!out.includes('<tool_use_error>')) return false;
  return out.includes(READ_FILE_NOT_FOUND_MARKER);
}

/**
 * Issue #511 — detect the Edit tool's "String to replace not found"
 * failure shape so it renders as an info-chip rather than the same
 * red envelope used for genuine permission denials. The SDK emits
 * this whenever an Edit's `old_string` argument doesn't appear
 * verbatim in the target file (whitespace differs, agent edits from
 * memory rather than a fresh Read, file moved between read+edit).
 *
 * Pre-fix the envelope was indistinguishable from a permission
 * denial — both wrapped in `<tool_use_error>...</tool_use_error>`
 * markup. That conflation:
 *   - misleads users into thinking they need to allowlist something
 *     (no — Edit is always allowed; the issue is the `old_string`)
 *   - wastes agent turns when the agent retries the wrong fix
 *
 * Format pinned against the literal SDK template (verified against
 * `claude.exe` bundled with `@anthropic-ai/claude-agent-sdk@0.2.x`):
 *
 *   `<tool_use_error>String to replace not found in file.
 *    String: ${oldString}</tool_use_error>`
 *
 * Distinct from `isEditReplaceAllRecoverableFailure` (which fires on
 * "Found N matches ... replace_all is false") — that one means the
 * substring DOES exist but matches too many places. This helper
 * means the substring doesn't exist at all.
 *
 * Match requires:
 *   1. tool name = `Edit` (other tools have different self-recovery shapes).
 *   2. The `<tool_use_error>` wrapper, so a chat bubble that quotes
 *      the error text in passing cannot trigger info-chip treatment.
 *   3. The literal `String to replace not found in file.` marker
 *      (with trailing period — pinned against the SDK template).
 *
 * Pure helper exported for unit tests.
 */
const EDIT_MATCH_FAILED_MARKER = 'String to replace not found in file.';

export function isEditMatchFailedFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Edit') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes('<tool_use_error>') && out.includes(EDIT_MATCH_FAILED_MARKER);
}

/**
 * Issue #512 — detect the `BulkInterview` gate's Phase-B intercept
 * tool_result. The gate (`isBulkInterviewPermitted` in
 * `agent-adapter.ts`) returns a verbose mandate-quoting string when
 * the agent tries to use `mcp__foundry__BulkInterview` outside its
 * three legitimate paths (express, quick bulk-pitch, sparse seed). That text
 * is exactly what the AGENT needs to correct its approach — but it
 * appears verbatim in the user-facing chat, where it reads as an
 * alarming runtime-mandate-enforcement message implicating the user.
 *
 * The marker pins against the literal opening sentence of
 * `BULK_INTERVIEW_INTERCEPT_PROMPT` so we never reclassify a chat
 * bubble that happens to mention BulkInterview in passing.
 *
 * Distinct from a permission denial: the agent self-recovers by
 * re-emitting as `AskUserQuestion` on the next turn, and the gate
 * is working exactly as designed — nothing for the user to fix.
 *
 * Render as a soft info chip (amber, one-line user-friendly copy)
 * via `isRecoverableToolError` / `recoverableToolErrorLabel`. The
 * raw mandate text stays on the wire so the agent's transcript
 * gets the full correction guidance — only the user-visible chip
 * is softened.
 *
 * Pure helper exported for unit tests.
 */
const BULK_INTERVIEW_INTERCEPT_MARKER =
  'You attempted to emit `mcp__foundry__BulkInterview` outside its three legitimate paths';

export function isBulkInterviewInterceptFailure(toolCall: ToolCallEntry): boolean {
  // BulkInterview lives behind the MCP namespace `mcp__foundry__`;
  // accept both the raw and namespaced names since the renderer's
  // tool-name discriminator can carry either depending on which SDK
  // chunk path produced the entry.
  const isBulkInterviewTool =
    toolCall.name === 'BulkInterview' || toolCall.name === 'mcp__foundry__BulkInterview';
  if (!isBulkInterviewTool) return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  // Bugbot LOW (PR #533, 2026-05-12): mirror the sibling
  // `isBashPolicyDenialFailure` belt-and-braces negative check so a
  // `pnpm test` run that exercises `agent-adapter.test.ts` (which
  // pins the literal `BULK_INTERVIEW_INTERCEPT_PROMPT` opening
  // sentence in its snapshots) can't trigger the soft amber chip on
  // a genuine red test-runner failure. Like the Bash policy deny
  // path, the BulkInterview intercept is emitted as a plain `text`
  // tool_result with `isError: true` — it does NOT arrive in a
  // `<tool_use_error>` envelope (the `<tool_use_error>` wrapper is
  // SDK-internal validation only, see the `isBashPolicyDenialFailure`
  // doc-comment above), so the right guard here is the same vitest /
  // pnpm test framing tokens used for the Bash matcher.
  if (BASH_TEST_RUNNER_MARKERS.some((marker) => out.includes(marker))) {
    return false;
  }
  return out.includes(BULK_INTERVIEW_INTERCEPT_MARKER);
}

/**
 * Issue #239: detect Bash commands that the Crucible allowlist denies
 * BEFORE the shell even sees them. The agent self-recovers on the
 * very next turn (uses Write to create a directory instead of
 * `mkdir`, abandons a `pnpm install` attempt and moves on, picks a
 * different binary that's actually on the allowlist, etc). Render as
 * amber \u25cb so the user doesn't read these structurally-fine
 * recoveries as scary failures during the CEO build-a-game video.
 *
 * Match conditions (Bash tool only) \u2014 each entry pins the FULL
 * canonical deny-reason text emitted by `evaluateBashCommand` in
 * `agent-permissions.ts`. We deliberately use the long phrasing
 * (rather than a fragment) so a real test-runner failure that quotes
 * the deny string in its assertion output cannot trigger amber
 * treatment \u2014 Codex / Cursor Bugbot review (2026-05-05) caught that
 * the existing `agent-permissions.test.ts` snapshots assert these
 * exact strings, so a `pnpm test` failure exercising those tests
 * would print them in its stderr and a fragment-based match would
 * silently amber-render the genuine failure.
 *
 *   1. `is only allowed via '--version' / '-v'` \u2014 the
 *      pnpm/npm/node version-flag restriction (line 1052 in
 *      `agent-permissions.ts`). The `'--version' / '-v'` suffix
 *      pins past arbitrary "is only allowed via \u2026" prose.
 *   2. `Bash command rejected \u2014 contains banned metacharacter` \u2014
 *      the metachar ban (line 968). Uses the em-dash from the deny
 *      message so an unrelated mention of "banned metacharacter"
 *      cannot trigger.
 *   3. `Read-only enumeration doesn't need pipes, redirects` \u2014
 *      the verbose tail of the metachar deny; matched separately
 *      so a future SDK drift that re-emits only the verbose half
 *      still classifies. The trailing `, redirects` keeps it
 *      specific.
 *   4. `is not on the allowlist.` \u2014 the unknown-binary deny (line
 *      1059) that Pratik observed for `mkdir` before hello-weekend
 *      v0.12.1 (issue #239 reads this loosely as "unknown command";
 *      the actual literal text is "is not on the allowlist."). The
 *      trailing period anchors against quotes / paraphrasing and
 *      Cursor Bugbot review (2026-05-05) flagged the bare
 *      "unknown command" form as too generic.
 *
 * Path-restriction denials emitted by `evaluateToolPolicy` for
 * non-Bash tools (`Tool '\u2026' on '\u2026' denied by Crucible policy: \u2026`)
 * are deliberately NOT included \u2014 `evaluateToolPolicy` short-circuits
 * to `evaluateBashCommand` for Bash and never produces that string
 * for the Bash branch (Codex P2 catch, 2026-05-05). If we ever want
 * to soften those denials they need their own helper keyed off the
 * Read / Grep / Glob tool name.
 *
 * Belt-and-braces: we ALSO refuse to classify when the output looks
 * like a test-runner / build-tool failure (`AssertionError`,
 * `ELIFECYCLE`, ` FAIL `, `Test failed.`, `expected `, `> expect(`,
 * `.toBe(`, `.toContain(`, `.toEqual(`). canUseTool deny messages
 * never contain those tokens \u2014 they're emitted by
 * `evaluateBashCommand` BEFORE the shell runs. A test-runner failure
 * that asserts deny text would print BOTH the deny text AND the
 * test-runner framing, so the negative check kicks in and the
 * failure stays red. This is the second line of defence on top of
 * the full-phrase markers above (Codex P2 catch, 2026-05-05).
 *
 * Note on the `<tool_use_error>` envelope: the other recoverable
 * helpers (`isEditReplaceAllRecoverableFailure`,
 * `isGrepMissingPathFailure`, `isReadFileNotFoundFailure`) require
 * the `<tool_use_error>` XML wrapper because their target shapes are
 * SDK-internal validation errors that ALWAYS arrive in that
 * envelope. Crucible's `canUseTool` deny path takes a different
 * route \u2014 the deny `message` flows through the SDK as a
 * `tool_result` with `is_error: true` and the raw reason as content,
 * with no XML wrapper. Requiring `<tool_use_error>` here would miss
 * every legitimate deny. The combination of full-phrase markers AND
 * the test-runner negative check is the sound substitute.
 *
 * Pure helper exported for unit tests.
 */
const BASH_DENIED_BY_POLICY_MARKERS = [
  "is only allowed via '--version' / '-v'",
  'Bash command rejected \u2014 contains banned metacharacter',
  "Read-only enumeration doesn't need pipes, redirects",
  'is not on the allowlist.',
] as const;

/**
 * Negative check \u2014 if the Bash output contains any of these
 * test-runner / build-tool framing tokens it's a real exit-code-1
 * failure rather than a Crucible policy deny. canUseTool deny
 * messages are pure prose emitted by the policy gate BEFORE the
 * shell runs and never contain these markers. This guards against
 * the case (Codex P2, 2026-05-05) where a `pnpm test` run exercising
 * `agent-permissions.test.ts` would print the literal deny strings
 * inside vitest assertion output.
 */
const BASH_TEST_RUNNER_MARKERS = [
  'AssertionError',
  'ELIFECYCLE',
  ' FAIL ',
  'Test failed.',
  'expected ',
  '> expect(',
  '.toBe(',
  '.toContain(',
  '.toEqual(',
] as const;

export function isBashPolicyDenialFailure(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Bash') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  if (BASH_TEST_RUNNER_MARKERS.some((marker) => out.includes(marker))) {
    return false;
  }
  return BASH_DENIED_BY_POLICY_MARKERS.some((marker) => out.includes(marker));
}

/**
 * Issue #216 / #224 / #239: union of "agent self-recovers on the next
 * turn" tool errors. Rendering the recoverable shapes as a soft amber
 * \u25cb (rather than red \u2717) keeps real failures visually distinct
 * while stripping noise from the chat transcript. Each entry is
 * matched by its own narrow helper above; this helper is the single
 * render-time gate.
 *
 * If you add a new shape, also add a regression test pinning the
 * exact tool error text \u2014 we have been bitten before by over-broad
 * substring matches papering over real failures.
 */
export function isRecoverableToolError(toolCall: ToolCallEntry): boolean {
  return (
    isEditNoopFailure(toolCall) ||
    isEditReplaceAllRecoverableFailure(toolCall) ||
    isEditMatchFailedFailure(toolCall) ||
    isGrepMissingPathFailure(toolCall) ||
    isReadFileNotFoundFailure(toolCall) ||
    isBashPolicyDenialFailure(toolCall) ||
    isBulkInterviewInterceptFailure(toolCall)
  );
}

/**
 * Short label rendered under the recoverable-tool-error icon. Each
 * shape gets its own copy so the user can see WHY the agent had to
 * retry. Returns `null` for non-recoverable calls so `ToolCallCard`
 * can fall through to the standard rendering.
 */
export function recoverableToolErrorLabel(toolCall: ToolCallEntry): string | null {
  if (isEditNoopFailure(toolCall)) return 'No change \u2014 value already matched.';
  if (isEditReplaceAllRecoverableFailure(toolCall)) {
    return 'Multiple matches \u2014 agent will retry with replace_all.';
  }
  // Issue #511. The Edit `old_string` didn't appear verbatim in the
  // target file \u2014 distinct shape from permission denials. Render as
  // an info chip so the user (and any future agent reading the
  // transcript) can tell at a glance this isn't a permissions issue.
  if (isEditMatchFailedFailure(toolCall)) {
    return 'Text not found \u2014 agent will re-read the file and retry.';
  }
  if (isGrepMissingPathFailure(toolCall)) {
    return 'Missing path \u2014 agent will retry with the right parameters.';
  }
  if (isReadFileNotFoundFailure(toolCall)) {
    return 'File not found \u2014 agent will continue without this file.';
  }
  if (isBashPolicyDenialFailure(toolCall)) {
    return 'Denied by policy \u2014 agent will pick a different approach.';
  }
  // Issue #512. BulkInterview Phase-B intercept is the gate doing
  // exactly what it should \u2014 the agent picked the wrong tool for the
  // phase and self-recovers by emitting AskUserQuestion on the next
  // turn. The full mandate text stays on the wire for the agent; the
  // user sees a short policy-reminder one-liner instead of the
  // alarming "You attempted to emit\u2026" mandate quotation.
  if (isBulkInterviewInterceptFailure(toolCall)) {
    return 'Build-game switched to per-question prompts \u2014 agent will continue with the next question.';
  }
  return null;
}

/**
 * Issue #200: detect Edit calls that target Foundry's build-game
 * interview-state JSON. The `/build-game` skill records the user's
 * Phase A answers by running a cascade of small Edits against
 * `.foundry/build-game-interview.json`. Surfacing the raw success
 * blob ("The file ... has been updated successfully") five times in
 * a row reads as noise \u2014 the user just clicked an answer button and
 * doesn't care about the file. Replace the output text with a
 * friendlier short label.
 *
 * Heuristic: name must be `Edit` AND `file_path` must point at
 * `build-game-interview.json` somewhere under `.foundry/` (i.e.
 * `<game-root>/.foundry/build-game-interview.json` after backslash
 * normalisation). Other `.foundry/*` writes \u2014 `dispatch/**` for
 * Phase B subagent staging and `dev-status.json` for `/diagnose` \u2014
 * are legitimate non-answer Edits and MUST keep their raw output
 * so the user can see what went on. Codex review (2026-05-04)
 * caught this \u2014 the original heuristic matched `(^|/)\\.foundry/`
 * which over-matched. Pinned negatively in the test suite.
 *
 * Pure helper exported for unit tests.
 */
const BUILD_GAME_INTERVIEW_BASENAME = '.foundry/build-game-interview.json';
/**
 * v9 \u00a75 Decision B / Phase 0a \u00a711.1 \u2014 under `CRUCIBLE_CLOUD_VM_V1=1`
 * the canonical interview file lives under `.foundry/persistent/`
 * (Codex round 8 P3). The agent Edit tool calls land at this
 * post-split path, so the matcher needs both shapes to keep the
 * friendly "Saved your answer." label in cloud-mode.
 */
const BUILD_GAME_INTERVIEW_BASENAME_PERSISTENT = '.foundry/persistent/build-game-interview.json';

export function buildGameInterviewEditLabel(toolCall: ToolCallEntry): string | null {
  if (toolCall.name !== 'Edit') return null;
  // Errors must NOT be papered over with a friendly success label \u2014
  // a genuine interview-state Edit failure (file missing, permission
  // denied) should still surface its raw output so the user can see
  // what actually went wrong. The only error this layer softens is
  // the dedicated "Edit was a no-op" case (handled separately by
  // `isEditNoopFailure`).
  if (toolCall.status === 'error') return null;
  const filePath = toolCall.input.file_path;
  if (typeof filePath !== 'string') return null;
  // Normalise path separators so a Windows-flavoured `.foundry\\u2026`
  // path (extremely unlikely from Anthropic's SDK, but cheap to
  // defend against) still matches.
  const normalised = filePath.replace(/\\/g, '/');
  // Match the canonical build-game-interview file at any depth
  // (game root may itself live under a workspace directory). Accept
  // BOTH the legacy `.foundry/build-game-interview.json` shape
  // (current flag-OFF behaviour) and the post-split
  // `.foundry/persistent/build-game-interview.json` shape (cloud-vm
  // flag ON). Reject every other `.foundry/*` file so dispatch
  // staging files and `dev-status.json` keep their raw output.
  const isInterviewFile =
    normalised === BUILD_GAME_INTERVIEW_BASENAME ||
    normalised === BUILD_GAME_INTERVIEW_BASENAME_PERSISTENT ||
    normalised.endsWith(`/${BUILD_GAME_INTERVIEW_BASENAME}`) ||
    normalised.endsWith(`/${BUILD_GAME_INTERVIEW_BASENAME_PERSISTENT}`);
  if (!isInterviewFile) return null;
  if (toolCall.status === 'running') return 'Recording your answer\u2026';
  return 'Saved your answer.';
}

function buildBulkInterviewFriendlyLabel(toolCall: ToolCallEntry): string | null {
  const isBulkInterview =
    toolCall.name === 'BulkInterview' || toolCall.name === 'mcp__foundry__BulkInterview';
  if (!isBulkInterview || toolCall.status === 'error') return null;
  return toolCall.status === 'running' ? 'Opening Fast questions...' : 'Opened Fast questions.';
}

export function buildToolCallFriendlyLabel(toolCall: ToolCallEntry): string | null {
  return buildBulkInterviewFriendlyLabel(toolCall) ?? buildGameInterviewEditLabel(toolCall);
}

// ---------------------------------------------------------------------------
// Issue #619 \u2014 pedagogical tool-result errors
//
// Tool-result errors with self-correction copy (Read file-too-large,
// Glob too-many-results, etc.) used to render as the same alarming red
// \u274c chip as genuine failures. The agent self-recovers on the next call
// WITHOUT user intervention \u2014 the red chip is misleading.
//
// This section adds a third tier to the routed-vs-blocked classification
// from PR #586:
//
//   1. `policy_routed`  \u2014 permission-gate nudge, rendered by RoutedHintChip
//      (set via `policy_decision` side-channel from agent-adapter).
//   2. `recoverable`    \u2014 agent self-recovers; amber \u25e6 chip (isRecoverableToolError).
//   3. `pedagogical`    \u2014 tool-result carries recovery guidance; subdued
//      blue/slate info chip (isPedagogicalToolError, this section).
//
// Fallback: anything NOT matched by the pedagogical patterns stays on
// the existing red-chip / amber-recoverable paths \u2014 no regression.
// ---------------------------------------------------------------------------

/**
 * Issue #619 \u2014 Read tool: the agent called Read on a file whose
 * token count exceeds the SDK's per-call limit. The error body
 * tells the agent exactly how to recover (use `offset`/`limit`
 * parameters or search instead). The agent ALWAYS self-recovers \u2014
 * this is pedagogical guidance, not a failure.
 *
 * Pinned against the literal SDK template (verified against
 * `@anthropic-ai/claude-agent-sdk` bundled in the Foundry binary):
 *
 *   `File content (N tokens) exceeds maximum allowed tokens (25000).
 *    Use offset and limit parameters to read specific portions of the
 *    file, or search for specific content instead of reading the whole
 *    file.`
 *
 * We require ALL of:
 *   1. tool name = `Read` (avoid matching other tools whose errors
 *      happen to mention "tokens").
 *   2. `exceeds maximum allowed tokens` \u2014 the core discriminator.
 *   3. `Use offset and limit parameters` \u2014 the recovery copy that
 *      confirms this is a pedagogical limit hit, not a permissions
 *      error.
 *
 * The pattern does NOT require the `<tool_use_error>` XML wrapper
 * because the SDK emits this as a plain `tool_result` with
 * `is_error: true` (same shape as the Bash-policy-deny path \u2014 no
 * SDK-internal XML envelope).
 *
 * Pure helper exported for unit tests.
 */
const READ_TOO_LARGE_MARKER_1 = 'exceeds maximum allowed tokens';
const READ_TOO_LARGE_MARKER_2 = 'Use offset and limit parameters';

export function isReadTooLargeError(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Read') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes(READ_TOO_LARGE_MARKER_1) && out.includes(READ_TOO_LARGE_MARKER_2);
}

/**
 * Issue #619 \u2014 Glob tool: the agent's pattern matched more results
 * than the SDK's truncation limit. The SDK returns a truncated list
 * with a trailing note. The agent self-recovers by narrowing the
 * pattern or using `path` to scope the search.
 *
 * Pinned against the SDK message shape:
 *
 *   `Note: only showing first N results. Use the path parameter to
 *    narrow results, or use a more specific pattern.`
 *
 * We require:
 *   1. tool name = `Glob`.
 *   2. The `only showing first` truncation marker.
 *   3. The `Use the path parameter` recovery copy.
 *
 * Note: Glob truncation MAY arrive as `is_error: false` in some SDK
 * versions (a warning, not a hard failure). This PR only classifies
 * `status: 'error'` because that is the only shape observed in the
 * wild so far. If a future SDK version emits truncation as a success-
 * status result, this helper will need to be widened. The doc-comment
 * previously said "status: 'error' OR status: 'success'" \u2014 that was
 * aspirational and incorrect; corrected here (Codex round-1).
 *
 * Pure helper exported for unit tests.
 */
const GLOB_TOO_MANY_MARKER_1 = 'only showing first';
const GLOB_TOO_MANY_MARKER_2 = 'Use the path parameter';

export function isGlobTooManyResultsError(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Glob') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  return out.includes(GLOB_TOO_MANY_MARKER_1) && out.includes(GLOB_TOO_MANY_MARKER_2);
}

/**
 * Issue #619 \u2014 Bash tool: the OS shell emitted a "Did you mean: <X>?"
 * suggestion immediately after a `command not found` line. This is
 * the POSIX shell / zsh / bash correction hint for a TYPO-ed binary
 * name (e.g. `gti` \u2192 `git`). The agent self-recovers on the next call
 * by using the suggested binary.
 *
 * Why this is safe to classify as pedagogical:
 *   - The OS shell only emits "Did you mean: <X>?" on the SAME line
 *     (or the following line) as the `<cmd>: command not found` shell
 *     message, and ONLY when the shell finds a near-match binary.
 *   - Real build/CI failures that mention "command not found" from a
 *     subprocess AND a "Did you mean" from a package manager (e.g.
 *     `npm ... Did you mean "\u2026"?`) contain very different surrounding
 *     prose. Codex round-1 flagged the bare two-substring match as
 *     too broad; the fix is:
 *       1. Require the OS shell's canonical `command not found` format:
 *          `": command not found"` (colon-space, matching the shell's
 *          `<cmd>: command not found` shape; npm/pnpm/cargo emit `not found`
 *          without the colon-prefixed command name in that exact form).
 *       2. Require `"Did you mean: "` (colon after "mean") \u2014 the shell's
 *          suggestion format. npm suggests `"did you mean \u2026?"` (lowercase,
 *          no colon); pnpm uses `"Did you mean \u2026?"` (no colon). Adding the
 *          colon avoids false-positives from package-manager "did you mean"
 *          messages that contain different prose.
 *
 * Belt-and-braces: also exclude test-runner framing tokens (same guard
 * as the Bash-policy-deny path above).
 *
 * Pure helper exported for unit tests.
 */
const BASH_COMMAND_NOT_FOUND_MARKER = ': command not found';
const BASH_DID_YOU_MEAN_MARKER = 'Did you mean: ';

export function isBashDidYouMeanError(toolCall: ToolCallEntry): boolean {
  if (toolCall.name !== 'Bash') return false;
  if (toolCall.status !== 'error') return false;
  const out = toolCall.output ?? '';
  // Guard: test-runner framing means the output is a real test failure
  // quoting error text, not an actual shell suggestion.
  if (BASH_TEST_RUNNER_MARKERS.some((marker) => out.includes(marker))) {
    return false;
  }
  return out.includes(BASH_COMMAND_NOT_FOUND_MARKER) && out.includes(BASH_DID_YOU_MEAN_MARKER);
}

/**
 * Issue #619 \u2014 union discriminator for all pedagogical tool-result
 * errors. A tool-result error is "pedagogical" when the output
 * body carries self-correction guidance the AGENT can act on
 * without any user intervention.
 *
 * Visual contract: rendered as a subdued blue/slate info chip
 * (`PedagogicalToolErrorChip`) rather than the existing red \u274c chip.
 * The recovery copy is still visible (click-to-expand) so the
 * curious user can see what happened, but the chip does NOT alarm
 * them.
 *
 * Fallback: any error NOT matched here stays on the existing red /
 * amber-recoverable render paths \u2014 no regression.
 *
 * Pure helper exported for unit tests.
 */
export function isPedagogicalToolError(toolCall: ToolCallEntry): boolean {
  return (
    isReadTooLargeError(toolCall) ||
    isGlobTooManyResultsError(toolCall) ||
    isBashDidYouMeanError(toolCall)
  );
}

/**
 * Short label for the closed-shape of `PedagogicalToolErrorChip`.
 * Each pattern gets a human-readable one-liner so the user
 * understands at a glance WHY the agent had to adapt, without
 * reading the full recovery copy. Returns `null` for non-pedagogical
 * calls (caller guards with `isPedagogicalToolError` first).
 *
 * Pure helper exported for unit tests.
 */
export function pedagogicalToolErrorLabel(toolCall: ToolCallEntry): string | null {
  if (isReadTooLargeError(toolCall)) {
    return 'File too large \u2014 agent will read in sections.';
  }
  if (isGlobTooManyResultsError(toolCall)) {
    return 'Too many matches \u2014 agent will narrow the pattern.';
  }
  if (isBashDidYouMeanError(toolCall)) {
    return 'Command not found \u2014 agent will use the suggested binary.';
  }
  return null;
}

/**
 * Issue #619 \u2014 subdued info chip for pedagogical tool-result errors.
 * Symmetric with `RoutedHintChip` (PR #586) but triggered by
 * renderer-side content inspection rather than the `policyKind`
 * side-channel from the main process.
 *
 * Visual contract:
 *   - subdued background (`bg-sky-900/30` + `border-sky-700/40`)
 *     distinguishes from the `RoutedHintChip` slate tones while
 *     sharing the "non-alarming" palette.
 *   - \u2139 glyph (literal Unicode, no SVG) to signal informational.
 *   - single-line label (from `pedagogicalToolErrorLabel`) in the
 *     closed shape; click-to-expand reveals the full recovery copy.
 *
 * Intentionally NOT exported as a generic chip \u2014 the only call site
 * is `ToolCallCard`. Pure presentational component, no IPC.
 */
export function PedagogicalToolErrorChip({
  toolCall,
}: {
  toolCall: ToolCallEntry;
}): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const output = toolCall.output ?? '';
  const label = pedagogicalToolErrorLabel(toolCall) ?? output.split('\n')[0] ?? output;
  return (
    <div
      className="rounded-lg border border-sky-700/40 bg-sky-900/30 px-3 py-2"
      data-testid="tool-call-pedagogical"
      data-pedagogical="true"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start gap-2 text-left text-xs text-sky-200/80 transition-colors hover:text-sky-100"
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide recovery details' : 'Show recovery details'}
      >
        {/* Literal "i in circle" glyph \u2014 no icon dep, accessibility-safe. */}
        <span className="select-none text-sky-400/90" aria-hidden="true">
          {'\u24d8'}
        </span>
        <span className="font-mono font-medium text-sky-200/70">{toolCall.name}</span>
        <span className="flex-1 truncate text-sky-300/60">{label}</span>
        <span className="select-none text-sky-400/60" aria-hidden="true">
          {expanded ? '\u25be' : '\u25b8'}
        </span>
      </button>
      {expanded && (
        <pre
          className="mt-2 max-h-48 cursor-text select-text overflow-auto whitespace-pre-wrap rounded border border-sky-700/30 bg-slate-800/40 px-2 py-1 text-xs text-sky-200/70"
          data-testid="tool-call-pedagogical-body"
        >
          {output}
        </pre>
      )}
    </div>
  );
}

/**
 * Issue #529 \u2014 subdued info chip for `policy_routed` denials. The
 * permission gate has nudged the agent toward a preferred shape (use
 * relative paths, dispatch a specialist via the Agent tool, use `&&`
 * instead of `;`, etc.); the agent recovers on the next call WITHOUT
 * user intervention. Pre-#529 these denials rendered as alarming red
 * error chips even though they're pedagogical, not failures \u2014 every
 * `/build-game` session exposed the user to a dozen of them.
 *
 * Visual contract:
 *   - subdued background (`bg-slate-700/40`) rather than red \u2014 does
 *     not pull the eye the way the existing red error chip does.
 *   - \u2139 icon to signal "informational, not error".
 *   - single-line summary ("Foundry routed this call \u2192 details") on
 *     the closed shape so the chat scroll stays compact.
 *   - click-to-expand body reveals the full deny reason for the
 *     curious + the agents-on-camera mode.
 *
 * Intentionally NOT exported as a generic chip \u2014 the only call site
 * is `ToolCallCard` when it detects `toolCall.policyKind === 'routed'`,
 * which is set by the chat reducer when a `policy_decision` chunk
 * lands. Pure presentational component, no IPC.
 */
export function RoutedHintChip({ toolCall }: { toolCall: ToolCallEntry }): React.JSX.Element {
  const [expanded, setExpanded] = useState(false);
  // The deny reason is the gate's full prose nudging the model
  // toward the preferred shape. The model has already self-
  // recovered by the time the user reads this, so the body is
  // collapsed by default \u2014 clicking reveals the full guidance.
  const reason = toolCall.output ?? '';
  // First-line preview: trim the prose to the first sentence (up to
  // 80 chars) so the closed shape reads as a single subdued line.
  // Falls back to the full reason when no sentence boundary is in
  // range.
  const previewSource = reason.split('\n')[0] ?? reason;
  const firstSentenceEnd = previewSource.indexOf('. ');
  const preview =
    firstSentenceEnd > 0 && firstSentenceEnd <= 80
      ? previewSource.slice(0, firstSentenceEnd + 1)
      : previewSource.length > 80
        ? previewSource.slice(0, 80) + '...'
        : previewSource;
  return (
    <div
      className="rounded-lg border border-slate-600/40 bg-slate-700/30 px-3 py-2"
      data-testid="tool-call-routed"
      data-policy-kind="routed"
    >
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-start gap-2 text-left text-xs text-slate-300/80 transition-colors hover:text-slate-200"
        aria-expanded={expanded}
        aria-label={expanded ? 'Hide routing details' : 'Show routing details'}
      >
        {/* Use a literal "i in circle" glyph rather than an SVG so the
            chip stays purely-text \u2014 no icon dependency, no
            accessibility-tree mismatch on a future asset swap. */}
        <span className="select-none text-slate-400/90" aria-hidden="true">
          {'\u24D8'}
        </span>
        <span className="font-mono font-medium text-slate-200/70">{toolCall.name}</span>
        <span className="flex-1 truncate text-slate-300/60">{preview}</span>
        <span className="select-none text-slate-400/60" aria-hidden="true">
          {expanded ? '\u25BE' : '\u25B8'}
        </span>
      </button>
      {expanded && (
        <pre
          className="mt-2 max-h-48 cursor-text select-text overflow-auto whitespace-pre-wrap rounded border border-slate-600/30 bg-slate-800/40 px-2 py-1 text-xs text-slate-200/70"
          data-testid="tool-call-routed-body"
        >
          {reason}
        </pre>
      )}
    </div>
  );
}

// ─── New-UI compact tool-call row (Claude Code-style) ────────────────────────

function toolVerbPast(name: string): string {
  const map: Record<string, string> = {
    write_file: 'Wrote',
    read_file: 'Read',
    run_command: 'Ran',
    list_files: 'Listed',
    Write: 'Wrote',
    Read: 'Read',
    Edit: 'Edited',
    Bash: 'Ran',
    Grep: 'Searched',
    Glob: 'Listed',
    Task: 'Spawned',
  };
  return map[name] ?? name;
}

function toolVerbPresent(name: string): string {
  const map: Record<string, string> = {
    write_file: 'Writing',
    read_file: 'Reading',
    run_command: 'Running',
    list_files: 'Listing',
    Write: 'Writing',
    Read: 'Reading',
    Edit: 'Editing',
    Bash: 'Running',
    Grep: 'Searching',
    Glob: 'Listing',
    Task: 'Spawning',
  };
  return map[name] ?? name;
}

function toolSubjectText(toolCall: ToolCallEntry): string {
  const { input } = toolCall;
  const path = (input.path ?? input.file_path ?? '') as string;
  if (path) return path;
  const cmd = (input.command ?? '') as string;
  if (cmd) return cmd;
  const pattern = (input.pattern ?? '') as string;
  if (pattern) return pattern;
  return '';
}

export type NewUiToolCallDisplaySegment =
  | { kind: 'routine-group'; key: string; toolCalls: readonly ToolCallEntry[] }
  | { kind: 'single'; toolCall: ToolCallEntry };

const NEW_UI_ROUTINE_TOOL_NAMES = new Set([
  'Bash',
  'Read',
  'Grep',
  'Glob',
  'LS',
  'Write',
  'Edit',
  'MultiEdit',
  'Search',
  'run_command',
  'read_file',
  'write_file',
  'list_files',
]);

export function groupNewUiToolCallsForDisplay(
  toolCalls: readonly ToolCallEntry[],
): NewUiToolCallDisplaySegment[] {
  const segments: NewUiToolCallDisplaySegment[] = [];
  let pendingRoutine: ToolCallEntry[] = [];
  const flushPendingRoutine = (): void => {
    if (pendingRoutine.length === 0) return;
    segments.push({
      kind: 'routine-group',
      key: `routine-${pendingRoutine[0]?.id ?? segments.length}`,
      toolCalls: pendingRoutine,
    });
    pendingRoutine = [];
  };
  for (const toolCall of toolCalls) {
    if (shouldCollapseRoutineToolCall(toolCall)) {
      pendingRoutine.push(toolCall);
      continue;
    }
    flushPendingRoutine();
    segments.push({ kind: 'single', toolCall });
  }
  flushPendingRoutine();
  return segments;
}

function shouldCollapseRoutineToolCall(toolCall: ToolCallEntry): boolean {
  if (toolCall.status !== 'success') return false;
  if (toolCall.name === 'AskUserQuestion') return false;
  if (toolCall.policyKind) return false;
  return NEW_UI_ROUTINE_TOOL_NAMES.has(toolCall.name);
}

function NewUiRoutineToolGroup({
  toolCalls,
}: {
  toolCalls: readonly ToolCallEntry[];
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const count = toolCalls.length;
  return (
    <div
      className="rounded-lg border border-white/5 bg-white/[0.025] px-2 py-1"
      data-testid="new-ui-routine-tool-group"
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 py-1 text-left text-xs text-white/45 transition-colors hover:text-white/65"
        aria-expanded={open}
        aria-label={open ? 'Hide routine activity details' : 'Show routine activity details'}
        onClick={() => setOpen((value) => !value)}
      >
        <span className="w-3.5 shrink-0 text-center text-white/25">✓</span>
        <span className="flex-1 truncate">
          Activity - {count} completed {count === 1 ? 'command' : 'commands'}
        </span>
        <span className="select-none text-white/25" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
      </button>
      {open && (
        <div className="mt-0.5 border-t border-white/5 pt-0.5">
          {toolCalls.map((toolCall) => (
            <NewUiToolCallRow key={toolCall.id} toolCall={toolCall} />
          ))}
        </div>
      )}
    </div>
  );
}

function NewUiToolCallRow({ toolCall }: { toolCall: ToolCallEntry }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const isRecoverable = isRecoverableToolError(toolCall);
  const recoverableLabel = isRecoverable ? recoverableToolErrorLabel(toolCall) : null;
  const friendlyLabel = !isRecoverable ? buildToolCallFriendlyLabel(toolCall) : null;
  const subject = toolSubjectText(toolCall);
  const verb =
    toolCall.status === 'running' ? toolVerbPresent(toolCall.name) : toolVerbPast(toolCall.name);
  const hasExpandable = !!toolCall.output && toolCall.status !== 'running' && !isRecoverable;

  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-1.5 py-[3px] text-left"
        onClick={() => hasExpandable && setOpen((o) => !o)}
      >
        <span className="w-3.5 shrink-0 text-center text-xs">
          {toolCall.status === 'running' ? (
            <span className="tool-call-spinner inline-block text-white/40">◠</span>
          ) : toolCall.status === 'success' ? (
            <span className="text-white/25">✓</span>
          ) : isRecoverable ? (
            <span className="text-amber-400/60">○</span>
          ) : (
            <span className="text-red-400">✕</span>
          )}
        </span>
        <span className="flex-1 truncate text-xs">
          {friendlyLabel ? (
            <span className="text-white/55">{friendlyLabel}</span>
          ) : (
            <>
              <span
                className={
                  toolCall.status === 'error' && !isRecoverable
                    ? 'text-red-400/80'
                    : 'text-white/55'
                }
              >
                {verb}
              </span>
              {subject && <span className="ml-1.5 font-mono text-white/35">{subject}</span>}
            </>
          )}
          {isRecoverable && recoverableLabel && (
            <span className="ml-1.5 italic text-amber-300/50">{recoverableLabel}</span>
          )}
        </span>
        {hasExpandable && (
          <svg
            className="ml-1 shrink-0 text-white/20"
            style={{
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.15s',
            }}
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
          >
            <path
              d="M2 1.5l3 2.5-3 2.5"
              stroke="currentColor"
              strokeWidth="1.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </button>
      {open && toolCall.output && (
        <pre className="ml-5 mt-0.5 max-h-24 cursor-text select-text overflow-auto whitespace-pre-wrap text-xs text-white/30">
          {toolCall.output.slice(0, 500)}
          {toolCall.output.length > 500 ? '…' : ''}
        </pre>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export function ToolCallCard({
  toolCall,
  newUi,
}: {
  toolCall: ToolCallEntry;
  newUi?: boolean;
}): React.JSX.Element {
  // Issue #529 \u2014 short-circuit to the subdued `RoutedHintChip` when
  // the chat reducer has tagged this tool call's `policyKind` as
  // `'routed'`. The chip carries the gate's deny prose verbatim
  // (already on `toolCall.output` from the SDK's synthesised
  // tool_result for the denied call). When `policyKind` is absent
  // OR `'blocked'`, the existing red-chip / amber-recoverable paths
  // below apply \u2014 no regression on un-classified deny sites.
  if (toolCall.policyKind === 'routed') {
    return <RoutedHintChip toolCall={toolCall} />;
  }
  // Issue #619 \u2014 pedagogical tool-result errors (Read file-too-large,
  // Glob too-many-results, Bash "Did you mean"). These carry recovery
  // copy the agent can act on; render as a subdued info chip rather
  // than the alarming red \u274C chip. Checked BEFORE the amber-recoverable
  // path so pedagogical errors get the dedicated chip rather than the
  // amber \u25E6 treatment.
  if (isPedagogicalToolError(toolCall)) {
    return <PedagogicalToolErrorChip toolCall={toolCall} />;
  }
  if (newUi) {
    return <NewUiToolCallRow toolCall={toolCall} />;
  }
  // Issue #200 / #216: detect tool errors the agent self-recovers
  // from on the very next turn. Render them as a soft amber \u25CB with
  // a short label, instead of the panic-inducing red \u2717 + raw
  // `<tool_use_error>` blob. Real failures (file not found,
  // permission denied, etc.) fall through and stay red so the user
  // can see what actually went wrong.
  const isRecoverable = isRecoverableToolError(toolCall);
  const recoverableLabel = isRecoverable ? recoverableToolErrorLabel(toolCall) : null;

  const statusColors: Record<ToolCallEntry['status'], string> = {
    running: 'text-blue-400',
    success: 'text-green-400',
    error: isRecoverable ? 'text-amber-300/70' : 'text-red-400',
  };
  // Crucible #787 (motion bundle item 1): in-flight tool-call rows
  // show a rotating spinner glyph instead of the static play
  // triangle so a glancer sees "this is actually running" without
  // leaning in. Done / error stays static. The `tool-call-spinner`
  // class is keyframe-driven in `globals.css`; reduced-motion drops
  // the rotation but keeps the glyph so the user still gets a non-
  // checkmark / non-cross signal.
  const statusIcons: Record<ToolCallEntry['status'], string> = {
    running: '\u25E0', // top-half circle, reads as a spinner under rotation
    success: '\u2713', // check
    // Hollow circle reads as "soft / non-fatal" so the user's eye
    // doesn't snap to it the way it would on a red \u2717.
    error: isRecoverable ? '\u25CB' : '\u2717',
  };

  // Format tool input for display
  const inputSummary =
    toolCall.name === 'write_file'
      ? ((toolCall.input.path as string) ?? '')
      : toolCall.name === 'read_file'
        ? ((toolCall.input.path as string) ?? '')
        : toolCall.name === 'list_files'
          ? ((toolCall.input.path as string) ?? '')
          : toolCall.name === 'run_command'
            ? ((toolCall.input.command as string) ?? '')
            : JSON.stringify(toolCall.input).slice(0, 60);

  const friendlyLabel = buildToolCallFriendlyLabel(toolCall);

  return (
    <div
      className="rounded-lg border border-white/10 bg-black/20 px-3 py-2"
      // The `tool-call-noop` testid is kept (rather than renamed to
      // `tool-call-recoverable`) so existing snapshot tests pinning
      // the #200 path keep working. New shapes added in #216 use the
      // same shell \u2014 the testid still means "this is a soft amber
      // recoverable-error card, not a real failure".
      data-testid={isRecoverable ? 'tool-call-noop' : undefined}
    >
      <div className="flex items-center gap-2 text-xs">
        <span
          className={`${statusColors[toolCall.status]}${toolCall.status === 'running' ? ' tool-call-spinner' : ''}`}
          data-testid="tool-call-status-icon"
          data-tool-call-status={toolCall.status}
        >
          {statusIcons[toolCall.status]}
        </span>
        <span className="font-mono font-medium text-white/60">{toolCall.name}</span>
        <span className="truncate text-white/30">{inputSummary}</span>
      </div>
      {isRecoverable && recoverableLabel && (
        <p className="mt-1 text-xs italic text-amber-200/60">{recoverableLabel}</p>
      )}
      {!isRecoverable && friendlyLabel && toolCall.status !== 'running' && (
        <p className="mt-1 text-xs italic text-white/50" data-testid="tool-call-friendly-label">
          {friendlyLabel}
        </p>
      )}
      {!isRecoverable && !friendlyLabel && toolCall.output && toolCall.status !== 'running' && (
        // Issue #328: tool-call output is the load-bearing copy target
        // (paths, error blobs, command output). Explicit `select-text`
        // keeps it copyable even if a future parent reintroduces
        // `select-none`. `cursor-text` advertises the affordance — kept
        // consistent with the message-content shim and the error card
        // (Codex flagged the inconsistency on PR #341).
        <pre
          className="mt-1 max-h-24 cursor-text select-text overflow-auto whitespace-pre-wrap text-xs text-white/40"
          data-testid="chat-message-tool-result"
        >
          {toolCall.output.slice(0, 500)}
          {toolCall.output.length > 500 ? '...' : ''}
        </pre>
      )}
    </div>
  );
}

/**
 * Schema cap for `AskUserQuestion` from the Claude Agent SDK
 * (`questions: { maxItems: 4 }`). The SKILL is supposed to clamp
 * to this, but we've seen cases (alpha.74 quick-batch walkthrough,
 * issue #551) where 5+ questions reach the renderer. When that
 * happens we render a structured `SchemaErrorCard` instead of
 * crashing inside `questions.map(...)` with the minified `H.map`
 * error users can't interpret, and auto-rebound the gate Promise
 * so the agent sees a structured rejection rather than a hung
 * tool call.
 */
export const ASK_USER_QUESTION_MAX = 4;

/**
 * Sentinel key on the `answers` payload the renderer uses to tell
 * the agent that an `AskUserQuestion` tool call was structurally
 * invalid and could not be rendered. The Anthropic SDK's gate
 * accepts any `Record<string, string | string[]>` so we piggy-back
 * the existing `agent:answer-user-question` IPC rather than
 * introducing a new channel — keeps the fix contained to the
 * renderer per issue #551. The model treats the sentinel as
 * structured feedback ("this tool call was rejected by the client
 * because of schema-violation: …") and pivots, typically to
 * `BulkInterview` or two split AskUserQuestion calls.
 */
export const ASK_USER_QUESTION_SCHEMA_ERROR_KEY = '__askq_schema_error__';

/**
 * Shape of what `parseQuestions` returns. Either a successful parse
 * with the validated array, or a schema-violation result the card
 * surfaces via `SchemaErrorCard`. Returned tagged-union rather than
 * `ParsedQuestion[]` + side-channel so the renderer can pattern-
 * match without re-deriving the violation reason.
 */
type ParseQuestionsResult =
  | { kind: 'ok'; questions: ParsedQuestion[] }
  | { kind: 'empty' }
  | { kind: 'schema-violation'; reason: string; rawCount: number };

/**
 * Renders an `AskUserQuestion` tool-call as an interactive form
 * instead of the JSON-blob generic ToolCallCard. User clicks an
 * option button → its label is dispatched as the next user message,
 * which the model picks up on its next turn.
 *
 * MVP: the answer round-trips as a plain chat message (the model
 * has the question's full context from its own AskUserQuestion call,
 * so it understands the answer). Plan #2 Phase 4 will replace this
 * with the SDK's programmatic `customPermissionHandler` answer path
 * (`{ behavior: 'allow', updatedInput: { answers: {...} } }`) so the
 * answer goes directly into the SDK's structured-answer slot rather
 * than as free-form text.
 *
 * Shape (matches `AskUserQuestionInput` from the Claude Agent SDK):
 *   questions: [{
 *     question: string;          // the full prompt text
 *     header?: string;           // short chip label
 *     options: [{ label, description, preview? }, ...];
 *   }, ...]
 *
 * Defensive against the model under-populating the input — the agent
 * sometimes emits AskUserQuestion with empty `options`. In that case
 * we fall back to a plain "type your answer" textarea so the user
 * always has *some* way to respond.
 *
 * Defensive against schema violations (#551) — if the SDK input
 * carries more than `ASK_USER_QUESTION_MAX` questions (or has a
 * fundamentally broken shape), render a `SchemaErrorCard` and
 * rebound the gate Promise with a structured rejection rather
 * than letting the `.map()` blow up with a minified runtime
 * error.
 */
export function AskUserQuestionCard({ toolCall }: { toolCall: ToolCallEntry }): React.JSX.Element {
  // Track WHAT was answered, not just WHETHER, so we can render a
  // summary instead of disabled-looking buttons (user feedback
  // 2026-05-01: greyed-out buttons read as "broken UI").
  const [answersByIndex, setAnswersByIndex] = useState<Record<number, string>>({});
  const [submittedAnswers, setSubmittedAnswers] = useState<Record<string, string> | null>(null);
  const [freeTextByIndex, setFreeTextByIndex] = useState<Record<number, string>>({});

  // Pull the question shape out of the SDK input. Defensive parse —
  // the field is `unknown` at this layer so a malformed payload
  // can't blow up the chat. Returns a tagged-union so we can
  // distinguish "nothing parseable" (fall through to JSON card)
  // from "schema violation" (render structured error + auto-reject
  // the gate) — see #551.
  const parsed = parseQuestions(toolCall.input);

  if (parsed.kind === 'schema-violation') {
    // #551 — render a structured error card AND auto-rebound the
    // gate Promise so the agent receives a useful diagnostic
    // instead of timing out / hanging on an un-renderable tool call.
    //
    // #936 — pass the original tool input through so the card can
    // fire a `diagnostics:capture-renderer-issue` IPC and persist
    // the malformed payload under `.foundry/diagnostics/`. The
    // captured blob is what unblocks future triage: the previous
    // bug-report pipeline only had stack traces from minified
    // bundles, which lose the actual payload shape.
    return (
      <SchemaErrorCard
        toolUseID={toolCall.id}
        reason={parsed.reason}
        rawCount={parsed.rawCount}
        payload={toolCall.input}
      />
    );
  }

  if (parsed.kind === 'empty') {
    // Couldn't parse anything — fall through to the generic card so
    // the user at least sees the JSON. This is the rare-but-possible
    // case where the model emits a totally empty input.
    return <ToolCallCard toolCall={toolCall} />;
  }

  const questions = parsed.questions;

  const updateFreeText = (questionIndex: number, value: string): void => {
    setFreeTextByIndex((prev) => ({ ...prev, [questionIndex]: value }));
  };

  /**
   * Stages an answer and submits only after every question has a value.
   *
   * Args:
   *   questionIndex: Zero-based row being edited.
   *   answer: Candidate answer text from an option or text input.
   *   source: Whether the answer came from a preset option or custom text.
   *
   * Returns:
   *   Nothing.
   *
   * Raises:
   *   None.
   */
  const sendAnswer = (questionIndex: number, answer: string, source: 'preset' | 'custom'): void => {
    const trimmedAnswer = answer.trim();
    if (!trimmedAnswer || submittedAnswers) return;

    const nextAnswersByIndex = { ...answersByIndex, [questionIndex]: trimmedAnswer };
    setAnswersByIndex(nextAnswersByIndex);
    if (source === 'preset') {
      // Preset clicks supersede any draft custom text for that row.
      setFreeTextByIndex((prev) => ({ ...prev, [questionIndex]: '' }));
    }

    if (!questions.every((_, index) => nextAnswersByIndex[index]?.trim())) return;

    // Use the SDK-native gate path. `agent:answer-user-question`
    // resolves the held-open `canUseTool` Promise with `{ behavior:
    // 'allow', updatedInput: { answers } }`, the SDK then feeds the
    // tool's completion (carrying the answer) back into the model
    // and continues the turn. Verified working in SDK 0.2.126.
    //
    // **Why we removed the chat-message fallback** (the legacy
    // `foundry:ask-user-question:answer` CustomEvent path):
    //
    //   The original card from PR #129 fired BOTH the IPC AND a
    //   CustomEvent that the workspace listener turned into a
    //   regular `agent:send`. That was belt-and-braces when the
    //   IPC gate path was newly wired and its semantics murky.
    //
    //   With both paths firing, the SDK's gate resolution
    //   continues the existing turn (`session.inFlight = true`),
    //   and milliseconds later the chat-message dispatch hits
    //   `agent:send` — which bounces with the in-flight bouncer
    //   error. User-reported on alpha.42 mid-build-game flow:
    //   click an option, immediately see the red "Another message
    //   is already in flight" bubble.
    //
    //   Drop the dispatch. If the gate path EVER fails to deliver
    //   the answer (it doesn't, currently), that's a fixable bug
    //   in agent-permissions / the SDK gate — not something to
    //   paper over with a misrouted chat message.
    const answers = questions.reduce<Record<string, string>>((acc, question, index) => {
      acc[question.question] = nextAnswersByIndex[index];
      return acc;
    }, {});
    setSubmittedAnswers(answers);
    void window.crucible
      .invoke('agent:answer-user-question', { toolUseID: toolCall.id, answers })
      .catch(() => {
        // Gate IPC failed — log only. We deliberately no longer
        // fall back to firing a chat message because that bounces
        // off the still-active in-flight turn. If this path turns
        // out to be flaky, the right fix is to harden the gate.
        console.error('[chat] agent:answer-user-question IPC failed');
      });
  };

  // After the user answers, replace the entire form with a clean
  // summary card showing what was picked. Avoids the "ghosted
  // disabled controls" UX that read as broken (user report
  // 2026-05-01: "everything's disabled out"). When the SDK then
  // emits its canonical tool_call chunk and we upsert the input
  // (e.g. previously-empty options become populated), the card
  // STAYS in summary mode rather than reverting to a fresh-form
  // appearance — the user already gave their answer.
  if (submittedAnswers) {
    const submittedEntries = Object.entries(submittedAnswers);
    return (
      <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
        <div className="flex items-start gap-2">
          <span className="mt-0.5 text-emerald-300/80">✓</span>
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wide text-white/40">You answered</p>
            <div className="mt-1 space-y-2">
              {submittedEntries.map(([question, answer]) => (
                <div key={question}>
                  <p className="text-sm font-medium text-white/90">{answer}</p>
                  <p className="mt-0.5 text-xs text-white/40 italic">
                    re: <span className="not-italic">{question}</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-crucible-accent/30 bg-crucible-accent/5 px-3 py-3">
      {questions.map((q, qi) => {
        const rowAnswer = answersByIndex[qi];
        const rowFreeText = freeTextByIndex[qi] ?? '';
        return (
          <div key={qi} className={qi === 0 ? '' : 'mt-4 border-t border-white/10 pt-4'}>
            {q.header && (
              <span className="mb-1 inline-block rounded-full bg-crucible-accent/20 px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wide text-crucible-accent-hover">
                {q.header}
              </span>
            )}
            <p className="mb-2 text-sm font-medium text-white/90">{q.question}</p>
            {rowAnswer && (
              <p className="mb-2 rounded border border-emerald-500/25 bg-emerald-500/5 px-2 py-1 text-xs text-white/50">
                Current answer: <span className="font-medium text-emerald-100/90">{rowAnswer}</span>
              </p>
            )}
            {q.options.length > 0 ? (
              <div className="grid gap-1.5">
                {q.options.map((opt, oi) => {
                  const isSelected = rowAnswer === opt.label;
                  return (
                    <button
                      key={oi}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => sendAnswer(qi, opt.label, 'preset')}
                      className={`group flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors ${
                        isSelected
                          ? 'border-emerald-400/60 bg-emerald-500/10 hover:bg-emerald-500/15'
                          : 'border-white/10 bg-black/20 hover:border-crucible-accent/40 hover:bg-crucible-accent/10'
                      }`}
                    >
                      <span className="flex w-full items-center justify-between gap-2">
                        <span className="text-sm font-medium text-white/90 group-hover:text-white">
                          {opt.label}
                        </span>
                        {isSelected && (
                          <span
                            aria-hidden="true"
                            className="text-[0.625rem] font-medium uppercase tracking-wide text-emerald-200/80"
                          >
                            Selected
                          </span>
                        )}
                      </span>
                      {opt.description && (
                        <span className="mt-0.5 text-xs text-white/50">{opt.description}</span>
                      )}
                    </button>
                  );
                })}
                {/* "Other" affordance — the SDK prompt says we should
                  always provide one. */}
                <details className="rounded-lg border border-white/10 bg-black/10 px-3 py-2">
                  <summary className="cursor-pointer text-xs text-white/40 hover:text-white/60">
                    None of these — type my own answer
                  </summary>
                  <div className="mt-2 flex gap-2">
                    <input
                      type="text"
                      value={rowFreeText}
                      onChange={(e) => updateFreeText(qi, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') sendAnswer(qi, rowFreeText, 'custom');
                      }}
                      placeholder="Type your answer..."
                      className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-xs focus:border-crucible-accent focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => sendAnswer(qi, rowFreeText, 'custom')}
                      disabled={!rowFreeText.trim()}
                      className="rounded bg-crucible-accent px-3 py-1 text-xs font-medium text-white disabled:opacity-40"
                    >
                      Send
                    </button>
                  </div>
                </details>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={rowFreeText}
                  onChange={(e) => updateFreeText(qi, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') sendAnswer(qi, rowFreeText, 'custom');
                  }}
                  placeholder="Type your answer..."
                  className="flex-1 rounded border border-white/10 bg-white/5 px-2 py-1 text-sm focus:border-crucible-accent focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => sendAnswer(qi, rowFreeText, 'custom')}
                  disabled={!rowFreeText.trim()}
                  className="rounded bg-crucible-accent px-3 py-1 text-sm font-medium text-white disabled:opacity-40"
                >
                  Send
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Parsed shape of a question we render. Matches the SDK type but
 * loosened (all fields optional) so a partially-populated input
 * still produces something sensible.
 */
interface ParsedQuestion {
  question: string;
  header?: string;
  options: { label: string; description?: string; preview?: string }[];
}

export function parseQuestions(input: Record<string, unknown>): ParseQuestionsResult {
  const raw = (input as { questions?: unknown })?.questions;
  // Missing/non-array `questions` is a hard schema violation —
  // there's nothing for us to render and the SDK contract says
  // `questions` is required + must be an array. Surface it as a
  // structured error so the agent gets a useful diagnostic
  // rather than the renderer silently falling back to the JSON
  // dump (which the user can't act on either).
  if (!Array.isArray(raw)) {
    return {
      kind: 'schema-violation',
      reason: 'missing-or-invalid-questions',
      rawCount: 0,
    };
  }
  // Schema cap: `questions: { maxItems: 4 }`. Trip BEFORE the
  // per-item parse loop so the user / agent gets the cap message
  // even when the over-cap items are individually well-formed.
  if (raw.length > ASK_USER_QUESTION_MAX) {
    return {
      kind: 'schema-violation',
      reason: 'too-many-questions',
      rawCount: raw.length,
    };
  }
  const out: ParsedQuestion[] = [];
  for (const q of raw) {
    if (!q || typeof q !== 'object') continue;
    const qq = q as Record<string, unknown>;
    const question = typeof qq.question === 'string' ? qq.question : '';
    if (!question) continue;
    const header = typeof qq.header === 'string' ? qq.header : undefined;
    const optionsRaw = qq.options;
    const options: ParsedQuestion['options'] = [];
    if (Array.isArray(optionsRaw)) {
      for (const o of optionsRaw) {
        if (!o || typeof o !== 'object') continue;
        const oo = o as Record<string, unknown>;
        const label = typeof oo.label === 'string' ? oo.label : undefined;
        if (!label) continue;
        options.push({
          label,
          description: typeof oo.description === 'string' ? oo.description : undefined,
          preview: typeof oo.preview === 'string' ? oo.preview : undefined,
        });
      }
    }
    out.push({ question, header, options });
  }
  // Post-parse: if every item was filtered out (no `question`
  // strings survived) treat it as empty rather than a violation
  // — the existing JSON-card fallback handles that case fine and
  // we don't want to spam the agent with a false-positive
  // rejection for a fundamentally empty input.
  if (out.length === 0) return { kind: 'empty' };
  return { kind: 'ok', questions: out };
}

/**
 * Decision for what a composer send should do while an `AskUserQuestion`
 * gate is open — the "Waiting on your input…" contradiction fix.
 *
 * The bug: the status row shows "Waiting on your input…" off the run-state
 * (`deriveChatStatusRow` → `waiting-on-user`), but the composer's
 * `user/send` queues purely on `isAiTyping` (`chat/state.ts`). During a gate
 * the turn is paused mid-tool, so `isAiTyping` stays true → the user's typed
 * answer lands in the QUEUED chip and never reaches the paused agent.
 *
 * The fix routes a typed reply to the open gate's answer — the SAME path
 * `AskUserQuestionCard` uses (`agent:answer-user-question` IPC), which RESUMES
 * the paused turn rather than starting a new one, so it does NOT bounce off
 * the in-flight turn (the #170 / alpha.42 "Another message is already in
 * flight" failure that killed the old chat-message fallback).
 *
 * Scope: a SINGLE pending question — the unambiguous clarifying-gate shape a
 * free-text composer reply can actually answer (and the case users hit). A
 * multi-question gate keeps the structured card as its answer surface, so we
 * fall through to the normal send/queue path there.
 */
export type ComposerSendDecision =
  | { readonly kind: 'send' }
  | {
      readonly kind: 'answer-question';
      readonly toolUseID: string;
      readonly answers: Record<string, string>;
    };

export function decideComposerSend(args: {
  readonly pinnedAskUserQuestion: ToolCallEntry | null;
  readonly text: string;
}): ComposerSendDecision {
  const text = args.text.trim();
  const pinned = args.pinnedAskUserQuestion;
  if (!text || !pinned) return { kind: 'send' };
  const parsed = parseQuestions(pinned.input ?? {});
  // Only route the free-text reply when there is exactly ONE question to
  // answer; anything else (no questions, schema violation, multi-question
  // card) keeps the existing send path + structured card.
  if (parsed.kind !== 'ok' || parsed.questions.length !== 1) return { kind: 'send' };
  return {
    kind: 'answer-question',
    toolUseID: pinned.id,
    answers: { [parsed.questions[0].question]: text },
  };
}

/**
 * #551 — error card the renderer shows when an `AskUserQuestion`
 * tool call is structurally invalid (too many questions, missing
 * `questions` array, etc). Two responsibilities:
 *
 *   1. Render a user-facing explanation of what went wrong so the
 *      chat doesn't just blank out / crash with a `H.map` minified
 *      error.
 *   2. Auto-rebound the SDK's gate Promise by invoking
 *      `agent:answer-user-question` with the
 *      `ASK_USER_QUESTION_SCHEMA_ERROR_KEY` sentinel. The agent
 *      receives this as the answer payload and pivots (typically to
 *      `BulkInterview` or two split sub-4 AskUserQuestion calls).
 *
 * The IPC fire is wrapped in a one-shot `useEffect` keyed on
 * `toolUseID` so a re-render (e.g. parent message-list update)
 * doesn't double-fire. The gate's `resolveAnswer` is idempotent on
 * stale IDs but we'd rather not flood it.
 */
export function SchemaErrorCard({
  toolUseID,
  reason,
  rawCount,
  payload,
}: {
  toolUseID: string;
  reason: string;
  rawCount: number;
  /** Issue #936 — the original `toolCall.input` that failed the
   *  schema check. Forwarded to `diagnostics:capture-renderer-issue`
   *  so we can repro from real malformed payloads instead of
   *  guessing at minified runtime errors. Optional so existing
   *  tests / callers without the new field keep working — the
   *  capture useEffect simply skips when payload is undefined. */
  payload?: unknown;
}): React.JSX.Element {
  // Fire the gate-rebound IPC exactly once per toolUseID. Stuffing
  // this in a useEffect keeps the render pure and lets vitest's
  // `node` environment evaluate the SSR output without crashing —
  // useEffect doesn't fire under `renderToStaticMarkup`, so the
  // unit test asserts the IPC contract by invoking the effect's
  // payload directly (see exported `buildSchemaErrorAnswers`).
  useEffect(() => {
    const answers = buildSchemaErrorAnswers(reason, rawCount);
    void window.crucible.invoke('agent:answer-user-question', { toolUseID, answers }).catch(() => {
      // Same log-only convention as the happy-path sendAnswer in
      // AskUserQuestionCard. The user already sees the schema
      // error card; a flaky IPC shouldn't promote that to a UI
      // failure too.
      console.error('[chat] agent:answer-user-question rejection IPC failed');
    });
  }, [toolUseID, reason, rawCount]);

  // #936 — fire-and-forget capture of the malformed `AskUserQuestion`
  // input. `gameId` comes via context from `<ChatErrorBoundary>`'s
  // surface provider; without it (a future render path mounting the
  // card outside the boundary) we skip the capture rather than
  // pollute a global "no-game" diagnostics directory. Best-effort —
  // a failed IPC stays log-only, the gate-rebound above is the
  // load-bearing recovery path.
  const { gameId: surfaceGameId } = useContext(ChatSurfaceContext);
  useEffect(() => {
    if (!surfaceGameId || payload === undefined) return;
    void window.crucible
      .invoke('diagnostics:capture-renderer-issue', {
        gameId: surfaceGameId,
        kind: 'malformed-askq',
        message: reason,
        payload,
        toolUseID,
      })
      .catch(() => {
        // Log-only — same convention as the gate-rebound above.
        console.error('[chat] diagnostics:capture-renderer-issue failed');
      });
  }, [surfaceGameId, toolUseID, reason, payload]);

  const userMessage =
    reason === 'too-many-questions'
      ? `Foundry couldn't render this AskUserQuestion: ${rawCount} questions were requested but the SDK schema caps the call at ${ASK_USER_QUESTION_MAX}. The agent has been told to retry by splitting into multiple smaller AskUserQuestion calls.`
      : `Foundry couldn't render this AskUserQuestion: the payload was missing a valid \`questions\` array. The agent has been told to retry.`;

  return (
    <div role="alert" className="rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 text-amber-300/80">!</span>
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-amber-200/80">
            Tool call rejected — schema violation
          </p>
          <p className="mt-0.5 text-sm text-white/80">{userMessage}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * Build the `answers` payload the `SchemaErrorCard` rebounds to
 * the SDK gate. Exported separately so the unit test can pin the
 * exact shape the agent receives without having to mock React's
 * effect scheduler — the message text is part of the rejection
 * contract.
 */
export function buildSchemaErrorAnswers(reason: string, rawCount: number): Record<string, string> {
  // Recovery hint copy — Codex round-1 LOW catch:
  //
  //   The main-side `BULK_INTERVIEW_INTERCEPT_PROMPT` only allows
  //   `BulkInterview` in express-mode Phase A; outside that, the
  //   adapter intercepts and routes the model back to
  //   `AskUserQuestion` anyway (see `agent-adapter.ts` →
  //   `BULK_INTERVIEW_INTERCEPT_PROMPT`). If we lead with
  //   "use BulkInterview", we'll steer the model into a known
  //   intercept path 9 times out of 10. So the message leads with
  //   the always-safe recovery (split into ≤4-question
  //   AskUserQuestion calls) and only mentions BulkInterview as a
  //   conditional fallback for the narrow phase where it's
  //   permitted.
  const message =
    reason === 'too-many-questions'
      ? `AskUserQuestion received ${rawCount} questions; the SDK schema caps this at ${ASK_USER_QUESTION_MAX} (maxItems). Foundry rejected the call. Retry by splitting into multiple AskUserQuestion calls of at most ${ASK_USER_QUESTION_MAX} questions each. (Use BulkInterview only if it's permitted for the current phase — outside express-mode Phase A it will be intercepted.)`
      : `AskUserQuestion was missing a valid \`questions\` array. Foundry rejected the call. Retry with a payload shaped { questions: [{ question, header?, options: [{ label, description?, preview? }] }, ...] } where questions.length is between 1 and ${ASK_USER_QUESTION_MAX}.`;
  return {
    [ASK_USER_QUESTION_SCHEMA_ERROR_KEY]: JSON.stringify({
      ok: false,
      reason: 'schema-violation',
      detail: reason,
      max: ASK_USER_QUESTION_MAX,
      received: rawCount,
      message,
    }),
  };
}

/**
 * `<LatestReplyPanel>` — Crucible #855 (PJ's report).
 *
 * Pins the most recent complete assistant message just above the
 * chat input so the orchestrator's actionable summary ("what landed
 * this invocation / what's outstanding for the next / files of
 * interest") is visible without scrolling. Pre-#855 PJ reported
 * that long agent runs produced responses where the actionable text
 * was buried mid-message and required scrolling to find.
 *
 * Behaviour contract:
 *   - Renders nothing while `isAiTyping` is true. The active turn's
 *     content is already visible in the live chat scroll; the panel
 *     would otherwise duplicate streaming chunks.
 *   - Renders nothing when no assistant message with non-empty
 *     content exists yet (e.g. a brand-new game with no agent
 *     output).
 *   - Otherwise renders an aside above the input strip, containing
 *     the latest assistant message's markdown content. Tool-call
 *     entries are stored in a separate `toolCalls` field on the
 *     message and are intentionally not surfaced here — the panel
 *     is the actionable summary, not the call trace.
 *
 * Collapse state is persisted in `localStorage` per `gameId` so the
 * user's preference survives game switches AND remounts within the
 * same game. The key is namespaced to avoid colliding with other
 * per-game panel preferences.
 *
 * The panel does NOT replace the corresponding chat bubble — the
 * latest reply still scrolls past in the main chat. The duplication
 * is intentional: chat shows the linear timeline; the panel pins
 * the most-recent useful artefact in a stable place. PJ's screenshot
 * showed long responses that scrolled past the actionable summary;
 * pinning the panel guarantees the summary is one glance away.
 */
export function LatestReplyPanel({
  gameId,
  messages,
  isAiTyping,
}: {
  gameId: string;
  messages: readonly ChatMessage[];
  isAiTyping: boolean;
}): React.JSX.Element | null {
  const storageKey = `latest-reply-panel:collapsed:${gameId}`;

  // Initialise collapse state from localStorage. The lazy initialiser
  // runs once on mount; subsequent toggles persist via the toggle
  // handler below. Wrapped in try/catch because Electron's renderer
  // can in principle disable storage (private-mode tab, quota error,
  // etc.) — the panel falls back to "expanded" rather than throwing.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  // When gameId changes the storage key changes; the existing state
  // is stale (was the previous game's preference). Re-read from the
  // new key. Persistence on toggle goes through `handleToggle` (not
  // a write-effect on `collapsed`) so the read-on-key-change effect
  // can't race the write-on-collapse-change effect and clobber the
  // newly-loaded value.
  useEffect(() => {
    try {
      setCollapsed(window.localStorage.getItem(storageKey) === '1');
    } catch {
      setCollapsed(false);
    }
  }, [storageKey]);

  const handleToggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        window.localStorage.setItem(storageKey, next ? '1' : '0');
      } catch {
        // best-effort; storage errors are non-fatal for this
        // affordance.
      }
      return next;
    });
  }, [storageKey]);

  // Find the latest assistant message with non-empty content. We
  // scan from the END so a long history doesn't pay a linear cost
  // for nothing — most calls return on the first or second iteration.
  const latest = useMemo<ChatMessage | null>(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role !== 'assistant') continue;
      if (typeof m.content !== 'string') continue;
      if (m.content.trim().length === 0) continue;
      return m;
    }
    return null;
  }, [messages]);

  // Codex round-1 review of PR #867 — the body's `overflow-y-auto`
  // container opens at `scrollTop = 0` by default, so for any
  // assistant reply long enough to trigger the `max-h-[30vh]` cap
  // the user would see the TOP of the message and still have to
  // scroll inside the panel to reach the orchestrator's actionable
  // summary at the END of the message. That's exactly the
  // scrolling-to-find-the-summary problem PJ filed (#855) and the
  // panel is meant to solve.
  //
  // Pin the body to `scrollTop = scrollHeight` whenever:
  //   - the latest message changes (a new reply landed)
  //   - the panel transitions out of the collapsed state (Show)
  //   - the panel transitions out of the isAiTyping=true hide
  //     branch (turn complete, OR an IPC send failure that flips
  //     isAiTyping back without a new message — Cursor Bugbot
  //     round-1 catch on PR #867: the body div is destroyed when
  //     the panel returns null, so it remounts at scrollTop=0; we
  //     need isAiTyping in the deps so the layout effect fires
  //     when the body is re-created)
  //
  // `useLayoutEffect` runs after the DOM is laid out but before
  // paint, so the user never sees the top-of-message frame. The
  // user can scroll up inside the panel to read earlier parts of
  // the reply — the pin only fires on the dependencies listed.
  const bodyRef = useRef<HTMLDivElement>(null);
  const latestMessageId = latest?.id ?? null;
  useLayoutEffect(() => {
    if (collapsed) return;
    const el = bodyRef.current;
    if (el === null) return;
    el.scrollTop = el.scrollHeight;
  }, [collapsed, isAiTyping, latestMessageId]);

  // Hide while a turn is in flight — the live chat scroll already
  // shows the streaming content, and the panel would otherwise
  // re-render on every chunk for no user benefit. Hide also when
  // there's no qualifying message yet.
  if (isAiTyping) return null;
  if (latest === null) return null;

  return (
    <aside
      data-testid="latest-reply-panel"
      aria-label="Latest reply"
      className="mb-2 rounded-lg border border-white/10 bg-crucible-surface/60"
    >
      <header className="flex items-center justify-between px-3 py-1.5">
        <h2 className="text-xs font-medium uppercase tracking-wide text-white/50">Latest reply</h2>
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={!collapsed}
          aria-controls="latest-reply-panel-body"
          data-testid="latest-reply-panel-toggle"
          className="rounded px-2 py-0.5 text-xs text-white/60 transition-colors hover:bg-white/10 hover:text-white/90"
        >
          {collapsed ? 'Show' : 'Hide'}
        </button>
      </header>
      {!collapsed && (
        <div
          ref={bodyRef}
          id="latest-reply-panel-body"
          data-testid="latest-reply-panel-body"
          // Max-height with internal scroll keeps the panel from
          // dominating the viewport on very long responses. The
          // body is auto-scrolled to the bottom on every content
          // change so the orchestrator's actionable summary (which
          // lives at the end of the message) is the visible portion
          // — see the #855 Codex round-1 note on `bodyRef` for the
          // rationale.
          className="max-h-[30vh] select-text overflow-y-auto border-t border-white/5 px-3 py-2 text-sm leading-relaxed text-white/85"
        >
          <MessageContent content={latest.content} />
        </div>
      )}
    </aside>
  );
}

/**
 * Render message content as Markdown — paragraphs, bold, italic,
 * unordered lists, fenced code blocks, inline code. We hand-roll a
 * tiny renderer rather than pulling in `react-markdown` because the
 * surface is small and the dep chain (remark + rehype + plugins)
 * would be 100x the size of the actual logic.
 *
 * What's supported (deliberately narrow):
 *   - Triple-backtick code fences with optional language tag
 *   - `inline code`
 *   - `**bold**` and `*italic*` (italic uses single asterisk and
 *     skips when at the start of a line so list bullets aren't
 *     mis-parsed)
 *   - `- item` / `* item` unordered lists at the start of a line
 *   - Blank lines split paragraphs
 *
 * What's NOT supported:
 *   - Headings, tables, links, images, blockquotes, ordered lists,
 *     nested lists. The agent rarely emits these in chat and the
 *     CommonMark complexity isn't worth it.
 */
function MessageContent({ content }: { content: string }): React.JSX.Element {
  // First split on triple-backtick code fences — they're the only
  // multi-line construct we care about that nests around other
  // formatting.
  const parts = content.split(/(```[\s\S]*?```)/g);

  // `overflow-wrap: anywhere` (inherited, so the inline <code> chips pick it
  // up) breaks long unbreakable tokens — file paths, URLs, IDs — that would
  // otherwise push the message past the panel width and make the chat scroll
  // horizontally. `min-w-0` lets this block shrink inside its flex ancestors.
  // Fenced ``` blocks keep their own overflow-x-auto (white-space: pre — they
  // scroll internally rather than wrap).
  return (
    <div className="min-w-0 [overflow-wrap:anywhere]">
      {parts.map((part, i) => {
        if (part.startsWith('```') && part.endsWith('```')) {
          // Strip optional language tag on the first line.
          const inner = part.slice(3, -3).replace(/^[\w-]+\n/, '');
          return (
            <pre
              key={i}
              className="my-3 min-w-0 overflow-x-auto rounded-xl p-4 font-mono text-[13px] leading-relaxed"
              style={{ background: 'var(--bg-elev-3)', color: 'var(--text-2)' }}
            >
              <code>{inner}</code>
            </pre>
          );
        }
        return <MarkdownProse key={i} text={part} />;
      })}
    </div>
  );
}

/**
 * Render a non-code-fence segment of markdown text. Splits into
 * paragraphs on blank lines, runs each paragraph through the inline
 * formatter, and renders a list when a paragraph is entirely
 * `- item` lines.
 */
function MarkdownProse({ text }: { text: string }): React.JSX.Element {
  const blocks = text.split(/\n{2,}/).filter((b) => b.trim().length > 0);
  return (
    <>
      {blocks.map((block, i) => {
        const lines = block.split('\n');
        // List detection: every non-blank line starts with `- ` or `* `.
        const isList = lines.every((l) => /^\s*[-*]\s+/.test(l));
        if (isList) {
          return (
            <ul key={i} className="my-4 space-y-1.5 pl-5" style={{ listStyleType: 'disc' }}>
              {lines.map((l, j) => (
                <li key={j} className="pl-1">
                  {renderInline(l.replace(/^\s*[-*]\s+/, ''))}
                </li>
              ))}
            </ul>
          );
        }
        // Ordered list detection: every non-blank line starts with `1. `, `2. `, etc.
        const isOrderedList = lines.every((l) => /^\s*\d+\.\s+/.test(l));
        if (isOrderedList) {
          return (
            <ol key={i} className="my-4 space-y-2 pl-5" style={{ listStyleType: 'decimal' }}>
              {lines.map((l, j) => (
                <li key={j} className="pl-1">
                  {renderInline(l.replace(/^\s*\d+\.\s+/, ''))}
                </li>
              ))}
            </ol>
          );
        }
        return (
          <p key={i} className={i === 0 ? '' : 'mt-5'}>
            {renderInline(block)}
          </p>
        );
      })}
    </>
  );
}

/**
 * Match an `http://` or `https://` URL through the next whitespace
 * (or backtick / closing bracket / end of string). The trailing-
 * punctuation strip below trims things like `.,;:!?)` that are
 * almost always sentence punctuation, not part of the URL.
 */
const URL_PATTERN = /https?:\/\/[^\s`)\]]+/;

/**
 * Strip a URL's trailing punctuation that's almost certainly part
 * of the surrounding sentence (e.g. `Visit https://example.com.` —
 * the `.` ends the sentence, not the path). Conservative: only
 * strips a single trailing punctuation character from a known
 * bad-tail set so we don't break URLs that legitimately end in `/`
 * or query strings ending in `?`. Returns the trimmed URL plus
 * the consumed length so the caller knows how far to advance.
 */
function trimUrlTail(url: string): { url: string; len: number } {
  const m = /[.,;:!?)\]]+$/.exec(url);
  if (m) {
    return { url: url.slice(0, m.index), len: m.index };
  }
  return { url, len: url.length };
}

/**
 * Render an inline anchor that opens in the system browser. Relies
 * on `setWindowOpenHandler` in main/index.ts to intercept the
 * `target="_blank"` navigation and route through `shell.openExternal`
 * — that handler is already wired for the About-dialog and other
 * external links elsewhere in the app.
 */
function ChatLink({
  href,
  children,
  className,
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className={className ?? 'text-crucible-accent-hover underline-offset-2 hover:underline'}
    >
      {children}
    </a>
  );
}

/**
 * Walk a single line of text and replace bold / italic / inline-code
 * / URL runs with React nodes. Uses a tokeniser rather than chained
 * regexes so a `**word*` pattern doesn't cross-fire bold and italic.
 *
 * URL handling has two routes:
 *   - Bare URL in plain text → wrapped in a styled `<a>` link
 *   - URL inside backticks (e.g. `http://localhost:3000`) → wrapped
 *     in an `<a>` AND keeps the inline-code styling, since users
 *     often format developer-facing URLs as code in markdown.
 */
function renderInline(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let i = 0;
  let buf = '';
  let key = 0;
  const flush = (): void => {
    if (buf) {
      // Convert single line breaks (within a paragraph) to <br>.
      const segs = buf.split('\n');
      segs.forEach((s, idx) => {
        if (idx > 0) out.push(<br key={`br-${key++}`} />);
        if (s) out.push(s);
      });
      buf = '';
    }
  };
  while (i < text.length) {
    // Inline code — highest priority, escapes everything else.
    if (text[i] === '`') {
      const end = text.indexOf('`', i + 1);
      if (end > i) {
        flush();
        const inner = text.slice(i + 1, end);
        // Linkify if the entire code-span is a URL — common for
        // developer URLs that the agent tends to wrap in backticks
        // (`http://localhost:3000` etc.).
        const codeClass =
          'rounded-md px-1.5 py-0.5 font-mono text-[13px]' + ' bg-white/[0.08] text-white/80';
        if (/^https?:\/\/\S+$/.test(inner)) {
          out.push(
            <ChatLink key={key++} href={inner} className={`${codeClass} hover:underline`}>
              {inner}
            </ChatLink>,
          );
        } else {
          out.push(
            <code key={key++} className={codeClass}>
              {inner}
            </code>,
          );
        }
        i = end + 1;
        continue;
      }
    }
    // Bold — `**...**`
    if (text[i] === '*' && text[i + 1] === '*') {
      const end = text.indexOf('**', i + 2);
      if (end > i + 1) {
        flush();
        out.push(<strong key={key++}>{renderInline(text.slice(i + 2, end))}</strong>);
        i = end + 2;
        continue;
      }
    }
    // Italic — `*...*`. Require non-space after opener and non-space
    // before closer so unbalanced asterisks (e.g. list bullets, `5 *
    // 3`) don't trigger.
    if (text[i] === '*' && text[i + 1] !== '*' && text[i + 1] !== ' ') {
      const end = text.indexOf('*', i + 1);
      if (end > i && text[end - 1] !== ' ') {
        flush();
        out.push(<em key={key++}>{renderInline(text.slice(i + 1, end))}</em>);
        i = end + 1;
        continue;
      }
    }
    // Bare URL — http:// or https://. Match against the slice
    // starting at the cursor so we don't have to scan the whole
    // string from the top each iteration.
    if (text[i] === 'h') {
      const m = URL_PATTERN.exec(text.slice(i));
      if (m && m.index === 0) {
        const trimmed = trimUrlTail(m[0]);
        if (trimmed.url.length > 8) {
          flush();
          out.push(
            <ChatLink key={key++} href={trimmed.url}>
              {trimmed.url}
            </ChatLink>,
          );
          i += trimmed.len;
          continue;
        }
      }
    }
    buf += text[i];
    i++;
  }
  flush();
  return out;
}

function TypingIndicator(): React.JSX.Element {
  return (
    <div className="flex items-center gap-1">
      <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-white/30 [animation-delay:0ms]" />
      <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-white/30 [animation-delay:150ms]" />
      <span className="inline-block h-2 w-2 animate-bounce rounded-full bg-white/30 [animation-delay:300ms]" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Resize Handle (shared)
// ---------------------------------------------------------------------------

function ResizeHandle({
  side,
  currentSize,
  minSize,
  maxSize,
  onResize,
}: {
  side: 'left' | 'right';
  currentSize: number;
  minSize: number;
  maxSize: number;
  onResize: (size: number) => void;
}): React.JSX.Element {
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX = e.clientX;
      const startSize = currentSize;

      const onMouseMove = (moveEvent: MouseEvent): void => {
        const delta = moveEvent.clientX - startX;
        const newSize = side === 'right' ? startSize + delta : startSize - delta;
        onResize(Math.max(minSize, Math.min(maxSize, newSize)));
      };

      const onMouseUp = (): void => {
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [side, currentSize, minSize, maxSize, onResize],
  );

  return (
    <div
      onMouseDown={handleMouseDown}
      className={`absolute top-0 ${
        side === 'right' ? 'right-0 translate-x-1/2' : 'left-0 -translate-x-1/2'
      } z-10 h-full w-1.5 cursor-col-resize hover:bg-crucible-accent/30 active:bg-crucible-accent/50`}
    />
  );
}
